// Lightweight i18n: no runtime deps. Provides <I18nProvider>, useT(), useLang().
// EN is canonical. FR is fully translated. ES + PT cover navigation, hero and
// CTAs; missing keys fall back to EN.
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

export const LANGS = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
];
export const LANG_CODES = LANGS.map((l) => l.code);
export const DEFAULT_LANG = "en";
const STORAGE_KEY = "geovinum.lang.v1";

const en = {
  // Navigation
  "nav.maps": "Maps",
  "nav.app": "App",
  "nav.interactive": "Interactive",
  "nav.experience": "Experience",
  "nav.pro.overview": "For pros — overview",
  "nav.pro_long": "For pros",
  "nav.explorer.all": "See all",
  "nav.explorer.appellations": "Regions · Vineyards · Appellations",
  "nav.explorer.cepages": "Grape varieties",
  "nav.explorer.producers": "Producers",
  "nav.explorer.articles": "Articles",
  "nav.explorer.guides_divider": "Guides & references",
  "nav.explorer.glossaire": "Wine glossary (506 terms)",
  "nav.explorer.cartographie": "Wine cartography",
  "nav.explorer.geographie": "Wine geography",
  "nav.explorer.appellations_pil": "Wine appellations",
  "nav.explorer.terroirs": "Wine terroirs",
  "nav.learn": "Learn",
  "nav.play": "Play",
  "nav.journal": "Journal",
  "nav.pro": "Pro",
  "nav.login": "Log in",
  "nav.logout": "Log out",
  "nav.account": "My account",
  "nav.chat": "Chat with us",
  "nav.search_placeholder": "Search maps, regions, grapes, styles…",
  "nav.shop": "Shop",
  "common.home": "Home",
  "common.search": "Search",
  "common.loading": "Loading…",
  "common.read_more": "Read more",
  "common.back": "Back",
  "common.see_all": "See all",
  "common.from": "From",
  "common.questions": "questions",
  "common.no_results": "No results.",
  "common.try_another": "Try another region, grape, or style.",
  "cmdk.quick_links": "Quick links",
  "cmdk.navigate": "Navigate",
  "cmdk.select": "Open",
  "common.you_might_also": "You might also like",
  "common.related": "Related",

  // Buttons
  "cta.explore_maps": "Explore maps",
  "cta.start_learning": "Start learning",
  "cta.start_learning_2min": "Start learning in 2 min",
  "cta.start_playing": "Start playing",
  "cta.discover_pro": "Discover geoVINUM Pro",
  "cta.contact_sales": "Contact sales",
  "cta.buy_now": "Buy now",
  "cta.add_cart": "Add to cart",
  "cta.view_all_maps": "View all maps",
  "cta.view_map": "View map",
  "cta.read_lesson": "Read lesson",
  "cta.continue": "Continue",
  "cta.start_quiz": "Start quiz",
  "cta.take_quiz": "Take quiz",
  "cta.play": "Play",
  "cta.go_dashboard": "Go to dashboard",
  "cta.send_link": "Send link",
  "cta.reset_password": "Reset password",
  "cta.get_started": "Get started",
  "cta.log_in": "Log in",
  "cta.back_to_journal": "Back to the journal",
  "cta.back_to_login": "Back to login",

  // Home — hero
  "home.hero.eyebrow": "World Wine Maps · by geoVINUM",
  "home.hero.h1a": "Understand wine",
  "home.hero.h1b": "visually.",
  "home.hero.tagline": "Maps. Learning. Play.",
  "home.hero.line": "Wine is complex. We make it visual.",
  "home.hero.support": "Explore wine regions, understand grapes, and learn in minutes.",
  "home.hero.rating": "4.8/5 from wine lovers",
  "home.hero.secure_payment": "Secure payment",
  "home.hero.shipping": "Worldwide shipping",

  // Home — story
  "story.a": "Wine is often complicated.",
  "story.b": "Regions, grapes, terroirs…",
  "story.c": "geoVINUM makes it simple.",

  // How it works
  "how.eyebrow": "How it works",
  "how.title_a": "Three ways to fall for",
  "how.title_b": "wine",
  "how.see.title": "See",
  "how.see.text": "Wine regions become clear instantly.",
  "how.learn.title": "Learn",
  "how.learn.text": "Understand what actually matters.",
  "how.play.title": "Play",
  "how.play.text": "Remember it without effort.",

  // Home — sections
  "home.maps.eyebrow": "The collection",
  "home.maps.title_a": "Explore wine through",
  "home.maps.title_b": "maps",
  "home.learn.title_a": "Short lessons,",
  "home.learn.title_b": "big insights.",
  "home.learn.text": "Understand a wine region in 3 minutes.",
  "home.learn.lesson_label": "Lesson 03",
  "home.learn.lesson_minutes": "3 min",
  "home.learn.lesson_title": "The Left Bank of Bordeaux",
  "home.learn.lesson_text": "Gravel soils. Cabernet-dominant blends. Power and structure.",
  "home.learn.badge_new": "New",
  "home.learn.badge_unit": "Unit",
  "home.play.title_a": "Test your knowledge,",
  "home.play.title_b": "have fun.",
  "home.play.text": "Most people forget what they learn. You won't.",
  "home.play.quiz_label": "Quiz · Level 2",
  "home.play.question": "Question",
  "home.play.q1": "Which grape dominates the Right Bank of Bordeaux?",
  "home.pro.eyebrow": "For professionals",
  "home.pro.title_a": "For wine",
  "home.pro.title_b": "professionals.",
  "home.pro.text": "Explain wine better, train your team, and sell with confidence.",
  "home.pro.used_by": "Used by wine professionals worldwide",
  "home.final.title": "Start your wine journey",
  "home.final.text": "Explore, learn, play. In that order — or any order.",

  // Trust
  "trust.premium_quality": "Premium quality",
  "trust.made_to_learn": "Made to learn",
  "trust.loved_by": "Loved by wine lovers",
  "trust.worldwide_shipping": "Worldwide shipping",
  "trust.designed_with_experts": "Designed with wine experts",
  "trust.secure_payment": "Secure payment",
  "trust.premium_print": "Premium print quality",

  // Footer
  "footer.brand_line": "Wine becomes clear when you see it. Maps, lessons, and play — for curious drinkers and professionals.",
  "footer.explore": "Explore",
  "footer.contact": "Contact",
  "footer.legal": "Legal",
  "footer.cookie_settings": "Cookie settings",
  "footer.secure_payments": "Secure payments",
  "footer.alcohol_disclaimer": "⚠️ Alcohol abuse is dangerous for your health. Consume in moderation.",
  "footer.rights": "All rights reserved.",

  // Cookie banner
  "cookies.title": "Cookies",
  "cookies.body": "We use cookies to improve your experience. Essential cookies are always on. Analytics and marketing cookies require your consent.",
  "cookies.accept_all": "Accept all",
  "cookies.reject_all": "Reject all",
  "cookies.preferences": "Preferences",
  "cookies.save": "Save preferences",
  "cookies.essential": "Essential",
  "cookies.essential_desc": "Authentication, security, core functionality.",
  "cookies.analytics": "Analytics",
  "cookies.analytics_desc": "Traffic measurement and usage analysis.",
  "cookies.marketing": "Marketing",
  "cookies.marketing_desc": "Campaign tracking and segmentation.",

  // Maps page
  "maps.eyebrow": "Gallery",
  "maps.h1_a": "Explore wine",
  "maps.h1_b": "visually",
  "maps.sub": "Maps, artworks, and visual stories of wine.",
  "maps.featured_eyebrow": "Featured",
  "maps.featured_title_a": "The easiest way to understand",
  "maps.featured_title_b": "Bordeaux",
  "maps.featured_text": "Every appellation, every classification, mapped in one poster.",
  "maps.featured_cta": "Discover the map",
  "maps.collections": "Collections",
  "maps.all_pieces": "All pieces",
  "maps.filter": "Filter",
  "maps.no_pieces": "No pieces match those filters yet.",
  "maps.label_country": "Country",
  "maps.label_type": "Type",
  "maps.understand_what_you_see": "Understand what you see",
  "maps.test_yourself": "Test yourself",
  "maps.col.maps": "Maps",
  "maps.col.maps_desc": "Understand wine regions visually",
  "maps.col.artworks": "Artworks",
  "maps.col.artworks_desc": "Aesthetic interpretations",
  "maps.col.photography": "Photography",
  "maps.col.photography_desc": "Moments & terroirs",
  "maps.col.oneline": "One line",
  "maps.col.oneline_desc": "Minimalist illustrations",

  // Learn page
  "learn.eyebrow": "Learn",
  "learn.h1_a": "Learn wine",
  "learn.h1_b": "simply",
  "learn.sub1": "Short lessons. Clear insights.",
  "learn.sub2": "Understand a wine region in 3 minutes.",
  "learn.story1": "Wine is often complicated…",
  "learn.story2": "We make it simple.",
  "learn.start_here": "Start here",
  "learn.start_here_title": "Start here:",
  "learn.structure": "Structure",
  "learn.cat.regions": "Regions",
  "learn.cat.regions_desc": "Understand where wine comes from.",
  "learn.cat.grapes": "Grapes",
  "learn.cat.grapes_desc": "Meet the personalities behind each wine.",
  "learn.cat.basics": "Basics",
  "learn.cat.basics_desc": "The essentials, clearly explained.",
  "learn.dont_just_learn": "Don't just learn. Remember.",
  "learn.see_what_you_learn": "See what you learn",

  // Play page
  "play.eyebrow": "Play",
  "play.h1_a": "Learn by",
  "play.h1_b": "playing",
  "play.sub": "Most people forget what they learn. You won't.",
  "play.test_knowledge": "Test your wine knowledge",
  "play.tagline": "Quick, fun, memorable",
  "play.mini_eyebrow": "Atlas",
  "play.mini_title_a": "The",
  "play.mini_title_b": "Google Maps",
  "play.mini_title_c": "of wine.",
  "play.mini_text": "Zoom, explore, understand.",
  "play.open_atlas": "Open the atlas",
  "play.books": "Learn with books",
  "play.books_text": "Hand-picked reads to go deeper — coming soon. Meanwhile, each map doubles as a study tool.",
  "play.transition_a": "Play. Explore.",
  "play.transition_b": "Understand.",
  "play.learn_basics": "Learn the basics",
  "play.see_wine_world": "See the wine world",
  "play.difficulty.easy": "Easy",
  "play.difficulty.medium": "Medium",
  "play.difficulty.hard": "Hard",

  // Pro page deep content
  "pro.hero.eyebrow": "For professionals",
  "pro.hero.h1": "geoVINUM for",
  "pro.hero.h1_italic": "professionals",
  "pro.hero.sub": "Sell maps, upsell tools, or embed interactive wine visualizations.",
  "pro.subnav.work_with_us": "Work with geoVINUM",
  "pro.shops.title": "Wine shops",
  "pro.shops.desc": "Turn every visit into a learning moment. Sell maps, books and gift products that double as teaching tools.",
  "pro.shops.headline_a": "Help your customers",
  "pro.shops.headline_b": "understand",
  "pro.shops.headline_c": "what they taste.",
  "pro.shops.f1_t": "Wholesale maps",
  "pro.shops.f1_d": "Stock museum-grade maps that sell themselves.",
  "pro.shops.f2_t": "Gift-ready bundles",
  "pro.shops.f2_d": "Maps paired with books and accessories.",
  "pro.shops.f3_t": "Higher basket size",
  "pro.shops.f3_d": "Visual merchandising that invites longer visits.",
  "pro.shops.cta": "Become a wine-shop partner",
  "pro.schools.title": "Wine schools",
  "pro.schools.desc": "Upsell visual learning tools to students — lessons, maps and quizzes that stick.",
  "pro.schools.headline_a": "Faster, clearer,",
  "pro.schools.headline_b": "more memorable",
  "pro.schools.headline_c": "learning.",
  "pro.schools.f1_t": "Student licenses",
  "pro.schools.f1_d": "Bulk access to lessons and quizzes.",
  "pro.schools.f2_t": "Classroom kits",
  "pro.schools.f2_d": "Large-format maps for on-wall teaching.",
  "pro.schools.f3_t": "Upsell programs",
  "pro.schools.f3_d": "Add visual content to your curriculum.",
  "pro.schools.cta": "Discover the academic offer",
  "pro.pros.title": "Professionals",
  "pro.pros.desc": "Iframe, API and widgets to embed geoVINUM data inside your platform.",
  "pro.pros.headline_a": "Integrate",
  "pro.pros.headline_b": "geoVINUM",
  "pro.pros.headline_c": "into your product.",
  "pro.pros.f1_t": "Iframe embeds",
  "pro.pros.f1_d": "Drop interactive maps into your site in one line.",
  "pro.pros.f2_t": "REST API",
  "pro.pros.f2_d": "Query terroir, appellation, and grape data.",
  "pro.pros.f3_t": "Widgets",
  "pro.pros.f3_d": "Prebuilt UI blocks you can white-label.",
  "pro.pros.f4_t": "Custom maps",
  "pro.pros.f4_d": "Tailored to your domain, brand, or catalog.",
  "pro.pros.cta": "See the developer docs",
  "pro.cta": "Work with geoVINUM",
  "pro.cta_text": "Tell us about your shop, school or product — we'll tailor a partnership that fits.",
  "pro.value.faster": "Visual learning is faster",
  "pro.value.understand": "Customers understand better",
  "pro.value.teams": "Teams are more confident",
  "pro.value.sales": "Sales improve",

  // Product page
  "product.from": "From",
  "product.rating": "4.8/5 from wine lovers",
  "product.details": "Details",
  "product.detail1": "High-quality print",
  "product.detail2": "Clear visual structure",
  "product.detail3": "Designed for learning",
  "product.value_title_a": "See wine",
  "product.value_title_b": "differently",
  "product.value_text": "Understand regions, grapes, and structure at a glance.",
  "product.learn_this_region": "Learn this region",
  "product.learn_this_region_text": "Understand {region} in 3 minutes.",
  "product.test_knowledge": "Test your knowledge",
  "product.test_knowledge_text": "Can you recognize {region} wines?",
  "product.not_found": "Product not found.",

  // Journal
  "journal.eyebrow": "Knowledge hub",
  "journal.h1_a": "Learn. Explore.",
  "journal.h1_b": "Understand",
  "journal.sub": "Guides, insights and tools to understand wine visually.",
  "journal.search_placeholder": "Search articles, regions, guides…",
  "journal.pillar.insights": "Wine insights",
  "journal.pillar.insights_desc": "Regions, appellations, terroirs explained.",
  "journal.pillar.guides": "Guides",
  "journal.pillar.guides_desc": "Practical reads to make sense of wine.",
  "journal.pillar.get_started": "Get started",
  "journal.pillar.get_started_desc": "Onboarding, how-tos, tutorials.",
  "journal.pillar.for_pros": "For professionals",
  "journal.pillar.for_pros_desc": "Training, selling, scaling with geoVINUM.",
  "journal.filter.all": "All",
  "journal.loading": "Loading articles…",
  "journal.error": "Couldn't reach the journal.",
  "journal.empty": "No articles yet.",
  "journal.empty_lang": "No articles available in this language yet.",
  "journal.empty_lang_text": "We're translating more content. Switch language or come back soon.",
  "journal.empty_try": "Try another topic or search term.",
  "journal.clear_theme": "Clear theme filter",
  "journal.ready": "Ready to see it?",
  "journal.read_article": "Read article",

  // Search
  "search.h1": "Results for:",
  "search.filter.all": "All",
  "search.filter.maps": "Maps",
  "search.filter.learn": "Learn",
  "search.filter.play": "Play",
  "search.filter.journal": "Journal",
  "search.filter.products": "Products",
  "search.suggestion": "You might also explore",

  // Auth
  "auth.welcome_back": "Welcome back",
  "auth.welcome_back_sub": "Log in to access your maps, lessons, and quizzes.",
  "auth.no_account": "Don't have an account?",
  "auth.sign_up": "Sign up",
  "auth.forgot_password": "Forgot password?",
  "auth.create_account": "Create your account",
  "auth.start_journey": "Start your wine journey",
  "auth.have_account": "Already have an account?",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.min6": "Min. 6 characters",
  "auth.reset_title": "Reset your password",
  "auth.reset_sub": "Enter your email to receive a reset link.",
  "auth.check_inbox": "Check your inbox",
  "auth.check_inbox_text": "If {email} matches an account, a reset link is on its way.",
  "auth.new_password": "Create a new password",
  "auth.new_password_sub": "Choose a new password for your account.",
  "auth.new_password_label": "New password",
  "auth.confirm_password": "Confirm password",
  "auth.repeat_password": "Repeat password",
  "auth.password_updated": "Password updated",
  "auth.redirecting": "Redirecting you to login…",
  "auth.err_min": "Password must be at least 6 characters.",
  "auth.err_match": "Passwords don't match.",
  "auth.err_invalid": "Use a valid email and a password of 6+ characters.",
  "auth.err_both": "Please fill in both fields.",
  "auth.err_generic": "Something went wrong.",

  // Dashboard
  "dashboard.welcome_back": "Welcome back",
  "dashboard.signed_in_as": "Signed in as",
  "dashboard.your_maps": "Your maps",
  "dashboard.your_maps_desc": "Access your purchased maps.",
  "dashboard.empty_maps": "You don't have any maps yet.",
  "dashboard.continue_learning": "Continue learning",
  "dashboard.continue_learning_desc": "Pick up where you left off.",
  "dashboard.empty_learning": "Start your first lesson.",
  "dashboard.enroll": "Enroll",
  "dashboard.enrolling": "Enrolling…",
  "dashboard.continue": "Continue",
  "dashboard.enrolled_ok": "You're enrolled! Opening the course…",
  "dashboard.already_enrolled": "You're already enrolled in this course.",
  "dashboard.enroll_failed": "Enrolment failed. Please try again later.",
  "dashboard.keep_playing": "Keep playing",
  "dashboard.keep_playing_desc": "Test your knowledge and improve.",
  "dashboard.recommendations": "You might also like",
  "dashboard.recommendations_desc": "Discover more maps and lessons.",
  "dashboard.explore_more": "Explore more",
  "dashboard.logout": "Logout",

  // Thank you
  "thanks.title": "Thank you",
  "thanks.sub": "Your wine journey starts now.",
  "thanks.map_ready": "Your map is ready",
  "thanks.understand_region": "Understand this region",
  "thanks.test_yourself": "Test yourself",
  "thanks.order_confirmation": "Order confirmation",
  "thanks.receipt_sent": "A receipt has been sent to your email.",

  // Boutique
  "shop.maps.title": "Maps",
  "shop.maps.sub": "Premium printed wine maps — explore regions visually.",
  "shop.books.title": "Books",
  "shop.books.sub": "Beautifully designed wine books.",
  "shop.ebooks.title": "Ebooks",
  "shop.ebooks.sub": "Wine knowledge, instantly downloadable.",
  "shop.courses.title": "Courses",
  "shop.courses.sub": "Complete wine courses to master regions and grapes.",
  "shop.bundles.title": "Bundles",
  "shop.bundles.sub": "Curated bundles — maps, books and courses together.",
  "shop.empty": "No products in this category yet — come back soon!",
  "nav.boutique": "Shop",

  "auth.full_name": "Full name",
  "auth.full_name_placeholder": "Your name",

  "home.for.b2c_eyebrow": "For wine lovers",
  "home.for.b2c_title": "Discover, learn and",
  "home.for.b2c_title_em": "collect.",
  "home.for.b2c_text": "From beautifully printed maps to personalised AI coaching — explore wine at your own pace, your own way.",
  "home.for.b2c_cta": "Explore products",
  "home.for.b2c_alt": "Wine lover exploring a vineyard map",
  "home.for.b2b_eyebrow": "For professionals",
  "home.for.b2b_title": "Engage customers,",
  "home.for.b2b_title_em": "train teams, integrate.",
  "home.for.b2b_text": "Wineries, schools and platforms use geoVINUM to teach faster, engage longer and integrate richer wine experiences.",
  "home.for.b2b_cta": "Discover Pro solutions",
  "home.for.b2b_alt": "Wine professional using geoVINUM Pro",

  // Hero — strengthened promise
  "hero.promise": "Maps. Stories. Knowledge.",

  // Products / pillars
  "pillar.explore": "Explore",
  "pillar.discover": "Discover",
  "pillar.experience": "Experience",
  "pillar.learn": "Learn",
  "pillar.collect": "Collect",
  "pillar.integrate": "Integrate",
  "product.status.available": "Available",
  "product.status.coming_soon": "Coming soon",
  "product.type.app": "App",
  "product.type.physical": "Physical product",
  "product.type.platform": "Platform",
  "product.type.service": "Service",
  "product.cta.discover": "Discover",
  "product.cta.try": "Try it",
  "product.cta.start": "Get started",
  "product.cta.contact": "Contact sales",
  "product.cta.notify": "Notify me",
  "product.cta.become_beta": "Become a Beta tester",
  "product.status.beta_program": "Coming soon · Beta program",
  "product.cta.visit": "Visit",
  "product.cta.see_versions": "See versions",
  "product.versions": "Available versions",
  "product.pricing": "Pricing",
  "product.starting_from": "Starting from",
  "product.download_pdf": "Download PDF",
  "print.upsell_eyebrow": "Print on demand",
  "print.upsell_title": "Order your map as a poster.",
  "print.upsell_subtitle": "Premium giclée print on 180gsm uncoated paper, shipped from our local production hub.",
  "print.shipping": "Ships worldwide in 3–6 business days",
  "print.paper": "180gsm premium uncoated — museum-grade",
  "print.cta": "Order now",
  "print.academy_hint": "Subscribers to Academy automatically get –10% on every print.",
  "print.success_title": "Order received!",
  "print.success_subtitle": "Your poster is being prepared and will be shipped to the address you provided.",
  "print.success_back": "Back to product",
  "print.success_pending": "Verifying your payment…",
  "print.success_failed": "Payment not completed. You can retry from the product page.",
  "product.trial": "free trial included",
  "product.trial_7d": "7-day free trial included",
  "product.platform": "Platform",
  "product.price": "Price",
  "product.price.free": "Free",
  "product.price.premium": "Premium",
  "product.price.subscription": "Professional Subscription",
  "product.price.contact": "Contact us",
  "product.price.coming_soon": "Coming soon",
  "product.atlas.tagline": "Explore the world's wine regions and terroirs.",
  "product.atlas.desc": "GeoVINUM Atlas helps wine lovers discover wine regions, appellations, grape varieties and producers through interactive maps and educational content. Explore territories, understand wine origins and plan your wine journeys.",
  "product.atlas.mini.title": "geoVINUM Atlas",
  "product.atlas.mini.desc": "Perfect for wine enthusiasts, students and curious explorers who want quick access to key wine regions, appellations and grape varieties.",
  "product.atlas.magnum.title": "geoVINUM Atlas",
  "product.atlas.magnum.desc": "Designed for serious wine enthusiasts and students seeking a more comprehensive understanding of wine geography, terroirs and appellations.",
  "product.atlas.pro.title": "geoVINUM Atlas",
  "product.atlas.pro.desc": "A complete solution for wine professionals who need reliable wine region data, advanced mapping tools and premium educational resources.",

  // Section labels
  "product.section.for_who": "For who",
  "product.section.features": "Features",
  "product.section.use_cases": "Use cases",
  "product.section.examples": "Examples",

  // ----- Coach extended sections -----
  "product.coach.features.title": "What can geoVINUM Coach help you with?",
  "product.coach.feature.appellations.title": "Understand appellations",
  "product.coach.feature.appellations.desc": "Ask questions about any wine region.",
  "product.coach.feature.certs.title": "Prepare certifications",
  "product.coach.feature.certs.desc": "WSET, Court of Master Sommeliers, Wine Scholar, university programs.",
  "product.coach.feature.grapes.title": "Learn grape varieties",
  "product.coach.feature.grapes.desc": "Discover key grapes and their regions.",
  "product.coach.feature.terroirs.title": "Explore terroirs",
  "product.coach.feature.terroirs.desc": "Understand soils, climate and geography.",
  "product.coach.feature.tasting.title": "Build tasting knowledge",
  "product.coach.feature.tasting.desc": "Improve wine analysis and tasting skills.",
  "product.coach.feature.instant.title": "Get instant answers",
  "product.coach.feature.instant.desc": "Your personal wine mentor available 24/7.",
  "product.coach.examples.title": "Example questions",
  "product.coach.example.q1": "Why is Chablis different from Napa Chardonnay?",
  "product.coach.example.q2": "Explain Burgundy classifications.",
  "product.coach.example.q3": "What soils are found in Barolo?",
  "product.coach.example.q4": "Compare Rioja and Ribera del Duero.",
  "product.coach.for_who.title": "Who is it for?",
  "product.coach.for_who.students.title": "Wine students",
  "product.coach.for_who.students.desc": "Master regions, grapes and tasting fundamentals.",
  "product.coach.for_who.sommeliers.title": "Sommeliers",
  "product.coach.for_who.sommeliers.desc": "A 24/7 reference at the table.",
  "product.coach.for_who.hospitality.title": "Hospitality teams",
  "product.coach.for_who.hospitality.desc": "Onboard staff faster on wine knowledge.",
  "product.coach.for_who.merchants.title": "Wine merchants",
  "product.coach.for_who.merchants.desc": "Sharpen your selling story for every bottle.",
  "product.coach.for_who.enthusiasts.title": "Enthusiasts",
  "product.coach.for_who.enthusiasts.desc": "Learn at your pace, on any topic.",

  // ----- Iframe extended sections -----
  "product.iframe.for_who.title": "Who is it for?",
  "product.iframe.for_who.wineries.title": "Wineries",
  "product.iframe.for_who.wineries.desc": "Show your terroir online.",
  "product.iframe.for_who.regions.title": "Wine regions",
  "product.iframe.for_who.regions.desc": "Promote appellations interactively.",
  "product.iframe.for_who.tourism.title": "Wine tourism",
  "product.iframe.for_who.tourism.desc": "Engage visitors before their trip.",
  "product.iframe.for_who.schools.title": "Wine schools",
  "product.iframe.for_who.schools.desc": "Improve educational content.",
  "product.iframe.use_cases.title": "Use cases",
  "product.iframe.use.winery.title": "Winery website",
  "product.iframe.use.winery.desc": "Show vineyard locations.",
  "product.iframe.use.regional.title": "Regional website",
  "product.iframe.use.regional.desc": "Present appellations.",
  "product.iframe.use.tourism.title": "Tourism portal",
  "product.iframe.use.tourism.desc": "Create interactive wine routes.",
  "product.iframe.use.education.title": "Education platform",
  "product.iframe.use.education.desc": "Teach wine geography visually.",
  "product.iframe.features.title": "How does it work?",
  "product.iframe.feature.step1.title": "Step 1",
  "product.iframe.feature.step1.desc": "Choose a map.",
  "product.iframe.feature.step2.title": "Step 2",
  "product.iframe.feature.step2.desc": "Copy the embed code.",
  "product.iframe.feature.step3.title": "Step 3",
  "product.iframe.feature.step3.desc": "Paste it into your website.",
  "product.iframe.feature.step4.title": "Step 4",
  "product.iframe.feature.step4.desc": "Your map is live.",

  // ----- Explore "Which Atlas" -----
  "explore.which.eyebrow": "Choose your Atlas",
  "explore.which.title": "Which Atlas is right for you?",
  "explore.mini.tagline": "The Google Maps of wine",
  "explore.mini.desc": "Explore wine regions, appellations and wineries worldwide.",
  "explore.mini.audience": "Wine enthusiasts · Travellers · Beginners · Curious wine lovers",
  "explore.mini.cta": "Explore geoVINUM Atlas",
  "explore.magnum.tagline": "Your personalized vineyard map",
  "explore.magnum.desc": "Display one or multiple appellations with cadastral references and custom vineyard maps.",
  "explore.magnum.audience": "Wineries · Estates · Wine tourism projects · Regional organizations",
  "explore.magnum.cta": "Discover geoVINUM Atlas",
  "explore.pro.tagline": "Advanced terroir intelligence",
  "explore.pro.desc": "Access professional terroir data, 3D visualization and advanced wine region analysis.",
  "explore.pro.audience": "Consultants · Importers · Educators · Wine experts · Interprofessions",
  "explore.pro.cta": "Discover geoVINUM Atlas",
  "explore.cta.learn_more": "Learn more",
  "explore.cta.subscribe": "Subscribe",
  "explore.cta.download": "Download",
  "explore.trial_days": "{n} days free",

  // ----- Iframe pricing (EN) -----
  "iframeP.eyebrow": "Choose your plan",
  "iframeP.title": "From small site to enterprise — pick your volume.",
  "iframeP.cycle.monthly": "Monthly",
  "iframeP.cycle.yearly": "Annual",
  "iframeP.popular": "Most popular",
  "iframeP.ht_per": "excl. VAT /",
  "iframeP.year": "year",
  "iframeP.month": "month",
  "iframeP.save_with_yearly": "Save with annual ({amount} / year)",
  "iframeP.unavailable": "Unavailable",
  "iframeP.subscribe": "Subscribe",
  "iframeP.request": "request",
  "iframeP.requests_per_year": "requests / year",
  "iframeP.contact_badge": "Custom",
  "iframeP.from": "Starting price",
  "iframeP.custom_quote": "Custom quote",
  "iframeP.talk_to_sales": "Talk to sales",
  "iframeP.footer_note": "Secure payment by Stripe · VAT automatically collected · Cancel anytime from your account.",

  // ----- Portfolio page (EN) -----
  "portfolioP.hero.eyebrow": "Portfolio · Digital Passport",
  "portfolioP.hero.title": "The intelligent digital passport for every product.",
  "portfolioP.hero.subtitle": "Producers, retailers, distributors, sommeliers and educators centralize, enrich and share dynamic product fact-sheets — automatically tailored to each audience.",
  "portfolioP.hero.cta_beta": "Become a Beta tester",
  "portfolioP.hero.cta_contact": "Talk to us",
  "portfolioP.status.beta": "Coming soon · Beta",
  "portfolioP.status.note": "Get early access by joining the beta program",

  "portfolioP.truth.eyebrow": "One source of truth",
  "portfolioP.truth.title": "One product. One smart passport. Infinite uses.",
  "portfolioP.truth.before": "Instead of juggling…",
  "portfolioP.truth.before.files_pdf": "Multiple technical PDFs",
  "portfolioP.truth.before.sales_decks": "Multiple sales decks",
  "portfolioP.truth.before.translations": "Multiple translated versions",
  "portfolioP.truth.before.training": "Multiple training materials",
  "portfolioP.truth.before.pitch": "Multiple sales pitches",
  "portfolioP.truth.after": "With Portfolio",
  "portfolioP.truth.after_desc": "All product information lives in one platform. A single dynamic space generates the most relevant version for every audience and context.",
  "portfolioP.truth.after_note": "Edit once, deploy everywhere — bottles, fairs, wine lists, exports, training.",

  "portfolioP.ai.eyebrow": "Smart pre-fill",
  "portfolioP.ai.title": "A passport that writes itself.",
  "portfolioP.ai.subtitle": "Portfolio never starts from a blank page. Every fact-sheet is auto-enriched from GeoVINUM data and AI generations.",
  "portfolioP.ai.geovinum_title": "GeoVINUM data",
  "portfolioP.ai.geovinum.region": "Region",
  "portfolioP.ai.geovinum.appellation": "Appellation",
  "portfolioP.ai.geovinum.grapes": "Grape varieties",
  "portfolioP.ai.geovinum.climate": "Climate",
  "portfolioP.ai.geovinum.soils": "Soils",
  "portfolioP.ai.geovinum.altitude": "Altitude",
  "portfolioP.ai.geovinum.plots": "Plots",
  "portfolioP.ai.geovinum.producers": "Producers",
  "portfolioP.ai.geovinum.history": "Historical data",
  "portfolioP.ai.geovinum.geo": "Geographic data",
  "portfolioP.ai.ai_title": "AI generations",
  "portfolioP.ai.ai.summary": "Product summary",
  "portfolioP.ai.ai.storytelling": "Storytelling",
  "portfolioP.ai.ai.sales": "Sales presentation",
  "portfolioP.ai.ai.retailer": "Retailer pitch",
  "portfolioP.ai.ai.export": "Export pitch",
  "portfolioP.ai.ai.pairing": "Food & wine pairings",
  "portfolioP.ai.ai.profiles": "Consumer profiles",
  "portfolioP.ai.ai.training": "Educational content",
  "portfolioP.ai.ai.i18n": "Multilingual translations",
  "portfolioP.ai.editable": "Pros can always edit or extend the generated content.",

  "portfolioP.modules.eyebrow": "Modular by design",
  "portfolioP.modules.title": "Switch modules on or off, à la carte.",
  "portfolioP.module.origin.title": "Origin & Terroir",
  "portfolioP.module.origin.desc": "Interactive map, plots, geology, climate, altitude, environment.",
  "portfolioP.module.story.title": "Story & Storytelling",
  "portfolioP.module.story.desc": "Estate history, philosophy, know-how, product presentation.",
  "portfolioP.module.tech.title": "Technical",
  "portfolioP.module.tech.desc": "Production, aging, certifications, analyses, regulatory sheets.",
  "portfolioP.module.tasting.title": "Tasting",
  "portfolioP.module.tasting.desc": "Notes, aromatic profile, sensory radar, personal notes.",
  "portfolioP.module.training.title": "Training",
  "portfolioP.module.training.desc": "WSET format, teaching sheets, quizzes, training content.",
  "portfolioP.module.pairing.title": "Pairings",
  "portfolioP.module.pairing.desc": "Food pairings, serving suggestions, occasions.",
  "portfolioP.module.media.title": "Media",
  "portfolioP.module.media.desc": "Photos, videos, interviews, marketing content.",
  "portfolioP.module.trade.title": "Commercial",
  "portfolioP.module.trade.desc": "Prices, packaging, availability, export markets.",

  "portfolioP.position.eyebrow": "Market positioning",
  "portfolioP.position.title": "Understand where your product stands.",
  "portfolioP.position.subtitle": "Portfolio doesn't just describe — it positions. Combining GeoVINUM data, market data and AI, it surfaces:",
  "portfolioP.position.position.title": "Market positioning",
  "portfolioP.position.position.desc": "Entry-level, premium, super-premium, icon.",
  "portfolioP.position.competitive.title": "Competitive analysis",
  "portfolioP.position.competitive.desc": "Similar products, competitor references, alternatives in the same category.",
  "portfolioP.position.value.title": "Valuation",
  "portfolioP.position.value.desc": "Value estimate, price/market coherence, positioning evolution, taster perception.",
  "portfolioP.position.differentiation.title": "Differentiation",
  "portfolioP.position.differentiation.desc": "What sets the product apart — its commercial strengths and unique selling points.",
  "portfolioP.position.tier.entry": "Entry-level",
  "portfolioP.position.tier.premium": "Premium",
  "portfolioP.position.tier.super_premium": "Super-premium",
  "portfolioP.position.tier.icon": "Icon",

  "portfolioP.audience.eyebrow": "Audience-aware",
  "portfolioP.audience.title": "One Portfolio. Five faces.",
  "portfolioP.audience.consumer.title": "Consumer",
  "portfolioP.audience.consumer.list": "Story · Terroir · Tasting · Pairings · Videos",
  "portfolioP.audience.retailer.title": "Retailer",
  "portfolioP.audience.retailer.list": "Sales pitch · Sensory profile · Similar products · Price positioning",
  "portfolioP.audience.importer.title": "Importer",
  "portfolioP.audience.importer.list": "Certifications · Available volumes · Export info",
  "portfolioP.audience.sommelier.title": "Sommelier",
  "portfolioP.audience.sommelier.list": "Detailed tasting · Pairings · Service temperature",
  "portfolioP.audience.educator.title": "Educator",
  "portfolioP.audience.educator.list": "Teaching content · Maps · Quizzes · Educational resources",

  "portfolioP.diffusion.eyebrow": "Multi-channel",
  "portfolioP.diffusion.title": "Distributed wherever your product travels.",
  "portfolioP.diffusion.subtitle": "Each channel can surface different modules — the right info in the right context.",
  "portfolioP.diffusion.qr_bottle": "QR code on bottle",
  "portfolioP.diffusion.qr_fair": "QR code at trade fairs",
  "portfolioP.diffusion.wine_list": "Digital wine list",
  "portfolioP.diffusion.catalog": "Sales catalog",
  "portfolioP.diffusion.export": "Export fact-sheet",
  "portfolioP.diffusion.training": "Training material",
  "portfolioP.diffusion.private_link": "Private or public link",

  "portfolioP.promise.eyebrow": "The promise",
  "portfolioP.promise.title": "Portfolio turns a simple product sheet into a smart digital asset.",
  "portfolioP.promise.body": "Pre-filled by GeoVINUM data, enriched by AI, infinitely customizable, adaptable to every audience — Portfolio helps you understand, valorize, present, train and sell a product, better.",
  "portfolioP.promise.tagline": "One sheet. Infinite uses.",
  "portfolioP.promise.cta": "Become a Beta tester",
  "portfolioP.promise.cta_contact": "Contact us",

  // ----- Portfolio: How it works (EN) -----
  "portfolioP.how.eyebrow": "How it works",
  "portfolioP.how.title": "Three steps. Then it adapts to every audience.",
  "portfolioP.how.scan.title": "Identify the product",
  "portfolioP.how.scan.desc": "Pick an appellation, a wine, a vintage. GeoVINUM finds the geographic and historical data instantly.",
  "portfolioP.how.ai.title": "AI pre-fills",
  "portfolioP.how.ai.desc": "Story, sales pitch, pairings, tasting notes, translations — all generated. You stay in control and edit anything.",
  "portfolioP.how.share.title": "Share everywhere",
  "portfolioP.how.share.desc": "QR on bottles, wine lists, export sheets, training. Each channel shows the right modules for the right audience.",

  // ----- Portfolio: Live demo (EN) -----
  "portfolioP.demo.eyebrow": "Live preview",
  "portfolioP.demo.title": "One product. Four views. Zero duplication.",
  "portfolioP.demo.subtitle": "Watch how the same product page automatically adapts to who's looking at it.",
  "portfolioP.demo.lens": "Audience lens",
  "portfolioP.demo.note": "Static preview. Real Portfolio pages are fully interactive — edit, reorder modules, switch language, export PDF.",
  "portfolioP.demo.consumer.story": "Born on the warm gravels of Margaux, this vintage tells of a long, dry summer. Velvety nose, refined palate — a wine that rewards patience.",
  "portfolioP.demo.retailer.story": "Iconic 1er Grand Cru with strong secondary demand. RRP > 800€. Excellent price/score ratio vs Latour 2018.",
  "portfolioP.demo.sommelier.story": "Subtle violet & cedar nose. Medium-plus tannins, lifted acidity. Decant 2h. Service: 17°C. Cellaring: 2028–2050.",
  "portfolioP.demo.educator.story": "Textbook Left Bank Cabernet-dominant blend — perfect case study for gravelly terroir, Médoc style, and the 1855 classification.",
  "portfolioP.demo.section.story": "Story",
  "portfolioP.demo.section.story.value": "Wax-sealed legacy since 1855",
  "portfolioP.demo.section.terroir": "Terroir",
  "portfolioP.demo.section.terroir.value": "Gravel · 12m elevation",
  "portfolioP.demo.section.tasting": "Tasting",
  "portfolioP.demo.section.tasting.value": "Cassis · Cedar · Graphite",
  "portfolioP.demo.section.pairing": "Pairing",
  "portfolioP.demo.section.pairing.value": "Lamb · Aged hard cheese",
  "portfolioP.demo.section.sales": "Sales angle",
  "portfolioP.demo.section.sales.value": "Investment-grade icon",
  "portfolioP.demo.section.sensory": "Sensory profile",
  "portfolioP.demo.section.sensory.value": "Full body · Tannin 7/10",
  "portfolioP.demo.section.similar": "Compare to",
  "portfolioP.demo.section.similar.value": "Latour · Mouton 2018",
  "portfolioP.demo.section.price": "Positioning",
  "portfolioP.demo.section.price.value": "850€ · Super-premium",
  "portfolioP.demo.section.tasting_detail": "Tasting (deep)",
  "portfolioP.demo.section.tasting_detail.value": "16.2/20 · Decant 2h",
  "portfolioP.demo.section.service": "Service",
  "portfolioP.demo.section.service.value": "17°C · Bordeaux glass",
  "portfolioP.demo.section.wset": "WSET ref",
  "portfolioP.demo.section.wset.value": "L3 case study",
  "portfolioP.demo.section.maps": "Maps",
  "portfolioP.demo.section.maps.value": "Médoc · Plot view",
  "portfolioP.demo.section.quiz": "Quiz",
  "portfolioP.demo.section.quiz.value": "12 questions",
  "portfolioP.demo.section.resources": "Resources",
  "portfolioP.demo.section.resources.value": "PDF · Slides · Cards",


  // ----- geoVINUM Atlas dedicated page -----
  "product.atlas-magnum.tagline": "Your personalized vineyard map.",
  "product.atlas-magnum.desc": "Show your vineyards, parcels and appellations through interactive maps designed for wineries and wine organizations.",
  "product.atlas-magnum.for_who.title": "Designed for",
  "product.atlas-magnum.for_who.wineries.title": "Wineries",
  "product.atlas-magnum.for_who.wineries.desc": "Present your vineyard professionally.",
  "product.atlas-magnum.for_who.tourism.title": "Wine tourism",
  "product.atlas-magnum.for_who.tourism.desc": "Create engaging visitor experiences.",
  "product.atlas-magnum.for_who.interprof.title": "Interprofessions",
  "product.atlas-magnum.for_who.interprof.desc": "Promote appellations and wine regions.",
  "product.atlas-magnum.for_who.consultants.title": "Consultants",
  "product.atlas-magnum.for_who.consultants.desc": "Provide clients with visual vineyard data.",
  "product.atlas-magnum.features.title": "What you can do",
  "product.atlas-magnum.feature.appellations.title": "Display appellations",
  "product.atlas-magnum.feature.appellations.desc": "Interactive vineyard maps.",
  "product.atlas-magnum.feature.cadastre.title": "Show cadastral references",
  "product.atlas-magnum.feature.cadastre.desc": "Detailed parcel information.",
  "product.atlas-magnum.feature.custom.title": "Customize your maps",
  "product.atlas-magnum.feature.custom.desc": "Branding, colors and territories.",
  "product.atlas-magnum.feature.share.title": "Share online",
  "product.atlas-magnum.feature.share.desc": "Web access and presentations.",
  "product.atlas-magnum.use_cases.title": "Use cases",
  "product.atlas-magnum.use.website.title": "Winery website",
  "product.atlas-magnum.use.website.desc": "Embed your custom vineyard map.",
  "product.atlas-magnum.use.fair.title": "Wine fair presentation",
  "product.atlas-magnum.use.fair.desc": "Engage buyers with visual storytelling.",
  "product.atlas-magnum.use.export.title": "Export catalog",
  "product.atlas-magnum.use.export.desc": "Help international clients understand your terroir.",
  "product.atlas-magnum.use.tourism.title": "Tourism experience",
  "product.atlas-magnum.use.tourism.desc": "Welcome visitors with interactive maps.",

  // ----- geoVINUM Atlas dedicated page -----
  "product.atlas-pro.tagline": "Advanced terroir intelligence for wine professionals.",
  "product.atlas-pro.desc": "Go beyond maps with terroir analysis, 3D visualization and professional wine region insights.",
  "product.atlas-pro.for_who.title": "Designed for",
  "product.atlas-pro.for_who.importers.title": "Importers",
  "product.atlas-pro.for_who.importers.desc": "Understand regions faster.",
  "product.atlas-pro.for_who.consultants.title": "Consultants",
  "product.atlas-pro.for_who.consultants.desc": "Deliver deeper terroir expertise.",
  "product.atlas-pro.for_who.educators.title": "Wine educators",
  "product.atlas-pro.for_who.educators.desc": "Teach wine geography visually.",
  "product.atlas-pro.for_who.interprof.title": "Interprofessions",
  "product.atlas-pro.for_who.interprof.desc": "Analyze and present territories.",
  "product.atlas-pro.features.title": "Advanced features",
  "product.atlas-pro.feature.terroir_data.title": "Terroir data",
  "product.atlas-pro.feature.terroir_data.desc": "Geology, soils, altitude, climate.",
  "product.atlas-pro.feature.viz_3d.title": "3D visualization",
  "product.atlas-pro.feature.viz_3d.desc": "Understand vineyard topography.",
  "product.atlas-pro.feature.mapping.title": "Advanced mapping",
  "product.atlas-pro.feature.mapping.desc": "Professional cartographic tools.",
  "product.atlas-pro.feature.analysis.title": "Custom analysis",
  "product.atlas-pro.feature.analysis.desc": "Explore wine regions in depth.",
  "product.atlas-pro.use_cases.title": "Use cases",
  "product.atlas-pro.use.import.title": "Build smarter portfolios",
  "product.atlas-pro.use.import.desc": "Importers shortlist new estates with terroir-level confidence — climate, soils, altitudes, slope at a glance.",
  "product.atlas-pro.use.education.title": "WSET / Master Sommelier prep",
  "product.atlas-pro.use.education.desc": "Schools turn dense regional content into visual masterclasses that students remember on exam day.",
  "product.atlas-pro.use.consulting.title": "Premium client briefings",
  "product.atlas-pro.use.consulting.desc": "Consultants ship deeper, more visual terroir reports without rebuilding maps from scratch.",
  "product.atlas-pro.use.research.title": "Comparative research",
  "product.atlas-pro.use.research.desc": "Cross-compare appellations, vintages and parcels in one workspace built for wine pros.",
  "product.atlas-pro.examples.title": "What pros do with geoVINUM Atlas",
  "product.atlas-pro.example.importer": "I shortlist three new Burgundy estates in one afternoon — terroir, slope and exposure, all in one place.",
  "product.atlas-pro.example.consultant": "My retainer clients see exactly why their vineyards taste different from their neighbour's.",
  "product.atlas-pro.example.educator": "My WSET D3 students nail regional questions because they finally see the geography.",
  "product.atlas-pro.why.title": "Why geoVINUM Atlas?",
  "product.atlas-pro.why.body": "Instead of simply locating vineyards, geoVINUM Atlas helps you understand why each wine is different — soil to slope, vintage to vintage. Built for the people whose paycheck depends on knowing wine.",

  // geoVINUM Atlas — examples + why
  "product.atlas-magnum.examples.title": "Stories from estates using geoVINUM Atlas",
  "product.atlas-magnum.example.winery": "Buyers now spend 3 minutes on our terroir page instead of 20 seconds.",
  "product.atlas-magnum.example.tourism": "Our visitors arrive prepared — we sell more wine and book more tours.",
  "product.atlas-magnum.example.interprof": "Our appellation finally has a map that travels across borders.",
  "product.atlas-magnum.why.title": "Why geoVINUM Atlas?",
  "product.atlas-magnum.why.body": "A generic Google Maps embed says where you are. A geoVINUM Atlas map says why your vineyard tastes the way it does — and convinces a buyer in 30 seconds.",


  // Positioning labels
  "product.pos.b2c": "For wine enthusiasts",
  "product.pos.b2b": "For wine professionals",
  "product.pos.education": "For education & training",
  "product.ideal_for": "Ideal for",
  "product.key_benefits": "Key benefits",

  // Audience tags
  "audience.beginners": "Wine beginners",
  "audience.wine_lovers": "Wine lovers",
  "audience.travellers": "Travellers",
  "audience.casual_learners": "Casual learners",
  "audience.wset_students": "WSET students",
  "audience.sommeliers": "Sommeliers",
  "audience.wine_pros": "Wine professionals",
  "audience.advanced_enthusiasts": "Advanced enthusiasts",
  "audience.importers": "Importers",
  "audience.distributors": "Distributors",
  "audience.wine_merchants": "Wine merchants",
  "audience.wine_educators": "Wine educators",
  "audience.consultants": "Consultants",
  "audience.wine_students": "Wine students",
  "audience.hospitality_pros": "Hospitality professionals",
  "audience.wine_schools": "Wine schools",
  "audience.cert_students": "Certification students",
  "audience.sales_teams": "Sales teams",
  "audience.educators": "Educators",
  "audience.tourism": "Wine tourism projects",
  "audience.wineries": "Wineries",
  "audience.wine_regions": "Wine regions",
  "audience.tourism_orgs": "Tourism organisations",
  "audience.wine_brands": "Wine brands",
  "audience.wine_tourism_ops": "Wine tourism operators",
  "audience.wine_bars": "Wine bars",
  "audience.events": "Events",
  "audience.hospitality": "Hospitality businesses",

  // Benefit lines
  "benefit.interactive_maps": "Interactive wine maps",
  "benefit.major_regions": "Major wine regions worldwide",
  "benefit.essential_terroir": "Essential terroir knowledge",
  "benefit.mobile_friendly": "Mobile-friendly access",
  "benefit.detailed_maps": "Detailed wine maps",
  "benefit.expanded_appellations": "Expanded appellation coverage",
  "benefit.in_depth_terroir": "In-depth terroir information",
  "benefit.advanced_edu": "Advanced educational content",
  "benefit.full_premium_maps": "Full access to premium wine maps",
  "benefit.pro_region_db": "Professional wine region database",
  "benefit.advanced_insights": "Advanced terroir insights",
  "benefit.business_ready": "Business-ready resources",
  "benefit.structured_paths": "Structured learning paths",
  "benefit.regional_courses": "Regional wine courses",
  "benefit.practical_knowledge": "Practical wine knowledge",
  "benefit.personalized": "Personalized guidance",
  "benefit.instant_answers": "Instant answers",
  "benefit.interactive_learning": "Interactive learning",
  "benefit.expertise_on_demand": "Wine expertise on demand",
  "benefit.interactive_exploration": "Interactive exploration",
  "benefit.detailed_regions": "Detailed wine regions",
  "benefit.educational_content": "Educational content",
  "benefit.multi_device": "Mobile and desktop access",
  "benefit.seamless_integration": "Seamless website integration",
  "benefit.engagement_boost": "Enhanced visitor engagement",
  "benefit.terroir_storytelling": "Interactive terroir storytelling",
  "benefit.no_tech": "No technical expertise required",
  "benefit.qr_code": "QR-code experiences",
  "benefit.interactive_pages": "Interactive bottle pages",
  "benefit.customer_engagement": "Customer engagement",
  "benefit.guided_tasting": "Guided tasting experiences",
  "benefit.mobile_first": "Mobile-first experience",
  "benefit.enhanced_interaction": "Enhanced visitor interaction",

  "product.portfolio.tagline": "Connect every bottle to its story.",
  "product.portfolio.desc": "Create digital wine experiences that allow customers to discover the origin, terroir and identity behind every bottle.",
  "product.tasting.tagline": "Transform wine tastings into immersive experiences.",
  "product.tasting.desc": "Deliver interactive tasting journeys that help visitors understand wines, regions and terroirs while increasing engagement.",
  "product.coach.tagline": "Your personal wine learning companion.",
  "product.coach.desc": "A Duolingo-style microlearning app that helps users understand wine regions, grape varieties, appellations and terroirs faster.",
  "product.maps.tagline": "Interactive wine maps built for education and engagement.",
  "product.maps.desc": "Explore wine regions through beautifully designed interactive maps that bring terroirs and appellations to life.",
  "product.iframe.tagline": "Bring interactive wine maps to your own website.",
  "product.iframe.desc": "Easily embed geoVINUM maps into your website, educational platform or wine tourism project.",
  "product.academy.tagline": "Learn wine through maps, terroirs and real-world examples.",
  "product.academy.desc": "An interactive learning platform designed to make wine education visual, engaging and easy to understand.",
  // Coach pricing
  "coach.plan.weekly": "Weekly plan",
  "coach.plan.yearly": "Yearly plan",
  "coach.plan.lifetime": "Lifetime plan",
  "coach.per.week": "/ week",
  "coach.per.year": "/ year",
  "coach.per.month": "/ month",
  "coach.per.one_time": "one-time payment",
  "coach.equivalent": "Equivalent",
  "coach.badge.flexible": "Flexible",
  "coach.badge.best_value": "Best value",
  "coach.badge.lifetime": "Lifetime access",
  "coach.feature.learning_content": "Full access to learning content",
  "coach.feature.quizzes": "Interactive quizzes",
  "coach.feature.personalized": "Personalised recommendations",
  "coach.feature.ai_coaching": "AI coaching",
  "coach.feature.all_features": "Full access to all features",
  "coach.feature.premium_paths": "Premium learning paths",
  "coach.feature.exclusive_content": "Exclusive educational content",
  "coach.feature.priority_updates": "Priority updates",
  "coach.feature.permanent_access": "Permanent platform access",
  "coach.feature.future_updates": "Future updates included",
  "coach.feature.premium_content": "Premium content access",

  // Home pillars
  "home.pillars.title_a": "One ecosystem,",
  "home.pillars.title_b": "many ways to explore wine.",
  "home.pillars.sub": "From maps to learning to professional integrations — discover the geoVINUM family.",

  // Nav
  "nav.products": "Products",
  "nav.resources": "Resources",
  "nav.explore": "Explore",
  "nav.academy": "Academy",
  "nav.maps_short": "Maps",
  "nav.shop": "Shop",
  "nav.business": "Business",
  "nav.terroir_ai": "geoVINUM app",
  "nav.blog": "Blog",
  "nav.resources": "Resources",
  "nav.resources.articles": "Blog · Articles",
  "nav.resources.atlas": "Appellation atlas",
  "nav.resources.cepages": "Grape varieties",
  "nav.resources.ebooks": "Free ebooks",
  "cmd.kind.atlas_appellation": "Atlas · Appellation",
  "cmd.kind.atlas_cepage": "Atlas · Grape variety",
  "cmd.kind.blog": "Article",
  "cmd.kind.academy": "Academy series",
  "cmd.kind.shop": "Shop",
  "nav.open_app": "Open app",
  "nav.download_app": "Open app",
  "nav.atlas_mini": "geoVINUM Atlas",
  "nav.atlas_magnum": "geoVINUM Atlas",
  "nav.atlas_pro": "geoVINUM Atlas",
  "nav.product.academy_short": "Academy",
  "nav.product.coach_short": "Coach",
  "nav.product.iframe_short": "Iframe",
  "nav.product.cellar_short": "Portfolio",
  "nav.product.tasting_short": "Tasting",
  "business.h1_a": "Wine experiences for",
  "business.h1_b": "your business.",
  "business.sub": "Integrate interactive maps, engage customers and create memorable tasting experiences.",
  "business.cta_title": "Talk to our team",
  "business.cta_text": "Tell us about your shop, school, winery or product — we'll tailor a partnership that fits.",

  // ----- Academy v2 (Netflix UX + subscription) -----
  "academy.seo.title": "geoVINUM Academy — The Netflix of wine terroirs",
  "academy.seo.desc": "Watch beautifully crafted wine series. Learn regions, grapes and terroirs at your own pace.",
  "academy.badge": "Streaming series",
  "academy.hero_a": "The Netflix of",
  "academy.hero_b": "wine terroirs.",
  "academy.hero_sub": "Beautifully crafted series exploring the world's wine regions, grapes and people. New episodes every month.",
  "academy.cta_start": "Start watching",
  "academy.cta_featured": "Featured series",
  "academy.row.featured": "Featured this month",
  "academy.row.regions": "Regions of the world",
  "academy.row.fundamentals": "Wine fundamentals",
  "academy.row.tasting": "Tasting & pairing",
  "academy.pricing.eyebrow": "Subscription",
  "academy.pricing.title": "One subscription, unlimited terroirs.",
  "academy.pricing.sub": "Cancel anytime. New episodes every month.",
  "academy.plan.monthly.title": "Monthly",
  "academy.plan.monthly.per": "/ month HT",
  "academy.plan.monthly.note": "Billed monthly. Cancel anytime.",
  "academy.plan.yearly.title": "Yearly",
  "academy.plan.yearly.per": "/ year HT",
  "academy.plan.yearly.note": "Best value · ~€15.83/month",
  "academy.plan.yearly.badge": "Save 17%",
  "academy.plan.cta": "Subscribe",
  "academy.vat_note": "Prices excl. VAT. VAT will be added at checkout based on your country.",
  "academy.feature.all_courses": "All series and episodes",
  "academy.feature.new_episodes": "New episodes every month",
  "academy.feature.cancel_anytime": "Cancel anytime",
  "academy.feature.multi_language": "Available in 4 languages",
  "academy.feature.priority": "Priority access to new releases",

  // ----- Library coming soon -----
  "library.seo.title": "geoVINUM Library — Books, notebooks & wine accessories",
  "library.seo.desc": "A coming-soon shop for wine lovers: handcrafted books, tasting notebooks and curated accessories.",
  "library.eyebrow": "Coming soon",
  "library.title_a": "The geoVINUM",
  "library.title_b": "library.",
  "library.sub": "A curated collection of books, tasting notebooks and wine accessories — designed for collectors, sommeliers and lovers of fine geography.",
  "library.notify_me": "Notify me",
  "library.success": "You're on the list. We'll write when the first editions ship.",
  "library.tiles_eyebrow": "What to expect",
  "library.tiles_title": "Three product lines",
  "library.tile.books.eyebrow": "Books",
  "library.tile.books.title": "Wine atlases & terroir books",
  "library.tile.books.desc": "Hardcover editions of the world's wine regions, with maps, photography and terroir essays.",
  "library.tile.notebooks.eyebrow": "Notebooks",
  "library.tile.notebooks.title": "Tasting notebooks",
  "library.tile.notebooks.desc": "Premium-paper tasting journals to record your visits, vintages and food pairings.",
  "library.tile.accessories.eyebrow": "Accessories",
  "library.tile.accessories.title": "Curated wine accessories",
  "library.tile.accessories.desc": "Decanters, glassware, openers and small objects we love and use at the portfolio.",


  "biz.hero.title_a": "Turn your terroir into a sales, marketing and educational",
  "biz.hero.title_b": "asset.",
  "biz.hero.sub": "geoVINUM helps wineries, wine regions, importers and educators showcase terroirs through interactive maps, storytelling and immersive wine experiences.",
  "biz.hero.cta_primary": "Request a demo",
  "biz.hero.cta_secondary": "See use cases",

  "biz.why.eyebrow": "Why geoVINUM",
  "biz.why.title": "Why organisations choose geoVINUM",
  "biz.why.bullet.terroirs": "Explain terroirs visually",
  "biz.why.bullet.storytelling": "Improve wine storytelling",
  "biz.why.bullet.engagement": "Increase website engagement",
  "biz.why.bullet.education": "Support wine education",
  "biz.why.bullet.differentiate": "Differentiate your brand",

  "biz.who.eyebrow": "Who is it for",
  "biz.who.title": "Built for the wine industry",
  "biz.who.wineries.title": "Wineries",
  "biz.who.wineries.desc": "Showcase your terroir, strengthen your brand story and create engaging wine experiences.",
  "biz.who.importers.title": "Importers & Distributors",
  "biz.who.importers.desc": "Help your teams understand wine regions and communicate wines more effectively.",
  "biz.who.regions.title": "Wine Regions & Interprofessions",
  "biz.who.regions.desc": "Promote appellations and territories through interactive and educational experiences.",
  "biz.who.education.title": "Education & Training",
  "biz.who.education.desc": "Bring wine geography and terroirs to life for students and professionals.",

  "biz.problem.eyebrow": "The problem",
  "biz.problem.title": "Why most wine content fails to engage",
  "biz.problem.p1": "Traditional wine communication often relies on static PDFs, technical sheets and long descriptions.",
  "biz.problem.p2": "Customers struggle to understand terroirs, wine regions and what makes a wine unique.",
  "biz.problem.p3": "geoVINUM transforms complex wine information into visual and interactive experiences.",

  "biz.benefits.eyebrow": "Benefits",
  "biz.benefits.title": "What geoVINUM helps you achieve",
  "biz.benefits.storytelling.title": "Improve wine storytelling",
  "biz.benefits.storytelling.desc": "Help customers understand the origin and identity of your wines.",
  "biz.benefits.engagement.title": "Increase engagement",
  "biz.benefits.engagement.desc": "Interactive maps create memorable experiences and keep visitors engaged longer.",
  "biz.benefits.sales.title": "Support sales teams",
  "biz.benefits.sales.desc": "Provide visual tools that make wine explanations easier and more impactful.",
  "biz.benefits.education.title": "Enhance wine education",
  "biz.benefits.education.desc": "Turn complex wine regions into easy-to-understand learning experiences.",

  "biz.solutions.eyebrow": "Solutions",
  "biz.solutions.title": "Our wine experience platform",
  "biz.solutions.maps.desc": "Interactive wine maps that can be embedded into websites, landing pages and educational platforms.",
  "biz.solutions.maps.use.winery_websites": "Winery websites",
  "biz.solutions.maps.use.wine_tourism": "Wine tourism",
  "biz.solutions.maps.use.edu_content": "Educational content",
  "biz.solutions.maps.use.regional_promo": "Regional promotion",
  "biz.solutions.magnum.desc": "The most complete educational wine atlas — 100+ Academy masterclasses, interactive maps and offline mode. Built for wine professionals in training.",
  "biz.solutions.magnum.use.wset_students": "WSET students",
  "biz.solutions.magnum.use.sommeliers": "Sommeliers",
  "biz.solutions.magnum.use.wine_pros": "Wine professionals",
  "biz.solutions.magnum.use.education": "Continuous education",
  "biz.solutions.pro.desc": "B2B wine atlas with multi-user accounts, white-label API, custom training and priority support. For teams that sell, teach or distribute wine.",
  "biz.solutions.pro.use.importers": "Importers",
  "biz.solutions.pro.use.distributors": "Distributors",
  "biz.solutions.pro.use.merchants": "Wine merchants",
  "biz.solutions.pro.use.schools": "Wine schools",
  "biz.solutions.portfolio.desc": "Digital wine experiences that connect customers with the story behind every bottle.",
  "biz.solutions.portfolio.use.winery_visits": "Winery visits",
  "biz.solutions.portfolio.use.qr_code": "QR code experiences",
  "biz.solutions.portfolio.use.storytelling": "Product storytelling",
  "biz.solutions.portfolio.use.brand_engagement": "Brand engagement",
  "biz.solutions.tasting.desc": "Interactive tasting experiences designed to engage visitors and educate consumers.",
  "biz.solutions.tasting.use.events": "Events",
  "biz.solutions.tasting.use.wine_fairs": "Wine fairs",
  "biz.solutions.tasting.use.cellar_doors": "Portfolio doors",
  "biz.solutions.tasting.use.hospitality": "Hospitality",

  "biz.usecases.eyebrow": "Use cases",
  "biz.usecases.title": "How professionals use geoVINUM",
  "biz.usecases.tradefair.title": "Before a trade fair",
  "biz.usecases.tradefair.desc": "Create engaging visual supports to present your wines and regions.",
  "biz.usecases.website.title": "On a winery website",
  "biz.usecases.website.desc": "Add interactive terroir maps to improve visitor engagement.",
  "biz.usecases.export.title": "For export sales",
  "biz.usecases.export.desc": "Help international buyers understand your wine regions.",
  "biz.usecases.tourism.title": "For wine tourism",
  "biz.usecases.tourism.desc": "Create memorable experiences for visitors.",
  "biz.usecases.training.title": "For training teams",
  "biz.usecases.training.desc": "Teach wine geography more efficiently.",

  "biz.examples.eyebrow": "Examples",
  "biz.examples.title": "See geoVINUM in action",
  "biz.examples.map": "Interactive wine map",
  "biz.examples.domain": "Embedded into your domain",
  "biz.examples.mobile": "Mobile and tablet experience",
  "biz.examples.sheet": "Enriched wine sheet",

  "biz.position.eyebrow": "Why geoVINUM",
  "biz.position.title": "More than maps",
  "biz.position.p1": "geoVINUM combines wine geography, terroir storytelling and digital experiences in a single platform.",
  "biz.position.p2": "Unlike generic mapping or presentation tools, geoVINUM is built specifically for the wine industry.",

  "biz.social.eyebrow": "Social proof",
  "biz.social.title": "Trusted by wine professionals",
  "biz.social.stat.regions.value": "30+",
  "biz.social.stat.regions.label": "Wine regions",
  "biz.social.stat.wineries.value": "200+",
  "biz.social.stat.wineries.label": "Wineries",
  "biz.social.stat.maps.value": "500+",
  "biz.social.stat.maps.label": "Interactive maps",
  "biz.social.stat.countries.value": "25+",
  "biz.social.stat.countries.label": "Countries",

  "biz.faq.eyebrow": "FAQ",
  "biz.faq.title": "Frequently asked questions",
  "biz.faq.orgs.q": "What types of organisations use geoVINUM?",
  "biz.faq.orgs.a": "Wineries, wine regions and interprofessions, importers and distributors, education and training institutions, hospitality and wine tourism professionals.",
  "biz.faq.embed.q": "Can maps be embedded into our website?",
  "biz.faq.embed.a": "Yes — geoVINUM Maps can be embedded into any website or landing page via a simple iframe snippet, fully responsive and customisable.",
  "biz.faq.languages.q": "Is geoVINUM available in multiple languages?",
  "biz.faq.languages.a": "Yes — geoVINUM is available in English, French, Spanish and Portuguese, with more languages on the roadmap.",
  "biz.faq.tourism.q": "Can geoVINUM support wine tourism projects?",
  "biz.faq.tourism.a": "Absolutely. Many regions use geoVINUM to enrich wine tourism offerings with interactive maps, terroir storytelling and portfolio door experiences.",
  "biz.faq.education.q": "Can geoVINUM be used for wine education?",
  "biz.faq.education.a": "Yes — schools, sommelier programs and corporate training teams use geoVINUM Academy and Maps to teach wine geography effectively.",
  "biz.faq.custom.q": "Do you offer custom projects?",
  "biz.faq.custom.a": "Yes — we design custom maps, branded experiences and bespoke integrations for partners. Contact us to discuss your project.",

  "biz.final.title": "Ready to showcase your terroir differently?",
  "biz.final.body": "Discover how geoVINUM can help your organisation create engaging wine experiences and better communicate the value of its wines.",
  "biz.final.cta_primary": "Request a demo",
  "biz.final.cta_secondary": "Contact us",

  "biz.shared.use_cases_label": "Use cases",
  "biz.shared.coming_soon": "Coming soon",
  "biz.shared.view_demo": "View demo",
  "biz.shared.join_waitlist": "Join waitlist",
  "biz.shared.learn_more": "Learn more",
  "home.hero.h1_b": "Discover wine through terroir.",
  "home.hero.h1_main": "",
  "home.hero.sub_v2": "Explore wine regions, learn wine culture and experience terroirs through maps, education and interactive tools.",
  "home.cta.explore_atlas": "Explore Atlas",
  "home.cta.browse_courses": "Browse Courses",
  "home.cta.browse_collection": "Browse Collection",
  "home.cta.discover_iframe": "Discover Iframe",
  "home.cta.join_waitlist": "Join Waitlist",
  "home.s1.eyebrow": "Explore Wine Regions",
  "home.s1.title": "Navigate wine regions, appellations and grape varieties with",
  "home.s1.title_em": "interactive maps.",
  "home.s2.eyebrow": "Learn Wine",
  "home.s2.title": "Structured courses, certifications and",
  "home.s2.title_em": "educational content.",
  "home.s3.eyebrow": "Collect Wine Regions",
  "home.s3.title": "Premium wine maps and",
  "home.s3.title_em": "educational posters.",
  "home.s4.eyebrow": "For Wine Businesses",
  "home.s4.title": "Embed, engage and",
  "home.s4.title_em": "delight your customers.",
  "footer.heading.products": "Products",
  "footer.heading.resources": "Resources",
  "footer.heading.company": "Company",
  "footer.heading.social": "Social",
  "footer.link.about": "About",
  "footer.link.contact": "Contact",
  "footer.link.newsletter": "Newsletter",
  "footer.link.support": "Support",
  "nav.product.atlas": "Atlas — Explore",

  // ----- Newsletter signup (EN) -----
  "newsletter.placeholder.email": "your@email.com",
  "newsletter.submit": "Subscribe",
  "newsletter.consent.label": "I agree to receive geoVINUM's monthly newsletter and consent to the processing of my email address for this purpose. I can unsubscribe at any time via the link in every email.",
  "newsletter.success": "Thanks! You're subscribed. Check your inbox.",
  "newsletter.error.consent_required": "Please accept the consent box to subscribe.",
  "newsletter.error.generic": "Subscription failed. Please try again.",
  // ----- Newsletter standalone page (EN) -----
  "newsletterPage.meta.title": "Newsletter — geoVINUM",
  "newsletterPage.meta.desc": "Subscribe to the monthly geoVINUM newsletter — appellations, terroirs, tastings and behind-the-scenes news.",
  "newsletterPage.eyebrow": "Stay in the loop",
  "newsletterPage.title": "The Terroirs Letter",
  "newsletterPage.subtitle": "The geoVINUM newsletter — one monthly email. New maps, appellations spotlight, tastings and stories from the vineyard. No spam, unsubscribe in one click.",
  "newsletterPage.form.eyebrow": "Subscribe",
  "newsletterPage.contactSent.title": "Message received — thanks!",
  "newsletterPage.contactSent.desc": "We've received your message and Audrey will reply within 48h (business days). While you're here, want to stay in touch through the newsletter?",

  // ----- Contact form (EN) -----
  "contact.eyebrow": "Contact us",
  "contact.title": "Get in touch",
  "contact.subtitle": "A question, a partnership idea, or feedback? Write us — we reply within 48h on business days.",
  "contact.field.name": "Name",
  "contact.field.email": "Email",
  "contact.field.subject": "Subject",
  "contact.field.message": "Message",
  "contact.submit": "Send message",
  "contact.consent.label": "I consent to geoVINUM processing my personal data to respond to this enquiry. Legal basis: my consent (GDPR art. 6.1.a). I can withdraw it at any time by writing to hello@geovinum.io.",
  "contact.success.title": "Message sent",
  "contact.success.desc": "We've received your message and will reply soon.",
  "contact.error.consent_required": "Please accept the consent box to send your message.",
  "contact.error.generic": "Could not send the message. Please try again.",
  "nav.product.portfolio": "Portfolio — Discover",
  "nav.product.tasting": "Tasting — Experience",
  "nav.product.coach": "Coach — Learn",
  "nav.product.maps": "Maps — Collect",
  "nav.product.iframe": "Iframe — Integrate",
  "nav.product.academy": "Academy — Learn",

  // Article
  "article.not_found": "Article not found.",

  // Legal
  "legal.eyebrow": "Legal",
  "legal.sections": "Sections",
  "legal.contact_q": "Questions about this document? Contact",

  // Language switcher
  "lang.switch_label": "Language",

  // === Auto-injected i18n keys for new pages (en) ===
  "academyM.seo.title": "geoVINUM Academy — Learn wine visually",
  "academyM.seo.desc": "100+ streaming masterclasses · {days} days free.",
  "academyM.hero.title": "Understand wine, visually.",
  "academyM.hero.desc": "Over 100 streaming masterclasses for WSET students, wine pros and enthusiasts. Interactive maps, expert-led terroir lessons, tasting methodology — all in one place.",
  "academyM.hero.binge": "✦ Binge every series, without limits — full access included in your subscription.",
  "academyM.hero.cta_primary": "Start {days} days free",
  "academyM.hero.cta_secondary": "Browse series",
  "academyM.hero.trust.no_commitment": "No commitment",
  "academyM.hero.trust.cancel_anytime": "Cancel anytime",
  "academyM.hero.trust.hd_streaming": "HD streaming",
  "academyM.why.eyebrow": "Why Academy",
  "academyM.why.title": "Wine, finally clear",
  "academyM.why.desc": "A visual and progressive approach to wine, designed by pros for pros and serious enthusiasts.",
  "academyM.why.visual.title": "Visual learning",
  "academyM.why.visual.desc": "Interactive maps, diagrams, 3D terroir visualisations. You see what you learn.",
  "academyM.why.wset.title": "WSET-aligned",
  "academyM.why.wset.desc": "Curriculum aligned with WSET Level 2 and 3. Ideal to revise, deepen, or start a career in wine.",
  "academyM.why.experts.title": "Certified experts",
  "academyM.why.experts.desc": "Masterclasses led by certified sommeliers, oenologists and specialist wine journalists.",
  "academyM.audience.eyebrow": "For whom",
  "academyM.audience.title": "Built for 3 profiles",
  "academyM.audience.wset.title": "WSET students",
  "academyM.audience.wset.desc": "Prepare WSET Level 2 and 3 certifications with unmatched visual support. Revising a region takes 15 minutes instead of an hour.",
  "academyM.audience.wset.badge": "Most popular",
  "academyM.audience.pros.title": "Wine professionals",
  "academyM.audience.pros.desc": "Sommeliers, wine merchants, restaurateurs: level up on the regions you sell or present to your clients.",
  "academyM.audience.amateurs.title": "Advanced enthusiasts",
  "academyM.audience.amateurs.desc": "You love wine and want to go beyond labels. Understand terroir, climate, history — without jargon.",
  "academyM.how.eyebrow": "How it works",
  "academyM.how.title": "3 steps to get started",
  "academyM.how.step1.title": "Activate trial",
  "academyM.how.step1.desc": "Sign up in 30 seconds. {days} days of full access, no credit card required to start.",
  "academyM.how.step2.title": "Choose a series",
  "academyM.how.step2.desc": "Browse 100+ masterclasses by category: regions, grapes, tasting, business.",
  "academyM.how.step3.title": "Watch & progress",
  "academyM.how.step3.desc": "HD streaming, offline mode on the geoVINUM Atlas app. Resume where you left off.",
  "academyM.preview.eyebrow": "Preview",
  "academyM.preview.title": "A few series from the library",
  "academyM.preview.see_all": "See all series",
  "academyM.preview.loading": "Loading series…",
  "academyM.slider.featured": "Featured",
  "academyM.slider.videos": "videos",
  "academyM.slider.goto": "Go to series {n}",
  "academyM.final.title": "Start your {days} days free",
  "academyM.final.desc": "No credit card. No commitment. Cancel anytime from your profile.",
  "academyM.final.cta": "Start now",
  "academyW.seo.title": "Academy — geoVINUM",
  "academyW.seo.desc": "Your streaming masterclasses.",
  "academyW.upsell.text": "Browse the series freely — the subscription unlocks unlimited viewing.",
  "academyW.upsell.cta": "7-day free trial",
  "academyW.hero.eyebrow_featured": "Featured this week",
  "academyW.hero.default_title": "Understand wine, visually.",
  "academyW.hero.default_desc": "100+ streaming masterclasses, interactive maps and a pedagogical atlas for WSET students, sommeliers and enthusiasts.",
  "academyW.hero.watch": "Watch the series",
  "academyW.hero.trial30": "30 days free",
  "academyW.hero.search_cta": "Search a series…",
  "academyW.chip.all": "All",
  "academyW.cat.wine_insights": "Wine insights",
  "academyW.cat.guides": "Guides",
  "academyW.cat.get_started": "Get started",
  "academyW.cat.for_professionals": "For professionals",
  "academyW.loading": "Loading series…",
  "academyW.empty.title": "No series published yet",
  "academyW.empty.desc": "Academy series will be available here soon. In the meantime, discover our Atlas products.",
  "academyW.series_count_one": "series",
  "academyW.series_count_other": "series",
  "academyW.search.placeholder": "Search by series, topic, region…",
  "academyW.search.no_results": "No results for « {q} »",
  "academyW.videos_label": "videos",
  "atlasP.not_found.title": "Atlas product not found",
  "atlasP.not_found.cta": "View all Atlas products",
  "atlasP.bc.home": "Home",
  "atlasP.bc.atlas": "Atlas",
  "atlasP.trial_days": "{days} days free",
  "atlasP.who.label": "Who is it for?",
  "atlasP.when.label": "When to use it?",
  "atlasP.when.title": "At the right moment",
  "atlasP.why.label": "Why?",
  "atlasP.why.title": "Unique value",
  "atlasP.features.eyebrow": "Features",
  "atlasP.features.title": "What {name} brings you",
  "atlasP.upsell.title": "Go further",
  "atlasP.upsell.discover": "Discover",
  "atlasP.final.title": "Ready to try {name}?",
  "atlasP.final.trial": "{days} days free · no commitment · cancel anytime",
  "atlasP.mini.tagline": "Get started simply",
  "atlasP.mini.cta_primary": "Open the app",
  "atlasP.mini.cta_secondary": "Explore geoVINUM Atlas",
  "atlasP.mini.price": "Free",
  "atlasP.mini.price_note": "Web — no install needed",
  "atlasP.mini.audience.label": "Beginners & curious",
  "atlasP.mini.audience.desc": "Enthusiasts discovering wine who want to visually explore the world's wine regions on any device.",
  "atlasP.mini.when": "When you're at a restaurant, in a portfolio, or travelling and want to quickly understand where a wine comes from.",
  "atlasP.mini.why": "A simple, free and intuitive web app — the gateway into the geoVINUM universe.",
  "atlasP.mini.feat.1": "30+ wine regions mapped",
  "atlasP.mini.feat.2": "Search by appellation",
  "atlasP.mini.feat.3": "Vineyard geolocation",
  "atlasP.mini.feat.4": "Pedagogical summary by region",
  "atlasP.mini.feat.5": "100% free, no install",
  "atlasP.mini.upsell.magnum.title": "geoVINUM Atlas",
  "atlasP.mini.upsell.magnum.desc": "Go further in your wine education",
  "atlasP.mini.upsell.academy.title": "geoVINUM Academy",
  "atlasP.mini.upsell.academy.desc": "100+ streaming masterclasses",
  "atlasP.magnum.tagline": "The reference tool to understand wine",
  "atlasP.magnum.cta_primary": "Start 30 days free",
  "atlasP.magnum.cta_secondary": "See the Pro version",
  "atlasP.magnum.price": "Monthly subscription",
  "atlasP.magnum.price_note": "30 days free, no commitment",
  "atlasP.magnum.audience.label": "WSET students, professionals, advanced enthusiasts",
  "atlasP.magnum.audience.desc": "Those who want to master wine with a complete pedagogical tool: sommeliers in training, WSET students, hospitality pros.",
  "atlasP.magnum.when": "When you're prepping a WSET exam, revising a region before service, or building your wine culture over the long term.",
  "atlasP.magnum.why": "100+ integrated Academy video masterclasses, high-definition interactive maps, detailed appellation sheets, continuous updates.",
  "atlasP.magnum.feat.1": "All wine regions worldwide",
  "atlasP.magnum.feat.2": "High-definition interactive maps",
  "atlasP.magnum.feat.3": "Academy included: 100+ masterclasses",
  "atlasP.magnum.feat.4": "Detailed appellation sheets",
  "atlasP.magnum.feat.5": "Offline mode for travel",
  "atlasP.magnum.feat.6": "Automatic updates",
  "atlasP.magnum.upsell.pro.title": "geoVINUM Atlas (B2B)",
  "atlasP.magnum.upsell.pro.desc": "For importers & wine merchants",
  "atlasP.magnum.upsell.shop.title": "Printed maps",
  "atlasP.magnum.upsell.shop.desc": "Posters & framed prints of your regions",
  "atlasP.pro.tagline": "The B2B solution for wine professionals",
  "atlasP.pro.cta_primary": "Request a demo",
  "atlasP.pro.cta_secondary": "30-day trial",
  "atlasP.pro.price": "Quote on demand",
  "atlasP.pro.price_note": "Annual B2B subscription · 30-day trial",
  "atlasP.pro.audience.label": "Importers, distributors, wine merchants, wine schools",
  "atlasP.pro.audience.desc": "Organisations that need to train their sales teams, equip their force, or integrate geoVINUM into their website and catalogue.",
  "atlasP.pro.when": "When you run a sales team, a tasting room, a wine school, or an e-commerce site that must sell the story before the product.",
  "atlasP.pro.why": "Multi-user account, full Academy library access, API to embed our maps on your site (iframe), bespoke training, priority support.",
  "atlasP.pro.feat.1": "Multi-user accounts (teams)",
  "atlasP.pro.feat.2": "Full Academy + Atlas access",
  "atlasP.pro.feat.3": "API & iframe for your website",
  "atlasP.pro.feat.4": "White-label customisable maps",
  "atlasP.pro.feat.5": "Bespoke training included",
  "atlasP.pro.feat.6": "Dedicated support & onboarding",
  "atlasP.pro.feat.7": "Usage reporting per team",
  "atlasP.pro.upsell.iframe.title": "geoVINUM Iframe",
  "atlasP.pro.upsell.iframe.desc": "Embed our maps on your website",
  "atlasP.pro.upsell.business.title": "Discover Business",
  "atlasP.pro.upsell.business.desc": "Portfolio, Tasting, and more",
  "blogP.seo.title": "Blog — geoVINUM",
  "blogP.seo.desc": "Articles, guides and tips to understand wine visually.",
  "blogP.bc.home": "Home",
  "blogP.bc.blog": "Blog",
  "blogP.title": "geoVINUM Blog",
  "blogP.intro": "Wine insights, practical guides, first steps and content for pros — all our wine editorial in one place.",
  "blogP.search.placeholder": "Search an article…",
  "blogP.chip.all": "All",
  "blogP.cat.wine_insights": "Wine insights",
  "blogP.cat.guides": "Guides",
  "blogP.cat.get_started": "Get started",
  "blogP.cat.for_professionals": "For professionals",
  "blogP.cat.ebook": "Ebook",
  "blogP.cat.cepage": "Grape varieties",
  "ebookF.eyebrow": "Free ebook",
  "ebookF.title": "Download the ebook",
  "ebookF.intro": "Leave your email below to receive the PDF instantly. You'll also get a copy by email so you can revisit it from any device.",
  "ebookF.email_label": "Email",
  "ebookF.email_placeholder": "you@domain.com",
  "ebookF.consent_text": "I agree that geoVINUM may store this email to send me the ebook and respond to any related request. Right to access, rectify and erasure at any time.",
  "ebookF.newsletter_label": "Also subscribe me to the geoVINUM newsletter (1 to 2 emails/month — unsubscribe in one click).",
  "ebookF.submit": "Get the ebook",
  "ebookF.submitting": "Sending…",
  "ebookF.success_title": "Ebook ready",
  "ebookF.success_email_sent": "We've also emailed you the link.",
  "ebookF.success_msg": "Your download should start in a new tab. If it didn't, use the button below.",
  "ebookF.download_btn": "Download now",
  "ebookF.pages_label": "pages",
  "ebookF.gdpr_hint": "Your data stays with geoVINUM. No spam.",
  "ebookF.error_email": "A valid email is required.",
  "ebookF.error_consent": "Please tick the consent box to download the ebook.",
  "blogP.loading": "Loading…",
  "blogP.empty.title": "No articles yet",
  "blogP.empty.no_results": "No results for « {q} »",
  "blogP.read_minutes": "min",
  "blogP.article.loading": "Loading…",
  "blogP.article.not_found": "Article not found",
  "blogP.article.back": "Back to blog",
  "blogP.article.by": "By",
  "blogP.article.read_time": "min read",
  "blogP.article.cross.eyebrow": "Go further",
  "blogP.article.cross.title": "Explore the geoVINUM universe",
  "blogP.article.cross.shop_label": "Shop",
  "blogP.article.cross.shop_title": "Vineyard maps",
  "blogP.article.cross.shop_desc": "Posters, framed prints, canvas",
  "blogP.article.cross.academy_label": "Academy",
  "blogP.article.cross.academy_title": "Streaming masterclasses",
  "blogP.article.cross.academy_desc": "7 days free",
  "blogP.article.cross.terroir_label": "geoVINUM app",
  "blogP.article.cross.terroir_title": "Talk to terroir",
  "blogP.article.cross.terroir_desc": "Free to try · 4 languages",
  "bizM.eyebrow": "Comparison",
  "bizM.title": "When · Who · Why",
  "bizM.subtitle": "Find at a glance which geoVINUM solution matches your need.",
  "bizM.col.solution": "Solution",
  "bizM.col.when": "When to use it",
  "bizM.col.who": "Who it's for",
  "bizM.col.why": "Why",
  "bizM.col.action": "Action",
  "bizM.soon": "Soon",
  "bizM.discover": "Discover",
  "bizM.label.when": "When",
  "bizM.label.who": "For whom",
  "bizM.label.why": "Why",
  "bizM.magnum.name": "geoVINUM Atlas",
  "bizM.magnum.when": "Preparing a WSET exam, levelling up, equipping a sommellerie team",
  "bizM.magnum.who": "Sommeliers, WSET students, hospitality pros",
  "bizM.magnum.why": "The most complete pedagogical tool on the market, 100+ masterclasses included",
  "bizM.pro.name": "geoVINUM Atlas",
  "bizM.pro.when": "Training a sales force, building an interactive catalogue, equipping a tasting room",
  "bizM.pro.who": "Importers, distributors, wine merchants, wine schools",
  "bizM.pro.why": "Multi-user, white-label API, dedicated support, measurable ROI",
  "bizM.iframe.name": "Iframe",
  "bizM.iframe.when": "You have a website and want to tell the terroir story before selling",
  "bizM.iframe.who": "Wineries, syndicates, tourism offices, wine e-commerce",
  "bizM.iframe.why": "1-line integration, HD maps, multi-language",
  "bizM.portfolio.name": "Portfolio",
  "bizM.portfolio.when": "You want to digitalise the customer experience in your portfolio or shop",
  "bizM.portfolio.who": "Wineries, cooperatives, experiential boutiques",
  "bizM.portfolio.why": "Immersive QR codes, visual storytelling, drive visits into revenue",
  "bizM.tasting.name": "Tasting",
  "bizM.tasting.when": "You run trade fairs, wine events or wine tourism experiences",
  "bizM.tasting.who": "Trade fairs, event organisers, premium hospitality",
  "bizM.tasting.why": "Playful, memorable gamification — boosts lead capture rate",

  // === searchP keys (en) ===
  "searchP.seo.title": "Search — geoVINUM",
  "searchP.seo.desc": "Search across the geoVINUM blog, Academy series and Shop.",
  "searchP.title": "Search",
  "searchP.intro": "Find articles, Academy series and printed maps — all in one place.",
  "searchP.placeholder": "Search a region, topic, grape, series…",
  "searchP.chip.all": "All",
  "searchP.chip.blog": "Blog",
  "searchP.chip.academy": "Academy",
  "searchP.chip.shop": "Shop",
  "searchP.kind.blog": "Blog",
  "searchP.kind.academy": "Academy",
  "searchP.kind.shop": "Shop",
  "searchP.loading": "Searching…",
  "searchP.from": "from",
  "searchP.empty.prompt": "Type something to start searching.",
  "searchP.empty.no_results_title": "No results",
  "searchP.empty.no_results_desc": "Try another region, grape or topic.",

  // === dashboard keys (en) ===
  "dashboard.track_shipment": "Track shipment",
  "dashboard.view_all_orders": "View all orders",

  // === hero slides (en) ===
  "hero.label.for": "For whom",
  "hero.label.why": "Why",
  "hero.see_all": "See all products",
  "hero.slide.promise.eye": "geoVINUM",
  "hero.slide.promise.title": "Understand wine, visually.",
  "hero.slide.promise.for": "Wine lovers, students & pros — from Bordeaux to Mendoza",
  "hero.slide.promise.why": "+3 000 wine appellations mapped — explore, learn and choose wine without the jargon.",
  "hero.slide.promise.cta": "Discover geoVINUM",
  "hero.slide.mini.eye": "geoVINUM Atlas",
  "hero.slide.mini.title": "Discover wine on your phone, for free.",
  "hero.slide.mini.for": "Curious beginners & travellers",
  "hero.slide.mini.why": "30+ regions mapped, geolocation, 100% free web app.",
  "hero.slide.mini.cta": "Open geoVINUM Atlas",
  "hero.slide.terroir.eye": "geoVINUM app",
  "hero.slide.terroir.title": "Talk to terroir. Anywhere, in any language.",
  "hero.slide.terroir.for": "Wine lovers, wine bars, merchants, schools & producers",
  "hero.slide.terroir.why": "One AI app for everything — Q&A, flashcards, blind tasting, photo-to-guide, multiplayer. PWA, no install.",
  "hero.slide.terroir.cta": "Launch geoVINUM app",
  "hero.slide.magnum.eye": "geoVINUM Atlas",
  "hero.slide.magnum.title": "Master wine, visually.",
  "hero.slide.magnum.for": "WSET students, sommeliers, hospitality pros",
  "hero.slide.magnum.why": "100+ video masterclasses + interactive maps. 30 days free.",
  "hero.slide.magnum.cta": "Try geoVINUM Atlas free",
  "hero.slide.academy.eye": "Academy",
  "hero.slide.academy.title": "Wine masterclasses, on demand.",
  "hero.slide.academy.for": "Anyone who wants to truly understand wine",
  "hero.slide.academy.why": "Binge 100+ HD masterclasses. 7 days free, then 9€/mo.",
  "hero.slide.academy.cta": "Start 7 days free",
  "hero.slide.shop.eye": "Shop",
  "hero.slide.shop.title": "Wine maps you can hang on the wall.",
  "hero.slide.shop.for": "Collectors, restaurants, gift seekers",
  "hero.slide.shop.why": "Premium posters, framed prints, canvas — printed in EU, shipped worldwide.",
  "hero.slide.shop.cta": "Browse maps",
  "hero.slide.pro.eye": "geoVINUM Atlas",
  "hero.slide.pro.title": "Equip your wine team.",
  "hero.slide.pro.for": "Importers, distributors, wine schools",
  "hero.slide.pro.why": "Multi-user accounts, white-label API, bespoke training, priority support.",
  "hero.slide.pro.cta": "Book a demo",
  "hero.slide.iframe.eye": "Iframe",
  "hero.slide.iframe.title": "Embed our maps on your site.",
  "hero.slide.iframe.for": "Wineries, tourism offices, wine e-commerce",
  "hero.slide.iframe.why": "1 line of HTML, HD interactive maps, multi-language ready.",
  "hero.slide.iframe.cta": "Get the embed code",
  "hero.slide.blog.eye": "Blog",
  "hero.slide.blog.title": "Wine, explained one article at a time.",
  "hero.slide.blog.for": "Lifelong learners",
  "hero.slide.blog.why": "Insights, guides, first steps and pro tips — every week.",
  "hero.slide.blog.cta": "Read the blog",

  // === hero slides extra (en) ===
  "hero.soon": "Soon",
  "hero.slide.portfolio.eye": "Portfolio",
  "hero.slide.portfolio.title": "Turn your portfolio into a story.",
  "hero.slide.portfolio.for": "Wineries, cooperatives, experiential boutiques",
  "hero.slide.portfolio.why": "Immersive QR codes & visual storytelling — turn visits into revenue.",
  "hero.slide.portfolio.cta": "Discover Portfolio",
  "hero.slide.tasting.eye": "Tasting",
  "hero.slide.tasting.title": "Gamified wine tasting for events.",
  "hero.slide.tasting.for": "Trade fairs, event organisers, premium hospitality",
  "hero.slide.tasting.why": "Memorable, playful gamification — boost lead capture rates.",
  "hero.slide.tasting.cta": "Discover Tasting",
  "hero.slide.coach.eye": "Coach",
  "hero.slide.coach.title": "Your AI wine coach, 24/7.",
  "hero.slide.coach.for": "Everyone who wants a personal wine mentor",
  "hero.slide.coach.why": "Ask anything: pairings, regions, tasting tips — instant expert answers.",
  "hero.slide.coach.cta": "Discover Coach",

  // === catalog cta (en) ===
  "cta.full_catalog": "Download catalogue (PDF)",
  "cta.product_catalog": "Product sheet (PDF)",

  // === catalog capture (en) ===
  "catalogCapture.eyebrow": "FREE · PDF · 13 pages",
  "catalogCapture.title": "Download the full catalogue",
  "catalogCapture.subtitle": "Just your email — we'll send our latest updates twice a year, no spam.",
  "catalogCapture.email": "Email *",
  "catalogCapture.first_name": "First name (optional)",
  "catalogCapture.company": "Company (optional)",
  "catalogCapture.category_placeholder": "What's your role? (optional)",
  "catalogCapture.cta": "Download PDF",
  "catalogCapture.loading": "Preparing your PDF…",
  "catalogCapture.skip": "Skip — just open the PDF",
  "catalogCapture.close": "Close",
  "catalogCapture.err.email": "Please enter a valid email address.",
  "catalogCapture.privacy": "By downloading, you accept to be contacted about geoVINUM. Unsubscribe in 1 click.",
  "catalogCapture.cat.domaines": "Winery",
  "catalogCapture.cat.cavistes": "Wine merchant",
  "catalogCapture.cat.negociants": "Négociant",
  "catalogCapture.cat.restaurateurs": "Restaurant",
  "catalogCapture.cat.distributeurs": "Distributor",
  "catalogCapture.cat.importateurs": "Importer",
  "catalogCapture.cat.ecoles": "Wine school",
  "catalogCapture.cat.drinks_tech": "Drinks Tech (startup)",
  "catalogCapture.cat.e_commerce": "Wine e-commerce",
  "catalogCapture.cat.autre": "Other",

  // Navigation — Cartes hub
  "nav.cartes": "Maps hub",

  // /products/iframe — Live demo section
  "iframeLive.eyebrow": "Interactive demo",
  "iframeLive.title": "Test the map on any appellation.",
  "iframeLive.subtitle": "Type an appellation, pick from the list — the map reloads live, exactly as it will look on your site.",
  "iframeLive.placeholder": "Sancerre, Chablis, Côte-Rôtie…",
  "iframeLive.cta": "Test",
  "iframeLive.suggestions_label": "Suggestions:",
  "iframeLive.footer": "The demo uses the very same component as the blog articles and your future embed.",

  // /products/iframe — Install guide
  "iframeInstall.eyebrow": "Install",
  "iframeInstall.title": "Four steps and your site shows the map.",
  "iframeInstall.subtitle": "No framework imposed. As long as you can spin up a server route, you can embed geoVINUM.",
  "iframeInstall.step1.title": "1. Subscribe and get your API key",
  "iframeInstall.step1.body": "Pick your subscription plan below. Right after payment, you receive your EtOH API key by email.",
  "iframeInstall.step2.title": "2. Request the token",
  "iframeInstall.step2.body": "From your server, exchange your API key for a short-lived JWT token (1h, shape `{ \"access\": \"<JWT>\" }`) — exactly like the route powering this site. Cache it 55 min and forward it to the front. **Never expose the API key in the browser.**",
  "iframeInstall.step3.title": "3. Bind your dynamic appellation field",
  "iframeInstall.step3.body": "In your CMS or template, read the appellation name from your product fact-sheet (`product.appellation`, an ACF field, a Shopify metafield, etc.) and pass it as `app=` in the iframe URL. One field — every map adapts.",
  "iframeInstall.step4.title": "4. The map appears on every product card",
  "iframeInstall.step4.body": "That's it. On each fact-sheet, an iframe `https://iframe.geovinum.app/?token=…&app=<appellation>` is generated automatically — the right map shows up for every product, with no manual work per page.",
  "iframeInstall.personalize.title": "Personalise the snippet with your key",
  "iframeInstall.personalize.body": "Paste your EtOH API key below — the snippets fill in automatically. Your key **stays in your browser**, it's never sent to our servers.",
  "iframeInstall.personalize.placeholder": "ex: 362ec4bf-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
  "iframeInstall.personalize.clear": "Clear",
  "iframeInstall.personalize.ready": "Personalised snippets ({0}) — copying uses the full key.",
  "iframeInstall.show_key": "Show key",
  "iframeInstall.hide_key": "Hide key",
  "iframeInstall.copy": "Copy",
  "iframeInstall.copied": "Copied",
  "iframeInstall.help_prefix": "Need help integrating?",
  "iframeInstall.help_link": "Contact us",
  "iframeInstall.help_suffix": "— our team supports you for free.",

  // /cartes — Appellation hub
  "cartes.seo_title": "Wine appellation maps | geoVINUM",
  "cartes.seo_desc": "Explore every wine appellation on an interactive map: Sancerre, Chablis, Saint-Émilion, Châteauneuf-du-Pape… geoVINUM maps the world's vineyards.",
  "cartes.eyebrow": "Terroir catalogue",
  "cartes.h1_line1": "Every appellation,",
  "cartes.h1_line2": "an interactive map.",
  "cartes.subtitle": "Discover {0} appellations mapped in detail, with cadastral parcels, grape varieties and dedicated articles.",
  "cartes.subtitle_empty": "Discover our mapped appellations in detail, with cadastral parcels, grape varieties and dedicated articles.",
  "cartes.search_placeholder": "Search an appellation… (Sancerre, Châteauneuf, etc.)",
  "cartes.region_all": "All",
  "cartes.loading": "Loading the catalogue…",
  "cartes.empty": "No appellation matches.",
  "cartes.empty_reset": "Reset",
  "cartes.count_one": "{0} appellation",
  "cartes.count_many": "{0} appellations",
  "cartes.card.eyebrow": "Appellation",
  "cartes.card.preview": "Map preview",
  "cartes.card.hide_preview": "Hide map",
  "cartes.card.read": "Read",
  "cartes.cta.eyebrow": "For professionals",
  "cartes.cta.title": "This very map on your site, in four steps.",
  "cartes.cta.subtitle": "Wine retailers, publishers, wine-tourism agencies: embed the interactive map of any appellation into your site with our iframe.",
  "cartes.cta.button": "Discover the geoVINUM iframe",
  "cartes.region.bordeaux":  "Bordeaux & South-West",
  "cartes.region.bourgogne": "Burgundy",
  "cartes.region.beaujolais":"Beaujolais",
  "cartes.region.rhone":     "Rhône Valley",
  "cartes.region.loire":     "Loire Valley",
  "cartes.region.champagne": "Champagne & Alsace",
  "cartes.region.languedoc": "Languedoc-Roussillon",
  "cartes.region.provence":  "Provence & Corsica",
  "cartes.region.jura":      "Jura & Savoie",
  "cartes.region.italy":     "Italy",
  "cartes.region.spain":     "Spain",
  "cartes.region.portugal":  "Portugal",
  "cartes.region.other":     "Other terroirs",

  // geoVINUM app landing page
  "terroir.seo.title": "geoVINUM app — The wine AI for everyone, everywhere",
  "terroir.seo.desc": "Talk to terroir. Discover appellations, grapes, producers and events. Build your wine route, learn with flashcards, taste blind. For wine lovers and pros.",
  "terroir.hero.eyebrow": "geoVINUM app · Powered by geoVINUM",
  "terroir.hero.h1_a": "Talk to terroir.",
  "terroir.hero.h1_b": "Anywhere, in any language.",
  "terroir.hero.sub": "Your AI companion for wine. Ask anything about appellations, grapes, producers, events. Build a wine route, learn with flashcards, taste blind, snap a photo and let the AI guide you. One app — for amateurs and professionals.",
  "terroir.hero.cta_primary": "Launch the web app",
  "terroir.hero.cta_secondary": "Create an account",
  "terroir.hero.pwa_note": "No download required · Works on any device · 4 languages",
  "terroir.demo.title": "Try it now",
  "terroir.demo.sub": "Live preview embedded right here. Tap a question to start a conversation.",
  "terroir.demo.open_in_new": "Open in a new tab",
  "terroir.usecases.title": "What you can do with geoVINUM app",
  "terroir.usecases.sub": "Use cases, not features. Pick the one that fits your moment.",
  "terroir.uc.amateur.tag": "For wine lovers",
  "terroir.uc.amateur.1.title": "I'm planning a wine weekend",
  "terroir.uc.amateur.1.body": "Type your dates and region. geoVINUM app builds your itinerary — wineries open this weekend, tastings, restaurants, the best appellations to taste right now.",
  "terroir.uc.amateur.2.title": "I'm in front of a bottle and I want to learn",
  "terroir.uc.amateur.2.body": "Snap the label. The AI tells you the story of that appellation, the grape, the producer, what to expect in the glass and what to pair it with.",
  "terroir.uc.amateur.3.title": "I want to learn a little every day",
  "terroir.uc.amateur.3.body": "Daily micro-courses, flashcards on grapes and regions, blind tasting drills. 5 minutes a day to become a confident taster.",
  "terroir.uc.amateur.4.title": "I want to play with friends",
  "terroir.uc.amateur.4.body": "Multiplayer quiz on appellations, grapes, regions. Perfect for an evening, a tasting club, or your next dinner party.",
  "terroir.uc.pro.tag": "For wine pros",
  "terroir.uc.pro.1.title": "I run a wine bar",
  "terroir.uc.pro.1.body": "Turn your wine list into a playful tasting experience. Customers scan a QR code and explore the terroir of each bottle, alone or in teams. More engagement, more bottles sold.",
  "terroir.uc.pro.2.title": "I'm a wine merchant",
  "terroir.uc.pro.2.body": "Add a 'geoVINUM app' assistant on your website or in store. Your customers ask questions, get recommendations, build their cellar — without a sommelier on staff 24/7.",
  "terroir.uc.pro.3.title": "I lead a wine club",
  "terroir.uc.pro.3.body": "Animate your monthly meetings with multiplayer games, blind tastings, themed flashcards. Members come back because they learn while having fun.",
  "terroir.uc.pro.4.title": "I teach wine",
  "terroir.uc.pro.4.body": "Wine schools and academies: build modules, quizzes and blind tasting sessions. Track progress, gamify your curriculum, and turn classrooms into experiences.",
  "terroir.uc.pro.5.title": "I'm a producer",
  "terroir.uc.pro.5.body": "Embed geoVINUM app on your domain page. Visitors ask anything about your appellation, your grapes, your story — even when you can't be at the phone.",
  "terroir.audiences.title": "One app, two ways to use it",
  "terroir.audiences.amateur.title": "Amateur",
  "terroir.audiences.amateur.body": "Free to try · PWA · works in 4 languages.",
  "terroir.audiences.pro.title": "Professional",
  "terroir.audiences.pro.body": "Wine bars, merchants, clubs, schools, producers. Branded experience, gamified, scalable.",
  "terroir.audiences.pro.cta": "Talk to our team",
  "terroir.how.title": "How it works",
  "terroir.how.1.title": "Open the app",
  "terroir.how.1.body": "No install needed. Web app — same experience on every device.",
  "terroir.how.2.title": "Ask, scan, play",
  "terroir.how.2.body": "Type a question, snap a label, start a flashcard set, challenge a friend.",
  "terroir.how.3.title": "Get smarter every day",
  "terroir.how.3.body": "Micro-courses, blind tastings, itineraries. Your wine literacy compounds.",
  "terroir.cta.title": "Ready to talk to terroir?",
  "terroir.cta.sub": "Free to try. No credit card. Works on any device.",
  "terroir.cta.web": "Open the web app",
  "terroir.cta.ios": "Open the app",

  // geoVINUM app — Pro Demo lead form
  "pro_demo.eyebrow": "Pro Demo · 30 seconds",
  "pro_demo.title": "Book a tailored Pro demo",
  "pro_demo.subtitle": "Wine bar, merchant, club, school, producer — we plug geoVINUM app into your venue with your branding, your wine list, your customer journey. Pay-per-visitor, no upfront cost.",
  "pro_demo.bullet_1": "Branded experience on your existing site or QR code",
  "pro_demo.bullet_2": "Gamified flashcards, blind tastings, multiplayer quizzes",
  "pro_demo.bullet_3": "Lead capture + analytics dashboard included",
  "pro_demo.form.eyebrow": "Tell us about you",
  "pro_demo.form.title": "We reply within 24 hours",
  "pro_demo.field.name": "Your name",
  "pro_demo.field.email": "Pro email",
  "pro_demo.field.business": "Business",
  "pro_demo.field.visitors": "Monthly visitors",
  "pro_demo.business.wine_bar": "Wine bar",
  "pro_demo.business.merchant": "Wine merchant",
  "pro_demo.business.club": "Wine club",
  "pro_demo.business.school": "School / Academy",
  "pro_demo.business.producer": "Producer",
  "pro_demo.business.other": "Other",
  "pro_demo.consent_text": "I agree to be contacted by the geoVINUM team about my demo request. My data is stored 24 months and I can request deletion at any time (GDPR).",
  "pro_demo.submit": "Request my demo",
  "pro_demo.reply_time": "We reply within 24 business hours",
  "pro_demo.error.consent": "Please accept the consent checkbox to continue.",
  "pro_demo.success.title": "Demo request received!",
  "pro_demo.success.body": "Check your inbox — we just sent you a confirmation. Want to accelerate things? Book a 20-min call directly:",
  "pro_demo.success.calendar": "Book a 20-min call",

  // Home — simplified product sections
  "home.s_terroir.eyebrow": "geoVINUM app · Consumer AI app",
  "home.s_terroir.title": "Talk to terroir.",
  "home.s_terroir.title_em": "anywhere, any language.",
  "home.s_terroir.sub": "Wine lovers and pros use one and the same AI app: ask questions about appellations, grapes, producers, build a wine route, snap a label, play multiplayer quizzes, learn with daily flashcards.",
  "home.s_academy.eyebrow": "Academy · Netflix of terroir",
  "home.s_academy.title": "Watch wine,",
  "home.s_academy.title_em": "binge a region.",
  "home.s_academy.sub": "100+ HD masterclasses on every wine region, grape and tradition. Stream on any device. 7 days free, then 9€/month.",
  "home.s_shop.eyebrow": "Shop · Print on demand",
  "home.s_shop.title": "Wine maps you can",
  "home.s_shop.title_em": "hang on the wall.",
  "home.s_shop.sub": "Premium posters, framed prints, canvas — every major appellation, printed and shipped from the EU. The collector's atlas.",
  "home.s_iframe.eyebrow": "Iframe · B2B embed",
  "home.s_iframe.title": "Add the right map to",
  "home.s_iframe.title_em": "every product card.",
  "home.s_iframe.sub": "Wine sites, marketplaces, importers: bind one appellation field and our interactive maps appear automatically on each fact-sheet. From 6 000 to unlimited requests, pay-per-call from 0.01€.",
  "home.cta.discover_terroir": "Discover geoVINUM app",
  "home.cta.open_webapp": "Open the web app",
  "home.cta.talk_to_sales": "Talk to sales",
  "home.closing.title": "One ecosystem, one promise.",
  "home.closing.sub": "Free to try, available in 4 languages, works on any device — no install needed.",
};

const fr = {
  "nav.maps": "Cartes",
  "nav.app": "App",
  "nav.interactive": "Interactive",
  "nav.experience": "Experience",
  "nav.pro.overview": "Pour les pros — vue d'ensemble",
  "nav.pro_long": "Pour les pros",
  "nav.explorer.all": "Tout voir",
  "nav.explorer.appellations": "Régions · Vignobles · Appellations",
  "nav.explorer.cepages": "Cépages",
  "nav.explorer.producers": "Producteurs",
  "nav.explorer.articles": "Articles",
  "nav.explorer.guides_divider": "Guides & références",
  "nav.explorer.glossaire": "Glossaire du vin (506 termes)",
  "nav.explorer.cartographie": "Cartographie viticole",
  "nav.explorer.geographie": "Géographie du vin",
  "nav.explorer.appellations_pil": "Appellations viticoles",
  "nav.explorer.terroirs": "Terroirs viticoles",
  "nav.learn": "Apprendre",
  "nav.play": "Jouer",
  "nav.journal": "Journal",
  "nav.pro": "Pro",
  "nav.login": "Connexion",
  "nav.logout": "Déconnexion",
  "nav.account": "Mon compte",
  "nav.chat": "Discuter",
  "nav.search_placeholder": "Rechercher cartes, régions, cépages, styles…",
  "nav.shop": "Boutique",
  "common.home": "Accueil",
  "common.search": "Rechercher",
  "common.loading": "Chargement…",
  "common.read_more": "Lire la suite",
  "common.back": "Retour",
  "common.see_all": "Tout voir",
  "common.from": "Dès",
  "common.questions": "questions",
  "common.no_results": "Aucun résultat.",
  "common.try_another": "Essayez une autre région, un autre cépage ou un autre style.",
  "cmdk.quick_links": "Accès rapides",
  "cmdk.navigate": "Naviguer",
  "cmdk.select": "Ouvrir",
  "common.you_might_also": "Vous aimerez aussi",
  "common.related": "À découvrir",

  "cta.explore_maps": "Explorer les cartes",
  "cta.start_learning": "Commencer",
  "cta.start_learning_2min": "Apprendre en 2 min",
  "cta.start_playing": "Jouer",
  "cta.discover_pro": "Découvrir geoVINUM Pro",
  "cta.contact_sales": "Contacter l'équipe",
  "cta.buy_now": "Acheter",
  "cta.add_cart": "Ajouter au panier",
  "cta.view_all_maps": "Voir toutes les cartes",
  "cta.view_map": "Voir la carte",
  "cta.read_lesson": "Lire la leçon",
  "cta.continue": "Continuer",
  "cta.start_quiz": "Lancer le quiz",
  "cta.take_quiz": "Faire le quiz",
  "cta.play": "Jouer",
  "cta.go_dashboard": "Aller au tableau de bord",
  "cta.send_link": "Envoyer le lien",
  "cta.reset_password": "Réinitialiser",
  "cta.get_started": "Démarrer",
  "cta.log_in": "Se connecter",
  "cta.back_to_journal": "Retour au journal",
  "cta.back_to_login": "Retour à la connexion",

  "home.hero.eyebrow": "Cartes du monde du vin · par geoVINUM",
  "home.hero.h1a": "Comprenez le vin",
  "home.hero.h1b": "visuellement.",
  "home.hero.tagline": "Cartes. Apprentissage. Jeu.",
  "home.hero.line": "Le vin est complexe. Nous le rendons visuel.",
  "home.hero.support": "Explorez les régions, comprenez les cépages, apprenez en quelques minutes.",
  "home.hero.rating": "4,8/5 par les amateurs",
  "home.hero.secure_payment": "Paiement sécurisé",
  "home.hero.shipping": "Livraison mondiale",

  "story.a": "Le vin est souvent compliqué.",
  "story.b": "Régions, cépages, terroirs…",
  "story.c": "geoVINUM le rend simple.",

  "how.eyebrow": "Comment ça marche",
  "how.title_a": "Trois façons de tomber pour",
  "how.title_b": "le vin",
  "how.see.title": "Voir",
  "how.see.text": "Les régions deviennent claires instantanément.",
  "how.learn.title": "Apprendre",
  "how.learn.text": "Comprenez ce qui compte vraiment.",
  "how.play.title": "Jouer",
  "how.play.text": "Retenez sans effort.",

  "home.maps.eyebrow": "La collection",
  "home.maps.title_a": "Explorez le vin par les",
  "home.maps.title_b": "cartes",
  "home.learn.title_a": "Leçons courtes,",
  "home.learn.title_b": "grandes idées.",
  "home.learn.text": "Comprenez une région viticole en 3 minutes.",
  "home.learn.lesson_label": "Leçon 03",
  "home.learn.lesson_minutes": "3 min",
  "home.learn.lesson_title": "La rive gauche de Bordeaux",
  "home.learn.lesson_text": "Sols de graves. Assemblages dominés par le Cabernet. Puissance et structure.",
  "home.learn.badge_new": "Nouveau",
  "home.learn.badge_unit": "Unité",
  "home.play.title_a": "Testez vos connaissances,",
  "home.play.title_b": "amusez-vous.",
  "home.play.text": "La plupart oublient ce qu'ils apprennent. Pas vous.",
  "home.play.quiz_label": "Quiz · Niveau 2",
  "home.play.question": "Question",
  "home.play.q1": "Quel cépage domine la rive droite de Bordeaux ?",
  "home.pro.eyebrow": "Pour les pros",
  "home.pro.title_a": "Pour les",
  "home.pro.title_b": "professionnels du vin.",
  "home.pro.text": "Expliquez mieux, formez vos équipes, vendez avec confiance.",
  "home.pro.used_by": "Utilisé par des pros du vin dans le monde entier",
  "home.final.title": "Lancez votre voyage du vin",
  "home.final.text": "Explorer, apprendre, jouer. Dans cet ordre — ou un autre.",

  "trust.premium_quality": "Qualité premium",
  "trust.made_to_learn": "Conçu pour apprendre",
  "trust.loved_by": "Plébiscité par les amateurs",
  "trust.worldwide_shipping": "Livraison mondiale",
  "trust.designed_with_experts": "Conçu avec des experts",
  "trust.secure_payment": "Paiement sécurisé",
  "trust.premium_print": "Impression premium",

  "footer.brand_line": "Le vin devient clair quand on le voit. Cartes, leçons et jeu — pour les curieux et les pros.",
  "footer.explore": "Explorer",
  "footer.contact": "Contact",
  "footer.legal": "Légal",
  "footer.cookie_settings": "Gestion des cookies",
  "footer.secure_payments": "Paiement sécurisé",
  "footer.alcohol_disclaimer": "⚠️ L'abus d'alcool est dangereux pour la santé. À consommer avec modération.",
  "footer.rights": "Tous droits réservés.",

  "cookies.title": "Cookies",
  "cookies.body": "Nous utilisons des cookies pour améliorer votre expérience. Les cookies essentiels sont toujours actifs. Les cookies analytiques et marketing nécessitent votre consentement.",
  "cookies.accept_all": "Tout accepter",
  "cookies.reject_all": "Tout refuser",
  "cookies.preferences": "Préférences",
  "cookies.save": "Enregistrer",
  "cookies.essential": "Essentiels",
  "cookies.essential_desc": "Authentification, sécurité, fonctionnalités de base.",
  "cookies.analytics": "Analytiques",
  "cookies.analytics_desc": "Mesure d'audience et analyse d'usage.",
  "cookies.marketing": "Marketing",
  "cookies.marketing_desc": "Suivi de campagnes et segmentation.",

  "maps.eyebrow": "Galerie",
  "maps.h1_a": "Explorez le vin",
  "maps.h1_b": "visuellement",
  "maps.sub": "Cartes, œuvres et histoires visuelles du vin.",
  "maps.featured_eyebrow": "À la une",
  "maps.featured_title_a": "La façon la plus simple de comprendre",
  "maps.featured_title_b": "Bordeaux",
  "maps.featured_text": "Toutes les appellations, tous les classements, sur une seule affiche.",
  "maps.featured_cta": "Découvrir la carte",
  "maps.collections": "Collections",
  "maps.all_pieces": "Toutes les pièces",
  "maps.filter": "Filtrer",
  "maps.no_pieces": "Aucune pièce ne correspond à ces filtres.",
  "maps.label_country": "Pays",
  "maps.label_type": "Type",
  "maps.understand_what_you_see": "Comprendre ce que vous voyez",
  "maps.test_yourself": "Testez-vous",
  "maps.col.maps": "Cartes",
  "maps.col.maps_desc": "Comprendre les régions visuellement",
  "maps.col.artworks": "Œuvres",
  "maps.col.artworks_desc": "Interprétations esthétiques",
  "maps.col.photography": "Photographie",
  "maps.col.photography_desc": "Instants & terroirs",
  "maps.col.oneline": "One line",
  "maps.col.oneline_desc": "Illustrations minimalistes",

  "learn.eyebrow": "Apprendre",
  "learn.h1_a": "Apprenez le vin",
  "learn.h1_b": "simplement",
  "learn.sub1": "Leçons courtes. Idées claires.",
  "learn.sub2": "Comprenez une région viticole en 3 minutes.",
  "learn.story1": "Le vin est souvent compliqué…",
  "learn.story2": "Nous le rendons simple.",
  "learn.start_here": "Commencer ici",
  "learn.start_here_title": "Commencer ici :",
  "learn.structure": "Structure",
  "learn.cat.regions": "Régions",
  "learn.cat.regions_desc": "Comprendre d'où vient le vin.",
  "learn.cat.grapes": "Cépages",
  "learn.cat.grapes_desc": "Rencontrer les personnalités derrière chaque vin.",
  "learn.cat.basics": "Bases",
  "learn.cat.basics_desc": "L'essentiel, clairement expliqué.",
  "learn.dont_just_learn": "Ne vous contentez pas d'apprendre. Retenez.",
  "learn.see_what_you_learn": "Voyez ce que vous apprenez",

  "play.eyebrow": "Jouer",
  "play.h1_a": "Apprenez en",
  "play.h1_b": "jouant",
  "play.sub": "La plupart oublient ce qu'ils apprennent. Pas vous.",
  "play.test_knowledge": "Testez vos connaissances",
  "play.tagline": "Rapide, fun, mémorable",
  "play.mini_eyebrow": "Atlas",
  "play.mini_title_a": "Le",
  "play.mini_title_b": "Google Maps",
  "play.mini_title_c": "du vin.",
  "play.mini_text": "Zoomer, explorer, comprendre.",
  "play.open_atlas": "Ouvrir l'atlas",
  "play.books": "Apprendre avec des livres",
  "play.books_text": "Une sélection pour aller plus loin — bientôt disponible. En attendant, chaque carte est un outil d'étude.",
  "play.transition_a": "Jouer. Explorer.",
  "play.transition_b": "Comprendre.",
  "play.learn_basics": "Apprendre les bases",
  "play.see_wine_world": "Voir le monde du vin",
  "play.difficulty.easy": "Facile",
  "play.difficulty.medium": "Intermédiaire",
  "play.difficulty.hard": "Difficile",

  "pro.hero.eyebrow": "Pour les pros",
  "pro.hero.h1": "geoVINUM pour les",
  "pro.hero.h1_italic": "professionnels",
  "pro.hero.sub": "Vendez des cartes, proposez des outils, ou intégrez nos visualisations.",
  "pro.subnav.work_with_us": "Travailler avec geoVINUM",
  "pro.shops.title": "Cavistes",
  "pro.shops.desc": "Transformez chaque visite en moment d'apprentissage. Vendez des cartes, livres et cadeaux qui enseignent.",
  "pro.shops.headline_a": "Aidez vos clients à",
  "pro.shops.headline_b": "comprendre",
  "pro.shops.headline_c": "ce qu'ils dégustent.",
  "pro.shops.f1_t": "Cartes en gros",
  "pro.shops.f1_d": "Des cartes de qualité musée qui se vendent toutes seules.",
  "pro.shops.f2_t": "Coffrets cadeaux",
  "pro.shops.f2_d": "Cartes associées à des livres et accessoires.",
  "pro.shops.f3_t": "Panier moyen plus élevé",
  "pro.shops.f3_d": "Un merchandising visuel qui invite à rester plus longtemps.",
  "pro.shops.cta": "Devenir partenaire caviste",
  "pro.schools.title": "Écoles du vin",
  "pro.schools.desc": "Proposez à vos étudiants des outils visuels — leçons, cartes, quiz qui marquent.",
  "pro.schools.headline_a": "Plus rapide, plus clair,",
  "pro.schools.headline_b": "plus mémorable",
  "pro.schools.headline_c": " — l'apprentissage.",
  "pro.schools.f1_t": "Licences étudiantes",
  "pro.schools.f1_d": "Accès groupé aux leçons et quiz.",
  "pro.schools.f2_t": "Kits pédagogiques",
  "pro.schools.f2_d": "Grandes cartes pour enseigner au mur.",
  "pro.schools.f3_t": "Programmes d'upsell",
  "pro.schools.f3_d": "Ajoutez du contenu visuel à votre cursus.",
  "pro.schools.cta": "Découvrir l'offre académique",
  "pro.pros.title": "Professionnels",
  "pro.pros.desc": "Iframe, API et widgets pour intégrer geoVINUM à votre plateforme.",
  "pro.pros.headline_a": "Intégrez",
  "pro.pros.headline_b": "geoVINUM",
  "pro.pros.headline_c": "dans votre produit.",
  "pro.pros.f1_t": "Embeds iframe",
  "pro.pros.f1_d": "Insérez des cartes interactives en une ligne.",
  "pro.pros.f2_t": "API REST",
  "pro.pros.f2_d": "Requêtez terroirs, appellations et cépages.",
  "pro.pros.f3_t": "Widgets",
  "pro.pros.f3_d": "Blocs UI prêts à l'emploi, en marque blanche.",
  "pro.pros.f4_t": "Cartes sur mesure",
  "pro.pros.f4_d": "Adaptées à votre domaine, marque ou catalogue.",
  "pro.pros.cta": "Voir la doc développeur",
  "pro.cta": "Travailler avec geoVINUM",
  "pro.cta_text": "Parlez-nous de votre boutique, école ou produit — nous proposerons un partenariat sur mesure.",
  "pro.value.faster": "L'apprentissage visuel est plus rapide",
  "pro.value.understand": "Les clients comprennent mieux",
  "pro.value.teams": "Les équipes sont plus confiantes",
  "pro.value.sales": "Les ventes progressent",

  "product.from": "Dès",
  "product.rating": "4,8/5 par les amateurs",
  "product.details": "Détails",
  "product.detail1": "Impression haute qualité",
  "product.detail2": "Structure visuelle claire",
  "product.detail3": "Conçu pour apprendre",
  "product.value_title_a": "Voyez le vin",
  "product.value_title_b": "autrement",
  "product.value_text": "Comprenez régions, cépages et structure d'un coup d'œil.",
  "product.learn_this_region": "Apprendre cette région",
  "product.learn_this_region_text": "Comprenez {region} en 3 minutes.",
  "product.test_knowledge": "Testez vos connaissances",
  "product.test_knowledge_text": "Saurez-vous reconnaître les vins de {region} ?",
  "product.not_found": "Produit introuvable.",

  "journal.eyebrow": "Centre de connaissances",
  "journal.h1_a": "Apprendre. Explorer.",
  "journal.h1_b": "Comprendre",
  "journal.sub": "Guides, analyses et outils pour comprendre le vin visuellement.",
  "journal.search_placeholder": "Rechercher articles, régions, guides…",
  "journal.pillar.insights": "Regards sur le vin",
  "journal.pillar.insights_desc": "Régions, appellations, terroirs expliqués.",
  "journal.pillar.guides": "Guides",
  "journal.pillar.guides_desc": "Lectures pratiques pour comprendre le vin.",
  "journal.pillar.get_started": "Démarrer",
  "journal.pillar.get_started_desc": "Onboarding, tutoriels, prises en main.",
  "journal.pillar.for_pros": "Pour les pros",
  "journal.pillar.for_pros_desc": "Former, vendre, grandir avec geoVINUM.",
  "journal.filter.all": "Tous",
  "journal.loading": "Chargement des articles…",
  "journal.error": "Impossible de joindre le journal.",
  "journal.empty": "Aucun article pour le moment.",
  "journal.empty_lang": "Aucun article disponible dans cette langue pour le moment.",
  "journal.empty_lang_text": "Nous traduisons toujours plus de contenu. Changez de langue ou revenez bientôt.",
  "journal.empty_try": "Essayez un autre sujet ou un autre mot-clé.",
  "journal.clear_theme": "Réinitialiser le thème",
  "journal.ready": "Prêt à voir ?",
  "journal.read_article": "Lire l'article",

  "search.h1": "Résultats pour :",
  "search.filter.all": "Tout",
  "search.filter.maps": "Cartes",
  "search.filter.learn": "Apprendre",
  "search.filter.play": "Jouer",
  "search.filter.journal": "Journal",
  "search.filter.products": "Produits",
  "search.suggestion": "À explorer aussi",

  "auth.welcome_back": "Bienvenue",
  "auth.welcome_back_sub": "Connectez-vous pour accéder à vos cartes, leçons et quiz.",
  "auth.no_account": "Pas encore de compte ?",
  "auth.sign_up": "Créer un compte",
  "auth.forgot_password": "Mot de passe oublié ?",
  "auth.create_account": "Créer votre compte",
  "auth.start_journey": "Lancez votre voyage du vin",
  "auth.have_account": "Vous avez déjà un compte ?",
  "auth.email": "E-mail",
  "auth.password": "Mot de passe",
  "auth.min6": "6 caractères minimum",
  "auth.reset_title": "Réinitialiser votre mot de passe",
  "auth.reset_sub": "Entrez votre e-mail pour recevoir un lien de réinitialisation.",
  "auth.check_inbox": "Vérifiez votre boîte mail",
  "auth.check_inbox_text": "Si {email} correspond à un compte, un lien de réinitialisation a été envoyé.",
  "auth.new_password": "Créer un nouveau mot de passe",
  "auth.new_password_sub": "Choisissez un nouveau mot de passe pour votre compte.",
  "auth.new_password_label": "Nouveau mot de passe",
  "auth.confirm_password": "Confirmer le mot de passe",
  "auth.repeat_password": "Répéter le mot de passe",
  "auth.password_updated": "Mot de passe mis à jour",
  "auth.redirecting": "Redirection vers la connexion…",
  "auth.err_min": "Le mot de passe doit contenir au moins 6 caractères.",
  "auth.err_match": "Les mots de passe ne correspondent pas.",
  "auth.err_invalid": "Utilisez un e-mail valide et un mot de passe d'au moins 6 caractères.",
  "auth.err_both": "Veuillez remplir les deux champs.",
  "auth.err_generic": "Une erreur est survenue.",

  "dashboard.welcome_back": "Bon retour parmi nous",
  "dashboard.signed_in_as": "Connecté en tant que",
  "dashboard.your_maps": "Vos cartes",
  "dashboard.your_maps_desc": "Accédez à vos cartes achetées.",
  "dashboard.empty_maps": "Vous n'avez pas encore de cartes.",
  "dashboard.continue_learning": "Continuer à apprendre",
  "dashboard.continue_learning_desc": "Reprenez là où vous vous étiez arrêté.",
  "dashboard.enroll": "S'inscrire",
  "dashboard.enrolling": "Inscription…",
  "dashboard.continue": "Continuer",
  "dashboard.enrolled_ok": "Vous êtes inscrit·e ! Ouverture du cours…",
  "dashboard.already_enrolled": "Vous êtes déjà inscrit·e à ce cours.",
  "dashboard.enroll_failed": "L'inscription a échoué. Veuillez réessayer plus tard.",
  "dashboard.empty_learning": "Commencez votre première leçon.",
  "dashboard.keep_playing": "Continuer à jouer",
  "dashboard.keep_playing_desc": "Testez vos connaissances et progressez.",
  "dashboard.recommendations": "Vous aimerez aussi",
  "dashboard.recommendations_desc": "Découvrez d'autres cartes et leçons.",
  "dashboard.explore_more": "Explorer plus",
  "dashboard.logout": "Déconnexion",

  "thanks.title": "Merci",
  "thanks.sub": "Votre voyage du vin commence maintenant.",
  "thanks.map_ready": "Votre carte est prête",
  "thanks.understand_region": "Comprendre cette région",
  "thanks.test_yourself": "Testez-vous",
  "thanks.order_confirmation": "Confirmation de commande",
  "thanks.receipt_sent": "Un reçu vous a été envoyé par e-mail.",

  "shop.maps.title": "Cartes",
  "shop.maps.sub": "Cartes du vin imprimées premium — explorez les régions visuellement.",
  "shop.books.title": "Livres",
  "shop.books.sub": "Des livres du vin au design soigné.",
  "shop.ebooks.title": "Ebooks",
  "shop.ebooks.sub": "Le savoir du vin, téléchargeable instantanément.",
  "shop.courses.title": "Cours",
  "shop.courses.sub": "Cours complets pour maîtriser régions et cépages.",
  "shop.bundles.title": "Coffrets",
  "shop.bundles.sub": "Coffrets curatés — cartes, livres et cours réunis.",
  "shop.empty": "Aucun produit dans cette catégorie pour le moment — revenez bientôt !",
  "nav.boutique": "Boutique",

  "auth.full_name": "Nom complet",
  "auth.full_name_placeholder": "Votre nom",

  "home.for.b2c_eyebrow": "Pour les amateurs",
  "home.for.b2c_title": "Découvrez, apprenez,",
  "home.for.b2c_title_em": "collectionnez.",
  "home.for.b2c_text": "Des cartes imprimées au coaching IA personnalisé — explorez le vin à votre rythme, à votre façon.",
  "home.for.b2c_cta": "Découvrir les produits",
  "home.for.b2c_alt": "Amateur de vin explorant une carte de vignoble",
  "home.for.b2b_eyebrow": "Pour les pros",
  "home.for.b2b_title": "Engagez vos clients,",
  "home.for.b2b_title_em": "formez vos équipes, intégrez.",
  "home.for.b2b_text": "Domaines, écoles et plateformes utilisent geoVINUM pour enseigner plus vite, engager plus longtemps et offrir des expériences œnologiques riches.",
  "home.for.b2b_cta": "Découvrir les solutions Pro",
  "home.for.b2b_alt": "Pro du vin utilisant geoVINUM Pro",

  "hero.promise": "Cartes. Histoires. Savoirs.",

  // Products / piliers
  "pillar.explore": "Explorer",
  "pillar.discover": "Découvrir",
  "pillar.experience": "Vivre",
  "pillar.learn": "Apprendre",
  "pillar.collect": "Collectionner",
  "pillar.integrate": "Intégrer",
  "product.status.available": "Disponible",
  "product.status.coming_soon": "Bientôt disponible",
  "product.type.app": "Application",
  "product.type.physical": "Produit physique",
  "product.type.platform": "Plateforme",
  "product.type.service": "Service",
  "product.cta.discover": "Découvrir",
  "product.cta.try": "Essayer",
  "product.cta.start": "Commencer",
  "product.cta.contact": "Nous contacter",
  "product.cta.notify": "Me prévenir",
  "product.cta.become_beta": "Devenir Bêta testeur",
  "product.status.beta_program": "Bientôt · Programme Bêta",
  "product.cta.visit": "Visiter",
  "product.cta.see_versions": "Voir les versions",
  "product.versions": "Versions disponibles",
  "product.pricing": "Tarifs",
  "product.starting_from": "À partir de",
  "product.download_pdf": "Télécharger en PDF",
  "print.upsell_eyebrow": "Impression à la demande",
  "print.upsell_title": "Commandez votre carte en poster.",
  "print.upsell_subtitle": "Impression giclée premium sur papier non couché 180g, expédiée depuis nos ateliers locaux.",
  "print.shipping": "Livraison mondiale en 3 à 6 jours ouvrés",
  "print.paper": "Papier premium non couché 180g — qualité musée",
  "print.cta": "Commander",
  "print.academy_hint": "Les abonnés Academy bénéficient automatiquement de –10 % sur chaque impression.",
  "print.success_title": "Commande reçue !",
  "print.success_subtitle": "Votre poster est en cours de préparation et sera expédié à l'adresse que vous avez indiquée.",
  "print.success_back": "Retour au produit",
  "print.success_pending": "Vérification de votre paiement…",
  "print.success_failed": "Le paiement n'a pas abouti. Vous pouvez réessayer depuis la page produit.",
  "product.trial": "essai gratuit inclus",
  "product.trial_7d": "7 jours d'essai gratuit inclus",
  "product.platform": "Plateforme",
  "product.price": "Prix",
  "product.price.free": "Gratuit",
  "product.price.premium": "Premium",
  "product.price.subscription": "Abonnement Pro",
  "product.price.contact": "Nous contacter",
  "product.price.coming_soon": "Bientôt disponible",
  "product.atlas.tagline": "Explorez les régions viticoles et les terroirs du monde.",
  "product.atlas.desc": "GeoVINUM Atlas aide les amateurs de vin à découvrir les régions viticoles, appellations, cépages et producteurs grâce à des cartes interactives et du contenu pédagogique. Explorez les territoires, comprenez les origines du vin et planifiez vos voyages œnologiques.",
  "product.atlas.mini.title": "geoVINUM Atlas",
  "product.atlas.mini.desc": "Idéal pour les amateurs, étudiants et curieux qui veulent accéder rapidement aux principales régions, appellations et cépages.",
  "product.atlas.magnum.title": "geoVINUM Atlas",
  "product.atlas.magnum.desc": "Conçu pour les passionnés et étudiants qui veulent une compréhension plus complète de la géographie du vin, des terroirs et des appellations.",
  "product.atlas.pro.title": "geoVINUM Atlas",
  "product.atlas.pro.desc": "Une solution complète pour les professionnels du vin qui ont besoin de données fiables, d'outils cartographiques avancés et de ressources pédagogiques premium.",

  // ----- Explore "Which Atlas" (FR) -----
  "explore.which.eyebrow": "Choisis ton Atlas",
  "explore.which.title": "Quel Atlas est fait pour vous ?",
  "explore.mini.tagline": "Le Google Maps du vin",
  "explore.mini.desc": "Explorez les régions viticoles, appellations et domaines du monde entier.",
  "explore.mini.audience": "Amateurs · Voyageurs · Débutants · Curieux du vin",
  "explore.mini.cta": "Explorer geoVINUM Atlas",
  "explore.magnum.tagline": "Votre carte vignoble personnalisée",
  "explore.magnum.desc": "Affichez une ou plusieurs appellations avec références cadastrales et cartes de vignobles personnalisées.",
  "explore.magnum.audience": "Domaines · Châteaux · Œnotourisme · Interprofessions régionales",
  "explore.magnum.cta": "Découvrir geoVINUM Atlas",
  "explore.pro.tagline": "Intelligence avancée du terroir",
  "explore.pro.desc": "Accédez aux données professionnelles de terroir, à la visualisation 3D et à l'analyse avancée des régions viticoles.",
  "explore.pro.audience": "Consultants · Importateurs · Formateurs · Experts du vin · Interprofessions",
  "explore.pro.cta": "Découvrir geoVINUM Atlas",
  "explore.cta.learn_more": "En savoir plus",
  "explore.cta.subscribe": "S'abonner",
  "explore.cta.download": "Télécharger",
  "explore.trial_days": "{n} jours gratuits",

  // ----- Iframe pricing (FR) -----
  "iframeP.eyebrow": "Choisissez votre formule",
  "iframeP.title": "Du petit site à l'enterprise — choisissez votre volume.",
  "iframeP.cycle.monthly": "Mensuel",
  "iframeP.cycle.yearly": "Annuel",
  "iframeP.popular": "Le plus populaire",
  "iframeP.ht_per": "HT /",
  "iframeP.year": "an",
  "iframeP.month": "mois",
  "iframeP.save_with_yearly": "Économisez avec l'annuel ({amount} / an)",
  "iframeP.unavailable": "Indisponible",
  "iframeP.subscribe": "S'abonner",
  "iframeP.request": "requête",
  "iframeP.requests_per_year": "requêtes / an",
  "iframeP.contact_badge": "Sur-mesure",
  "iframeP.from": "À partir de",
  "iframeP.custom_quote": "Devis personnalisé",
  "iframeP.talk_to_sales": "Parler à l'équipe",
  "iframeP.footer_note": "Paiement sécurisé par Stripe · TVA collectée automatiquement · Annulation à tout moment depuis votre compte.",

  // ----- Portfolio page (FR) -----
  "portfolioP.hero.eyebrow": "Portfolio · Passeport Numérique",
  "portfolioP.hero.title": "Le passeport numérique intelligent de chaque produit.",
  "portfolioP.hero.subtitle": "Producteurs, cavistes, distributeurs, sommeliers et formateurs centralisent, valorisent et partagent une fiche dynamique automatiquement adaptée à chaque audience.",
  "portfolioP.hero.cta_beta": "Devenir Bêta testeur",
  "portfolioP.hero.cta_contact": "Nous parler",
  "portfolioP.status.beta": "Bientôt · Programme Bêta",
  "portfolioP.status.note": "Accède en avant-première en rejoignant le programme bêta",

  "portfolioP.truth.eyebrow": "Une seule source de vérité",
  "portfolioP.truth.title": "Un produit. Un passeport intelligent. Une infinité d'usages.",
  "portfolioP.truth.before": "Au lieu de gérer…",
  "portfolioP.truth.before.files_pdf": "Plusieurs fiches techniques PDF",
  "portfolioP.truth.before.sales_decks": "Plusieurs présentations commerciales",
  "portfolioP.truth.before.translations": "Plusieurs versions traduites",
  "portfolioP.truth.before.training": "Plusieurs supports de formation",
  "portfolioP.truth.before.pitch": "Plusieurs argumentaires de vente",
  "portfolioP.truth.after": "Avec Portfolio",
  "portfolioP.truth.after_desc": "Toutes les informations du produit centralisées dans une seule plateforme. Un espace unique génère automatiquement la version la plus pertinente selon la cible et le contexte.",
  "portfolioP.truth.after_note": "Édite une fois, déploie partout — bouteille, salon, carte, export, formation.",

  "portfolioP.ai.eyebrow": "Pré-remplissage intelligent",
  "portfolioP.ai.title": "Un passeport qui s'écrit tout seul.",
  "portfolioP.ai.subtitle": "La fiche n'est jamais créée à partir d'une page blanche : elle est automatiquement enrichie grâce aux données GeoVINUM et à l'intelligence artificielle.",
  "portfolioP.ai.geovinum_title": "Données GeoVINUM",
  "portfolioP.ai.geovinum.region": "Région",
  "portfolioP.ai.geovinum.appellation": "Appellation",
  "portfolioP.ai.geovinum.grapes": "Cépages",
  "portfolioP.ai.geovinum.climate": "Climat",
  "portfolioP.ai.geovinum.soils": "Sols",
  "portfolioP.ai.geovinum.altitude": "Altitude",
  "portfolioP.ai.geovinum.plots": "Parcelles",
  "portfolioP.ai.geovinum.producers": "Producteurs",
  "portfolioP.ai.geovinum.history": "Données historiques",
  "portfolioP.ai.geovinum.geo": "Données géographiques",
  "portfolioP.ai.ai_title": "Générations IA",
  "portfolioP.ai.ai.summary": "Résumé du produit",
  "portfolioP.ai.ai.storytelling": "Storytelling",
  "portfolioP.ai.ai.sales": "Présentation commerciale",
  "portfolioP.ai.ai.retailer": "Argumentaire caviste",
  "portfolioP.ai.ai.export": "Argumentaire export",
  "portfolioP.ai.ai.pairing": "Accords mets-produits",
  "portfolioP.ai.ai.profiles": "Profils consommateurs",
  "portfolioP.ai.ai.training": "Contenus pédagogiques",
  "portfolioP.ai.ai.i18n": "Traductions multilingues",
  "portfolioP.ai.editable": "Le professionnel conserve toujours la possibilité de modifier ou compléter les contenus.",

  "portfolioP.modules.eyebrow": "Entièrement modulable",
  "portfolioP.modules.title": "Active ou désactive chaque module à la carte.",
  "portfolioP.module.origin.title": "Origine & Terroir",
  "portfolioP.module.origin.desc": "Cartographie interactive, parcelles, géologie, climat, altitude, environnement.",
  "portfolioP.module.story.title": "Histoire & Storytelling",
  "portfolioP.module.story.desc": "Histoire du domaine, philosophie, savoir-faire, présentation du produit.",
  "portfolioP.module.tech.title": "Technique",
  "portfolioP.module.tech.desc": "Production, élevage, certifications, analyses, fiches réglementaires.",
  "portfolioP.module.tasting.title": "Dégustation",
  "portfolioP.module.tasting.desc": "Commentaires, profil aromatique, radar sensoriel, notes personnelles.",
  "portfolioP.module.training.title": "Formation",
  "portfolioP.module.training.desc": "Format WSET, fiches pédagogiques, quiz, contenus de formation.",
  "portfolioP.module.pairing.title": "Accords",
  "portfolioP.module.pairing.desc": "Mets-produits, suggestions de service, occasions de consommation.",
  "portfolioP.module.media.title": "Médias",
  "portfolioP.module.media.desc": "Photos, vidéos, interviews, contenus marketing.",
  "portfolioP.module.trade.title": "Commercial",
  "portfolioP.module.trade.desc": "Prix, conditionnements, disponibilité, marchés export.",

  "portfolioP.position.eyebrow": "Valorisation marché",
  "portfolioP.position.title": "Comprends la place de chaque produit sur le marché.",
  "portfolioP.position.subtitle": "Portfolio ne se contente pas de décrire un produit. Grâce aux données GeoVINUM, aux données commerciales et à l'IA, la plateforme propose :",
  "portfolioP.position.position.title": "Positionnement marché",
  "portfolioP.position.position.desc": "Entrée de gamme, premium, super premium, icône.",
  "portfolioP.position.competitive.title": "Analyse concurrentielle",
  "portfolioP.position.competitive.desc": "Produits similaires, références concurrentes, alternatives dans la même catégorie.",
  "portfolioP.position.value.title": "Valorisation",
  "portfolioP.position.value.desc": "Estimation de valeur, cohérence prix / marché, évolution de positionnement, perception des dégustateurs.",
  "portfolioP.position.differentiation.title": "Différenciation",
  "portfolioP.position.differentiation.desc": "Ce qui distingue le produit — ses forces commerciales et ses arguments uniques de vente.",
  "portfolioP.position.tier.entry": "Entrée de gamme",
  "portfolioP.position.tier.premium": "Premium",
  "portfolioP.position.tier.super_premium": "Super premium",
  "portfolioP.position.tier.icon": "Icône",

  "portfolioP.audience.eyebrow": "Adapté à chaque cible",
  "portfolioP.audience.title": "Un Portfolio. Cinq visages.",
  "portfolioP.audience.consumer.title": "Consommateur",
  "portfolioP.audience.consumer.list": "Histoire · Terroir · Dégustation · Accords · Vidéos",
  "portfolioP.audience.retailer.title": "Caviste",
  "portfolioP.audience.retailer.list": "Argumentaire commercial · Profil sensoriel · Produits similaires · Positionnement prix",
  "portfolioP.audience.importer.title": "Importateur",
  "portfolioP.audience.importer.list": "Certifications · Volumes disponibles · Informations export",
  "portfolioP.audience.sommelier.title": "Sommelier",
  "portfolioP.audience.sommelier.list": "Dégustation détaillée · Accords · Température de service",
  "portfolioP.audience.educator.title": "Formateur",
  "portfolioP.audience.educator.list": "Contenus pédagogiques · Cartes · Quiz · Ressources éducatives",

  "portfolioP.diffusion.eyebrow": "Multi-canal",
  "portfolioP.diffusion.title": "Diffusé partout où ton produit voyage.",
  "portfolioP.diffusion.subtitle": "Chaque canal peut afficher des modules différents — la bonne information dans le bon contexte.",
  "portfolioP.diffusion.qr_bottle": "QR code sur bouteille",
  "portfolioP.diffusion.qr_fair": "QR code en salon professionnel",
  "portfolioP.diffusion.wine_list": "Carte des vins numérique",
  "portfolioP.diffusion.catalog": "Catalogue commercial",
  "portfolioP.diffusion.export": "Fiche export",
  "portfolioP.diffusion.training": "Support de formation",
  "portfolioP.diffusion.private_link": "Lien privé ou public",

  "portfolioP.promise.eyebrow": "La promesse",
  "portfolioP.promise.title": "Portfolio transforme une simple fiche produit en un actif numérique intelligent.",
  "portfolioP.promise.body": "Pré-rempli grâce aux données GeoVINUM, enrichi par l'IA, personnalisable à l'infini et adaptable à chaque audience — Portfolio te permet de mieux comprendre, valoriser, présenter, former et vendre un produit.",
  "portfolioP.promise.tagline": "Une seule fiche. Une infinité d'usages.",
  "portfolioP.promise.cta": "Devenir Bêta testeur",
  "portfolioP.promise.cta_contact": "Nous contacter",

  // ----- Portfolio: Comment ça marche (FR) -----
  "portfolioP.how.eyebrow": "Comment ça marche",
  "portfolioP.how.title": "Trois étapes. Puis ça s'adapte à chaque audience.",
  "portfolioP.how.scan.title": "Identifie le produit",
  "portfolioP.how.scan.desc": "Choisis une appellation, un vin, un millésime. GeoVINUM récupère les données géographiques et historiques en un instant.",
  "portfolioP.how.ai.title": "L'IA pré-remplit",
  "portfolioP.how.ai.desc": "Storytelling, argumentaires, accords, dégustation, traductions — tout est généré. Tu gardes la main et peux modifier librement.",
  "portfolioP.how.share.title": "Diffuse partout",
  "portfolioP.how.share.desc": "QR sur bouteille, carte des vins, fiche export, formation. Chaque canal affiche les bons modules pour la bonne audience.",

  // ----- Portfolio: Démo live (FR) -----
  "portfolioP.demo.eyebrow": "Aperçu live",
  "portfolioP.demo.title": "Un produit. Quatre lectures. Zéro duplication.",
  "portfolioP.demo.subtitle": "Regarde comment la même fiche produit s'adapte automatiquement à celui qui la consulte.",
  "portfolioP.demo.lens": "Vue audience",
  "portfolioP.demo.note": "Aperçu statique. Les vraies fiches Portfolio sont entièrement interactives — édition, réorganisation, changement de langue, export PDF.",
  "portfolioP.demo.consumer.story": "Né sur les graves chaudes de Margaux, ce millésime raconte un long été sec. Nez velouté, palais ciselé — un vin qui récompense la patience.",
  "portfolioP.demo.retailer.story": "1er Grand Cru iconique avec forte demande secondaire. Prix conseillé > 800€. Excellente cohérence prix/notation face à Latour 2018.",
  "portfolioP.demo.sommelier.story": "Nez subtil de violette & cèdre. Tanins fermes, acidité élevée. Carafer 2h. Service : 17°C. Garde : 2028–2050.",
  "portfolioP.demo.educator.story": "Assemblage Rive Gauche dominé par le Cabernet — étude de cas idéale pour terroir graveleux, style médocain et classement de 1855.",
  "portfolioP.demo.section.story": "Histoire",
  "portfolioP.demo.section.story.value": "Héritage scellé depuis 1855",
  "portfolioP.demo.section.terroir": "Terroir",
  "portfolioP.demo.section.terroir.value": "Graves · 12m altitude",
  "portfolioP.demo.section.tasting": "Dégustation",
  "portfolioP.demo.section.tasting.value": "Cassis · Cèdre · Graphite",
  "portfolioP.demo.section.pairing": "Accords",
  "portfolioP.demo.section.pairing.value": "Agneau · Comté affiné",
  "portfolioP.demo.section.sales": "Angle commercial",
  "portfolioP.demo.section.sales.value": "Icône investissable",
  "portfolioP.demo.section.sensory": "Profil sensoriel",
  "portfolioP.demo.section.sensory.value": "Corsé · Tanins 7/10",
  "portfolioP.demo.section.similar": "Comparable à",
  "portfolioP.demo.section.similar.value": "Latour · Mouton 2018",
  "portfolioP.demo.section.price": "Positionnement",
  "portfolioP.demo.section.price.value": "850€ · Super-premium",
  "portfolioP.demo.section.tasting_detail": "Dégustation détaillée",
  "portfolioP.demo.section.tasting_detail.value": "16,2/20 · Carafe 2h",
  "portfolioP.demo.section.service": "Service",
  "portfolioP.demo.section.service.value": "17°C · Verre Bordeaux",
  "portfolioP.demo.section.wset": "Réf WSET",
  "portfolioP.demo.section.wset.value": "Étude de cas L3",
  "portfolioP.demo.section.maps": "Cartes",
  "portfolioP.demo.section.maps.value": "Médoc · Vue parcellaire",
  "portfolioP.demo.section.quiz": "Quiz",
  "portfolioP.demo.section.quiz.value": "12 questions",
  "portfolioP.demo.section.resources": "Ressources",
  "portfolioP.demo.section.resources.value": "PDF · Slides · Fiches",

  "product.pos.b2c": "Pour les amateurs",
  "product.pos.b2b": "Pour les pros du vin",
  "product.pos.education": "Pour l'éducation & la formation",
  "product.ideal_for": "Idéal pour",
  "product.key_benefits": "Bénéfices clés",

  "audience.beginners": "Débutants en vin",
  "audience.wine_lovers": "Amateurs de vin",
  "audience.travellers": "Voyageurs",
  "audience.casual_learners": "Apprenants curieux",
  "audience.wset_students": "Étudiants WSET",
  "audience.sommeliers": "Sommeliers",
  "audience.wine_pros": "Professionnels du vin",
  "audience.advanced_enthusiasts": "Passionnés avancés",
  "audience.importers": "Importateurs",
  "audience.distributors": "Distributeurs",
  "audience.wine_merchants": "Cavistes",
  "audience.wine_educators": "Formateurs vin",
  "audience.consultants": "Consultants",
  "audience.wine_students": "Étudiants en vin",
  "audience.hospitality_pros": "Pros de l'hôtellerie",
  "audience.wine_schools": "Écoles du vin",
  "audience.cert_students": "Étudiants en certification",
  "audience.sales_teams": "Équipes commerciales",
  "audience.educators": "Formateurs",
  "audience.tourism": "Projets œnotouristiques",
  "audience.wineries": "Domaines viticoles",
  "audience.wine_regions": "Régions viticoles",
  "audience.tourism_orgs": "Organisations touristiques",
  "audience.wine_brands": "Marques de vin",
  "audience.wine_tourism_ops": "Opérateurs œnotouristiques",
  "audience.wine_bars": "Bars à vin",
  "audience.events": "Événements",
  "audience.hospitality": "Hôtellerie & restauration",

  "benefit.interactive_maps": "Cartes vin interactives",
  "benefit.major_regions": "Principales régions du monde",
  "benefit.essential_terroir": "Connaissance essentielle des terroirs",
  "benefit.mobile_friendly": "Accès optimisé mobile",
  "benefit.detailed_maps": "Cartes détaillées",
  "benefit.expanded_appellations": "Couverture étendue des appellations",
  "benefit.in_depth_terroir": "Information terroir approfondie",
  "benefit.advanced_edu": "Contenu pédagogique avancé",
  "benefit.full_premium_maps": "Accès complet aux cartes premium",
  "benefit.pro_region_db": "Base de données pro des régions",
  "benefit.advanced_insights": "Insights terroir avancés",
  "benefit.business_ready": "Ressources prêtes à l'emploi",
  "benefit.structured_paths": "Parcours d'apprentissage structurés",
  "benefit.regional_courses": "Cours par région viticole",
  "benefit.practical_knowledge": "Connaissances pratiques",
  "benefit.personalized": "Accompagnement personnalisé",
  "benefit.instant_answers": "Réponses instantanées",
  "benefit.interactive_learning": "Apprentissage interactif",
  "benefit.expertise_on_demand": "Expertise vin à la demande",
  "benefit.interactive_exploration": "Exploration interactive",
  "benefit.detailed_regions": "Régions viticoles détaillées",
  "benefit.educational_content": "Contenu pédagogique",
  "benefit.multi_device": "Accès mobile & desktop",
  "benefit.seamless_integration": "Intégration site web fluide",
  "benefit.engagement_boost": "Engagement visiteurs renforcé",
  "benefit.terroir_storytelling": "Storytelling de terroir interactif",
  "benefit.no_tech": "Sans compétence technique",
  "benefit.qr_code": "Expériences QR code",
  "benefit.interactive_pages": "Pages bouteille interactives",
  "benefit.customer_engagement": "Engagement client",
  "benefit.guided_tasting": "Dégustations guidées",
  "benefit.mobile_first": "Expérience mobile-first",
  "benefit.enhanced_interaction": "Interaction visiteurs renforcée",

  "product.portfolio.tagline": "Reliez chaque bouteille à son histoire.",
  "product.portfolio.desc": "Créez des expériences vin digitales qui permettent à vos clients de découvrir l'origine, le terroir et l'identité de chaque bouteille.",
  "product.tasting.tagline": "Transformez les dégustations en expériences immersives.",
  "product.tasting.desc": "Proposez des parcours de dégustation interactifs qui aident les visiteurs à comprendre les vins, régions et terroirs tout en augmentant l'engagement.",
  "product.coach.tagline": "Votre coach personnel d'apprentissage du vin.",
  "product.coach.desc": "Une app de microlearning façon Duolingo qui aide à comprendre plus vite les régions, cépages, appellations et terroirs.",
  "product.maps.tagline": "Des cartes vin interactives pensées pour la pédagogie et l'engagement.",
  "product.maps.desc": "Explorez les régions viticoles à travers des cartes interactives au design soigné qui donnent vie aux terroirs et appellations.",
  "product.iframe.tagline": "Intégrez des cartes vin interactives sur votre propre site.",
  "product.iframe.desc": "Embarquez facilement les cartes geoVINUM sur votre site web, plateforme pédagogique ou projet œnotouristique.",
  "product.academy.tagline": "Apprenez le vin avec des cartes, des terroirs et des exemples concrets.",
  "product.academy.desc": "Une plateforme d'apprentissage interactive pensée pour rendre la formation vin visuelle, engageante et facile à comprendre.",
  "coach.plan.weekly": "Plan hebdomadaire",
  "coach.plan.yearly": "Plan annuel",
  "coach.plan.lifetime": "Plan à vie",
  "coach.per.week": "/ semaine",
  "coach.per.year": "/ an",
  "coach.per.month": "/ mois",
  "coach.per.one_time": "paiement unique",
  "coach.equivalent": "Soit",
  "coach.badge.flexible": "Flexible",
  "coach.badge.best_value": "Meilleur rapport",
  "coach.badge.lifetime": "Accès à vie",
  "coach.feature.learning_content": "Accès complet au contenu pédagogique",
  "coach.feature.quizzes": "Quiz interactifs",
  "coach.feature.personalized": "Recommandations personnalisées",
  "coach.feature.ai_coaching": "Coaching IA",
  "coach.feature.all_features": "Accès complet à toutes les fonctionnalités",
  "coach.feature.premium_paths": "Parcours premium",
  "coach.feature.exclusive_content": "Contenu pédagogique exclusif",
  "coach.feature.priority_updates": "Mises à jour prioritaires",
  "coach.feature.permanent_access": "Accès permanent à la plateforme",
  "coach.feature.future_updates": "Mises à jour futures incluses",
  "coach.feature.premium_content": "Accès au contenu premium",

  "home.pillars.title_a": "Un écosystème,",
  "home.pillars.title_b": "plusieurs façons d'explorer le vin.",
  "home.pillars.sub": "Des cartes à l'apprentissage en passant par l'intégration pro — découvrez la famille geoVINUM.",

  "nav.products": "Produits",
  "nav.resources": "Ressources",
  "nav.explore": "Explorer",
  "nav.academy": "Academy",
  "nav.maps_short": "Cartes",
  "nav.shop": "Boutique",
  "nav.business": "Business",
  "nav.terroir_ai": "geoVINUM app",
  "nav.blog": "Blog",
  "nav.resources": "Ressources",
  "nav.resources.articles": "Blog · Articles",
  "nav.resources.atlas": "Atlas des appellations",
  "nav.resources.cepages": "Cépages",
  "nav.resources.ebooks": "Ebooks gratuits",
  "cmd.kind.atlas_appellation": "Atlas · Appellation",
  "cmd.kind.atlas_cepage": "Atlas · Cépage",
  "cmd.kind.blog": "Article",
  "cmd.kind.academy": "Série Academy",
  "cmd.kind.shop": "Boutique",
  "nav.open_app": "Accéder à l'app",
  "nav.download_app": "Accéder à l'app",
  "nav.atlas_mini": "geoVINUM Atlas",
  "nav.atlas_magnum": "geoVINUM Atlas",
  "nav.atlas_pro": "geoVINUM Atlas",
  "nav.product.academy_short": "Academy",
  "nav.product.coach_short": "Coach",
  "nav.product.iframe_short": "Iframe",
  "nav.product.cellar_short": "Portfolio",
  "nav.product.tasting_short": "Tasting",

  // ----- geoVINUM Atlas (page dédiée FR) -----
  "product.atlas-magnum.tagline": "Votre carte de vignoble personnalisée.",
  "product.atlas-magnum.desc": "Présentez vos vignobles, parcelles et appellations à travers des cartes interactives conçues pour les domaines et les organismes vitivinicoles. Une carte qui raconte votre terroir — et qui convertit.",
  "product.atlas-magnum.for_who.title": "Conçu pour",
  "product.atlas-magnum.for_who.wineries.title": "Domaines viticoles",
  "product.atlas-magnum.for_who.wineries.desc": "Présentez votre vignoble avec une carte digne de votre vin.",
  "product.atlas-magnum.for_who.tourism.title": "Œnotourisme",
  "product.atlas-magnum.for_who.tourism.desc": "Offrez à vos visiteurs une expérience immersive avant même l'arrivée.",
  "product.atlas-magnum.for_who.interprof.title": "Interprofessions",
  "product.atlas-magnum.for_who.interprof.desc": "Valorisez vos appellations et défendez votre région à l'international.",
  "product.atlas-magnum.for_who.consultants.title": "Consultants & œnologues",
  "product.atlas-magnum.for_who.consultants.desc": "Apportez à vos clients une lecture cartographique professionnelle.",
  "product.atlas-magnum.features.title": "Ce que vous pouvez faire",
  "product.atlas-magnum.feature.appellations.title": "Afficher vos appellations",
  "product.atlas-magnum.feature.appellations.desc": "Cartes interactives, fond IGN, données officielles.",
  "product.atlas-magnum.feature.cadastre.title": "Références cadastrales",
  "product.atlas-magnum.feature.cadastre.desc": "Visualisez vos parcelles avec précision.",
  "product.atlas-magnum.feature.custom.title": "Personnalisation",
  "product.atlas-magnum.feature.custom.desc": "Couleurs, logos, territoires sur mesure.",
  "product.atlas-magnum.feature.share.title": "Partage en ligne",
  "product.atlas-magnum.feature.share.desc": "Intégration site web, lien public, présentations clients.",
  "product.atlas-magnum.use_cases.title": "Cas d'usage",
  "product.atlas-magnum.use.website.title": "Site du domaine",
  "product.atlas-magnum.use.website.desc": "Intégrez votre carte directement sur votre site, en iframe responsive.",
  "product.atlas-magnum.use.fair.title": "Salon professionnel",
  "product.atlas-magnum.use.fair.desc": "Captez l'attention des acheteurs avec une cartographie qui raconte le terroir.",
  "product.atlas-magnum.use.export.title": "Catalogue export",
  "product.atlas-magnum.use.export.desc": "Aidez vos clients internationaux à comprendre vos vins en un coup d'œil.",
  "product.atlas-magnum.use.tourism.title": "Accueil œnotouristique",
  "product.atlas-magnum.use.tourism.desc": "Préparez vos visiteurs avant la visite, prolongez l'expérience après.",
  "product.atlas-magnum.examples.title": "Ce que disent les domaines",
  "product.atlas-magnum.example.winery": "Les acheteurs passent désormais 3 minutes sur notre page terroir, contre 20 secondes avant.",
  "product.atlas-magnum.example.tourism": "Nos visiteurs arrivent préparés — on vend plus de vin et on réserve plus de visites.",
  "product.atlas-magnum.example.interprof": "Notre appellation a enfin une carte qui voyage à l'international.",
  "product.atlas-magnum.why.title": "Pourquoi geoVINUM Atlas ?",
  "product.atlas-magnum.why.body": "Une simple carte Google dit où vous êtes. Une carte geoVINUM Atlas dit pourquoi votre vignoble a ce goût-là — et convainc un acheteur en 30 secondes.",

  // ----- geoVINUM Atlas (page dédiée FR) -----
  "product.atlas-pro.tagline": "L'intelligence terroir pour les pros du vin.",
  "product.atlas-pro.desc": "Allez plus loin que la carte : analyse de terroir, visualisation 3D, données de référence et insights professionnels sur les régions viticoles du monde.",
  "product.atlas-pro.for_who.title": "Conçu pour",
  "product.atlas-pro.for_who.importers.title": "Importateurs & distributeurs",
  "product.atlas-pro.for_who.importers.desc": "Sélectionnez vos domaines avec une lecture terroir précise.",
  "product.atlas-pro.for_who.consultants.title": "Consultants & œnologues",
  "product.atlas-pro.for_who.consultants.desc": "Apportez une expertise terroir d'un niveau supérieur.",
  "product.atlas-pro.for_who.educators.title": "Formateurs WSET / sommellerie",
  "product.atlas-pro.for_who.educators.desc": "Enseignez la géographie du vin de manière visuelle.",
  "product.atlas-pro.for_who.interprof.title": "Interprofessions",
  "product.atlas-pro.for_who.interprof.desc": "Analysez vos territoires et présentez-les avec rigueur.",
  "product.atlas-pro.features.title": "Fonctionnalités avancées",
  "product.atlas-pro.feature.terroir_data.title": "Données terroir",
  "product.atlas-pro.feature.terroir_data.desc": "Géologie, sols, altitude, climat, exposition.",
  "product.atlas-pro.feature.viz_3d.title": "Visualisation 3D",
  "product.atlas-pro.feature.viz_3d.desc": "Comprenez la topographie d'un vignoble en un regard.",
  "product.atlas-pro.feature.mapping.title": "Cartographie avancée",
  "product.atlas-pro.feature.mapping.desc": "Outils cartographiques de niveau professionnel.",
  "product.atlas-pro.feature.analysis.title": "Analyse personnalisée",
  "product.atlas-pro.feature.analysis.desc": "Explorez les régions viticoles en profondeur.",
  "product.atlas-pro.use_cases.title": "Cas d'usage",
  "product.atlas-pro.use.import.title": "Sourcing import",
  "product.atlas-pro.use.import.desc": "Pré-sélectionnez vos prochains domaines avec une confiance terroir, sol et exposition.",
  "product.atlas-pro.use.education.title": "Préparation WSET / Master Som",
  "product.atlas-pro.use.education.desc": "Transformez le contenu régional dense en masterclasses visuelles que les étudiants retiennent.",
  "product.atlas-pro.use.consulting.title": "Briefings clients premium",
  "product.atlas-pro.use.consulting.desc": "Livrez des rapports terroir plus visuels sans tout refaire à la main.",
  "product.atlas-pro.use.research.title": "Recherche comparative",
  "product.atlas-pro.use.research.desc": "Comparez appellations, millésimes et parcelles dans un seul espace conçu pour les pros.",
  "product.atlas-pro.examples.title": "Ce que les pros font avec geoVINUM Atlas",
  "product.atlas-pro.example.importer": "Je présélectionne trois nouveaux domaines bourguignons en un après-midi — terroir, pente et exposition, tout au même endroit.",
  "product.atlas-pro.example.consultant": "Mes clients retainer voient exactement pourquoi leurs vignes ont un goût différent de celles du voisin.",
  "product.atlas-pro.example.educator": "Mes étudiants WSET niveau 3 maîtrisent enfin les questions de géographie parce qu'ils voient le terrain.",
  "product.atlas-pro.why.title": "Pourquoi geoVINUM Atlas ?",
  "product.atlas-pro.why.body": "geoVINUM Atlas ne se contente pas de situer les vignobles : il vous fait comprendre pourquoi chaque vin est différent — du sol à la pente, du millésime au millésime. Conçu pour ceux dont le métier dépend de connaître le vin.",

  "business.h1_a": "Des expériences vin pour",
  "business.h1_b": "votre activité.",
  "business.sub": "Intégrez des cartes interactives, engagez vos clients et créez des dégustations mémorables.",
  "business.cta_title": "Parlons-en",
  "business.cta_text": "Dites-nous tout sur votre boutique, école, domaine ou produit — on construit le partenariat sur mesure.",

  // ----- Academy v2 (FR) -----
  "academy.seo.title": "geoVINUM Academy — Le Netflix des terroirs",
  "academy.seo.desc": "Regardez des séries soignées sur le vin. Apprenez les régions, cépages et terroirs à votre rythme.",
  "academy.badge": "Séries en streaming",
  "academy.hero_a": "Le Netflix des",
  "academy.hero_b": "terroirs viticoles.",
  "academy.hero_sub": "Des séries soignées qui explorent les régions, cépages et figures du vin. De nouveaux épisodes chaque mois.",
  "academy.cta_start": "Commencer à regarder",
  "academy.cta_featured": "Série en vedette",
  "academy.row.featured": "À la une ce mois-ci",
  "academy.row.regions": "Régions du monde",
  "academy.row.fundamentals": "Les bases du vin",
  "academy.row.tasting": "Dégustation & accords",
  "academy.pricing.eyebrow": "Abonnement",
  "academy.pricing.title": "Un abonnement, des terroirs sans fin.",
  "academy.pricing.sub": "Sans engagement. De nouveaux épisodes chaque mois.",
  "academy.plan.monthly.title": "Mensuel",
  "academy.plan.monthly.per": "/ mois HT",
  "academy.plan.monthly.note": "Facturé chaque mois. Annulation à tout moment.",
  "academy.plan.yearly.title": "Annuel",
  "academy.plan.yearly.per": "/ an HT",
  "academy.plan.yearly.note": "Meilleur rapport · ~15,83 €/mois",
  "academy.plan.yearly.badge": "−17 %",
  "academy.plan.cta": "S'abonner",
  "academy.vat_note": "Prix HT. La TVA sera ajoutée au paiement selon votre pays.",
  "academy.feature.all_courses": "Toutes les séries et épisodes",
  "academy.feature.new_episodes": "Nouveaux épisodes chaque mois",
  "academy.feature.cancel_anytime": "Sans engagement",
  "academy.feature.multi_language": "Disponible en 4 langues",
  "academy.feature.priority": "Accès prioritaire aux nouveautés",

  // ----- Library coming soon (FR) -----
  "library.seo.title": "geoVINUM Librairie — Livres, carnets & accessoires du vin",
  "library.seo.desc": "Une boutique à venir pour les amateurs : livres reliés, carnets de dégustation et accessoires choisis.",
  "library.eyebrow": "Bientôt disponible",
  "library.title_a": "La librairie",
  "library.title_b": "geoVINUM.",
  "library.sub": "Une sélection soignée de livres, carnets de dégustation et accessoires — pensés pour les collectionneurs, sommeliers et amoureux de la géographie du vin.",
  "library.notify_me": "Prévenez-moi",
  "library.success": "Vous êtes sur la liste. On vous écrit dès les premières éditions.",
  "library.tiles_eyebrow": "Au programme",
  "library.tiles_title": "Trois familles de produits",
  "library.tile.books.eyebrow": "Livres",
  "library.tile.books.title": "Atlas du vin & livres de terroir",
  "library.tile.books.desc": "Éditions reliées sur les régions viticoles du monde, avec cartes, photographies et essais de terroir.",
  "library.tile.notebooks.eyebrow": "Carnets",
  "library.tile.notebooks.title": "Carnets de dégustation",
  "library.tile.notebooks.desc": "Journaux de dégustation papier premium pour noter visites, millésimes et accords.",
  "library.tile.accessories.eyebrow": "Accessoires",
  "library.tile.accessories.title": "Accessoires du vin choisis",
  "library.tile.accessories.desc": "Carafes, verrerie, ouvre-bouteilles et petits objets que l'on aime et utilise au caveau.",


  "biz.hero.title_a": "Transformez votre terroir en levier commercial, marketing et",
  "biz.hero.title_b": "pédagogique.",
  "biz.hero.sub": "geoVINUM aide domaines viticoles, régions, importateurs et formateurs à mettre en valeur les terroirs grâce à des cartes interactives, du storytelling et des expériences immersives.",
  "biz.hero.cta_primary": "Demander une démo",
  "biz.hero.cta_secondary": "Voir les cas d'usage",

  "biz.why.eyebrow": "Pourquoi geoVINUM",
  "biz.why.title": "Pourquoi les pros choisissent geoVINUM",
  "biz.why.bullet.terroirs": "Expliquer les terroirs visuellement",
  "biz.why.bullet.storytelling": "Améliorer le storytelling du vin",
  "biz.why.bullet.engagement": "Augmenter l'engagement sur votre site",
  "biz.why.bullet.education": "Soutenir la formation vin",
  "biz.why.bullet.differentiate": "Différencier votre marque",

  "biz.who.eyebrow": "Pour qui",
  "biz.who.title": "Conçu pour la filière vin",
  "biz.who.wineries.title": "Domaines viticoles",
  "biz.who.wineries.desc": "Mettez en valeur votre terroir, renforcez votre récit de marque et créez des expériences vin engageantes.",
  "biz.who.importers.title": "Importateurs & Distributeurs",
  "biz.who.importers.desc": "Aidez vos équipes à comprendre les régions viticoles et à mieux communiquer sur les vins.",
  "biz.who.regions.title": "Régions viticoles & Interprofessions",
  "biz.who.regions.desc": "Valorisez appellations et territoires à travers des expériences interactives et pédagogiques.",
  "biz.who.education.title": "Éducation & Formation",
  "biz.who.education.desc": "Donnez vie à la géographie du vin et aux terroirs pour étudiants et professionnels.",

  "biz.problem.eyebrow": "Le problème",
  "biz.problem.title": "Pourquoi la majorité du contenu vin n'engage pas",
  "biz.problem.p1": "La communication vin repose souvent sur des PDF statiques, des fiches techniques et de longues descriptions.",
  "biz.problem.p2": "Les clients peinent à comprendre les terroirs, les régions viticoles et ce qui rend un vin unique.",
  "biz.problem.p3": "geoVINUM transforme cette information complexe en expériences visuelles et interactives.",

  "biz.benefits.eyebrow": "Bénéfices",
  "biz.benefits.title": "Ce que geoVINUM vous permet d'accomplir",
  "biz.benefits.storytelling.title": "Améliorer le storytelling",
  "biz.benefits.storytelling.desc": "Aidez vos clients à comprendre l'origine et l'identité de vos vins.",
  "biz.benefits.engagement.title": "Augmenter l'engagement",
  "biz.benefits.engagement.desc": "Les cartes interactives créent des expériences mémorables et retiennent vos visiteurs plus longtemps.",
  "biz.benefits.sales.title": "Soutenir les équipes commerciales",
  "biz.benefits.sales.desc": "Offrez des outils visuels qui rendent l'explication des vins plus simple et plus percutante.",
  "biz.benefits.education.title": "Renforcer la formation vin",
  "biz.benefits.education.desc": "Transformez la complexité des régions viticoles en expériences d'apprentissage limpides.",

  "biz.solutions.eyebrow": "Solutions",
  "biz.solutions.title": "Notre plateforme d'expériences vin",
  "biz.solutions.maps.desc": "Cartes vin interactives à intégrer sur sites web, landing pages et plateformes pédagogiques.",
  "biz.solutions.maps.use.winery_websites": "Sites de domaines",
  "biz.solutions.maps.use.wine_tourism": "Œnotourisme",
  "biz.solutions.maps.use.edu_content": "Contenu pédagogique",
  "biz.solutions.maps.use.regional_promo": "Promotion régionale",
  "biz.solutions.magnum.desc": "L'atlas pédagogique le plus complet — 100+ masterclasses Academy intégrées, cartes interactives haute-définition, mode hors-ligne. Pour les pros du vin en formation.",
  "biz.solutions.magnum.use.wset_students": "Étudiants WSET",
  "biz.solutions.magnum.use.sommeliers": "Sommeliers",
  "biz.solutions.magnum.use.wine_pros": "Pros du vin",
  "biz.solutions.magnum.use.education": "Formation continue",
  "biz.solutions.pro.desc": "Atlas B2B avec comptes multi-utilisateurs, API white-label, formations sur mesure et support prioritaire. Pour les équipes qui vendent, forment ou distribuent du vin.",
  "biz.solutions.pro.use.importers": "Importateurs",
  "biz.solutions.pro.use.distributors": "Distributeurs",
  "biz.solutions.pro.use.merchants": "Cavistes",
  "biz.solutions.pro.use.schools": "Écoles du vin",
  "biz.solutions.portfolio.desc": "Des expériences digitales qui relient vos clients à l'histoire derrière chaque bouteille.",
  "biz.solutions.portfolio.use.winery_visits": "Visites au domaine",
  "biz.solutions.portfolio.use.qr_code": "Expériences QR code",
  "biz.solutions.portfolio.use.storytelling": "Storytelling produit",
  "biz.solutions.portfolio.use.brand_engagement": "Engagement de marque",
  "biz.solutions.tasting.desc": "Des dégustations interactives pensées pour engager les visiteurs et éduquer les consommateurs.",
  "biz.solutions.tasting.use.events": "Événements",
  "biz.solutions.tasting.use.wine_fairs": "Salons du vin",
  "biz.solutions.tasting.use.cellar_doors": "Caveaux",
  "biz.solutions.tasting.use.hospitality": "Hôtellerie & restauration",

  "biz.usecases.eyebrow": "Cas d'usage",
  "biz.usecases.title": "Comment les pros utilisent geoVINUM",
  "biz.usecases.tradefair.title": "Avant un salon professionnel",
  "biz.usecases.tradefair.desc": "Créez des supports visuels engageants pour présenter vos vins et vos régions.",
  "biz.usecases.website.title": "Sur le site d'un domaine",
  "biz.usecases.website.desc": "Ajoutez des cartes de terroirs interactives pour booster l'engagement des visiteurs.",
  "biz.usecases.export.title": "Pour les ventes à l'export",
  "biz.usecases.export.desc": "Aidez vos acheteurs internationaux à comprendre vos régions viticoles.",
  "biz.usecases.tourism.title": "Pour l'œnotourisme",
  "biz.usecases.tourism.desc": "Créez des expériences mémorables pour vos visiteurs.",
  "biz.usecases.training.title": "Pour former vos équipes",
  "biz.usecases.training.desc": "Enseignez la géographie du vin plus efficacement.",

  "biz.examples.eyebrow": "Exemples",
  "biz.examples.title": "geoVINUM en action",
  "biz.examples.map": "Carte vin interactive",
  "biz.examples.domain": "Intégration sur votre domaine",
  "biz.examples.mobile": "Expérience mobile & tablette",
  "biz.examples.sheet": "Fiche vin enrichie",

  "biz.position.eyebrow": "Pourquoi geoVINUM",
  "biz.position.title": "Bien plus que des cartes",
  "biz.position.p1": "geoVINUM combine géographie du vin, storytelling de terroir et expériences digitales dans une seule plateforme.",
  "biz.position.p2": "Contrairement aux outils de cartographie ou de présentation génériques, geoVINUM est conçu spécifiquement pour la filière vin.",

  "biz.social.eyebrow": "Ils nous font confiance",
  "biz.social.title": "Approuvé par les professionnels du vin",
  "biz.social.stat.regions.value": "30+",
  "biz.social.stat.regions.label": "Régions viticoles",
  "biz.social.stat.wineries.value": "200+",
  "biz.social.stat.wineries.label": "Domaines",
  "biz.social.stat.maps.value": "500+",
  "biz.social.stat.maps.label": "Cartes interactives",
  "biz.social.stat.countries.value": "25+",
  "biz.social.stat.countries.label": "Pays",

  "biz.faq.eyebrow": "FAQ",
  "biz.faq.title": "Questions fréquentes",
  "biz.faq.orgs.q": "Quels types d'organisations utilisent geoVINUM ?",
  "biz.faq.orgs.a": "Domaines viticoles, régions et interprofessions, importateurs et distributeurs, écoles et organismes de formation, professionnels de l'œnotourisme et de l'hôtellerie.",
  "biz.faq.embed.q": "Peut-on intégrer les cartes sur notre site ?",
  "biz.faq.embed.a": "Oui — geoVINUM Maps s'intègre sur n'importe quel site ou landing page via un simple iframe, entièrement responsive et personnalisable.",
  "biz.faq.languages.q": "geoVINUM est-il disponible en plusieurs langues ?",
  "biz.faq.languages.a": "Oui — geoVINUM est disponible en français, anglais, espagnol et portugais, et d'autres langues arrivent.",
  "biz.faq.tourism.q": "geoVINUM peut-il accompagner des projets d'œnotourisme ?",
  "biz.faq.tourism.a": "Absolument. De nombreuses régions utilisent geoVINUM pour enrichir l'œnotourisme avec cartes interactives, storytelling de terroir et expériences caveau.",
  "biz.faq.education.q": "geoVINUM peut-il servir à la formation vin ?",
  "biz.faq.education.a": "Oui — écoles, programmes sommellerie et équipes formation utilisent geoVINUM Academy et Maps pour enseigner la géographie du vin efficacement.",
  "biz.faq.custom.q": "Proposez-vous des projets sur mesure ?",
  "biz.faq.custom.a": "Oui — nous concevons cartes sur mesure, expériences brandées et intégrations dédiées. Contactez-nous pour échanger sur votre projet.",

  "biz.final.title": "Prêt à valoriser votre terroir autrement ?",
  "biz.final.body": "Découvrez comment geoVINUM peut aider votre organisation à créer des expériences vin engageantes et à mieux communiquer la valeur de ses vins.",
  "biz.final.cta_primary": "Demander une démo",
  "biz.final.cta_secondary": "Nous contacter",

  "biz.shared.use_cases_label": "Cas d'usage",
  "biz.shared.coming_soon": "Bientôt disponible",
  "biz.shared.view_demo": "Voir la démo",
  "biz.shared.join_waitlist": "Liste d'attente",
  "biz.shared.learn_more": "En savoir plus",
  "home.hero.h1_b": "Découvrez le vin par le terroir.",
  "home.hero.h1_main": "",
  "home.hero.sub_v2": "Explorez les régions viticoles, apprenez la culture du vin et vivez les terroirs à travers cartes, formations et outils interactifs.",
  "home.cta.explore_atlas": "Explorer Atlas",
  "home.cta.browse_courses": "Voir les cours",
  "home.cta.browse_collection": "Voir la collection",
  "home.cta.discover_iframe": "Découvrir Iframe",
  "home.cta.join_waitlist": "Rejoindre la liste d'attente",
  "home.s1.eyebrow": "Explorez les régions",
  "home.s1.title": "Naviguez régions, appellations et cépages avec",
  "home.s1.title_em": "des cartes interactives.",
  "home.s2.eyebrow": "Apprenez le vin",
  "home.s2.title": "Cours structurés, certifications et",
  "home.s2.title_em": "contenu pédagogique.",
  "home.s3.eyebrow": "Collectionnez les régions",
  "home.s3.title": "Des cartes du vin premium et",
  "home.s3.title_em": "des affiches pédagogiques.",
  "home.s4.eyebrow": "Pour les pros du vin",
  "home.s4.title": "Intégrez, engagez et",
  "home.s4.title_em": "fidélisez vos clients.",
  "footer.heading.products": "Produits",
  "footer.heading.resources": "Ressources",
  "footer.heading.company": "Société",
  "footer.heading.social": "Social",
  "footer.link.about": "À propos",
  "footer.link.contact": "Contact",
  "footer.link.newsletter": "Newsletter",
  "footer.link.support": "Support",
  "nav.product.atlas": "Atlas — Explorer",

  // ----- Newsletter signup (FR) -----
  "newsletter.placeholder.email": "votre@email.com",
  "newsletter.submit": "S'abonner",
  "newsletter.consent.label": "J'accepte de recevoir la newsletter mensuelle de geoVINUM et consens au traitement de mon adresse email à cette fin. Je peux me désinscrire à tout moment via le lien présent dans chaque email.",
  "newsletter.success": "Merci ! Votre inscription est confirmée. Surveillez votre boîte de réception.",
  "newsletter.error.consent_required": "Merci de cocher la case de consentement pour vous abonner.",
  "newsletter.error.generic": "L'inscription a échoué. Réessaie dans quelques instants.",
  // ----- Newsletter standalone page (FR) -----
  "newsletterPage.meta.title": "Newsletter — geoVINUM",
  "newsletterPage.meta.desc": "Abonne-toi à la newsletter mensuelle geoVINUM — appellations, terroirs, dégustations et coulisses du vignoble.",
  "newsletterPage.eyebrow": "Reste dans la boucle",
  "newsletterPage.title": "La Lettre des Terroirs",
  "newsletterPage.subtitle": "La newsletter geoVINUM — un email par mois. Nouvelles cartes, appellation à la une, dégustations et histoires de vignoble. Zéro spam, désinscription en un clic.",
  "newsletterPage.form.eyebrow": "S'abonner",
  "newsletterPage.contactSent.title": "Message bien reçu — merci !",
  "newsletterPage.contactSent.desc": "Nous avons bien reçu ton message. Audrey te répond sous 48h (jours ouvrés). Tu as reçu une copie par email. Pendant que tu es là, envie de rester en contact via la newsletter ?",

  // ----- Contact form (FR) -----
  "contact.eyebrow": "Contact",
  "contact.title": "Une question ? On répond.",
  "contact.subtitle": "Un partenariat, une question, un retour ? Écris-nous — nous répondons sous 48h les jours ouvrés.",
  "contact.field.name": "Nom",
  "contact.field.email": "Email",
  "contact.field.subject": "Sujet",
  "contact.field.message": "Message",
  "contact.submit": "Envoyer",
  "contact.consent.label": "Je consens à ce que geoVINUM traite mes données personnelles afin de répondre à ma demande. Base légale : mon consentement (RGPD art. 6.1.a). Je peux le retirer à tout moment en écrivant à hello@geovinum.io.",
  "contact.success.title": "Message envoyé",
  "contact.success.desc": "Nous avons bien reçu ton message et reviendrons vers toi rapidement.",
  "contact.error.consent_required": "Merci de cocher la case de consentement pour envoyer ton message.",
  "contact.error.generic": "Impossible d'envoyer le message. Réessaie.",
  "nav.product.portfolio": "Portfolio — Découvrir",
  "nav.product.tasting": "Tasting — Vivre",
  "nav.product.coach": "Coach — Apprendre",
  "nav.product.maps": "Maps — Collectionner",
  "nav.product.iframe": "Iframe — Intégrer",
  "nav.product.academy": "Academy — Apprendre",

  "article.not_found": "Article introuvable.",
  "legal.eyebrow": "Légal",
  "legal.sections": "Sections",
  "legal.contact_q": "Une question sur ce document ? Contactez",
  "lang.switch_label": "Langue",

  // === Auto-injected i18n keys for new pages (fr) ===
  "academyM.seo.title": "geoVINUM Academy — Apprendre le vin visuellement",
  "academyM.seo.desc": "Plus de 100 masterclasses streaming · {days} jours gratuits.",
  "academyM.hero.title": "Comprendre le vin, visuellement.",
  "academyM.hero.desc": "Plus de 100 masterclasses streaming pour étudiants WSET, professionnels du vin et passionnés. Cartes interactives, terroirs commentés, méthode de dégustation — tout au même endroit.",
  "academyM.hero.binge": "✦ Bingez toutes les séries, de façon illimitée — accès complet inclus dans l'abonnement.",
  "academyM.hero.cta_primary": "Commencer {days} jours gratuits",
  "academyM.hero.cta_secondary": "Découvrir les séries",
  "academyM.hero.trust.no_commitment": "Sans engagement",
  "academyM.hero.trust.cancel_anytime": "Annulable à tout moment",
  "academyM.hero.trust.hd_streaming": "Streaming HD",
  "academyM.why.eyebrow": "Pourquoi Academy",
  "academyM.why.title": "Le vin, enfin clair",
  "academyM.why.desc": "Une approche visuelle et progressive du vin, pensée par des pros pour des pros et amateurs avancés.",
  "academyM.why.visual.title": "Pédagogie visuelle",
  "academyM.why.visual.desc": "Cartes interactives, schémas, visualisations 3D des terroirs et appellations. Vous voyez ce que vous apprenez.",
  "academyM.why.wset.title": "Niveau WSET",
  "academyM.why.wset.desc": "Programme aligné sur les standards WSET 2 et 3. Idéal pour réviser, approfondir, ou démarrer une carrière dans le vin.",
  "academyM.why.experts.title": "Experts certifiés",
  "academyM.why.experts.desc": "Masterclasses animées par des sommeliers diplômés, œnologues et journalistes spécialisés.",
  "academyM.audience.eyebrow": "Pour qui",
  "academyM.audience.title": "Conçu pour 3 profils",
  "academyM.audience.wset.title": "Étudiants WSET",
  "academyM.audience.wset.desc": "Préparez les certifications WSET 2 et 3 avec un support visuel inégalé. Réviser une région prend 15 minutes au lieu d'une heure.",
  "academyM.audience.wset.badge": "Le + populaire",
  "academyM.audience.pros.title": "Professionnels du vin",
  "academyM.audience.pros.desc": "Sommeliers, cavistes, restaurateurs : montez en compétence sur les régions que vous vendez ou présentez à vos clients.",
  "academyM.audience.amateurs.title": "Amateurs avancés",
  "academyM.audience.amateurs.desc": "Vous aimez le vin, vous voulez aller au-delà des étiquettes. Comprendre le terroir, le climat, l'histoire — sans jargon.",
  "academyM.how.eyebrow": "Comment ça marche",
  "academyM.how.title": "3 étapes pour commencer",
  "academyM.how.step1.title": "Activer l'essai",
  "academyM.how.step1.desc": "Inscription en 30 secondes. {days} jours d'accès complet, sans carte bancaire requise pour démarrer.",
  "academyM.how.step2.title": "Choisir une série",
  "academyM.how.step2.desc": "Parcourez 100+ masterclasses par catégorie : régions, cépages, dégustation, business.",
  "academyM.how.step3.title": "Regarder & progresser",
  "academyM.how.step3.desc": "Streaming HD, mode hors-ligne sur l'app geoVINUM Atlas. Reprendre où vous vous êtes arrêté.",
  "academyM.preview.eyebrow": "Aperçu",
  "academyM.preview.title": "Quelques séries de la bibliothèque",
  "academyM.preview.see_all": "Voir toutes les séries",
  "academyM.preview.loading": "Chargement des séries…",
  "academyM.slider.featured": "À la une",
  "academyM.slider.videos": "vidéos",
  "academyM.slider.goto": "Aller à la série {n}",
  "academyM.final.title": "Démarrez vos {days} jours gratuits",
  "academyM.final.desc": "Sans carte bancaire. Sans engagement. Annulable à tout moment depuis votre profil.",
  "academyM.final.cta": "Commencer maintenant",
  "academyW.seo.title": "Academy — geoVINUM",
  "academyW.seo.desc": "Vos masterclasses streaming.",
  "academyW.upsell.text": "Explorez les séries librement — l'abonnement débloque le visionnage illimité.",
  "academyW.upsell.cta": "Essai 7 jours gratuits",
  "academyW.hero.eyebrow_featured": "À la une cette semaine",
  "academyW.hero.default_title": "Comprendre le vin, visuellement.",
  "academyW.hero.default_desc": "100+ masterclasses streaming, cartes interactives, et un atlas pédagogique pour étudiants WSET, sommeliers et passionnés.",
  "academyW.hero.watch": "Voir la série",
  "academyW.hero.trial30": "30 jours gratuits",
  "academyW.hero.search_cta": "Rechercher une série…",
  "academyW.chip.all": "Tout",
  "academyW.cat.wine_insights": "Wine insights",
  "academyW.cat.guides": "Guides",
  "academyW.cat.get_started": "Get started",
  "academyW.cat.for_professionals": "For professionals",
  "academyW.loading": "Chargement des séries…",
  "academyW.empty.title": "Pas encore de séries publiées",
  "academyW.empty.desc": "Les séries Academy seront bientôt disponibles ici. En attendant, découvrez nos Atlas.",
  "academyW.series_count_one": "série",
  "academyW.series_count_other": "séries",
  "academyW.search.placeholder": "Cherchez par série, sujet, région…",
  "academyW.search.no_results": "Aucun résultat pour « {q} »",
  "academyW.videos_label": "vidéos",
  "atlasP.not_found.title": "Produit Atlas introuvable",
  "atlasP.not_found.cta": "Voir tous les Atlas",
  "atlasP.bc.home": "Accueil",
  "atlasP.bc.atlas": "Atlas",
  "atlasP.trial_days": "{days} jours gratuits",
  "atlasP.who.label": "Pour qui ?",
  "atlasP.when.label": "Quand l'utiliser ?",
  "atlasP.when.title": "Au bon moment",
  "atlasP.why.label": "Pourquoi ?",
  "atlasP.why.title": "La valeur unique",
  "atlasP.features.eyebrow": "Fonctionnalités",
  "atlasP.features.title": "Ce que {name} vous apporte",
  "atlasP.upsell.title": "Pour aller plus loin",
  "atlasP.upsell.discover": "Découvrir",
  "atlasP.final.title": "Prêt à essayer {name} ?",
  "atlasP.final.trial": "{days} jours d'essai gratuit · sans engagement · annulable à tout moment",
  "atlasP.mini.tagline": "Pour commencer simplement",
  "atlasP.mini.cta_primary": "Accéder à l'app",
  "atlasP.mini.cta_secondary": "Découvrir geoVINUM Atlas",
  "atlasP.mini.price": "Gratuit",
  "atlasP.mini.price_note": "Web — aucune installation",
  "atlasP.mini.audience.label": "Débutants & curieux",
  "atlasP.mini.audience.desc": "Les amateurs qui découvrent le vin et veulent explorer visuellement les régions viticoles du monde depuis n'importe quel appareil.",
  "atlasP.mini.when": "Quand vous êtes en restaurant, en cave, ou en voyage et que vous voulez comprendre rapidement d'où vient un vin.",
  "atlasP.mini.why": "Une app web simple, gratuite, intuitive — la porte d'entrée vers l'univers geoVINUM.",
  "atlasP.mini.feat.1": "30+ régions viticoles cartographiées",
  "atlasP.mini.feat.2": "Recherche par appellation",
  "atlasP.mini.feat.3": "Géolocalisation des vignobles",
  "atlasP.mini.feat.4": "Synthèse pédagogique par région",
  "atlasP.mini.feat.5": "100% gratuit, sans installation",
  "atlasP.mini.upsell.magnum.title": "geoVINUM Atlas",
  "atlasP.mini.upsell.magnum.desc": "Pour aller plus loin dans la pédagogie",
  "atlasP.mini.upsell.academy.title": "geoVINUM Academy",
  "atlasP.mini.upsell.academy.desc": "100+ masterclasses en streaming",
  "atlasP.magnum.tagline": "L'outil de référence pour comprendre le vin",
  "atlasP.magnum.cta_primary": "Commencer 30 jours gratuits",
  "atlasP.magnum.cta_secondary": "Voir la version Pro",
  "atlasP.magnum.price": "Abonnement mensuel",
  "atlasP.magnum.price_note": "30 jours gratuits, sans engagement",
  "atlasP.magnum.audience.label": "Étudiants WSET, professionnels, amateurs avancés",
  "atlasP.magnum.audience.desc": "Ceux qui veulent maîtriser le vin avec un outil pédagogique complet : sommeliers en formation, étudiants WSET, professionnels de la restauration.",
  "atlasP.magnum.when": "Quand vous préparez un examen WSET, quand vous voulez réviser une région avant un service, quand vous bâtissez votre culture du vin sur la durée.",
  "atlasP.magnum.why": "Plus de 100 masterclasses video Academy intégrées, cartes interactives haute définition, fiches techniques par appellation, mise à jour permanente.",
  "atlasP.magnum.feat.1": "Toutes les régions du monde",
  "atlasP.magnum.feat.2": "Cartes interactives haute-définition",
  "atlasP.magnum.feat.3": "Academy intégrée : 100+ masterclasses",
  "atlasP.magnum.feat.4": "Fiches d'appellation détaillées",
  "atlasP.magnum.feat.5": "Mode hors-ligne pour les voyages",
  "atlasP.magnum.feat.6": "Mises à jour automatiques",
  "atlasP.magnum.upsell.pro.title": "geoVINUM Atlas (B2B)",
  "atlasP.magnum.upsell.pro.desc": "Pour les importateurs & cavistes",
  "atlasP.magnum.upsell.shop.title": "Cartes imprimées",
  "atlasP.magnum.upsell.shop.desc": "Posters & encadrés de vos régions",
  "atlasP.pro.tagline": "La solution B2B pour les professionnels du vin",
  "atlasP.pro.cta_primary": "Demander une démo",
  "atlasP.pro.cta_secondary": "Essai 30 jours",
  "atlasP.pro.price": "Sur devis",
  "atlasP.pro.price_note": "Abonnement B2B annuel · 30 jours d'essai",
  "atlasP.pro.audience.label": "Importateurs, distributeurs, cavistes, écoles",
  "atlasP.pro.audience.desc": "Les structures qui ont besoin de former leurs équipes commerciales, équiper leur force de vente, ou intégrer geoVINUM à leur site web et catalogue.",
  "atlasP.pro.when": "Quand vous gérez une équipe commerciale, un caveau, une école du vin, ou un site e-commerce qui doit vendre l'histoire du vin avant le produit.",
  "atlasP.pro.why": "Un compte multi-utilisateurs, accès à toute la bibliothèque Academy, API pour intégrer nos cartes sur votre site (iframe), formations sur mesure, support prioritaire.",
  "atlasP.pro.feat.1": "Comptes multi-utilisateurs (équipes)",
  "atlasP.pro.feat.2": "Accès complet à l'Academy + Atlas",
  "atlasP.pro.feat.3": "API & iframe pour votre site",
  "atlasP.pro.feat.4": "Cartes white-label personnalisables",
  "atlasP.pro.feat.5": "Formations sur mesure incluses",
  "atlasP.pro.feat.6": "Support dédié & onboarding",
  "atlasP.pro.feat.7": "Reporting d'usage par équipe",
  "atlasP.pro.upsell.iframe.title": "Iframe geoVINUM",
  "atlasP.pro.upsell.iframe.desc": "Intégrez nos cartes sur votre site",
  "atlasP.pro.upsell.business.title": "Découvrir Business",
  "atlasP.pro.upsell.business.desc": "Portfolio, Tasting, et plus",
  "blogP.seo.title": "Blog — geoVINUM",
  "blogP.seo.desc": "Articles, guides et conseils pour comprendre le vin visuellement.",
  "blogP.bc.home": "Accueil",
  "blogP.bc.blog": "Blog",
  "blogP.title": "Blog geoVINUM",
  "blogP.intro": "Wine insights, guides pratiques, premiers pas et contenu pour les pros — toute notre éditorial vin au même endroit.",
  "blogP.search.placeholder": "Rechercher un article…",
  "blogP.chip.all": "Tout",
  "blogP.cat.wine_insights": "Wine insights",
  "blogP.cat.guides": "Guides",
  "blogP.cat.get_started": "Get started",
  "blogP.cat.for_professionals": "For professionals",
  "blogP.cat.ebook": "Ebook",
  "blogP.cat.cepage": "Cépages",
  "ebookF.eyebrow": "Ebook gratuit",
  "ebookF.title": "Téléchargez l'ebook",
  "ebookF.intro": "Laissez votre email ci-dessous pour recevoir le PDF immédiatement. Vous en recevrez aussi une copie par email afin de le retrouver sur tous vos appareils.",
  "ebookF.email_label": "Email",
  "ebookF.email_placeholder": "vous@domaine.com",
  "ebookF.consent_text": "J'accepte que geoVINUM conserve cet email pour m'envoyer l'ebook et répondre à toute demande associée. Droit d'accès, de rectification et de suppression à tout moment.",
  "ebookF.newsletter_label": "M'abonner aussi à la newsletter geoVINUM (1 à 2 emails/mois — désinscription en un clic).",
  "ebookF.submit": "Obtenir l'ebook",
  "ebookF.submitting": "Envoi en cours…",
  "ebookF.success_title": "Ebook prêt",
  "ebookF.success_email_sent": "Nous vous avons aussi envoyé le lien par email.",
  "ebookF.success_msg": "Votre téléchargement devrait s'ouvrir dans un nouvel onglet. Sinon, utilisez le bouton ci-dessous.",
  "ebookF.download_btn": "Télécharger maintenant",
  "ebookF.pages_label": "pages",
  "ebookF.gdpr_hint": "Vos données restent chez geoVINUM. Aucun spam.",
  "ebookF.error_email": "Une adresse email valide est requise.",
  "ebookF.error_consent": "Merci de cocher la case de consentement pour télécharger l'ebook.",
  "blogP.loading": "Chargement…",
  "blogP.empty.title": "Aucun article pour le moment",
  "blogP.empty.no_results": "Aucun résultat pour « {q} »",
  "blogP.read_minutes": "min",
  "blogP.article.loading": "Chargement…",
  "blogP.article.not_found": "Article introuvable",
  "blogP.article.back": "Retour au blog",
  "blogP.article.by": "Par",
  "blogP.article.read_time": "min de lecture",
  "blogP.article.cross.eyebrow": "Pour aller plus loin",
  "blogP.article.cross.title": "Explorez l'univers geoVINUM",
  "blogP.article.cross.shop_label": "Boutique",
  "blogP.article.cross.shop_title": "Cartes des vignobles",
  "blogP.article.cross.shop_desc": "Posters, encadrés, canvas",
  "blogP.article.cross.academy_label": "Academy",
  "blogP.article.cross.academy_title": "Masterclasses streaming",
  "blogP.article.cross.academy_desc": "7 jours gratuits",
  "blogP.article.cross.terroir_label": "geoVINUM app",
  "blogP.article.cross.terroir_title": "Parlez au terroir",
  "blogP.article.cross.terroir_desc": "Gratuit à essayer · 4 langues",
  "bizM.eyebrow": "Comparatif",
  "bizM.title": "Quand · Qui · Pourquoi",
  "bizM.subtitle": "Trouvez en un coup d'œil la solution geoVINUM qui correspond à votre besoin.",
  "bizM.col.solution": "Solution",
  "bizM.col.when": "Quand l'utiliser",
  "bizM.col.who": "Pour qui",
  "bizM.col.why": "Pourquoi",
  "bizM.col.action": "Action",
  "bizM.soon": "Bientôt",
  "bizM.discover": "Découvrir",
  "bizM.label.when": "Quand",
  "bizM.label.who": "Pour qui",
  "bizM.label.why": "Pourquoi",
  "bizM.magnum.name": "geoVINUM Atlas",
  "bizM.magnum.when": "Préparer un examen WSET, monter en compétence, équiper une équipe sommellerie",
  "bizM.magnum.who": "Sommeliers, étudiants WSET, professionnels CHR",
  "bizM.magnum.why": "Outil pédagogique le plus complet du marché, 100+ masterclasses incluses",
  "bizM.pro.name": "geoVINUM Atlas",
  "bizM.pro.when": "Former une force de vente, créer un catalogue interactif, équiper un caveau",
  "bizM.pro.who": "Importateurs, distributeurs, cavistes, écoles du vin",
  "bizM.pro.why": "Multi-utilisateurs, API white-label, support dédié, ROI mesurable",
  "bizM.iframe.name": "Iframe",
  "bizM.iframe.when": "Vous avez un site web et voulez raconter le terroir avant de vendre",
  "bizM.iframe.who": "Domaines viticoles, syndicats, offices de tourisme, e-commerce vin",
  "bizM.iframe.why": "Intégration en 1 ligne, cartes haute-définition, multi-langues",
  "bizM.portfolio.name": "Portfolio",
  "bizM.portfolio.when": "Vous voulez digitaliser l'expérience client en cave / boutique",
  "bizM.portfolio.who": "Domaines viticoles, caves coopératives, boutiques expérientielles",
  "bizM.portfolio.why": "QR codes immersifs, storytelling visuel, drainent les visites en chiffre d'affaires",
  "bizM.tasting.name": "Tasting",
  "bizM.tasting.when": "Vous animez salons, foires aux vins, événements œnotouristiques",
  "bizM.tasting.who": "Salons pro, organisateurs d'événements, hôtellerie haut de gamme",
  "bizM.tasting.why": "Gamification ludique, mémorable, augmente le taux de captation leads",

  // === searchP keys (fr) ===
  "searchP.seo.title": "Recherche — geoVINUM",
  "searchP.seo.desc": "Recherchez dans le blog, l'Academy et la boutique geoVINUM.",
  "searchP.title": "Recherche",
  "searchP.intro": "Trouvez articles, séries Academy et cartes imprimées — tout au même endroit.",
  "searchP.placeholder": "Cherchez une région, sujet, cépage, série…",
  "searchP.chip.all": "Tout",
  "searchP.chip.blog": "Blog",
  "searchP.chip.academy": "Academy",
  "searchP.chip.shop": "Boutique",
  "searchP.kind.blog": "Blog",
  "searchP.kind.academy": "Academy",
  "searchP.kind.shop": "Boutique",
  "searchP.loading": "Recherche en cours…",
  "searchP.from": "à partir de",
  "searchP.empty.prompt": "Saisissez quelque chose pour commencer.",
  "searchP.empty.no_results_title": "Aucun résultat",
  "searchP.empty.no_results_desc": "Essayez une autre région, cépage ou sujet.",

  // === dashboard keys (fr) ===
  "dashboard.track_shipment": "Suivre la livraison",
  "dashboard.view_all_orders": "Voir toutes les commandes",

  // === hero slides (fr) ===
  "hero.label.for": "Pour qui",
  "hero.label.why": "Pourquoi",
  "hero.see_all": "Tous les produits",
  "hero.slide.promise.eye": "geoVINUM",
  "hero.slide.promise.title": "Comprendre le vin, visuellement.",
  "hero.slide.promise.for": "Amateurs, étudiants & pros — de Bordeaux à Mendoza",
  "hero.slide.promise.why": "+3 000 appellations viticoles cartographiées — explorez, apprenez et choisissez le vin sans jargon.",
  "hero.slide.promise.cta": "Découvrir geoVINUM",
  "hero.slide.mini.eye": "geoVINUM Atlas",
  "hero.slide.mini.title": "Découvrez le vin sur votre téléphone, gratuitement.",
  "hero.slide.mini.for": "Débutants curieux & voyageurs",
  "hero.slide.mini.why": "30+ régions cartographiées, géolocalisation, app web 100% gratuite.",
  "hero.slide.mini.cta": "Accéder à geoVINUM Atlas",
  "hero.slide.terroir.eye": "geoVINUM app",
  "hero.slide.terroir.title": "Parlez au terroir. Partout, dans toutes les langues.",
  "hero.slide.terroir.for": "Amateurs, bars à vin, cavistes, écoles & producteurs",
  "hero.slide.terroir.why": "Une seule app IA pour tout — Q&R, flashcards, dégustation à l'aveugle, photo-to-guide, multijoueur. PWA, sans installation.",
  "hero.slide.terroir.cta": "Lancer geoVINUM app",
  "hero.slide.magnum.eye": "geoVINUM Atlas",
  "hero.slide.magnum.title": "Maîtrisez le vin, visuellement.",
  "hero.slide.magnum.for": "Étudiants WSET, sommeliers, pros CHR",
  "hero.slide.magnum.why": "100+ masterclasses vidéo + cartes interactives. 30 jours gratuits.",
  "hero.slide.magnum.cta": "Essayer geoVINUM Atlas gratuitement",
  "hero.slide.academy.eye": "Academy",
  "hero.slide.academy.title": "Masterclasses du vin, à la demande.",
  "hero.slide.academy.for": "Quiconque veut vraiment comprendre le vin",
  "hero.slide.academy.why": "Bingez 100+ masterclasses HD. 7 jours gratuits, puis 9€/mois.",
  "hero.slide.academy.cta": "Commencer 7 jours gratuits",
  "hero.slide.shop.eye": "Boutique",
  "hero.slide.shop.title": "Des cartes des vins à accrocher au mur.",
  "hero.slide.shop.for": "Collectionneurs, restaurants, idées cadeau",
  "hero.slide.shop.why": "Posters premium, encadrés, canvas — imprimés en UE, livrés dans le monde.",
  "hero.slide.shop.cta": "Voir les cartes",
  "hero.slide.pro.eye": "geoVINUM Atlas",
  "hero.slide.pro.title": "Équipez votre équipe vin.",
  "hero.slide.pro.for": "Importateurs, distributeurs, écoles du vin",
  "hero.slide.pro.why": "Comptes multi-utilisateurs, API white-label, formations sur-mesure, support prioritaire.",
  "hero.slide.pro.cta": "Réserver une démo",
  "hero.slide.iframe.eye": "Iframe",
  "hero.slide.iframe.title": "Intégrez nos cartes sur votre site.",
  "hero.slide.iframe.for": "Domaines, offices de tourisme, e-commerce vin",
  "hero.slide.iframe.why": "1 ligne d'HTML, cartes interactives HD, multi-langues.",
  "hero.slide.iframe.cta": "Récupérer le code",
  "hero.slide.blog.eye": "Blog",
  "hero.slide.blog.title": "Le vin expliqué, un article à la fois.",
  "hero.slide.blog.for": "Apprenants curieux",
  "hero.slide.blog.why": "Insights, guides, premiers pas et conseils pros — chaque semaine.",
  "hero.slide.blog.cta": "Lire le blog",

  // === hero slides extra (fr) ===
  "hero.soon": "Bientôt",
  "hero.slide.portfolio.eye": "Portfolio",
  "hero.slide.portfolio.title": "Transformez votre cave en histoire.",
  "hero.slide.portfolio.for": "Domaines viticoles, caves coopératives, boutiques expérientielles",
  "hero.slide.portfolio.why": "QR codes immersifs & storytelling visuel — transformez vos visites en CA.",
  "hero.slide.portfolio.cta": "Découvrir Portfolio",
  "hero.slide.tasting.eye": "Tasting",
  "hero.slide.tasting.title": "La dégustation ludique pour vos événements.",
  "hero.slide.tasting.for": "Salons pro, organisateurs d'événements, hôtellerie haut de gamme",
  "hero.slide.tasting.why": "Gamification ludique et mémorable — augmente la captation de leads.",
  "hero.slide.tasting.cta": "Découvrir Tasting",
  "hero.slide.coach.eye": "Coach",
  "hero.slide.coach.title": "Votre coach vin IA, 24/7.",
  "hero.slide.coach.for": "Quiconque veut un mentor personnel du vin",
  "hero.slide.coach.why": "Posez n'importe quelle question : accords, régions, dégustation — réponses d'expert instantanées.",
  "hero.slide.coach.cta": "Découvrir Coach",

  // === catalog cta (fr) ===
  "cta.full_catalog": "Télécharger le catalogue (PDF)",
  "cta.product_catalog": "Fiche produit (PDF)",

  // === catalog capture (fr) ===
  "catalogCapture.eyebrow": "GRATUIT · PDF · 13 pages",
  "catalogCapture.title": "Téléchargez le catalogue complet",
  "catalogCapture.subtitle": "Juste votre email — on partage nos actus deux fois par an, jamais de spam.",
  "catalogCapture.email": "Email *",
  "catalogCapture.first_name": "Prénom (optionnel)",
  "catalogCapture.company": "Société (optionnel)",
  "catalogCapture.category_placeholder": "Vous êtes ? (optionnel)",
  "catalogCapture.cta": "Télécharger le PDF",
  "catalogCapture.loading": "Préparation du PDF…",
  "catalogCapture.skip": "Passer — ouvrir directement le PDF",
  "catalogCapture.close": "Fermer",
  "catalogCapture.err.email": "Merci d'indiquer une adresse email valide.",
  "catalogCapture.privacy": "En téléchargeant, vous acceptez d'être contacté(e) au sujet de geoVINUM. Désinscription en 1 clic.",
  "catalogCapture.cat.domaines": "Domaine viticole",
  "catalogCapture.cat.cavistes": "Caviste",
  "catalogCapture.cat.negociants": "Négociant",
  "catalogCapture.cat.restaurateurs": "Restaurateur",
  "catalogCapture.cat.distributeurs": "Distributeur",
  "catalogCapture.cat.importateurs": "Importateur",
  "catalogCapture.cat.ecoles": "École du vin",
  "catalogCapture.cat.drinks_tech": "Drinks Tech (startup)",
  "catalogCapture.cat.e_commerce": "E-commerce vin",
  "catalogCapture.cat.autre": "Autre",

  "nav.cartes": "Cartes",

  "iframeLive.eyebrow": "Démo interactive",
  "iframeLive.title": "Testez la carte sur n'importe quelle appellation.",
  "iframeLive.subtitle": "Tapez le nom d'une appellation, choisissez dans la liste, la carte se recharge en direct — exactement comme elle s'affichera sur votre site.",
  "iframeLive.placeholder": "Sancerre, Chablis, Côte-Rôtie…",
  "iframeLive.cta": "Tester",
  "iframeLive.suggestions_label": "Suggestions :",
  "iframeLive.footer": "La démo utilise exactement le même composant que les articles de blog et que votre futur embed.",

  "iframeInstall.eyebrow": "Installation",
  "iframeInstall.title": "En 4 étapes, votre site affiche la carte.",
  "iframeInstall.subtitle": "Aucun framework imposé. Tant que vous pouvez créer une route serveur, vous pouvez intégrer geoVINUM.",
  "iframeInstall.step1.title": "1. Souscrire et obtenir votre clé API",
  "iframeInstall.step1.body": "Choisissez votre abonnement ci-dessous. Après paiement, vous recevez immédiatement votre clé API EtOH par email.",
  "iframeInstall.step2.title": "2. Demander le token",
  "iframeInstall.step2.body": "Depuis votre serveur, échangez votre clé API contre un token JWT court (1h, format `{ \"access\": \"<JWT>\" }`) — exactement comme la route qui propulse ce site. Mettez-le en cache 55 min et renvoyez-le au front. **N'exposez jamais la clé API dans le navigateur.**",
  "iframeInstall.step3.title": "3. Lier votre champ appellation dynamique",
  "iframeInstall.step3.body": "Dans votre CMS ou votre template, lisez le nom de l'appellation depuis votre fiche produit (`product.appellation`, un champ ACF, un metafield Shopify, etc.) et passez-le en `app=` dans l'URL de l'iframe. Un seul champ — chaque carte s'adapte toute seule.",
  "iframeInstall.step4.title": "4. La carte s'affiche sur chaque fiche",
  "iframeInstall.step4.body": "C'est tout. Sur chaque fiche produit, une iframe `https://iframe.geovinum.app/?token=…&app=<appellation>` est générée automatiquement — la bonne carte apparaît pour chaque produit, sans le moindre travail manuel page par page.",
  "iframeInstall.personalize.title": "Personnaliser le snippet avec votre clé",
  "iframeInstall.personalize.body": "Collez votre clé API EtOH ci-dessous — les snippets se rempliront automatiquement. Votre clé **reste dans votre navigateur**, elle n'est jamais envoyée à nos serveurs.",
  "iframeInstall.personalize.placeholder": "ex: 362ec4bf-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
  "iframeInstall.personalize.clear": "Effacer",
  "iframeInstall.personalize.ready": "Snippets personnalisés ({0}) — la copie utilise la clé complète.",
  "iframeInstall.show_key": "Afficher la clé",
  "iframeInstall.hide_key": "Masquer la clé",
  "iframeInstall.copy": "Copier",
  "iframeInstall.copied": "Copié",
  "iframeInstall.help_prefix": "Besoin d'aide pour intégrer ?",
  "iframeInstall.help_link": "Contactez-nous",
  "iframeInstall.help_suffix": "— notre équipe vous accompagne gratuitement.",

  "cartes.seo_title": "Cartes des appellations viticoles | geoVINUM",
  "cartes.seo_desc": "Explorez chaque appellation viticole sur une carte interactive : Sancerre, Chablis, Saint-Émilion, Châteauneuf-du-Pape… geoVINUM cartographie le vignoble mondial.",
  "cartes.eyebrow": "Catalogue des terroirs",
  "cartes.h1_line1": "Chaque appellation,",
  "cartes.h1_line2": "une carte interactive.",
  "cartes.subtitle": "Découvrez {0} appellations cartographiées en détail, avec leurs parcelles cadastrales, leurs cépages et leurs articles dédiés.",
  "cartes.subtitle_empty": "Découvrez nos appellations cartographiées en détail, avec leurs parcelles cadastrales, leurs cépages et leurs articles dédiés.",
  "cartes.search_placeholder": "Rechercher une appellation… (Sancerre, Châteauneuf, etc.)",
  "cartes.region_all": "Toutes",
  "cartes.loading": "Chargement du catalogue…",
  "cartes.empty": "Aucune appellation ne correspond.",
  "cartes.empty_reset": "Réinitialiser",
  "cartes.count_one": "{0} appellation",
  "cartes.count_many": "{0} appellations",
  "cartes.card.eyebrow": "Appellation",
  "cartes.card.preview": "Aperçu carte",
  "cartes.card.hide_preview": "Masquer la carte",
  "cartes.card.read": "Lire",
  "cartes.cta.eyebrow": "Pour les pros",
  "cartes.cta.title": "Cette même carte sur votre site, en 4 étapes.",
  "cartes.cta.subtitle": "Cavistes, éditeurs, agences œnotourisme : embarquez la carte interactive de n'importe quelle appellation dans votre site avec notre iframe.",
  "cartes.cta.button": "Découvrir l'iframe geoVINUM",
  "cartes.region.bordeaux":  "Bordeaux & Sud-Ouest",
  "cartes.region.bourgogne": "Bourgogne",
  "cartes.region.beaujolais":"Beaujolais",
  "cartes.region.rhone":     "Vallée du Rhône",
  "cartes.region.loire":     "Vallée de la Loire",
  "cartes.region.champagne": "Champagne & Alsace",
  "cartes.region.languedoc": "Languedoc-Roussillon",
  "cartes.region.provence":  "Provence & Corse",
  "cartes.region.jura":      "Jura & Savoie",
  "cartes.region.italy":     "Italie",
  "cartes.region.spain":     "Espagne",
  "cartes.region.portugal":  "Portugal",
  "cartes.region.other":     "Autres terroirs",

  // geoVINUM app landing page
  "terroir.seo.title": "geoVINUM app — L'IA du vin, partout, dans toutes les langues",
  "terroir.seo.desc": "Parlez au terroir. Découvrez les appellations, cépages, producteurs et événements. Construisez votre route des vins, apprenez avec des flashcards, dégustez à l'aveugle. Pour les amateurs comme pour les pros.",
  "terroir.hero.eyebrow": "geoVINUM app · Propulsé par geoVINUM",
  "terroir.hero.h1_a": "Parlez au terroir.",
  "terroir.hero.h1_b": "Partout, dans toutes les langues.",
  "terroir.hero.sub": "Votre compagnon IA du vin. Posez toutes vos questions sur les appellations, cépages, producteurs, événements. Construisez votre parcours œnotouristique, apprenez avec des flashcards, dégustez à l'aveugle, prenez une photo et laissez l'IA vous raconter. Une seule app — pour les amateurs et les pros.",
  "terroir.hero.cta_primary": "Lancer la web app",
  "terroir.hero.cta_secondary": "Créer un compte",
  "terroir.hero.pwa_note": "Aucun téléchargement requis · Fonctionne sur tous les appareils · 4 langues",
  "terroir.demo.title": "Essayez maintenant",
  "terroir.demo.sub": "Aperçu en direct embarqué ici-même. Cliquez sur une question pour démarrer la conversation.",
  "terroir.demo.open_in_new": "Ouvrir dans un nouvel onglet",
  "terroir.usecases.title": "Ce que vous pouvez faire avec geoVINUM app",
  "terroir.usecases.sub": "Des cas d'usage, pas une liste de fonctionnalités. Choisissez celui qui colle à votre moment.",
  "terroir.uc.amateur.tag": "Pour les amateurs",
  "terroir.uc.amateur.1.title": "Je prépare un week-end vin",
  "terroir.uc.amateur.1.body": "Tapez vos dates et la région. geoVINUM app construit votre itinéraire — domaines ouverts ce week-end, dégustations, restaurants, les meilleures appellations à goûter maintenant.",
  "terroir.uc.amateur.2.title": "J'ai une bouteille en main, je veux comprendre",
  "terroir.uc.amateur.2.body": "Photographiez l'étiquette. L'IA vous raconte l'histoire de l'appellation, du cépage, du producteur, ce que vous allez sentir dans le verre et avec quoi l'accorder.",
  "terroir.uc.amateur.3.title": "Je veux apprendre un peu chaque jour",
  "terroir.uc.amateur.3.body": "Micro-cours quotidiens, flashcards sur les cépages et régions, exercices de dégustation à l'aveugle. 5 minutes par jour pour devenir un dégustateur confiant.",
  "terroir.uc.amateur.4.title": "Je veux jouer entre amis",
  "terroir.uc.amateur.4.body": "Quiz multijoueur sur les appellations, cépages, régions. Parfait pour une soirée, un club de dégustation, ou votre prochain dîner.",
  "terroir.uc.pro.tag": "Pour les pros du vin",
  "terroir.uc.pro.1.title": "Je tiens un bar à vin",
  "terroir.uc.pro.1.body": "Transformez votre carte des vins en expérience de dégustation ludique. Les clients scannent un QR code et explorent le terroir de chaque bouteille, seuls ou en équipe. Plus d'engagement, plus de bouteilles vendues.",
  "terroir.uc.pro.2.title": "Je suis caviste",
  "terroir.uc.pro.2.body": "Ajoutez un assistant 'geoVINUM app' sur votre site ou en boutique. Vos clients posent leurs questions, reçoivent des recommandations, construisent leur cave — sans avoir un sommelier 24h/24.",
  "terroir.uc.pro.3.title": "J'anime un club d'œnologie",
  "terroir.uc.pro.3.body": "Animez vos rencontres mensuelles avec des jeux multijoueurs, des dégustations à l'aveugle, des flashcards thématiques. Vos membres reviennent parce qu'ils apprennent en s'amusant.",
  "terroir.uc.pro.4.title": "Je forme au vin",
  "terroir.uc.pro.4.body": "Écoles et académies du vin : construisez des modules, quiz et sessions de dégustation à l'aveugle. Suivez la progression, gamifiez vos cours, transformez vos salles en expériences.",
  "terroir.uc.pro.5.title": "Je suis producteur",
  "terroir.uc.pro.5.body": "Intégrez geoVINUM app sur la page de votre domaine. Vos visiteurs posent toutes leurs questions sur votre appellation, vos cépages, votre histoire — même quand vous ne pouvez pas être au téléphone.",
  "terroir.audiences.title": "Une seule app, deux usages",
  "terroir.audiences.amateur.title": "Amateur",
  "terroir.audiences.amateur.body": "Gratuit à essayer · PWA · disponible en 4 langues.",
  "terroir.audiences.pro.title": "Professionnel",
  "terroir.audiences.pro.body": "Bars à vin, cavistes, clubs, écoles, producteurs. Expérience brandée, gamifiée, scalable.",
  "terroir.audiences.pro.cta": "Parler à notre équipe",
  "terroir.how.title": "Comment ça marche",
  "terroir.how.1.title": "Ouvrir l'app",
  "terroir.how.1.body": "Pas d'installation. Une app web — même expérience sur tous les appareils.",
  "terroir.how.2.title": "Demander, scanner, jouer",
  "terroir.how.2.body": "Tapez une question, photographiez une étiquette, lancez un jeu de flashcards, défiez un ami.",
  "terroir.how.3.title": "Devenez plus fort chaque jour",
  "terroir.how.3.body": "Micro-cours, dégustations à l'aveugle, parcours. Votre culture du vin s'accumule.",
  "terroir.cta.title": "Prêt à parler au terroir ?",
  "terroir.cta.sub": "Gratuit à essayer. Sans carte bancaire. Sur tous les appareils.",
  "terroir.cta.web": "Ouvrir la web app",
  "terroir.cta.ios": "Accéder à l'app",

  // geoVINUM app — Formulaire Demo Pro
  "pro_demo.eyebrow": "Demo Pro · 30 secondes",
  "pro_demo.title": "Réservez une démo Pro sur-mesure",
  "pro_demo.subtitle": "Bar à vin, caviste, club, école, producteur — on intègre geoVINUM app dans votre établissement avec votre branding, votre carte, votre parcours client. Au coût par visiteur, sans engagement.",
  "pro_demo.bullet_1": "Expérience brandée sur votre site existant ou via QR code",
  "pro_demo.bullet_2": "Flashcards gamifiées, dégustations à l'aveugle, quiz multijoueur",
  "pro_demo.bullet_3": "Capture de leads + dashboard analytics inclus",
  "pro_demo.form.eyebrow": "Parlez-nous de vous",
  "pro_demo.form.title": "On revient sous 24h",
  "pro_demo.field.name": "Votre nom",
  "pro_demo.field.email": "Email pro",
  "pro_demo.field.business": "Établissement",
  "pro_demo.field.visitors": "Visiteurs / mois",
  "pro_demo.business.wine_bar": "Bar à vin",
  "pro_demo.business.merchant": "Caviste",
  "pro_demo.business.club": "Club d'œnologie",
  "pro_demo.business.school": "École / Académie",
  "pro_demo.business.producer": "Producteur",
  "pro_demo.business.other": "Autre",
  "pro_demo.consent_text": "J'accepte d'être contacté(e) par l'équipe geoVINUM au sujet de ma demande de démo. Mes données sont conservées 24 mois et je peux demander leur suppression à tout moment (RGPD).",
  "pro_demo.submit": "Demander ma démo",
  "pro_demo.reply_time": "Réponse sous 24h ouvrées",
  "pro_demo.error.consent": "Merci d'accepter le consentement pour continuer.",
  "pro_demo.success.title": "Demande reçue !",
  "pro_demo.success.body": "Vérifiez votre boîte mail — nous venons de vous envoyer une confirmation. Pour accélérer les choses, réservez directement un créneau de 20 min :",
  "pro_demo.success.calendar": "Réserver 20 min de call",

  // Home — sections produits simplifiées
  "home.s_terroir.eyebrow": "geoVINUM app · L'app IA grand public",
  "home.s_terroir.title": "Parlez au terroir.",
  "home.s_terroir.title_em": "partout, toutes langues.",
  "home.s_terroir.sub": "Amateurs et pros utilisent la même app IA : questions sur les appellations, cépages, producteurs, construction d'un parcours œnotouristique, photo d'étiquette, quiz multijoueur, flashcards quotidiennes.",
  "home.s_academy.eyebrow": "Academy · Netflix du terroir",
  "home.s_academy.title": "Le vin se",
  "home.s_academy.title_em": "regarde aussi.",
  "home.s_academy.sub": "100+ masterclasses HD sur chaque région, cépage et tradition. Streaming sur tous les appareils. 7 jours gratuits, puis 9€/mois.",
  "home.s_shop.eyebrow": "Shop · Print on demand",
  "home.s_shop.title": "Des cartes des vins",
  "home.s_shop.title_em": "à accrocher au mur.",
  "home.s_shop.sub": "Posters premium, encadrés, toiles — toutes les grandes appellations, imprimées et expédiées d'Europe. L'atlas du collectionneur.",
  "home.s_iframe.eyebrow": "Iframe · Embed B2B",
  "home.s_iframe.title": "La bonne carte sur",
  "home.s_iframe.title_em": "chaque fiche produit.",
  "home.s_iframe.sub": "Sites vin, marketplaces, importateurs : reliez un seul champ appellation et nos cartes interactives s'affichent automatiquement sur chaque fiche. De 6 000 à illimité, paiement à la requête dès 0,01€.",
  "home.cta.discover_terroir": "Découvrir geoVINUM app",
  "home.cta.open_webapp": "Ouvrir la web app",
  "home.cta.talk_to_sales": "Parler à l'équipe",
  "home.closing.title": "Un écosystème, une promesse.",
  "home.closing.sub": "Gratuit à essayer, disponible en 4 langues, sur tous les appareils — sans installation.",
};

const es = {
  "nav.maps": "Mapas",
  "nav.app": "App",
  "nav.interactive": "Interactivo",
  "nav.experience": "Experience",
  "nav.pro.overview": "Para profesionales — resumen",
  "nav.pro_long": "Para profesionales",
  "nav.explorer.all": "Ver todo",
  "nav.explorer.appellations": "Regiones · Viñedos · Denominaciones",
  "nav.explorer.cepages": "Variedades de uva",
  "nav.explorer.producers": "Productores",
  "nav.explorer.articles": "Artículos",
  "nav.explorer.guides_divider": "Guías y referencias",
  "nav.explorer.glossaire": "Glosario del vino (506 términos)",
  "nav.explorer.cartographie": "Cartografía vitivinícola",
  "nav.explorer.geographie": "Geografía del vino",
  "nav.explorer.appellations_pil": "Denominaciones vitivinícolas",
  "nav.explorer.terroirs": "Terroirs vitivinícolas",
  "nav.play": "Jugar",
  "cta.explore_maps": "Explorar mapas",
  "cta.start_learning": "Empezar",
  "cta.start_learning_2min": "Aprende en 2 min",
  "cta.start_playing": "Jugar",
  "cta.discover_pro": "Descubrir geoVINUM Pro",
  "cta.buy_now": "Comprar",
  "cta.add_cart": "Añadir al carrito",

  "home.hero.eyebrow": "Mapas del vino · por geoVINUM",
  "home.hero.h1a": "Entiende el vino",
  "home.hero.h1b": "visualmente.",
  "home.hero.tagline": "Mapas. Aprendizaje. Juego.",
  "home.hero.line": "El vino es complejo. Lo hacemos visual.",
  "home.hero.support": "Explora regiones, entiende uvas y aprende en minutos.",
  "home.hero.rating": "4,8/5 por los amantes del vino",
  "home.hero.secure_payment": "Pago seguro",
  "home.hero.shipping": "Envío mundial",

  "story.a": "El vino es a menudo complicado.",
  "story.b": "Regiones, uvas, terruños…",
  "story.c": "geoVINUM lo hace simple.",

  "trust.premium_quality": "Calidad premium",
  "trust.made_to_learn": "Hecho para aprender",
  "trust.loved_by": "Amado por los amantes del vino",
  "trust.worldwide_shipping": "Envío mundial",

  "footer.brand_line": "El vino se vuelve claro cuando se ve. Mapas, lecciones y juego — para curiosos y profesionales.",
  "footer.explore": "Explorar",
  "footer.contact": "Contacto",
  "footer.legal": "Legal",
  "footer.cookie_settings": "Cookies",
  "footer.alcohol_disclaimer": "⚠️ El abuso del alcohol es peligroso para la salud. Consuma con moderación.",

  "pro.hero.h1": "geoVINUM para",
  "pro.hero.h1_italic": "profesionales",

  "journal.h1_a": "Aprender. Explorar.",
  "journal.h1_b": "Entender",
  "journal.empty_lang": "Aún no hay artículos en este idioma.",
  "journal.empty_lang_text": "Estamos traduciendo más contenido. Cambia de idioma o vuelve pronto.",

  // Navigation + dropdowns
  "nav.products": "Productos",
  "nav.resources": "Recursos",
  "nav.explore": "Explorar",
  "nav.academy": "Academy",
  "nav.maps_short": "Mapas",
  "nav.shop": "Tienda",
  "nav.business": "Business",
  "nav.terroir_ai": "geoVINUM app",
  "nav.blog": "Blog",
  "nav.resources.articles": "Blog · Artículos",
  "nav.resources.atlas": "Atlas de denominaciones",
  "nav.resources.cepages": "Variedades",
  "nav.resources.ebooks": "Ebooks gratuitos",
  "cmd.kind.atlas_appellation": "Atlas · Denominación",
  "cmd.kind.atlas_cepage": "Atlas · Variedad",
  "cmd.kind.blog": "Artículo",
  "cmd.kind.academy": "Serie Academy",
  "cmd.kind.shop": "Tienda",
  "nav.open_app": "Abrir app",
  "nav.download_app": "Abrir app",
  "nav.atlas_mini": "geoVINUM Atlas",
  "nav.atlas_magnum": "geoVINUM Atlas",
  "nav.atlas_pro": "geoVINUM Atlas",
  "nav.product.academy_short": "Academy",
  "nav.product.coach_short": "Coach",
  "nav.product.iframe_short": "Iframe",
  "nav.product.cellar_short": "Portfolio",
  "nav.product.tasting_short": "Tasting",
  "business.h1_a": "Experiencias de vino para",
  "business.h1_b": "tu negocio.",
  "business.sub": "Integra mapas interactivos, implica clientes y crea catas memorables.",
  "business.cta_title": "Hablemos",
  "business.cta_text": "Cuéntanos sobre tu tienda, escuela o bodega — diseñamos la colaboración a medida.",

  // ----- Business page v2 (12 sections) -----
  "biz.seo.title": "geoVINUM para profesionales — Plataforma de experiencias vino",

  "biz.hero.title_a": "Convierte tu terroir en un activo comercial, de marketing y",
  "biz.hero.title_b": "educativo.",
  "biz.hero.sub": "geoVINUM ayuda a bodegas, regiones vinícolas, importadores y formadores a poner en valor los terroirs mediante mapas interactivos, storytelling y experiencias inmersivas.",
  "biz.hero.cta_primary": "Solicitar demo",
  "biz.hero.cta_secondary": "Ver casos de uso",

  "biz.why.eyebrow": "Por qué geoVINUM",
  "biz.why.title": "Por qué los profesionales eligen geoVINUM",
  "biz.why.bullet.terroirs": "Explica los terroirs visualmente",
  "biz.why.bullet.storytelling": "Mejora el storytelling del vino",
  "biz.why.bullet.engagement": "Aumenta el engagement en tu web",
  "biz.why.bullet.education": "Apoya la formación vinícola",
  "biz.why.bullet.differentiate": "Diferencia tu marca",

  "biz.who.eyebrow": "Para quién",
  "biz.who.title": "Pensado para el sector vinícola",
  "biz.who.wineries.title": "Bodegas",
  "biz.who.wineries.desc": "Pon en valor tu terroir, refuerza tu relato de marca y crea experiencias vino atractivas.",
  "biz.who.importers.title": "Importadores y distribuidores",
  "biz.who.importers.desc": "Ayuda a tus equipos a entender las regiones y comunicar mejor los vinos.",
  "biz.who.regions.title": "Regiones e interprofesiones",
  "biz.who.regions.desc": "Promociona denominaciones y territorios con experiencias interactivas y educativas.",
  "biz.who.education.title": "Educación y formación",
  "biz.who.education.desc": "Da vida a la geografía del vino y a los terroirs para estudiantes y profesionales.",

  "biz.problem.eyebrow": "El problema",
  "biz.problem.title": "Por qué la mayoría del contenido vino no engancha",
  "biz.problem.p1": "La comunicación del vino se basa a menudo en PDFs estáticos, fichas técnicas y descripciones largas.",
  "biz.problem.p2": "Los clientes tienen dificultades para entender terroirs, regiones y qué hace único a un vino.",
  "biz.problem.p3": "geoVINUM transforma esa información compleja en experiencias visuales e interactivas.",

  "biz.benefits.eyebrow": "Beneficios",
  "biz.benefits.title": "Lo que geoVINUM te ayuda a conseguir",
  "biz.benefits.storytelling.title": "Mejora el storytelling del vino",
  "biz.benefits.storytelling.desc": "Ayuda a tus clientes a entender el origen y la identidad de tus vinos.",
  "biz.benefits.engagement.title": "Aumenta el engagement",
  "biz.benefits.engagement.desc": "Los mapas interactivos crean experiencias memorables y retienen más tiempo a los visitantes.",
  "biz.benefits.sales.title": "Apoya a los equipos comerciales",
  "biz.benefits.sales.desc": "Ofrece herramientas visuales que hacen las explicaciones más sencillas e impactantes.",
  "biz.benefits.education.title": "Potencia la formación vinícola",
  "biz.benefits.education.desc": "Convierte regiones complejas en experiencias de aprendizaje fáciles de entender.",

  "biz.solutions.eyebrow": "Soluciones",
  "biz.solutions.title": "Nuestra plataforma de experiencias vino",
  "biz.solutions.maps.desc": "Mapas vínicos interactivos integrables en sitios web, landing pages y plataformas educativas.",
  "biz.solutions.maps.use.winery_websites": "Webs de bodegas",
  "biz.solutions.maps.use.wine_tourism": "Enoturismo",
  "biz.solutions.maps.use.edu_content": "Contenido educativo",
  "biz.solutions.maps.use.regional_promo": "Promoción regional",
  "biz.solutions.portfolio.desc": "Experiencias digitales que conectan a tus clientes con la historia detrás de cada botella.",
  "biz.solutions.portfolio.use.winery_visits": "Visitas a bodega",
  "biz.solutions.portfolio.use.qr_code": "Experiencias con QR",
  "biz.solutions.portfolio.use.storytelling": "Storytelling de producto",
  "biz.solutions.portfolio.use.brand_engagement": "Engagement de marca",
  "biz.solutions.tasting.desc": "Catas interactivas diseñadas para implicar a visitantes y educar a consumidores.",
  "biz.solutions.tasting.use.events": "Eventos",
  "biz.solutions.tasting.use.wine_fairs": "Ferias del vino",
  "biz.solutions.tasting.use.cellar_doors": "Sala de catas",
  "biz.solutions.tasting.use.hospitality": "Hostelería",

  "biz.usecases.eyebrow": "Casos de uso",
  "biz.usecases.title": "Cómo usan geoVINUM los profesionales",
  "biz.usecases.tradefair.title": "Antes de una feria",
  "biz.usecases.tradefair.desc": "Crea soportes visuales atractivos para presentar tus vinos y regiones.",
  "biz.usecases.website.title": "En la web de una bodega",
  "biz.usecases.website.desc": "Añade mapas de terroirs interactivos para mejorar el engagement.",
  "biz.usecases.export.title": "Para ventas de exportación",
  "biz.usecases.export.desc": "Ayuda a los compradores internacionales a entender tus regiones.",
  "biz.usecases.tourism.title": "Para enoturismo",
  "biz.usecases.tourism.desc": "Crea experiencias memorables para los visitantes.",
  "biz.usecases.training.title": "Para formar equipos",
  "biz.usecases.training.desc": "Enseña geografía del vino de forma más eficaz.",

  "biz.examples.eyebrow": "Ejemplos",
  "biz.examples.title": "geoVINUM en acción",
  "biz.examples.map": "Mapa vínico interactivo",
  "biz.examples.domain": "Integrado en tu dominio",
  "biz.examples.mobile": "Experiencia móvil y tablet",
  "biz.examples.sheet": "Ficha de vino enriquecida",

  "biz.position.eyebrow": "Por qué geoVINUM",
  "biz.position.title": "Mucho más que mapas",
  "biz.position.p1": "geoVINUM combina geografía del vino, storytelling de terroir y experiencias digitales en una sola plataforma.",
  "biz.position.p2": "A diferencia de las herramientas genéricas de cartografía o presentación, geoVINUM está pensado específicamente para el sector vinícola.",

  "biz.social.eyebrow": "Confían en nosotros",
  "biz.social.title": "Avalado por profesionales del vino",
  "biz.social.stat.regions.value": "30+",
  "biz.social.stat.regions.label": "Regiones vinícolas",
  "biz.social.stat.wineries.value": "200+",
  "biz.social.stat.wineries.label": "Bodegas",
  "biz.social.stat.maps.value": "500+",
  "biz.social.stat.maps.label": "Mapas interactivos",
  "biz.social.stat.countries.value": "25+",
  "biz.social.stat.countries.label": "Países",

  "biz.faq.eyebrow": "FAQ",
  "biz.faq.title": "Preguntas frecuentes",
  "biz.faq.orgs.q": "¿Qué tipo de organizaciones usan geoVINUM?",
  "biz.faq.orgs.a": "Bodegas, regiones e interprofesiones, importadores y distribuidores, escuelas y centros de formación, profesionales de enoturismo y hostelería.",
  "biz.faq.embed.q": "¿Se pueden integrar los mapas en nuestro sitio?",
  "biz.faq.embed.a": "Sí — geoVINUM Maps se integra en cualquier web o landing page mediante un iframe sencillo, totalmente responsive y personalizable.",
  "biz.faq.languages.q": "¿Está geoVINUM disponible en varios idiomas?",
  "biz.faq.languages.a": "Sí — geoVINUM está disponible en inglés, francés, español y portugués, con más idiomas en la hoja de ruta.",
  "biz.faq.tourism.q": "¿Puede geoVINUM apoyar proyectos de enoturismo?",
  "biz.faq.tourism.a": "Absolutamente. Muchas regiones usan geoVINUM para enriquecer el enoturismo con mapas interactivos, storytelling de terroir y experiencias de sala de catas.",
  "biz.faq.education.q": "¿Se puede usar geoVINUM para formación?",
  "biz.faq.education.a": "Sí — escuelas, programas de sumillería y equipos de formación usan geoVINUM Academy y Maps para enseñar geografía del vino con eficacia.",
  "biz.faq.custom.q": "¿Ofrecéis proyectos a medida?",
  "biz.faq.custom.a": "Sí — diseñamos mapas a medida, experiencias con marca propia e integraciones específicas. Contáctanos para hablar de tu proyecto.",

  "biz.final.title": "¿Listo para mostrar tu terroir de otra forma?",
  "biz.final.body": "Descubre cómo geoVINUM puede ayudar a tu organización a crear experiencias vino atractivas y comunicar mejor el valor de sus vinos.",
  "biz.final.cta_primary": "Solicitar demo",
  "biz.final.cta_secondary": "Contáctanos",

  "biz.shared.use_cases_label": "Casos de uso",
  "biz.shared.coming_soon": "Próximamente",
  "biz.shared.view_demo": "Ver demo",
  "biz.shared.join_waitlist": "Lista de espera",
  "home.hero.h1_b": "Descubre el vino por el terroir.",
  "home.hero.h1_main": "",
  "home.hero.sub_v2": "Explora regiones, aprende cultura del vino y vive terroirs con mapas, formación y herramientas interactivas.",

  // Status produits ES (compléments)
  "product.starting_from": "Desde",
  "product.trial_7d": "7 días de prueba gratis incluidos",
  "product.cta.see_versions": "Ver versiones",
  "product.versions": "Versiones disponibles",
  "product.pricing": "Precios",
  "product.price.free": "Gratis",
  "product.price.premium": "Premium",
  "product.price.subscription": "Suscripción Pro",
  "product.atlas.tagline": "Explora las regiones vinícolas y los terroirs del mundo.",
  "product.atlas.desc": "geoVINUM Atlas ayuda a los amantes del vino a descubrir regiones, denominaciones, uvas y productores con mapas interactivos y contenido educativo.",
  "product.atlas.mini.title": "geoVINUM Atlas",
  "product.atlas.mini.desc": "Perfecto para aficionados al vino, estudiantes y curiosos que quieren acceder rápido a regiones, denominaciones y uvas clave.",
  "product.atlas.magnum.title": "geoVINUM Atlas",
  "product.atlas.magnum.desc": "Diseñado para aficionados y estudiantes que buscan una comprensión más completa de la geografía del vino, los terroirs y las denominaciones.",
  "product.atlas.pro.title": "geoVINUM Atlas",
  "product.atlas.pro.desc": "Solución completa para profesionales del vino que necesitan datos fiables, herramientas cartográficas avanzadas y recursos pedagógicos premium.",

  // ----- Explore "Which Atlas" (ES) -----
  "explore.which.eyebrow": "Elige tu Atlas",
  "explore.which.title": "¿Qué Atlas es el adecuado para ti?",
  "explore.mini.tagline": "El Google Maps del vino",
  "explore.mini.desc": "Explora las regiones vinícolas, denominaciones y bodegas de todo el mundo.",
  "explore.mini.audience": "Aficionados · Viajeros · Principiantes · Curiosos del vino",
  "explore.mini.cta": "Explorar geoVINUM Atlas",
  "explore.magnum.tagline": "Tu mapa de viñedo personalizado",
  "explore.magnum.desc": "Muestra una o varias denominaciones con referencias catastrales y mapas de viñedo a medida.",
  "explore.magnum.audience": "Bodegas · Fincas · Enoturismo · Organizaciones regionales",
  "explore.magnum.cta": "Descubrir geoVINUM Atlas",
  "explore.pro.tagline": "Inteligencia avanzada del terruño",
  "explore.pro.desc": "Accede a datos profesionales de terruño, visualización 3D y análisis avanzado de regiones vinícolas.",
  "explore.pro.audience": "Consultores · Importadores · Formadores · Expertos en vino · Interprofesiones",
  "explore.pro.cta": "Descubrir geoVINUM Atlas",
  "explore.cta.learn_more": "Saber más",
  "explore.cta.subscribe": "Suscribirse",
  "explore.cta.download": "Descargar",
  "explore.trial_days": "{n} días gratis",

  // ----- Iframe pricing (ES) -----
  "iframeP.eyebrow": "Elige tu plan",
  "iframeP.title": "Del sitio pequeño al enterprise — elige tu volumen.",
  "iframeP.cycle.monthly": "Mensual",
  "iframeP.cycle.yearly": "Anual",
  "iframeP.popular": "Más popular",
  "iframeP.ht_per": "sin IVA /",
  "iframeP.year": "año",
  "iframeP.month": "mes",
  "iframeP.save_with_yearly": "Ahorra con el anual ({amount} / año)",
  "iframeP.unavailable": "No disponible",
  "iframeP.subscribe": "Suscribirse",
  "iframeP.request": "petición",
  "iframeP.requests_per_year": "peticiones / año",
  "iframeP.contact_badge": "A medida",
  "iframeP.from": "Desde",
  "iframeP.custom_quote": "Presupuesto personalizado",
  "iframeP.talk_to_sales": "Hablar con ventas",
  "iframeP.footer_note": "Pago seguro con Stripe · IVA aplicado automáticamente · Cancela cuando quieras desde tu cuenta.",

  // ----- Portfolio page (ES) -----
  "portfolioP.hero.eyebrow": "Portfolio · Pasaporte Digital",
  "portfolioP.hero.title": "El pasaporte digital inteligente de cada producto.",
  "portfolioP.hero.subtitle": "Productores, vinotecas, distribuidores, sumilleres y formadores centralizan, enriquecen y comparten una ficha dinámica adaptada automáticamente a cada audiencia.",
  "portfolioP.hero.cta_beta": "Ser Beta tester",
  "portfolioP.hero.cta_contact": "Hablar con nosotros",
  "portfolioP.status.beta": "Próximamente · Beta",
  "portfolioP.status.note": "Accede en primicia uniéndote al programa beta",

  "portfolioP.truth.eyebrow": "Una única fuente de verdad",
  "portfolioP.truth.title": "Un producto. Un pasaporte inteligente. Infinitos usos.",
  "portfolioP.truth.before": "En lugar de gestionar…",
  "portfolioP.truth.before.files_pdf": "Múltiples fichas técnicas en PDF",
  "portfolioP.truth.before.sales_decks": "Múltiples presentaciones comerciales",
  "portfolioP.truth.before.translations": "Múltiples versiones traducidas",
  "portfolioP.truth.before.training": "Múltiples materiales de formación",
  "portfolioP.truth.before.pitch": "Múltiples argumentos de venta",
  "portfolioP.truth.after": "Con Portfolio",
  "portfolioP.truth.after_desc": "Toda la información del producto en una sola plataforma. Un espacio dinámico genera la versión más relevante según el destinatario y el contexto.",
  "portfolioP.truth.after_note": "Edita una vez, despliega en todas partes — botella, feria, carta, exportación, formación.",

  "portfolioP.ai.eyebrow": "Pre-relleno inteligente",
  "portfolioP.ai.title": "Un pasaporte que se escribe solo.",
  "portfolioP.ai.subtitle": "La ficha nunca empieza desde cero: se enriquece automáticamente con los datos GeoVINUM y la inteligencia artificial.",
  "portfolioP.ai.geovinum_title": "Datos GeoVINUM",
  "portfolioP.ai.geovinum.region": "Región",
  "portfolioP.ai.geovinum.appellation": "Denominación",
  "portfolioP.ai.geovinum.grapes": "Variedades",
  "portfolioP.ai.geovinum.climate": "Clima",
  "portfolioP.ai.geovinum.soils": "Suelos",
  "portfolioP.ai.geovinum.altitude": "Altitud",
  "portfolioP.ai.geovinum.plots": "Parcelas",
  "portfolioP.ai.geovinum.producers": "Productores",
  "portfolioP.ai.geovinum.history": "Datos históricos",
  "portfolioP.ai.geovinum.geo": "Datos geográficos",
  "portfolioP.ai.ai_title": "Generaciones IA",
  "portfolioP.ai.ai.summary": "Resumen del producto",
  "portfolioP.ai.ai.storytelling": "Storytelling",
  "portfolioP.ai.ai.sales": "Presentación comercial",
  "portfolioP.ai.ai.retailer": "Argumento para vinotecas",
  "portfolioP.ai.ai.export": "Argumento para exportación",
  "portfolioP.ai.ai.pairing": "Maridajes",
  "portfolioP.ai.ai.profiles": "Perfiles de consumidor",
  "portfolioP.ai.ai.training": "Contenidos pedagógicos",
  "portfolioP.ai.ai.i18n": "Traducciones multilingües",
  "portfolioP.ai.editable": "El profesional siempre puede modificar o completar el contenido generado.",

  "portfolioP.modules.eyebrow": "Totalmente modular",
  "portfolioP.modules.title": "Activa o desactiva cada módulo a la carta.",
  "portfolioP.module.origin.title": "Origen y Terruño",
  "portfolioP.module.origin.desc": "Cartografía interactiva, parcelas, geología, clima, altitud, entorno.",
  "portfolioP.module.story.title": "Historia y Storytelling",
  "portfolioP.module.story.desc": "Historia del dominio, filosofía, know-how, presentación del producto.",
  "portfolioP.module.tech.title": "Técnica",
  "portfolioP.module.tech.desc": "Producción, crianza, certificaciones, análisis, fichas reglamentarias.",
  "portfolioP.module.tasting.title": "Cata",
  "portfolioP.module.tasting.desc": "Notas, perfil aromático, radar sensorial, notas personales.",
  "portfolioP.module.training.title": "Formación",
  "portfolioP.module.training.desc": "Formato WSET, fichas pedagógicas, quizzes, contenido formativo.",
  "portfolioP.module.pairing.title": "Maridajes",
  "portfolioP.module.pairing.desc": "Maridajes, sugerencias de servicio, ocasiones de consumo.",
  "portfolioP.module.media.title": "Medios",
  "portfolioP.module.media.desc": "Fotos, vídeos, entrevistas, contenidos de marketing.",
  "portfolioP.module.trade.title": "Comercial",
  "portfolioP.module.trade.desc": "Precios, presentaciones, disponibilidad, mercados de exportación.",

  "portfolioP.position.eyebrow": "Valorización de mercado",
  "portfolioP.position.title": "Entiende dónde se sitúa cada producto.",
  "portfolioP.position.subtitle": "Portfolio no se limita a describir. Con los datos GeoVINUM, los datos de mercado y la IA, surge:",
  "portfolioP.position.position.title": "Posicionamiento de mercado",
  "portfolioP.position.position.desc": "Entrada de gama, premium, super-premium, icono.",
  "portfolioP.position.competitive.title": "Análisis competitivo",
  "portfolioP.position.competitive.desc": "Productos similares, referencias competidoras, alternativas en la misma categoría.",
  "portfolioP.position.value.title": "Valorización",
  "portfolioP.position.value.desc": "Estimación de valor, coherencia precio / mercado, evolución del posicionamiento, percepción de los catadores.",
  "portfolioP.position.differentiation.title": "Diferenciación",
  "portfolioP.position.differentiation.desc": "Lo que distingue al producto — sus fortalezas comerciales y argumentos únicos de venta.",
  "portfolioP.position.tier.entry": "Entrada de gama",
  "portfolioP.position.tier.premium": "Premium",
  "portfolioP.position.tier.super_premium": "Super-premium",
  "portfolioP.position.tier.icon": "Icono",

  "portfolioP.audience.eyebrow": "Adaptado a cada destinatario",
  "portfolioP.audience.title": "Un Portfolio. Cinco caras.",
  "portfolioP.audience.consumer.title": "Consumidor",
  "portfolioP.audience.consumer.list": "Historia · Terruño · Cata · Maridajes · Vídeos",
  "portfolioP.audience.retailer.title": "Vinoteca",
  "portfolioP.audience.retailer.list": "Argumento comercial · Perfil sensorial · Productos similares · Posicionamiento de precio",
  "portfolioP.audience.importer.title": "Importador",
  "portfolioP.audience.importer.list": "Certificaciones · Volúmenes disponibles · Información de exportación",
  "portfolioP.audience.sommelier.title": "Sumiller",
  "portfolioP.audience.sommelier.list": "Cata detallada · Maridajes · Temperatura de servicio",
  "portfolioP.audience.educator.title": "Formador",
  "portfolioP.audience.educator.list": "Contenidos pedagógicos · Mapas · Quizzes · Recursos educativos",

  "portfolioP.diffusion.eyebrow": "Multicanal",
  "portfolioP.diffusion.title": "Distribuido allí donde viaja tu producto.",
  "portfolioP.diffusion.subtitle": "Cada canal puede mostrar módulos diferentes — la información adecuada en el contexto adecuado.",
  "portfolioP.diffusion.qr_bottle": "QR en la botella",
  "portfolioP.diffusion.qr_fair": "QR en feria profesional",
  "portfolioP.diffusion.wine_list": "Carta de vinos digital",
  "portfolioP.diffusion.catalog": "Catálogo comercial",
  "portfolioP.diffusion.export": "Ficha de exportación",
  "portfolioP.diffusion.training": "Material formativo",
  "portfolioP.diffusion.private_link": "Enlace privado o público",

  "portfolioP.promise.eyebrow": "La promesa",
  "portfolioP.promise.title": "Portfolio convierte una simple ficha de producto en un activo digital inteligente.",
  "portfolioP.promise.body": "Pre-rellenado con datos GeoVINUM, enriquecido por IA, personalizable hasta el infinito y adaptable a cada audiencia — Portfolio te ayuda a comprender, valorizar, presentar, formar y vender un producto mejor.",
  "portfolioP.promise.tagline": "Una sola ficha. Infinitos usos.",
  "portfolioP.promise.cta": "Ser Beta tester",
  "portfolioP.promise.cta_contact": "Contactar",

  // ----- Portfolio: Cómo funciona (ES) -----
  "portfolioP.how.eyebrow": "Cómo funciona",
  "portfolioP.how.title": "Tres pasos. Luego se adapta a cada audiencia.",
  "portfolioP.how.scan.title": "Identifica el producto",
  "portfolioP.how.scan.desc": "Elige una denominación, un vino, una añada. GeoVINUM recupera los datos geográficos e históricos al instante.",
  "portfolioP.how.ai.title": "La IA pre-rellena",
  "portfolioP.how.ai.desc": "Storytelling, argumentos, maridajes, cata, traducciones — todo generado. Tú mantienes el control y editas a voluntad.",
  "portfolioP.how.share.title": "Distribuye en todas partes",
  "portfolioP.how.share.desc": "QR en botella, carta de vinos, ficha de exportación, formación. Cada canal muestra los módulos adecuados.",

  // ----- Portfolio: Demo (ES) -----
  "portfolioP.demo.eyebrow": "Vista previa",
  "portfolioP.demo.title": "Un producto. Cuatro lecturas. Cero duplicación.",
  "portfolioP.demo.subtitle": "Mira cómo la misma ficha se adapta automáticamente al destinatario.",
  "portfolioP.demo.lens": "Vista audiencia",
  "portfolioP.demo.note": "Vista estática. Las fichas reales son totalmente interactivas — editar, reordenar, cambiar idioma, exportar PDF.",
  "portfolioP.demo.consumer.story": "Nacido en las gravas cálidas de Margaux, esta añada cuenta un largo verano seco. Aterciopelado en nariz, refinado en boca — un vino que recompensa la paciencia.",
  "portfolioP.demo.retailer.story": "1er Grand Cru icónico con fuerte demanda secundaria. PVP > 800€. Excelente relación precio/puntuación vs Latour 2018.",
  "portfolioP.demo.sommelier.story": "Nariz sutil de violeta y cedro. Taninos firmes, acidez alta. Decantar 2h. Servicio: 17°C. Guarda: 2028–2050.",
  "portfolioP.demo.educator.story": "Ensamblaje Margen Izquierda dominado por Cabernet — caso de estudio ideal para terruño grava, estilo Médoc y clasificación de 1855.",
  "portfolioP.demo.section.story": "Historia",
  "portfolioP.demo.section.story.value": "Legado lacrado desde 1855",
  "portfolioP.demo.section.terroir": "Terruño",
  "portfolioP.demo.section.terroir.value": "Grava · 12m altitud",
  "portfolioP.demo.section.tasting": "Cata",
  "portfolioP.demo.section.tasting.value": "Casis · Cedro · Grafito",
  "portfolioP.demo.section.pairing": "Maridaje",
  "portfolioP.demo.section.pairing.value": "Cordero · Queso curado",
  "portfolioP.demo.section.sales": "Ángulo comercial",
  "portfolioP.demo.section.sales.value": "Icono de inversión",
  "portfolioP.demo.section.sensory": "Perfil sensorial",
  "portfolioP.demo.section.sensory.value": "Cuerpo entero · Tanino 7/10",
  "portfolioP.demo.section.similar": "Comparable a",
  "portfolioP.demo.section.similar.value": "Latour · Mouton 2018",
  "portfolioP.demo.section.price": "Posicionamiento",
  "portfolioP.demo.section.price.value": "850€ · Super-premium",
  "portfolioP.demo.section.tasting_detail": "Cata detallada",
  "portfolioP.demo.section.tasting_detail.value": "16,2/20 · Decantar 2h",
  "portfolioP.demo.section.service": "Servicio",
  "portfolioP.demo.section.service.value": "17°C · Copa Burdeos",
  "portfolioP.demo.section.wset": "Ref WSET",
  "portfolioP.demo.section.wset.value": "Caso L3",
  "portfolioP.demo.section.maps": "Mapas",
  "portfolioP.demo.section.maps.value": "Médoc · Vista parcelaria",
  "portfolioP.demo.section.quiz": "Quiz",
  "portfolioP.demo.section.quiz.value": "12 preguntas",
  "portfolioP.demo.section.resources": "Recursos",
  "portfolioP.demo.section.resources.value": "PDF · Diapos · Fichas",

  "product.pos.b2c": "Para aficionados al vino",
  "product.pos.b2b": "Para profesionales del vino",
  "product.pos.education": "Para educación y formación",
  "product.ideal_for": "Ideal para",
  "product.key_benefits": "Beneficios clave",

  "audience.beginners": "Principiantes",
  "audience.wine_lovers": "Aficionados al vino",
  "audience.travellers": "Viajeros",
  "audience.casual_learners": "Curiosos",
  "audience.wset_students": "Estudiantes WSET",
  "audience.sommeliers": "Sumilleres",
  "audience.wine_pros": "Profesionales del vino",
  "audience.advanced_enthusiasts": "Aficionados avanzados",
  "audience.importers": "Importadores",
  "audience.distributors": "Distribuidores",
  "audience.wine_merchants": "Vinotecas",
  "audience.wine_educators": "Formadores",
  "audience.consultants": "Consultores",
  "audience.wine_students": "Estudiantes de vino",
  "audience.hospitality_pros": "Pros de hostelería",
  "audience.wine_schools": "Escuelas de vino",
  "audience.cert_students": "Estudiantes de certificación",
  "audience.sales_teams": "Equipos comerciales",
  "audience.educators": "Educadores",
  "audience.tourism": "Proyectos de enoturismo",
  "audience.wineries": "Bodegas",
  "audience.wine_regions": "Regiones vinícolas",
  "audience.tourism_orgs": "Organizaciones de turismo",
  "audience.wine_brands": "Marcas de vino",
  "audience.wine_tourism_ops": "Operadores de enoturismo",
  "audience.wine_bars": "Bares de vinos",
  "audience.events": "Eventos",
  "audience.hospitality": "Hostelería",

  "benefit.interactive_maps": "Mapas vínicos interactivos",
  "benefit.major_regions": "Principales regiones del mundo",
  "benefit.essential_terroir": "Conocimiento esencial de terroirs",
  "benefit.mobile_friendly": "Acceso optimizado para móvil",
  "benefit.detailed_maps": "Mapas detallados",
  "benefit.expanded_appellations": "Cobertura ampliada de denominaciones",
  "benefit.in_depth_terroir": "Información de terroir en profundidad",
  "benefit.advanced_edu": "Contenido educativo avanzado",
  "benefit.full_premium_maps": "Acceso completo a mapas premium",
  "benefit.pro_region_db": "Base de datos profesional de regiones",
  "benefit.advanced_insights": "Insights avanzados de terroir",
  "benefit.business_ready": "Recursos listos para negocio",
  "benefit.structured_paths": "Rutas de aprendizaje estructuradas",
  "benefit.regional_courses": "Cursos por región vinícola",
  "benefit.practical_knowledge": "Conocimientos prácticos",
  "benefit.personalized": "Acompañamiento personalizado",
  "benefit.instant_answers": "Respuestas instantáneas",
  "benefit.interactive_learning": "Aprendizaje interactivo",
  "benefit.expertise_on_demand": "Experiencia vinícola bajo demanda",
  "benefit.interactive_exploration": "Exploración interactiva",
  "benefit.detailed_regions": "Regiones detalladas",
  "benefit.educational_content": "Contenido educativo",
  "benefit.multi_device": "Acceso móvil y escritorio",
  "benefit.seamless_integration": "Integración web fluida",
  "benefit.engagement_boost": "Mayor engagement de visitantes",
  "benefit.terroir_storytelling": "Storytelling de terroir interactivo",
  "benefit.no_tech": "Sin conocimientos técnicos",
  "benefit.qr_code": "Experiencias QR",
  "benefit.interactive_pages": "Páginas de botella interactivas",
  "benefit.customer_engagement": "Engagement del cliente",
  "benefit.guided_tasting": "Catas guiadas",
  "benefit.mobile_first": "Experiencia mobile-first",
  "benefit.enhanced_interaction": "Mayor interacción con visitantes",

  "product.portfolio.tagline": "Conecta cada botella con su historia.",
  "product.portfolio.desc": "Crea experiencias digitales que permiten a los clientes descubrir el origen, el terroir y la identidad detrás de cada botella.",
  "product.tasting.tagline": "Convierte las catas en experiencias inmersivas.",
  "product.tasting.desc": "Ofrece recorridos de cata interactivos que ayudan a los visitantes a entender vinos, regiones y terroirs aumentando el engagement.",
  "product.coach.tagline": "Tu compañero personal de aprendizaje del vino.",
  "product.coach.desc": "Una app de microlearning estilo Duolingo que ayuda a entender más rápido regiones, uvas, denominaciones y terroirs.",
  "product.maps.tagline": "Mapas vínicos interactivos para educación y engagement.",
  "product.maps.desc": "Explora las regiones vinícolas con mapas interactivos cuidadosamente diseñados que dan vida a terroirs y denominaciones.",
  "product.iframe.tagline": "Lleva mapas vínicos interactivos a tu propia web.",
  "product.iframe.desc": "Integra fácilmente los mapas geoVINUM en tu web, plataforma educativa o proyecto de enoturismo.",
  "product.academy.tagline": "Aprende vino con mapas, terroirs y ejemplos reales.",
  "product.academy.desc": "Una plataforma de aprendizaje interactiva pensada para hacer la educación del vino visual, atractiva y fácil de entender.",
  "coach.plan.weekly": "Plan semanal",
  "coach.plan.yearly": "Plan anual",
  "coach.plan.lifetime": "Plan de por vida",
  "coach.per.week": "/ semana",
  "coach.per.year": "/ año",
  "coach.per.month": "/ mes",
  "coach.per.one_time": "pago único",
  "coach.equivalent": "Equivalente",
  "coach.badge.flexible": "Flexible",
  "coach.badge.best_value": "Mejor valor",
  "coach.badge.lifetime": "Acceso de por vida",
  "coach.feature.learning_content": "Acceso completo al contenido",
  "coach.feature.quizzes": "Quizzes interactivos",
  "coach.feature.personalized": "Recomendaciones personalizadas",
  "coach.feature.ai_coaching": "Coaching IA",
  "coach.feature.all_features": "Acceso completo a todas las funciones",
  "coach.feature.premium_paths": "Rutas premium",
  "coach.feature.exclusive_content": "Contenido exclusivo",
  "coach.feature.priority_updates": "Actualizaciones prioritarias",
  "coach.feature.permanent_access": "Acceso permanente",
  "coach.feature.future_updates": "Futuras actualizaciones incluidas",
  "coach.feature.premium_content": "Acceso a contenido premium",
  "product.type.app": "Aplicación",
  "product.type.physical": "Producto físico",
  "product.type.platform": "Plataforma",
  "product.type.service": "Servicio",
  "home.cta.explore_atlas": "Explorar Atlas",
  "home.cta.browse_courses": "Ver cursos",
  "home.cta.browse_collection": "Ver colección",
  "home.cta.discover_iframe": "Descubrir Iframe",
  "home.cta.join_waitlist": "Unirse a la lista",
  "home.s1.eyebrow": "Explora regiones",
  "home.s1.title": "Navega regiones, denominaciones y uvas con",
  "home.s1.title_em": "mapas interactivos.",
  "home.s2.eyebrow": "Aprende vino",
  "home.s2.title": "Cursos estructurados, certificaciones y",
  "home.s2.title_em": "contenido educativo.",
  "home.s3.eyebrow": "Colecciona regiones",
  "home.s3.title": "Mapas premium y",
  "home.s3.title_em": "pósters educativos.",
  "home.s4.eyebrow": "Para profesionales",
  "home.s4.title": "Integra, implica y",
  "home.s4.title_em": "fideliza tus clientes.",
  "footer.heading.products": "Productos",
  "footer.heading.resources": "Recursos",
  "footer.heading.company": "Empresa",
  "footer.heading.social": "Social",
  "footer.link.about": "Acerca de",
  "footer.link.contact": "Contacto",
  "footer.link.newsletter": "Newsletter",
  "footer.link.support": "Soporte",
  "nav.boutique": "Tienda",

  // ----- Newsletter signup (ES) -----
  "newsletter.placeholder.email": "tu@email.com",
  "newsletter.submit": "Suscribirse",
  "newsletter.consent.label": "Acepto recibir la newsletter mensual de geoVINUM y consiento el tratamiento de mi dirección de email para tal fin. Puedo darme de baja en cualquier momento mediante el enlace incluido en cada email.",
  "newsletter.success": "¡Gracias! Suscripción confirmada. Revisa tu bandeja de entrada.",
  "newsletter.error.consent_required": "Marca la casilla de consentimiento para suscribirte.",
  "newsletter.error.generic": "No se pudo completar la suscripción. Inténtalo de nuevo.",
  // ----- Newsletter standalone page (ES) -----
  "newsletterPage.meta.title": "Newsletter — geoVINUM",
  "newsletterPage.meta.desc": "Suscríbete a la newsletter mensual de geoVINUM — denominaciones, terroirs, catas y novedades del viñedo.",
  "newsletterPage.eyebrow": "Mantente al día",
  "newsletterPage.title": "La Carta de los Terroirs",
  "newsletterPage.subtitle": "La newsletter geoVINUM — un email al mes. Nuevos mapas, denominación destacada, catas e historias del viñedo. Sin spam, baja en un clic.",
  "newsletterPage.form.eyebrow": "Suscribirse",
  "newsletterPage.contactSent.title": "Mensaje recibido — ¡gracias!",
  "newsletterPage.contactSent.desc": "Hemos recibido tu mensaje. Audrey te responderá en 48h (días laborables). Has recibido una copia por email. Ya que estás aquí, ¿te apuntas a la newsletter?",

  // ----- Contact form (ES) -----
  "contact.eyebrow": "Contacto",
  "contact.title": "Ponte en contacto",
  "contact.subtitle": "¿Una pregunta, una idea de colaboración o comentarios? Escríbenos — respondemos en 48h en días laborables.",
  "contact.field.name": "Nombre",
  "contact.field.email": "Email",
  "contact.field.subject": "Asunto",
  "contact.field.message": "Mensaje",
  "contact.submit": "Enviar mensaje",
  "contact.consent.label": "Consiento que geoVINUM trate mis datos personales para responder a mi consulta. Base legal: mi consentimiento (RGPD art. 6.1.a). Puedo retirarlo en cualquier momento escribiendo a hello@geovinum.io.",
  "contact.success.title": "Mensaje enviado",
  "contact.success.desc": "Hemos recibido tu mensaje y te responderemos pronto.",
  "contact.error.consent_required": "Marca la casilla de consentimiento para enviar el mensaje.",
  "contact.error.generic": "No se pudo enviar el mensaje. Inténtalo de nuevo.",
  "nav.learn": "Aprender",
  "nav.journal": "Diario",
  "nav.pro": "Pro",
  "nav.login": "Iniciar sesión",
  "nav.logout": "Cerrar sesión",
  "nav.account": "Cuenta",
  "nav.chat": "Chatear",
  "nav.search_placeholder": "Buscar regiones, vinos, lecciones…",
  "shop.maps.title": "Mapas",
  "shop.books.title": "Libros",
  "shop.ebooks.title": "Ebooks",
  "shop.courses.title": "Cursos",
  "shop.bundles.title": "Packs",
  "shop.empty": "Aún no hay productos en esta categoría.",
  "common.home": "Inicio",
  "common.search": "Buscar",
  "common.loading": "Cargando…",
  "common.no_results": "Sin resultados",
  "common.see_all": "Ver todos los resultados para",
  "common.try_another": "Prueba otra palabra clave.",
  "common.you_might_also": "También te puede gustar",
  "pillar.explore": "Explorar",
  "pillar.discover": "Descubrir",
  "pillar.experience": "Vivir",
  "pillar.learn": "Aprender",
  "pillar.collect": "Coleccionar",
  "pillar.integrate": "Integrar",
  "product.status.available": "Disponible",
  "product.status.coming_soon": "Próximamente",
  "product.cta.discover": "Descubrir",
  "product.cta.visit": "Visitar",
  "product.cta.contact": "Contactar",
  "product.cta.notify": "Avisarme",
  "product.cta.become_beta": "Ser Beta tester",
  "product.status.beta_program": "Próximamente · Programa Beta",
  "home.pillars.title_a": "Un ecosistema,",
  "home.pillars.title_b": "muchas formas de explorar el vino.",
  "home.pillars.sub": "De los mapas al aprendizaje y a las integraciones pro — descubre la familia geoVINUM.",
  "home.for.b2c_eyebrow": "Para amantes",
  "home.for.b2c_title": "Descubre, aprende,",
  "home.for.b2c_title_em": "colecciona.",
  "home.for.b2c_text": "De los mapas impresos al coaching IA personalizado — explora el vino a tu ritmo.",
  "home.for.b2c_cta": "Descubrir productos",
  "home.for.b2b_eyebrow": "Para pros",
  "home.for.b2b_title": "Implica clientes,",
  "home.for.b2b_title_em": "forma equipos, integra.",
  "home.for.b2b_text": "Bodegas, escuelas y plataformas usan geoVINUM para enseñar más rápido y enganchar más tiempo.",
  "home.for.b2b_cta": "Soluciones Pro",
  "dashboard.welcome_back": "Bienvenida",
  "dashboard.signed_in_as": "Conectada como",
  "dashboard.your_maps": "Tus mapas",
  "dashboard.continue_learning": "Continuar aprendiendo",
  "dashboard.empty_maps": "Aún no tienes compras.",
  "dashboard.empty_learning": "Empieza tu viaje de aprendizaje.",
  "dashboard.enroll": "Inscribirme",
  "dashboard.enrolling": "Inscribiendo…",
  "dashboard.continue": "Continuar",
  "dashboard.enrolled_ok": "¡Inscripción realizada! Abriendo el curso…",
  "dashboard.already_enrolled": "Ya estás inscrita en este curso.",
  "dashboard.enroll_failed": "Inscripción fallida. Inténtalo más tarde.",
  "dashboard.logout": "Cerrar sesión",

  // --- Iteration 45: added ES/PT translations for critical paths ---
  "auth.welcome_back": "Bienvenido de nuevo",
  "auth.welcome_back_sub": "Inicia sesión para continuar.",
  "auth.start_journey": "Comienza tu viaje vinícola",
  "auth.create_account": "Crea tu cuenta",
  "auth.email": "Email",
  "auth.password": "Contraseña",
  "auth.new_password": "Nueva contraseña",
  "auth.new_password_label": "Nueva contraseña",
  "auth.new_password_sub": "Elige una nueva contraseña segura.",
  "auth.confirm_password": "Confirmar contraseña",
  "auth.repeat_password": "Repetir contraseña",
  "auth.forgot_password": "¿Olvidaste tu contraseña?",
  "auth.reset_title": "Restablecer contraseña",
  "auth.reset_sub": "Te enviaremos un enlace para restablecerla.",
  "auth.check_inbox": "Revisa tu bandeja de entrada",
  "auth.full_name": "Nombre completo",
  "auth.full_name_placeholder": "Tu nombre",
  "auth.have_account": "¿Ya tienes cuenta ?",
  "auth.no_account": "¿No tienes cuenta ?",
  "auth.sign_up": "Crear cuenta",
  "auth.min6": "Mínimo 6 caracteres",
  "auth.password_updated": "Contraseña actualizada",
  "auth.redirecting": "Redirigiendo…",
  "auth.err_both": "Por favor completa ambos campos",
  "auth.err_invalid": "Email o contraseña incorrectos",
  "auth.err_match": "Las contraseñas no coinciden",
  "auth.err_min": "Demasiado corta (mín. 6 caracteres)",
  "auth.err_generic": "Algo salió mal",
  "cookies.save": "Guardar preferencias",
  "cookies.accept_all": "Aceptar todo",
  "cookies.reject_all": "Rechazar todo",
  "cookies.customize": "Personalizar",
  "cookies.title": "Tus preferencias de cookies",
  "cookies.intro": "Usamos cookies para mejorar tu experiencia.",
  "cookies.necessary": "Necesarias",
  "cookies.analytics": "Analítica",
  "cookies.marketing": "Marketing",
  "cookies.functional": "Funcionales",
  "cookies.manage": "Gestionar cookies",
  "cookies.banner_text": "Aceptas las cookies para mejorar tu experiencia.",
  "cta.start_free": "Empieza gratis",
  "cta.learn_more": "Saber más",
  "cta.try_now": "Probar ahora",
  "cta.see_more": "Ver más",
  "cta.contact_us": "Contáctanos",
  "cta.download": "Descargar",
  "cta.book_demo": "Reservar demo",
  "cta.subscribe": "Suscribirse",
  "cta.get_started": "Comenzar",
  "article.not_found": "Artículo no encontrado",
  "journal.title": "Diario",
  "journal.read_more": "Leer más",
  "journal.search": "Buscar artículos…",
  "journal.no_results": "Sin resultados",
  "cmdk.search": "Buscar…",
  "cmdk.no_results": "Ningún resultado",
  "cmdk.placeholder": "Busca mapas, regiones, uvas…",
  "lang.fr": "Francés",
  "common.error": "Error",
  "common.retry": "Reintentar",
  "common.close": "Cerrar",
  "common.next": "Siguiente",
  "common.previous": "Anterior",
  "common.cancel": "Cancelar",
  "dashboard.welcome": "Hola",
  "dashboard.my_orders": "Mis pedidos",
  "dashboard.my_subscription": "Mi suscripción",
  "dashboard.my_profile": "Mi perfil",
  "legal.terms": "Términos",
  "legal.privacy": "Privacidad",
  "legal.cookies": "Cookies",
  "thanks.title": "¡Gracias por tu pedido !",
  "thanks.subtitle": "Tu pedido se ha confirmado.",
  "thanks.order_ref": "Referencia",
  "thanks.email_sent": "Te hemos enviado un email de confirmación.",
  "thanks.back_home": "Volver al inicio",
  "print.checkout": "Pagar",
  "print.shipping": "Envío",
  "print.subtotal": "Subtotal",
  "print.total": "Total",
  "print.quote": "Calcular envío",
  "print.pay": "Pagar de forma segura",
  "trust.secure_payment": "Pago seguro",
  "trust.free_shipping": "Envío gratis",
  "trust.satisfaction": "Satisfacción garantizada",

  // --- Iteration 46: vague 2 ES/PT translations (auto, EN→ES/PT) ---
  "print.paper": "180gsm premium uncoated — museum-grade",
  "pro.shops.cta": "Become a wine-shop partner",
  "journal.ready": "Ready to see it?",
  "journal.pillar.get_started": "Comenzar",
  "learn.structure": "Structure",
  "thanks.understand_region": "Understand this region",
  "pro.shops.f1_d": "Stock museum-grade maps that sell themselves.",
  "play.h1_a": "Learn by",
  "product.detail1": "High-quality print",
  "pro.schools.f1_t": "Student licenses",
  "library.success": "You're on the list. We'll write when the first editions ship.",
  "print.success_failed": "Pago not completed. You can retry from the product page.",
  "journal.pillar.guides": "Guides",
  "play.eyebrow": "Play",
  "print.success_title": "Pedido received!",
  "print.upsell_title": "Pedido your map as a poster.",
  "learn.story1": "Wine is often complicated…",
  "product.details": "Details",
  "library.eyebrow": "Coming soon",
  "pro.pros.desc": "Iframe, API and widgets to embed geoVINUM data inside your platform.",
  "cta.log_in": "Iniciar sesión",
  "maps.filter": "Filtrar",
  "print.success_back": "Volver to product",
  "legal.contact_q": "Questions about this document? Contact",
  "journal.filter.all": "Todos",
  "journal.error": "Couldn't reach the journal.",
  "cookies.preferences": "Preferences",
  "pro.shops.headline_b": "understand",
  "trust.premium_print": "Premium print quality",
  "cta.back_to_login": "Volver to login",
  "play.sub": "Most people forget what they learn. You won't.",
  "pro.pros.f1_t": "Iframe embeds",
  "journal.loading": "Cargando articles…",
  "cta.play": "Play",
  "pro.pros.f2_d": "Query terroir, appellation, and grape data.",
  "learn.sub2": "Understand a wine region in 3 minutes.",
  "journal.pillar.get_started_desc": "Onboarding, how-tos, tutorials.",
  "lang.switch_label": "Language",
  "pro.schools.headline_a": "Faster, clearer,",
  "cookies.essential_desc": "Authentication, security, core functionality.",
  "thanks.receipt_sent": "A receipt has been sent to your email.",
  "play.mini_text": "Zoom, explore, understand.",
  "pro.schools.f1_d": "Bulk access to lessons and quizzes.",
  "cta.contact_sales": "Contact sales",
  "thanks.sub": "Your wine journey starts now.",
  "thanks.test_yourself": "Test yourself",
  "journal.pillar.insights": "Wine insights",
  "journal.empty": "No articles yet.",
  "journal.pillar.for_pros_desc": "Training, selling, scaling with geoVINUM.",
  "dashboard.recommendations": "You might also like",
  "product.rating": "4.8/5 from wine lovers",
  "play.mini_title_c": "of wine.",
  "product.test_knowledge": "Test your knowledge",
  "pro.cta_text": "Tell us about your shop, school or product — we'll tailor a partnership that fits.",
  "print.success_pending": "Verifying your payment…",
  "play.books": "Learn with books",
  "thanks.order_confirmation": "Pedido confirmation",
  "pro.pros.headline_a": "Integrate",
  "library.sub": "A curated collection of books, tasting notebooks and wine accessories — designed for collectors, sommeliers and lovers of fine geography.",
  "pro.schools.cta": "Discover the academic offer",
  "pro.shops.f2_d": "Maps paired with books and accessories.",
  "play.open_atlas": "Open the atlas",
  "library.notify_me": "Notify me",
  "print.upsell_subtitle": "Premium giclée print on 180gsm uncoated paper, shipped from our local production hub.",
  "library.seo.desc": "A coming-soon shop for wine lovers: handcrafted books, tasting notebooks and curated accessories.",
  "pro.pros.headline_c": "into your product.",
  "cmdk.select": "Open",
  "pro.shops.f2_t": "Gift-ready bundles",
  "cta.go_dashboard": "Go to dashboard",
  "pro.pros.f3_d": "Prebuilt UI blocks you can white-label.",
  "product.learn_this_region": "Learn this region",
  "journal.pillar.guides_desc": "Practical reads to make sense of wine.",
  "cta.view_map": "View map",
  "play.test_knowledge": "Test your wine knowledge",
  "pro.shops.f3_d": "Visual merchandising that invites longer visits.",
  "cta.reset_password": "Reset password",
  "play.h1_b": "playing",
  "pro.hero.eyebrow": "For professionals",
  "library.seo.title": "geoVINUM Library — Books, notebooks & wine accessories",
  "dashboard.your_maps_desc": "Access your purchased maps.",
  "legal.sections": "Sections",
  "journal.eyebrow": "Knowledge hub",
  "library.tiles_eyebrow": "What to expect",
  "pro.pros.title": "Professionals",
  "learn.eyebrow": "Learn",
  "pro.value.sales": "Sales improve",
  "cta.read_lesson": "Read lesson",
  "journal.pillar.for_pros": "For professionals",
  "library.title_a": "The geoVINUM",
  "print.upsell_eyebrow": "Print on demand",
  "pro.schools.f3_t": "Upsell programs",
  "home.final.title": "Empezar your wine journey",
  "play.books_text": "Hand-picked reads to go deeper — coming soon. Meanwhile, each map doubles as a study tool.",
  "dashboard.explore_more": "Explore more",
  "learn.see_what_you_learn": "See what you learn",
  "pro.pros.f3_t": "Widgets",
  "maps.featured_eyebrow": "Featured",
  "pro.pros.cta": "See the developer docs",
  "pro.schools.title": "Wine schools",
  "thanks.map_ready": "Your map is ready",
  "product.detail3": "Designed for learning",
  "pro.value.teams": "Teams are more confident",
  "product.download_pdf": "Descargar PDF",
  "maps.no_pieces": "No pieces match those filters yet.",
  "learn.dont_just_learn": "Don't just learn. Remember.",
  "cookies.essential": "Essential",
  "pro.shops.f3_t": "Higher basket size",
  "pro.schools.f2_d": "Large-format maps for on-wall teaching.",
  "common.questions": "questions",
  "pro.pros.f4_d": "Tailored to your domain, brand, or catalog.",
  "product.value_text": "Understand regions, grapes, and structure at a glance.",
  "play.transition_b": "Understand.",
  "cookies.body": "We use cookies to improve your experience. Essential cookies are always on. Analytics and marketing cookies require your consent.",
  "pro.pros.f2_t": "REST API",
  "pro.schools.headline_c": "learning.",
  "pro.schools.headline_b": "more memorable",
  "cookies.analytics_desc": "Traffic measurement and usage analysis.",
  "print.cta": "Pedido now",
  "footer.rights": "Todos rights reserved.",
  "library.title_b": "library.",
  "learn.sub1": "Short lessons. Clear insights.",
  "product.value_title_a": "See wine",
  "legal.eyebrow": "Legal",
  "play.mini_eyebrow": "Atlas",
  "pro.value.faster": "Visual learning is faster",
  "play.transition_a": "Play. Explore.",
  "cta.take_quiz": "Take quiz",
  "pro.shops.headline_c": "what they taste.",
  "journal.empty_try": "Try another topic or search term.",
  "pro.shops.desc": "Turn every visit into a learning moment. Sell maps, books and gift products that double as teaching tools.",
  "journal.pillar.insights_desc": "Regions, appellations, terroirs explained.",
  "library.tiles_title": "Three product lines",
  "cta.send_link": "Enviar link",
  "cmdk.quick_links": "Quick links",
  "journal.search_placeholder": "Buscar articles, regions, guides…",
  "pro.cta": "Work with geoVINUM",
  "pro.pros.f4_t": "Custom maps",
  "pro.hero.sub": "Sell maps, upsell tools, or embed interactive wine visualizations.",
  "cmdk.navigate": "Navigate",
  "pro.schools.f2_t": "Classroom kits",
  "product.value_title_b": "differently",
  "pro.value.understand": "Customers understand better",
  "maps.all_pieces": "Todos pieces",
  "learn.start_here": "Empezar here",
  "play.learn_basics": "Learn the basics",
  "learn.h1_b": "simply",
  "journal.read_article": "Read article",
  "dashboard.recommendations_desc": "Discover more maps and lessons.",
  "journal.clear_theme": "Clear theme filter",
  "pro.pros.f1_d": "Drop interactive maps into your site in one line.",
  "product.detail2": "Clear visual structure",
  "cta.back_to_journal": "Volver to the journal",
  "pro.schools.f3_d": "Add visual content to your curriculum.",
  "play.mini_title_b": "Google Maps",
  "learn.story2": "We make it simple.",
  "dashboard.continue_learning_desc": "Pick up where you left off.",
  "pro.pros.headline_b": "geoVINUM",
  "learn.h1_a": "Learn wine",
  "common.from": "From",
  "play.mini_title_a": "The",
  "product.not_found": "Product not found.",
  "pro.shops.headline_a": "Ayuda your customers",
  "pro.schools.desc": "Upsell visual learning tools to students — lessons, maps and quizzes that stick.",
  "pro.shops.f1_t": "Wholesale maps",
  "pro.shops.title": "Wine shops",
  "print.academy_hint": "Subscribers to Academy automatically get –10% on every print.",
  "play.tagline": "Quick, fun, memorable",
  "journal.sub": "Guides, insights and tools to understand wine visually.",
  "cookies.marketing_desc": "Campaign tracking and segmentation.",
  "print.success_subtitle": "Your poster is being prepared and will be shipped to the address you provided.",
  "play.see_wine_world": "See the wine world",

  // === Auto-injected i18n keys for new pages (es) ===
  "academyM.seo.title": "geoVINUM Academy — Aprende el vino visualmente",
  "academyM.seo.desc": "Más de 100 masterclasses en streaming · {days} días gratis.",
  "academyM.hero.title": "Entender el vino, visualmente.",
  "academyM.hero.desc": "Más de 100 masterclasses en streaming para estudiantes WSET, profesionales del vino y aficionados. Mapas interactivos, terruños comentados, metodología de cata — todo en un mismo lugar.",
  "academyM.hero.binge": "✦ Mira todas las series sin límites — acceso completo incluido en tu suscripción.",
  "academyM.hero.cta_primary": "Empezar {days} días gratis",
  "academyM.hero.cta_secondary": "Descubrir las series",
  "academyM.hero.trust.no_commitment": "Sin compromiso",
  "academyM.hero.trust.cancel_anytime": "Cancela cuando quieras",
  "academyM.hero.trust.hd_streaming": "Streaming HD",
  "academyM.why.eyebrow": "Por qué Academy",
  "academyM.why.title": "El vino, por fin claro",
  "academyM.why.desc": "Un enfoque visual y progresivo del vino, pensado por profesionales para profesionales y aficionados avanzados.",
  "academyM.why.visual.title": "Pedagogía visual",
  "academyM.why.visual.desc": "Mapas interactivos, esquemas, visualizaciones 3D de los terruños y denominaciones. Ves lo que aprendes.",
  "academyM.why.wset.title": "Nivel WSET",
  "academyM.why.wset.desc": "Programa alineado con los estándares WSET 2 y 3. Ideal para repasar, profundizar o iniciar una carrera en el mundo del vino.",
  "academyM.why.experts.title": "Expertos certificados",
  "academyM.why.experts.desc": "Masterclasses impartidas por sumilleres diplomados, enólogos y periodistas especializados.",
  "academyM.audience.eyebrow": "Para quién",
  "academyM.audience.title": "Diseñado para 3 perfiles",
  "academyM.audience.wset.title": "Estudiantes WSET",
  "academyM.audience.wset.desc": "Prepara las certificaciones WSET 2 y 3 con un soporte visual incomparable. Repasar una región lleva 15 minutos en lugar de una hora.",
  "academyM.audience.wset.badge": "Más popular",
  "academyM.audience.pros.title": "Profesionales del vino",
  "academyM.audience.pros.desc": "Sumilleres, tiendas de vino, restauradores: mejora tus conocimientos sobre las regiones que vendes o presentas a tus clientes.",
  "academyM.audience.amateurs.title": "Aficionados avanzados",
  "academyM.audience.amateurs.desc": "Te encanta el vino y quieres ir más allá de las etiquetas. Comprender el terruño, el clima, la historia — sin jerga.",
  "academyM.how.eyebrow": "Cómo funciona",
  "academyM.how.title": "3 pasos para empezar",
  "academyM.how.step1.title": "Activar la prueba",
  "academyM.how.step1.desc": "Regístrate en 30 segundos. {days} días de acceso completo, sin tarjeta para empezar.",
  "academyM.how.step2.title": "Elige una serie",
  "academyM.how.step2.desc": "Navega más de 100 masterclasses por categoría: regiones, uvas, cata, negocio.",
  "academyM.how.step3.title": "Mira y progresa",
  "academyM.how.step3.desc": "Streaming HD, modo sin conexión en la app geoVINUM Atlas. Continúa donde lo dejaste.",
  "academyM.preview.eyebrow": "Vista previa",
  "academyM.preview.title": "Algunas series de la biblioteca",
  "academyM.preview.see_all": "Ver todas las series",
  "academyM.preview.loading": "Cargando series…",
  "academyM.slider.featured": "Destacado",
  "academyM.slider.videos": "vídeos",
  "academyM.slider.goto": "Ir a la serie {n}",
  "academyM.final.title": "Empieza tus {days} días gratis",
  "academyM.final.desc": "Sin tarjeta. Sin compromiso. Cancela cuando quieras desde tu perfil.",
  "academyM.final.cta": "Empezar ahora",
  "academyW.seo.title": "Academy — geoVINUM",
  "academyW.seo.desc": "Tus masterclasses en streaming.",
  "academyW.upsell.text": "Explora las series libremente — la suscripción desbloquea la visualización ilimitada.",
  "academyW.upsell.cta": "Prueba 7 días gratis",
  "academyW.hero.eyebrow_featured": "Destacado esta semana",
  "academyW.hero.default_title": "Entender el vino, visualmente.",
  "academyW.hero.default_desc": "Más de 100 masterclasses en streaming, mapas interactivos y un atlas pedagógico para estudiantes WSET, sumilleres y aficionados.",
  "academyW.hero.watch": "Ver la serie",
  "academyW.hero.trial30": "30 días gratis",
  "academyW.hero.search_cta": "Buscar una serie…",
  "academyW.chip.all": "Todo",
  "academyW.cat.wine_insights": "Wine insights",
  "academyW.cat.guides": "Guías",
  "academyW.cat.get_started": "Empezar",
  "academyW.cat.for_professionals": "Para profesionales",
  "academyW.loading": "Cargando series…",
  "academyW.empty.title": "Aún no hay series publicadas",
  "academyW.empty.desc": "Las series de Academy estarán disponibles aquí pronto. Mientras tanto, descubre nuestros Atlas.",
  "academyW.series_count_one": "serie",
  "academyW.series_count_other": "series",
  "academyW.search.placeholder": "Buscar por serie, tema, región…",
  "academyW.search.no_results": "Sin resultados para « {q} »",
  "academyW.videos_label": "vídeos",
  "atlasP.not_found.title": "Producto Atlas no encontrado",
  "atlasP.not_found.cta": "Ver todos los Atlas",
  "atlasP.bc.home": "Inicio",
  "atlasP.bc.atlas": "Atlas",
  "atlasP.trial_days": "{days} días gratis",
  "atlasP.who.label": "¿Para quién?",
  "atlasP.when.label": "¿Cuándo usarlo?",
  "atlasP.when.title": "En el momento adecuado",
  "atlasP.why.label": "¿Por qué?",
  "atlasP.why.title": "El valor único",
  "atlasP.features.eyebrow": "Funcionalidades",
  "atlasP.features.title": "Lo que {name} te aporta",
  "atlasP.upsell.title": "Ir más allá",
  "atlasP.upsell.discover": "Descubrir",
  "atlasP.final.title": "¿Listo para probar {name}?",
  "atlasP.final.trial": "{days} días de prueba gratis · sin compromiso · cancela cuando quieras",
  "atlasP.mini.tagline": "Para empezar de forma simple",
  "atlasP.mini.cta_primary": "Abrir la app",
  "atlasP.mini.cta_secondary": "Descubrir geoVINUM Atlas",
  "atlasP.mini.price": "Gratis",
  "atlasP.mini.price_note": "Web — sin instalación",
  "atlasP.mini.audience.label": "Principiantes y curiosos",
  "atlasP.mini.audience.desc": "Aficionados que descubren el vino y quieren explorar visualmente las regiones vitivinícolas del mundo desde cualquier dispositivo.",
  "atlasP.mini.when": "Cuando estás en un restaurante, una bodega, o de viaje y quieres entender rápidamente de dónde viene un vino.",
  "atlasP.mini.why": "Una app simple, gratuita e intuitiva — la puerta de entrada al universo geoVINUM.",
  "atlasP.mini.feat.1": "Más de 30 regiones vitivinícolas cartografiadas",
  "atlasP.mini.feat.2": "Búsqueda por denominación",
  "atlasP.mini.feat.3": "Geolocalización de viñedos",
  "atlasP.mini.feat.4": "Resumen pedagógico por región",
  "atlasP.mini.feat.5": "100% gratis, sin registro",
  "atlasP.mini.upsell.magnum.title": "geoVINUM Atlas",
  "atlasP.mini.upsell.magnum.desc": "Para ir más allá en la pedagogía",
  "atlasP.mini.upsell.academy.title": "geoVINUM Academy",
  "atlasP.mini.upsell.academy.desc": "Más de 100 masterclasses en streaming",
  "atlasP.magnum.tagline": "La herramienta de referencia para entender el vino",
  "atlasP.magnum.cta_primary": "Empezar 30 días gratis",
  "atlasP.magnum.cta_secondary": "Ver la versión Pro",
  "atlasP.magnum.price": "Suscripción mensual",
  "atlasP.magnum.price_note": "30 días gratis, sin compromiso",
  "atlasP.magnum.audience.label": "Estudiantes WSET, profesionales, aficionados avanzados",
  "atlasP.magnum.audience.desc": "Quienes quieren dominar el vino con una herramienta pedagógica completa: sumilleres en formación, estudiantes WSET, profesionales de la restauración.",
  "atlasP.magnum.when": "Cuando preparas un examen WSET, repasas una región antes de un servicio, o construyes tu cultura del vino a largo plazo.",
  "atlasP.magnum.why": "Más de 100 masterclasses de Academy integradas, mapas interactivos en alta definición, fichas detalladas por denominación, actualización continua.",
  "atlasP.magnum.feat.1": "Todas las regiones del mundo",
  "atlasP.magnum.feat.2": "Mapas interactivos en alta definición",
  "atlasP.magnum.feat.3": "Academy incluida: más de 100 masterclasses",
  "atlasP.magnum.feat.4": "Fichas detalladas por denominación",
  "atlasP.magnum.feat.5": "Modo sin conexión para viajes",
  "atlasP.magnum.feat.6": "Actualizaciones automáticas",
  "atlasP.magnum.upsell.pro.title": "geoVINUM Atlas (B2B)",
  "atlasP.magnum.upsell.pro.desc": "Para importadores y tiendas de vino",
  "atlasP.magnum.upsell.shop.title": "Mapas impresos",
  "atlasP.magnum.upsell.shop.desc": "Pósteres y enmarcados de tus regiones",
  "atlasP.pro.tagline": "La solución B2B para los profesionales del vino",
  "atlasP.pro.cta_primary": "Solicitar una demo",
  "atlasP.pro.cta_secondary": "Prueba de 30 días",
  "atlasP.pro.price": "Bajo presupuesto",
  "atlasP.pro.price_note": "Suscripción B2B anual · 30 días de prueba",
  "atlasP.pro.audience.label": "Importadores, distribuidores, tiendas, escuelas",
  "atlasP.pro.audience.desc": "Estructuras que necesitan formar a sus equipos comerciales, equipar su fuerza de ventas, o integrar geoVINUM en su sitio web y catálogo.",
  "atlasP.pro.when": "Cuando gestionas un equipo comercial, una sala de cata, una escuela del vino o un sitio de e-commerce que debe vender la historia antes del producto.",
  "atlasP.pro.why": "Cuenta multiusuario, acceso a toda la biblioteca de Academy, API para integrar nuestros mapas en tu sitio (iframe), formaciones a medida, soporte prioritario.",
  "atlasP.pro.feat.1": "Cuentas multiusuario (equipos)",
  "atlasP.pro.feat.2": "Acceso completo a Academy + Atlas",
  "atlasP.pro.feat.3": "API e iframe para tu sitio",
  "atlasP.pro.feat.4": "Mapas white-label personalizables",
  "atlasP.pro.feat.5": "Formaciones a medida incluidas",
  "atlasP.pro.feat.6": "Soporte dedicado y onboarding",
  "atlasP.pro.feat.7": "Informes de uso por equipo",
  "atlasP.pro.upsell.iframe.title": "Iframe geoVINUM",
  "atlasP.pro.upsell.iframe.desc": "Integra nuestros mapas en tu sitio",
  "atlasP.pro.upsell.business.title": "Descubrir Business",
  "atlasP.pro.upsell.business.desc": "Portfolio, Tasting y más",
  "blogP.seo.title": "Blog — geoVINUM",
  "blogP.seo.desc": "Artículos, guías y consejos para entender el vino visualmente.",
  "blogP.bc.home": "Inicio",
  "blogP.bc.blog": "Blog",
  "blogP.title": "Blog geoVINUM",
  "blogP.intro": "Wine insights, guías prácticas, primeros pasos y contenido profesional — todo nuestro contenido editorial sobre vino en un solo lugar.",
  "blogP.search.placeholder": "Buscar un artículo…",
  "blogP.chip.all": "Todo",
  "blogP.cat.wine_insights": "Wine insights",
  "blogP.cat.guides": "Guías",
  "blogP.cat.get_started": "Empezar",
  "blogP.cat.for_professionals": "Para profesionales",
  "blogP.cat.ebook": "Ebook",
  "blogP.cat.cepage": "Variedades",
  "ebookF.eyebrow": "Ebook gratuito",
  "ebookF.title": "Descarga el ebook",
  "ebookF.intro": "Deja tu email abajo para recibir el PDF al instante. También te enviaremos una copia por correo para que lo consultes desde cualquier dispositivo.",
  "ebookF.email_label": "Email",
  "ebookF.email_placeholder": "tu@dominio.com",
  "ebookF.consent_text": "Acepto que geoVINUM conserve este email para enviarme el ebook y responder a cualquier solicitud relacionada. Derecho de acceso, rectificación y supresión en cualquier momento.",
  "ebookF.newsletter_label": "Suscribirme también a la newsletter de geoVINUM (1 a 2 emails/mes — baja en un clic).",
  "ebookF.submit": "Obtener el ebook",
  "ebookF.submitting": "Enviando…",
  "ebookF.success_title": "Ebook listo",
  "ebookF.success_email_sent": "También te hemos enviado el enlace por email.",
  "ebookF.success_msg": "La descarga debería abrirse en una nueva pestaña. Si no, utiliza el botón siguiente.",
  "ebookF.download_btn": "Descargar ahora",
  "ebookF.pages_label": "páginas",
  "ebookF.gdpr_hint": "Tus datos se quedan en geoVINUM. Nada de spam.",
  "ebookF.error_email": "Se requiere un email válido.",
  "ebookF.error_consent": "Marca la casilla de consentimiento para descargar el ebook.",
  "blogP.loading": "Cargando…",
  "blogP.empty.title": "Aún no hay artículos",
  "blogP.empty.no_results": "Sin resultados para « {q} »",
  "blogP.read_minutes": "min",
  "blogP.article.loading": "Cargando…",
  "blogP.article.not_found": "Artículo no encontrado",
  "blogP.article.back": "Volver al blog",
  "blogP.article.by": "Por",
  "blogP.article.read_time": "min de lectura",
  "blogP.article.cross.eyebrow": "Ir más allá",
  "blogP.article.cross.title": "Explora el universo geoVINUM",
  "blogP.article.cross.shop_label": "Tienda",
  "blogP.article.cross.shop_title": "Mapas de los viñedos",
  "blogP.article.cross.shop_desc": "Pósteres, enmarcados, canvas",
  "blogP.article.cross.academy_label": "Academy",
  "blogP.article.cross.academy_title": "Masterclasses en streaming",
  "blogP.article.cross.academy_desc": "7 días gratis",
  "blogP.article.cross.terroir_label": "geoVINUM app",
  "blogP.article.cross.terroir_title": "Habla con el terruño",
  "blogP.article.cross.terroir_desc": "Gratis para probar · 4 idiomas",
  "bizM.eyebrow": "Comparativa",
  "bizM.title": "Cuándo · Quién · Por qué",
  "bizM.subtitle": "Encuentra de un vistazo la solución geoVINUM que coincide con tu necesidad.",
  "bizM.col.solution": "Solución",
  "bizM.col.when": "Cuándo usarlo",
  "bizM.col.who": "Para quién",
  "bizM.col.why": "Por qué",
  "bizM.col.action": "Acción",
  "bizM.soon": "Pronto",
  "bizM.discover": "Descubrir",
  "bizM.label.when": "Cuándo",
  "bizM.label.who": "Para quién",
  "bizM.label.why": "Por qué",
  "bizM.magnum.name": "geoVINUM Atlas",
  "bizM.magnum.when": "Preparar un examen WSET, subir de nivel, equipar a un equipo de sumillería",
  "bizM.magnum.who": "Sumilleres, estudiantes WSET, profesionales de hostelería",
  "bizM.magnum.why": "La herramienta pedagógica más completa del mercado, más de 100 masterclasses incluidas",
  "bizM.pro.name": "geoVINUM Atlas",
  "bizM.pro.when": "Formar a una fuerza de ventas, crear un catálogo interactivo, equipar una sala de cata",
  "bizM.pro.who": "Importadores, distribuidores, tiendas de vino, escuelas",
  "bizM.pro.why": "Multiusuario, API white-label, soporte dedicado, ROI medible",
  "bizM.iframe.name": "Iframe",
  "bizM.iframe.when": "Tienes un sitio web y quieres contar la historia del terruño antes de vender",
  "bizM.iframe.who": "Bodegas, sindicatos, oficinas de turismo, e-commerce de vino",
  "bizM.iframe.why": "Integración en 1 línea, mapas en alta definición, multi-idioma",
  "bizM.portfolio.name": "Portfolio",
  "bizM.portfolio.when": "Quieres digitalizar la experiencia del cliente en tu bodega o tienda",
  "bizM.portfolio.who": "Bodegas, cooperativas, tiendas experienciales",
  "bizM.portfolio.why": "QR inmersivos, storytelling visual, convierten visitas en ventas",
  "bizM.tasting.name": "Tasting",
  "bizM.tasting.when": "Animas ferias, salones del vino, eventos enoturísticos",
  "bizM.tasting.who": "Ferias profesionales, organizadores de eventos, hostelería premium",
  "bizM.tasting.why": "Gamificación lúdica y memorable — aumenta la captación de leads",

  // === searchP keys (es) ===
  "searchP.seo.title": "Buscar — geoVINUM",
  "searchP.seo.desc": "Busca en el blog, Academy y tienda de geoVINUM.",
  "searchP.title": "Buscar",
  "searchP.intro": "Encuentra artículos, series Academy y mapas impresos — todo en un solo lugar.",
  "searchP.placeholder": "Busca una región, tema, uva, serie…",
  "searchP.chip.all": "Todo",
  "searchP.chip.blog": "Blog",
  "searchP.chip.academy": "Academy",
  "searchP.chip.shop": "Tienda",
  "searchP.kind.blog": "Blog",
  "searchP.kind.academy": "Academy",
  "searchP.kind.shop": "Tienda",
  "searchP.loading": "Buscando…",
  "searchP.from": "desde",
  "searchP.empty.prompt": "Escribe algo para empezar a buscar.",
  "searchP.empty.no_results_title": "Sin resultados",
  "searchP.empty.no_results_desc": "Prueba otra región, uva o tema.",

  // === dashboard keys (es) ===
  "dashboard.track_shipment": "Seguir el envío",
  "dashboard.view_all_orders": "Ver todos los pedidos",

  // === hero slides (es) ===
  "hero.label.for": "Para quién",
  "hero.label.why": "Por qué",
  "hero.see_all": "Todos los productos",
  "hero.slide.promise.eye": "geoVINUM",
  "hero.slide.promise.title": "Entender el vino, visualmente.",
  "hero.slide.promise.for": "Aficionados, estudiantes y profesionales — de Burdeos a Mendoza",
  "hero.slide.promise.why": "+3 000 denominaciones vitícolas cartografiadas — explora, aprende y elige el vino sin jerga.",
  "hero.slide.promise.cta": "Descubrir geoVINUM",
  "hero.slide.mini.eye": "geoVINUM Atlas",
  "hero.slide.mini.title": "Descubre el vino en tu móvil, gratis.",
  "hero.slide.mini.for": "Principiantes curiosos y viajeros",
  "hero.slide.mini.why": "30+ regiones mapeadas, geolocalización, app web 100% gratis.",
  "hero.slide.mini.cta": "Abrir geoVINUM Atlas",
  "hero.slide.terroir.eye": "geoVINUM app",
  "hero.slide.terroir.title": "Habla con el terruño. En cualquier lugar, en cualquier idioma.",
  "hero.slide.terroir.for": "Amantes, bares de vino, vinaterías, escuelas y productores",
  "hero.slide.terroir.why": "Una sola app IA para todo — preguntas, flashcards, cata a ciegas, foto-a-guía, multijugador. PWA, sin instalar.",
  "hero.slide.terroir.cta": "Abrir geoVINUM app",
  "hero.slide.magnum.eye": "geoVINUM Atlas",
  "hero.slide.magnum.title": "Domina el vino, visualmente.",
  "hero.slide.magnum.for": "Estudiantes WSET, sumilleres, profesionales",
  "hero.slide.magnum.why": "100+ masterclasses en vídeo + mapas interactivos. 30 días gratis.",
  "hero.slide.magnum.cta": "Probar geoVINUM Atlas gratis",
  "hero.slide.academy.eye": "Academy",
  "hero.slide.academy.title": "Masterclasses del vino, a demanda.",
  "hero.slide.academy.for": "Cualquiera que quiera entender de verdad el vino",
  "hero.slide.academy.why": "Mira 100+ masterclasses HD. 7 días gratis, luego 9€/mes.",
  "hero.slide.academy.cta": "Empezar 7 días gratis",
  "hero.slide.shop.eye": "Tienda",
  "hero.slide.shop.title": "Mapas del vino que puedes colgar en la pared.",
  "hero.slide.shop.for": "Coleccionistas, restaurantes, regalos",
  "hero.slide.shop.why": "Pósteres premium, enmarcados, canvas — impresos en UE, envío mundial.",
  "hero.slide.shop.cta": "Ver los mapas",
  "hero.slide.pro.eye": "geoVINUM Atlas",
  "hero.slide.pro.title": "Equipa a tu equipo del vino.",
  "hero.slide.pro.for": "Importadores, distribuidores, escuelas",
  "hero.slide.pro.why": "Cuentas multiusuario, API white-label, formaciones a medida, soporte prioritario.",
  "hero.slide.pro.cta": "Reservar una demo",
  "hero.slide.iframe.eye": "Iframe",
  "hero.slide.iframe.title": "Integra nuestros mapas en tu sitio.",
  "hero.slide.iframe.for": "Bodegas, oficinas de turismo, e-commerce",
  "hero.slide.iframe.why": "1 línea de HTML, mapas interactivos HD, multi-idioma.",
  "hero.slide.iframe.cta": "Obtener el código",
  "hero.slide.blog.eye": "Blog",
  "hero.slide.blog.title": "El vino explicado, un artículo a la vez.",
  "hero.slide.blog.for": "Aprendices de por vida",
  "hero.slide.blog.why": "Insights, guías, primeros pasos y consejos pro — cada semana.",
  "hero.slide.blog.cta": "Leer el blog",

  // === hero slides extra (es) ===
  "hero.soon": "Pronto",
  "hero.slide.portfolio.eye": "Portfolio",
  "hero.slide.portfolio.title": "Convierte tu bodega en una historia.",
  "hero.slide.portfolio.for": "Bodegas, cooperativas, tiendas experienciales",
  "hero.slide.portfolio.why": "QR inmersivos y storytelling visual — convierte visitas en ingresos.",
  "hero.slide.portfolio.cta": "Descubrir Portfolio",
  "hero.slide.tasting.eye": "Tasting",
  "hero.slide.tasting.title": "Cata gamificada para tus eventos.",
  "hero.slide.tasting.for": "Ferias, organizadores de eventos, hostelería premium",
  "hero.slide.tasting.why": "Gamificación lúdica y memorable — aumenta la captación de leads.",
  "hero.slide.tasting.cta": "Descubrir Tasting",
  "hero.slide.coach.eye": "Coach",
  "hero.slide.coach.title": "Tu coach del vino con IA, 24/7.",
  "hero.slide.coach.for": "Cualquiera que quiera un mentor personal del vino",
  "hero.slide.coach.why": "Pregunta lo que sea: maridajes, regiones, consejos — respuestas de experto al instante.",
  "hero.slide.coach.cta": "Descubrir Coach",

  // === catalog cta (es) ===
  "cta.full_catalog": "Descargar el catálogo (PDF)",
  "cta.product_catalog": "Ficha de producto (PDF)",

  // === catalog capture (es) ===
  "catalogCapture.eyebrow": "GRATIS · PDF · 13 páginas",
  "catalogCapture.title": "Descarga el catálogo completo",
  "catalogCapture.subtitle": "Solo tu email — compartimos novedades dos veces al año, sin spam.",
  "catalogCapture.email": "Email *",
  "catalogCapture.first_name": "Nombre (opcional)",
  "catalogCapture.company": "Empresa (opcional)",
  "catalogCapture.category_placeholder": "¿Tu perfil? (opcional)",
  "catalogCapture.cta": "Descargar PDF",
  "catalogCapture.loading": "Preparando el PDF…",
  "catalogCapture.skip": "Saltar — abrir directamente el PDF",
  "catalogCapture.close": "Cerrar",
  "catalogCapture.err.email": "Por favor introduce un email válido.",
  "catalogCapture.privacy": "Al descargar, aceptas ser contactado sobre geoVINUM. Cancela en 1 clic.",
  "catalogCapture.cat.domaines": "Bodega",
  "catalogCapture.cat.cavistes": "Tienda de vinos",
  "catalogCapture.cat.negociants": "Casa de negocio",
  "catalogCapture.cat.restaurateurs": "Restaurante",
  "catalogCapture.cat.distributeurs": "Distribuidor",
  "catalogCapture.cat.importateurs": "Importador",
  "catalogCapture.cat.ecoles": "Escuela del vino",
  "catalogCapture.cat.drinks_tech": "Drinks Tech (startup)",
  "catalogCapture.cat.e_commerce": "E-commerce vino",
  "catalogCapture.cat.autre": "Otro",

  "nav.cartes": "Mapas hub",

  "iframeLive.eyebrow": "Demo interactiva",
  "iframeLive.title": "Prueba el mapa con cualquier denominación.",
  "iframeLive.subtitle": "Escribe el nombre de una denominación, elige en la lista, el mapa se recarga en directo — exactamente como se mostrará en tu sitio.",
  "iframeLive.placeholder": "Sancerre, Chablis, Côte-Rôtie…",
  "iframeLive.cta": "Probar",
  "iframeLive.suggestions_label": "Sugerencias:",
  "iframeLive.footer": "La demo usa exactamente el mismo componente que los artículos del blog y que tu futuro embed.",

  "iframeInstall.eyebrow": "Instalación",
  "iframeInstall.title": "En 4 pasos, tu sitio muestra el mapa.",
  "iframeInstall.subtitle": "Sin framework impuesto. Si puedes crear una ruta de servidor, puedes integrar geoVINUM.",
  "iframeInstall.step1.title": "1. Suscríbete y obtén tu clave API",
  "iframeInstall.step1.body": "Elige tu suscripción abajo. Tras el pago, recibes inmediatamente tu clave API EtOH por email.",
  "iframeInstall.step2.title": "2. Solicita el token",
  "iframeInstall.step2.body": "Desde tu servidor, intercambia tu clave API por un token JWT corto (1h, formato `{ \"access\": \"<JWT>\" }`) — exactamente como la ruta que impulsa este sitio. Cachealo 55 min y devuélvelo al front. **Nunca expongas la clave API en el navegador.**",
  "iframeInstall.step3.title": "3. Vincula tu campo de denominación dinámico",
  "iframeInstall.step3.body": "En tu CMS o plantilla, lee el nombre de la denominación desde tu ficha de producto (`product.appellation`, un campo ACF, un metafield de Shopify, etc.) y pásalo como `app=` en la URL del iframe. Un solo campo — cada mapa se adapta solo.",
  "iframeInstall.step4.title": "4. El mapa aparece en cada ficha",
  "iframeInstall.step4.body": "Eso es todo. En cada ficha de producto, se genera automáticamente un iframe `https://iframe.geovinum.app/?token=…&app=<denominación>` — el mapa correcto aparece para cada producto, sin trabajo manual página a página.",
  "iframeInstall.personalize.title": "Personalizar el snippet con tu clave",
  "iframeInstall.personalize.body": "Pega tu clave API EtOH abajo — los snippets se rellenarán automáticamente. Tu clave **permanece en tu navegador**, nunca se envía a nuestros servidores.",
  "iframeInstall.personalize.placeholder": "ej: 362ec4bf-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
  "iframeInstall.personalize.clear": "Borrar",
  "iframeInstall.personalize.ready": "Snippets personalizados ({0}) — la copia usa la clave completa.",
  "iframeInstall.show_key": "Mostrar clave",
  "iframeInstall.hide_key": "Ocultar clave",
  "iframeInstall.copy": "Copiar",
  "iframeInstall.copied": "Copiado",
  "iframeInstall.help_prefix": "¿Necesitas ayuda para integrar?",
  "iframeInstall.help_link": "Contáctanos",
  "iframeInstall.help_suffix": "— nuestro equipo te ayuda gratuitamente.",

  "cartes.seo_title": "Mapas de denominaciones vinícolas | geoVINUM",
  "cartes.seo_desc": "Explora cada denominación vinícola en un mapa interactivo: Sancerre, Chablis, Saint-Émilion, Châteauneuf-du-Pape… geoVINUM cartografía el viñedo mundial.",
  "cartes.eyebrow": "Catálogo de terruños",
  "cartes.h1_line1": "Cada denominación,",
  "cartes.h1_line2": "un mapa interactivo.",
  "cartes.subtitle": "Descubre {0} denominaciones cartografiadas en detalle, con sus parcelas catastrales, sus variedades y sus artículos dedicados.",
  "cartes.subtitle_empty": "Descubre nuestras denominaciones cartografiadas en detalle, con sus parcelas catastrales, sus variedades y sus artículos dedicados.",
  "cartes.search_placeholder": "Buscar una denominación… (Sancerre, Châteauneuf, etc.)",
  "cartes.region_all": "Todas",
  "cartes.loading": "Cargando el catálogo…",
  "cartes.empty": "Ninguna denominación coincide.",
  "cartes.empty_reset": "Reiniciar",
  "cartes.count_one": "{0} denominación",
  "cartes.count_many": "{0} denominaciones",
  "cartes.card.eyebrow": "Denominación",
  "cartes.card.preview": "Vista previa",
  "cartes.card.hide_preview": "Ocultar mapa",
  "cartes.card.read": "Leer",
  "cartes.cta.eyebrow": "Para profesionales",
  "cartes.cta.title": "Este mismo mapa en tu sitio, en 4 pasos.",
  "cartes.cta.subtitle": "Vinotecas, editores, agencias de enoturismo: integra el mapa interactivo de cualquier denominación en tu sitio con nuestro iframe.",
  "cartes.cta.button": "Descubrir el iframe geoVINUM",
  "cartes.region.bordeaux":  "Burdeos & Suroeste",
  "cartes.region.bourgogne": "Borgoña",
  "cartes.region.beaujolais":"Beaujolais",
  "cartes.region.rhone":     "Valle del Ródano",
  "cartes.region.loire":     "Valle del Loira",
  "cartes.region.champagne": "Champaña & Alsacia",
  "cartes.region.languedoc": "Languedoc-Rosellón",
  "cartes.region.provence":  "Provenza & Córcega",
  "cartes.region.jura":      "Jura & Saboya",
  "cartes.region.italy":     "Italia",
  "cartes.region.spain":     "España",
  "cartes.region.portugal":  "Portugal",
  "cartes.region.other":     "Otros terruños",

  // geoVINUM app landing page
  "terroir.seo.title": "geoVINUM app — La IA del vino, en todas partes, en todos los idiomas",
  "terroir.seo.desc": "Habla con el terruño. Descubre denominaciones, variedades, productores y eventos. Construye tu ruta del vino, aprende con flashcards, cata a ciegas. Para amantes y profesionales.",
  "terroir.hero.eyebrow": "geoVINUM app · Impulsado por geoVINUM",
  "terroir.hero.h1_a": "Habla con el terruño.",
  "terroir.hero.h1_b": "En cualquier lugar, en cualquier idioma.",
  "terroir.hero.sub": "Tu compañero IA del vino. Pregunta todo sobre denominaciones, variedades, productores, eventos. Construye tu ruta enoturística, aprende con flashcards, cata a ciegas, fotografía una etiqueta y deja que la IA te lo cuente. Una sola app — para amantes y profesionales.",
  "terroir.hero.cta_primary": "Abrir la web app",
  "terroir.hero.cta_secondary": "Crear una cuenta",
  "terroir.hero.pwa_note": "Sin descarga · Funciona en cualquier dispositivo · 4 idiomas",
  "terroir.demo.title": "Pruébala ahora",
  "terroir.demo.sub": "Vista previa en vivo aquí mismo. Toca una pregunta para empezar.",
  "terroir.demo.open_in_new": "Abrir en una nueva pestaña",
  "terroir.usecases.title": "Lo que puedes hacer con geoVINUM app",
  "terroir.usecases.sub": "Casos de uso, no una lista de funciones. Elige el que encaja con tu momento.",
  "terroir.uc.amateur.tag": "Para amantes del vino",
  "terroir.uc.amateur.1.title": "Estoy planeando un fin de semana enológico",
  "terroir.uc.amateur.1.body": "Indica fechas y región. geoVINUM app construye tu itinerario — bodegas abiertas, catas, restaurantes, las mejores denominaciones para probar ahora.",
  "terroir.uc.amateur.2.title": "Tengo una botella delante y quiero entenderla",
  "terroir.uc.amateur.2.body": "Fotografía la etiqueta. La IA te cuenta la historia de la denominación, la variedad, el productor, qué esperar en la copa y con qué maridarlo.",
  "terroir.uc.amateur.3.title": "Quiero aprender un poco cada día",
  "terroir.uc.amateur.3.body": "Micro-cursos diarios, flashcards sobre variedades y regiones, cata a ciegas. 5 minutos al día para ser un catador seguro.",
  "terroir.uc.amateur.4.title": "Quiero jugar con amigos",
  "terroir.uc.amateur.4.body": "Quiz multijugador sobre denominaciones, variedades, regiones. Perfecto para una velada, un club de cata o una cena.",
  "terroir.uc.pro.tag": "Para profesionales",
  "terroir.uc.pro.1.title": "Tengo un bar de vinos",
  "terroir.uc.pro.1.body": "Convierte tu carta de vinos en una experiencia de cata lúdica. Los clientes escanean un QR y exploran el terruño de cada botella, solos o en equipo. Más engagement, más botellas vendidas.",
  "terroir.uc.pro.2.title": "Soy vinatero",
  "terroir.uc.pro.2.body": "Añade un asistente 'geoVINUM app' en tu web o tienda. Tus clientes preguntan, reciben recomendaciones, construyen su bodega — sin tener un sumiller 24/7.",
  "terroir.uc.pro.3.title": "Lidero un club enológico",
  "terroir.uc.pro.3.body": "Anima las reuniones mensuales con juegos multijugador, catas a ciegas, flashcards temáticas. Los socios vuelven porque aprenden divirtiéndose.",
  "terroir.uc.pro.4.title": "Enseño vino",
  "terroir.uc.pro.4.body": "Escuelas y academias: módulos, quizzes y catas a ciegas. Sigue el progreso, gamifica tus clases, convierte las aulas en experiencias.",
  "terroir.uc.pro.5.title": "Soy productor",
  "terroir.uc.pro.5.body": "Integra geoVINUM app en la página de tu bodega. Los visitantes preguntan sobre tu denominación, tus variedades, tu historia — incluso cuando no puedes atender el teléfono.",
  "terroir.audiences.title": "Una sola app, dos usos",
  "terroir.audiences.amateur.title": "Amante",
  "terroir.audiences.amateur.body": "Gratis para probar · PWA · disponible en 4 idiomas.",
  "terroir.audiences.pro.title": "Profesional",
  "terroir.audiences.pro.body": "Bares de vino, vinaterías, clubes, escuelas, productores. Experiencia personalizada, gamificada, escalable.",
  "terroir.audiences.pro.cta": "Habla con nuestro equipo",
  "terroir.how.title": "Cómo funciona",
  "terroir.how.1.title": "Abre la app",
  "terroir.how.1.body": "Sin instalación. Una app web — misma experiencia en todos los dispositivos.",
  "terroir.how.2.title": "Pregunta, escanea, juega",
  "terroir.how.2.body": "Escribe una pregunta, fotografía una etiqueta, lanza unas flashcards, reta a un amigo.",
  "terroir.how.3.title": "Mejora cada día",
  "terroir.how.3.body": "Micro-cursos, catas a ciegas, itinerarios. Tu cultura del vino se acumula.",
  "terroir.cta.title": "¿Listo para hablar con el terruño?",
  "terroir.cta.sub": "Gratis para probar. Sin tarjeta. En cualquier dispositivo.",
  "terroir.cta.web": "Abrir la web app",
  "terroir.cta.ios": "Abrir la app",

  // geoVINUM app — Formulario Demo Pro
  "pro_demo.eyebrow": "Demo Pro · 30 segundos",
  "pro_demo.title": "Reserva una demo Pro a medida",
  "pro_demo.subtitle": "Bar de vinos, vinatería, club, escuela, productor — integramos geoVINUM app en tu establecimiento con tu branding, tu carta, tu recorrido cliente. Coste por visitante, sin compromiso.",
  "pro_demo.bullet_1": "Experiencia personalizada en tu sitio o por QR",
  "pro_demo.bullet_2": "Flashcards gamificadas, catas a ciegas, quiz multijugador",
  "pro_demo.bullet_3": "Captura de leads + dashboard analítico incluidos",
  "pro_demo.form.eyebrow": "Cuéntanos sobre ti",
  "pro_demo.form.title": "Te respondemos en 24h",
  "pro_demo.field.name": "Tu nombre",
  "pro_demo.field.email": "Email profesional",
  "pro_demo.field.business": "Establecimiento",
  "pro_demo.field.visitors": "Visitantes / mes",
  "pro_demo.business.wine_bar": "Bar de vinos",
  "pro_demo.business.merchant": "Vinatería",
  "pro_demo.business.club": "Club enológico",
  "pro_demo.business.school": "Escuela / Academia",
  "pro_demo.business.producer": "Productor",
  "pro_demo.business.other": "Otro",
  "pro_demo.consent_text": "Acepto ser contactado por el equipo geoVINUM sobre mi solicitud de demo. Mis datos se conservan 24 meses y puedo solicitar su eliminación en cualquier momento (RGPD).",
  "pro_demo.submit": "Solicitar mi demo",
  "pro_demo.reply_time": "Respondemos en 24h laborables",
  "pro_demo.error.consent": "Acepta el consentimiento para continuar.",
  "pro_demo.success.title": "¡Solicitud recibida!",
  "pro_demo.success.body": "Revisa tu correo — acabamos de enviarte una confirmación. ¿Quieres ir más rápido? Reserva un slot de 20 min:",
  "pro_demo.success.calendar": "Reservar 20 min",

  // Home — secciones de productos simplificadas
  "home.s_terroir.eyebrow": "geoVINUM app · La app IA para todos",
  "home.s_terroir.title": "Habla con el terruño.",
  "home.s_terroir.title_em": "en cualquier idioma.",
  "home.s_terroir.sub": "Amantes y profesionales usan la misma app IA: preguntas sobre denominaciones, variedades, productores, ruta enoturística, foto de etiqueta, quiz multijugador, flashcards diarias.",
  "home.s_academy.eyebrow": "Academy · Netflix del terruño",
  "home.s_academy.title": "El vino también",
  "home.s_academy.title_em": "se mira.",
  "home.s_academy.sub": "100+ masterclasses HD sobre cada región, variedad y tradición. Streaming en cualquier dispositivo. 7 días gratis, luego 9€/mes.",
  "home.s_shop.eyebrow": "Shop · Print on demand",
  "home.s_shop.title": "Mapas del vino",
  "home.s_shop.title_em": "para colgar.",
  "home.s_shop.sub": "Pósters premium, enmarcados, lienzos — todas las grandes denominaciones, impresas y enviadas desde la UE. El atlas del coleccionista.",
  "home.s_iframe.eyebrow": "Iframe · Embed B2B",
  "home.s_iframe.title": "El mapa correcto en",
  "home.s_iframe.title_em": "cada ficha.",
  "home.s_iframe.sub": "Sitios de vino, marketplaces, importadores: vincula un campo de denominación y nuestros mapas interactivos aparecen automáticamente en cada ficha. De 6 000 a ilimitadas, pago por petición desde 0,01€.",
  "home.cta.discover_terroir": "Descubrir geoVINUM app",
  "home.cta.open_webapp": "Abrir la web app",
  "home.cta.talk_to_sales": "Hablar con ventas",
  "home.closing.title": "Un ecosistema, una promesa.",
  "home.closing.sub": "Gratis para probar, en 4 idiomas, en cualquier dispositivo — sin instalar.",
};

const pt = {
  "nav.maps": "Mapas",
  "nav.app": "App",
  "nav.interactive": "Interativo",
  "nav.experience": "Experience",
  "nav.pro.overview": "Para profissionais — visão geral",
  "nav.pro_long": "Para profissionais",
  "nav.explorer.all": "Ver tudo",
  "nav.explorer.appellations": "Regiões · Vinhas · Denominações",
  "nav.explorer.cepages": "Castas",
  "nav.explorer.producers": "Produtores",
  "nav.explorer.articles": "Artigos",
  "nav.explorer.guides_divider": "Guias e referências",
  "nav.explorer.glossaire": "Glossário do vinho (506 termos)",
  "nav.explorer.cartographie": "Cartografia vitivinícola",
  "nav.explorer.geographie": "Geografia do vinho",
  "nav.explorer.appellations_pil": "Denominações vitivinícolas",
  "nav.explorer.terroirs": "Terroirs vitivinícolas",
  "nav.play": "Jogar",
  "cta.explore_maps": "Explorar mapas",
  "cta.start_learning": "Começar",
  "cta.start_learning_2min": "Aprender em 2 min",
  "cta.start_playing": "Jogar",
  "cta.discover_pro": "Descobrir geoVINUM Pro",
  "cta.buy_now": "Comprar",
  "cta.add_cart": "Adicionar ao carrinho",

  "home.hero.eyebrow": "Mapas do vinho · por geoVINUM",
  "home.hero.h1a": "Entenda o vinho",
  "home.hero.h1b": "visualmente.",
  "home.hero.tagline": "Mapas. Aprendizagem. Jogo.",
  "home.hero.line": "O vinho é complexo. Tornamo-lo visual.",
  "home.hero.support": "Explore regiões, entenda as castas e aprenda em minutos.",
  "home.hero.rating": "4,8/5 pelos amantes do vinho",
  "home.hero.secure_payment": "Pagamento seguro",
  "home.hero.shipping": "Envio mundial",

  "story.a": "O vinho é frequentemente complicado.",
  "story.b": "Regiões, castas, terroirs…",
  "story.c": "geoVINUM torna-o simples.",

  "trust.premium_quality": "Qualidade premium",
  "trust.made_to_learn": "Feito para aprender",
  "trust.loved_by": "Amado pelos amantes do vinho",
  "trust.worldwide_shipping": "Envio mundial",

  "footer.brand_line": "O vinho fica claro quando se vê. Mapas, lições e jogo — para curiosos e profissionais.",
  "footer.explore": "Explorar",
  "footer.contact": "Contacto",
  "footer.legal": "Legal",
  "footer.cookie_settings": "Cookies",
  "footer.alcohol_disclaimer": "⚠️ O abuso de álcool é perigoso para a saúde. Consuma com moderação.",

  "pro.hero.h1": "geoVINUM para",
  "pro.hero.h1_italic": "profissionais",

  "journal.h1_a": "Aprender. Explorar.",
  "journal.h1_b": "Entender",
  "journal.empty_lang": "Ainda não há artigos neste idioma.",
  "journal.empty_lang_text": "Estamos a traduzir mais conteúdo. Mude de idioma ou volte em breve.",

  // Navigation + dropdowns
  "nav.products": "Produtos",
  "nav.resources": "Recursos",
  "nav.explore": "Explorar",
  "nav.academy": "Academy",
  "nav.maps_short": "Mapas",
  "nav.shop": "Loja",
  "nav.business": "Business",
  "nav.terroir_ai": "geoVINUM app",
  "nav.blog": "Blog",
  "nav.resources": "Recursos",
  "nav.resources.articles": "Blog · Artigos",
  "nav.resources.atlas": "Atlas de denominações",
  "nav.resources.cepages": "Castas",
  "nav.resources.ebooks": "Ebooks gratuitos",
  "cmd.kind.atlas_appellation": "Atlas · Denominação",
  "cmd.kind.atlas_cepage": "Atlas · Casta",
  "cmd.kind.blog": "Artigo",
  "cmd.kind.academy": "Série Academy",
  "cmd.kind.shop": "Loja",
  "nav.download_app": "Descarregar app",
  "nav.atlas_mini": "geoVINUM Atlas",
  "nav.atlas_magnum": "geoVINUM Atlas",
  "nav.atlas_pro": "geoVINUM Atlas",
  "nav.product.academy_short": "Academy",
  "nav.product.coach_short": "Coach",
  "nav.product.iframe_short": "Iframe",
  "nav.product.cellar_short": "Portfolio",
  "nav.product.tasting_short": "Tasting",
  "business.h1_a": "Experiências de vinho para",
  "business.h1_b": "o seu negócio.",
  "business.sub": "Integre mapas interativos, envolva clientes e crie provas memoráveis.",
  "business.cta_title": "Falemos",
  "business.cta_text": "Conte-nos sobre a sua loja, escola ou domínio — desenhamos a parceria à medida.",

  // ----- Business page v2 (12 sections) -----
  "biz.seo.title": "geoVINUM para profissionais — Plataforma de experiências de vinho",

  "biz.hero.title_a": "Transforme o seu terroir em ativo comercial, de marketing e",
  "biz.hero.title_b": "educativo.",
  "biz.hero.sub": "A geoVINUM ajuda produtores, regiões vinícolas, importadores e formadores a valorizar os terroirs com mapas interativos, storytelling e experiências imersivas.",
  "biz.hero.cta_primary": "Pedir demo",
  "biz.hero.cta_secondary": "Ver casos de utilização",

  "biz.why.eyebrow": "Porquê geoVINUM",
  "biz.why.title": "Porque os profissionais escolhem a geoVINUM",
  "biz.why.bullet.terroirs": "Explicar os terroirs visualmente",
  "biz.why.bullet.storytelling": "Melhorar o storytelling do vinho",
  "biz.why.bullet.engagement": "Aumentar o engagement do site",
  "biz.why.bullet.education": "Apoiar a formação em vinho",
  "biz.why.bullet.differentiate": "Diferenciar a sua marca",

  "biz.who.eyebrow": "Para quem",
  "biz.who.title": "Pensado para o setor do vinho",
  "biz.who.wineries.title": "Produtores",
  "biz.who.wineries.desc": "Valorize o seu terroir, reforce o relato de marca e crie experiências de vinho envolventes.",
  "biz.who.importers.title": "Importadores e distribuidores",
  "biz.who.importers.desc": "Ajude as equipas a compreender as regiões e a comunicar os vinhos com mais eficácia.",
  "biz.who.regions.title": "Regiões e interprofissões",
  "biz.who.regions.desc": "Promova denominações e territórios através de experiências interativas e educativas.",
  "biz.who.education.title": "Educação e formação",
  "biz.who.education.desc": "Dê vida à geografia do vinho e aos terroirs para estudantes e profissionais.",

  "biz.problem.eyebrow": "O problema",
  "biz.problem.title": "Porque a maior parte dos conteúdos de vinho não envolve",
  "biz.problem.p1": "A comunicação tradicional baseia-se muitas vezes em PDFs estáticos, fichas técnicas e descrições longas.",
  "biz.problem.p2": "Os clientes têm dificuldade em compreender os terroirs, as regiões vinícolas e o que torna um vinho único.",
  "biz.problem.p3": "A geoVINUM transforma esta informação complexa em experiências visuais e interativas.",

  "biz.benefits.eyebrow": "Benefícios",
  "biz.benefits.title": "O que a geoVINUM ajuda a alcançar",
  "biz.benefits.storytelling.title": "Melhorar o storytelling",
  "biz.benefits.storytelling.desc": "Ajude os clientes a compreender a origem e a identidade dos seus vinhos.",
  "biz.benefits.engagement.title": "Aumentar o engagement",
  "biz.benefits.engagement.desc": "Mapas interativos criam experiências memoráveis e mantêm os visitantes mais tempo.",
  "biz.benefits.sales.title": "Apoiar as equipas comerciais",
  "biz.benefits.sales.desc": "Forneça ferramentas visuais que tornam as explicações mais simples e impactantes.",
  "biz.benefits.education.title": "Reforçar a formação em vinho",
  "biz.benefits.education.desc": "Transforme regiões vinícolas complexas em experiências de aprendizagem claras.",

  "biz.solutions.eyebrow": "Soluções",
  "biz.solutions.title": "A nossa plataforma de experiências de vinho",
  "biz.solutions.maps.desc": "Mapas vínicos interativos integráveis em websites, landing pages e plataformas educativas.",
  "biz.solutions.maps.use.winery_websites": "Sites de produtores",
  "biz.solutions.maps.use.wine_tourism": "Enoturismo",
  "biz.solutions.maps.use.edu_content": "Conteúdo educativo",
  "biz.solutions.maps.use.regional_promo": "Promoção regional",
  "biz.solutions.portfolio.desc": "Experiências digitais que ligam os clientes à história por trás de cada garrafa.",
  "biz.solutions.portfolio.use.winery_visits": "Visitas ao produtor",
  "biz.solutions.portfolio.use.qr_code": "Experiências QR code",
  "biz.solutions.portfolio.use.storytelling": "Storytelling de produto",
  "biz.solutions.portfolio.use.brand_engagement": "Envolvimento de marca",
  "biz.solutions.tasting.desc": "Provas interativas pensadas para envolver visitantes e educar consumidores.",
  "biz.solutions.tasting.use.events": "Eventos",
  "biz.solutions.tasting.use.wine_fairs": "Feiras de vinho",
  "biz.solutions.tasting.use.cellar_doors": "Salas de prova",
  "biz.solutions.tasting.use.hospitality": "Hotelaria & restauração",

  "biz.usecases.eyebrow": "Casos de utilização",
  "biz.usecases.title": "Como os profissionais usam a geoVINUM",
  "biz.usecases.tradefair.title": "Antes de uma feira",
  "biz.usecases.tradefair.desc": "Crie suportes visuais envolventes para apresentar vinhos e regiões.",
  "biz.usecases.website.title": "No site de um produtor",
  "biz.usecases.website.desc": "Acrescente mapas de terroirs interativos para aumentar o engagement.",
  "biz.usecases.export.title": "Para vendas de exportação",
  "biz.usecases.export.desc": "Ajude compradores internacionais a compreender as suas regiões.",
  "biz.usecases.tourism.title": "Para enoturismo",
  "biz.usecases.tourism.desc": "Crie experiências memoráveis para os visitantes.",
  "biz.usecases.training.title": "Para formar equipas",
  "biz.usecases.training.desc": "Ensine geografia do vinho com mais eficácia.",

  "biz.examples.eyebrow": "Exemplos",
  "biz.examples.title": "geoVINUM em ação",
  "biz.examples.map": "Mapa de vinho interativo",
  "biz.examples.domain": "Integrado no seu domínio",
  "biz.examples.mobile": "Experiência mobile e tablet",
  "biz.examples.sheet": "Ficha de vinho enriquecida",

  "biz.position.eyebrow": "Porquê geoVINUM",
  "biz.position.title": "Muito mais do que mapas",
  "biz.position.p1": "A geoVINUM combina geografia do vinho, storytelling de terroir e experiências digitais numa única plataforma.",
  "biz.position.p2": "Ao contrário de ferramentas genéricas de cartografia ou apresentação, a geoVINUM foi concebida especificamente para o setor do vinho.",

  "biz.social.eyebrow": "Confiam em nós",
  "biz.social.title": "Aprovado por profissionais do vinho",
  "biz.social.stat.regions.value": "30+",
  "biz.social.stat.regions.label": "Regiões vinícolas",
  "biz.social.stat.wineries.value": "200+",
  "biz.social.stat.wineries.label": "Produtores",
  "biz.social.stat.maps.value": "500+",
  "biz.social.stat.maps.label": "Mapas interativos",
  "biz.social.stat.countries.value": "25+",
  "biz.social.stat.countries.label": "Países",

  "biz.faq.eyebrow": "FAQ",
  "biz.faq.title": "Perguntas frequentes",
  "biz.faq.orgs.q": "Que tipo de organizações usam a geoVINUM?",
  "biz.faq.orgs.a": "Produtores, regiões vinícolas e interprofissões, importadores e distribuidores, escolas e centros de formação, profissionais de enoturismo e hotelaria.",
  "biz.faq.embed.q": "Os mapas podem ser integrados no nosso site?",
  "biz.faq.embed.a": "Sim — os mapas geoVINUM integram-se em qualquer site ou landing page através de um iframe simples, totalmente responsivo e personalizável.",
  "biz.faq.languages.q": "A geoVINUM está disponível em várias línguas?",
  "biz.faq.languages.a": "Sim — geoVINUM está disponível em português, inglês, francês e espanhol, com mais línguas no roadmap.",
  "biz.faq.tourism.q": "A geoVINUM pode apoiar projetos de enoturismo?",
  "biz.faq.tourism.a": "Sem dúvida. Muitas regiões usam a geoVINUM para enriquecer o enoturismo com mapas interativos, storytelling de terroir e experiências em sala de prova.",
  "biz.faq.education.q": "A geoVINUM pode ser usada para formação?",
  "biz.faq.education.a": "Sim — escolas, programas de escanção e equipas de formação usam a geoVINUM Academy e os Maps para ensinar geografia do vinho com eficácia.",
  "biz.faq.custom.q": "Oferecem projetos à medida?",
  "biz.faq.custom.a": "Sim — desenhamos mapas à medida, experiências de marca próprias e integrações dedicadas. Contacte-nos para falarmos do seu projeto.",

  "biz.final.title": "Pronto para mostrar o seu terroir de outra forma?",
  "biz.final.body": "Descubra como a geoVINUM pode ajudar a sua organização a criar experiências de vinho envolventes e a comunicar melhor o valor dos seus vinhos.",
  "biz.final.cta_primary": "Pedir demo",
  "biz.final.cta_secondary": "Contacte-nos",

  "biz.shared.use_cases_label": "Casos de utilização",
  "biz.shared.coming_soon": "Em breve",
  "biz.shared.view_demo": "Ver demo",
  "biz.shared.join_waitlist": "Lista de espera",
  "home.hero.h1_b": "Descubra o vinho pelo terroir.",
  "home.hero.h1_main": "",
  "home.hero.sub_v2": "Explore regiões, aprenda cultura do vinho e viva terroirs com mapas, formação e ferramentas interativas.",

  "product.starting_from": "A partir de",
  "product.trial_7d": "7 dias de teste grátis incluídos",
  "product.cta.see_versions": "Ver versões",
  "product.versions": "Versões disponíveis",
  "product.pricing": "Preços",
  "product.price.free": "Grátis",
  "product.price.premium": "Premium",
  "product.price.subscription": "Subscrição Pro",
  "product.atlas.tagline": "Explore as regiões vinícolas e os terroirs do mundo.",
  "product.atlas.desc": "geoVINUM Atlas ajuda os amantes do vinho a descobrir regiões, denominações, castas e produtores com mapas interativos e conteúdo educativo.",
  "product.atlas.mini.title": "geoVINUM Atlas",
  "product.atlas.mini.desc": "Ideal para apaixonados pelo vinho, estudantes e curiosos que querem acesso rápido a regiões, denominações e castas-chave.",
  "product.atlas.magnum.title": "geoVINUM Atlas",
  "product.atlas.magnum.desc": "Pensado para entusiastas e estudantes que procuram uma compreensão mais completa da geografia do vinho, dos terroirs e das denominações.",
  "product.atlas.pro.title": "geoVINUM Atlas",
  "product.atlas.pro.desc": "Solução completa para profissionais que precisam de dados fiáveis, ferramentas cartográficas avançadas e recursos pedagógicos premium.",

  // ----- Explore "Which Atlas" (PT) -----
  "explore.which.eyebrow": "Escolhe o teu Atlas",
  "explore.which.title": "Qual Atlas é o ideal para ti?",
  "explore.mini.tagline": "O Google Maps do vinho",
  "explore.mini.desc": "Explora as regiões vinícolas, denominações e produtores em todo o mundo.",
  "explore.mini.audience": "Apreciadores · Viajantes · Iniciantes · Curiosos do vinho",
  "explore.mini.cta": "Explorar geoVINUM Atlas",
  "explore.magnum.tagline": "O teu mapa de vinha personalizado",
  "explore.magnum.desc": "Mostra uma ou várias denominações com referências cadastrais e mapas de vinha à medida.",
  "explore.magnum.audience": "Produtores · Quintas · Enoturismo · Organizações regionais",
  "explore.magnum.cta": "Descobrir geoVINUM Atlas",
  "explore.pro.tagline": "Inteligência avançada do terroir",
  "explore.pro.desc": "Acede a dados profissionais de terroir, visualização 3D e análise avançada das regiões vinícolas.",
  "explore.pro.audience": "Consultores · Importadores · Formadores · Especialistas em vinho · Interprofissões",
  "explore.pro.cta": "Descobrir geoVINUM Atlas",
  "explore.cta.learn_more": "Saber mais",
  "explore.cta.subscribe": "Subscrever",
  "explore.cta.download": "Descarregar",
  "explore.trial_days": "{n} dias grátis",

  // ----- Iframe pricing (PT) -----
  "iframeP.eyebrow": "Escolhe o teu plano",
  "iframeP.title": "Do pequeno site ao enterprise — escolha o seu volume.",
  "iframeP.cycle.monthly": "Mensal",
  "iframeP.cycle.yearly": "Anual",
  "iframeP.popular": "Mais popular",
  "iframeP.ht_per": "sem IVA /",
  "iframeP.year": "ano",
  "iframeP.month": "mês",
  "iframeP.save_with_yearly": "Poupa com o anual ({amount} / ano)",
  "iframeP.unavailable": "Indisponível",
  "iframeP.subscribe": "Subscrever",
  "iframeP.request": "pedido",
  "iframeP.requests_per_year": "pedidos / ano",
  "iframeP.contact_badge": "Personalizado",
  "iframeP.from": "A partir de",
  "iframeP.custom_quote": "Orçamento personalizado",
  "iframeP.talk_to_sales": "Falar com vendas",
  "iframeP.footer_note": "Pagamento seguro por Stripe · IVA aplicado automaticamente · Cancela quando quiseres na tua conta.",

  // ----- Portfolio page (PT) -----
  "portfolioP.hero.eyebrow": "Portfolio · Passaporte Digital",
  "portfolioP.hero.title": "O passaporte digital inteligente de cada produto.",
  "portfolioP.hero.subtitle": "Produtores, garrafeiras, distribuidores, sommeliers e formadores centralizam, enriquecem e partilham uma ficha dinâmica automaticamente adaptada a cada audiência.",
  "portfolioP.hero.cta_beta": "Tornar-se Beta tester",
  "portfolioP.hero.cta_contact": "Falar connosco",
  "portfolioP.status.beta": "Em breve · Beta",
  "portfolioP.status.note": "Acede em primeira mão juntando-te ao programa beta",

  "portfolioP.truth.eyebrow": "Uma única fonte de verdade",
  "portfolioP.truth.title": "Um produto. Um passaporte inteligente. Infinitos usos.",
  "portfolioP.truth.before": "Em vez de gerir…",
  "portfolioP.truth.before.files_pdf": "Várias fichas técnicas em PDF",
  "portfolioP.truth.before.sales_decks": "Várias apresentações comerciais",
  "portfolioP.truth.before.translations": "Várias versões traduzidas",
  "portfolioP.truth.before.training": "Vários materiais de formação",
  "portfolioP.truth.before.pitch": "Vários argumentos de venda",
  "portfolioP.truth.after": "Com Portfolio",
  "portfolioP.truth.after_desc": "Toda a informação do produto numa única plataforma. Um espaço dinâmico gera a versão mais relevante consoante o destinatário e o contexto.",
  "portfolioP.truth.after_note": "Edita uma vez, dissemina por todo o lado — garrafa, feira, carta, exportação, formação.",

  "portfolioP.ai.eyebrow": "Pré-preenchimento inteligente",
  "portfolioP.ai.title": "Um passaporte que se escreve sozinho.",
  "portfolioP.ai.subtitle": "A ficha nunca começa em branco: é automaticamente enriquecida com os dados GeoVINUM e a inteligência artificial.",
  "portfolioP.ai.geovinum_title": "Dados GeoVINUM",
  "portfolioP.ai.geovinum.region": "Região",
  "portfolioP.ai.geovinum.appellation": "Denominação",
  "portfolioP.ai.geovinum.grapes": "Castas",
  "portfolioP.ai.geovinum.climate": "Clima",
  "portfolioP.ai.geovinum.soils": "Solos",
  "portfolioP.ai.geovinum.altitude": "Altitude",
  "portfolioP.ai.geovinum.plots": "Parcelas",
  "portfolioP.ai.geovinum.producers": "Produtores",
  "portfolioP.ai.geovinum.history": "Dados históricos",
  "portfolioP.ai.geovinum.geo": "Dados geográficos",
  "portfolioP.ai.ai_title": "Gerações IA",
  "portfolioP.ai.ai.summary": "Resumo do produto",
  "portfolioP.ai.ai.storytelling": "Storytelling",
  "portfolioP.ai.ai.sales": "Apresentação comercial",
  "portfolioP.ai.ai.retailer": "Argumento para garrafeiras",
  "portfolioP.ai.ai.export": "Argumento para exportação",
  "portfolioP.ai.ai.pairing": "Harmonizações",
  "portfolioP.ai.ai.profiles": "Perfis de consumidor",
  "portfolioP.ai.ai.training": "Conteúdos pedagógicos",
  "portfolioP.ai.ai.i18n": "Traduções multilingues",
  "portfolioP.ai.editable": "O profissional pode sempre editar ou completar os conteúdos gerados.",

  "portfolioP.modules.eyebrow": "Totalmente modular",
  "portfolioP.modules.title": "Ativa ou desativa cada módulo à la carte.",
  "portfolioP.module.origin.title": "Origem e Terroir",
  "portfolioP.module.origin.desc": "Cartografia interativa, parcelas, geologia, clima, altitude, ambiente.",
  "portfolioP.module.story.title": "História e Storytelling",
  "portfolioP.module.story.desc": "História da quinta, filosofia, know-how, apresentação do produto.",
  "portfolioP.module.tech.title": "Técnica",
  "portfolioP.module.tech.desc": "Produção, estágio, certificações, análises, fichas regulamentares.",
  "portfolioP.module.tasting.title": "Prova",
  "portfolioP.module.tasting.desc": "Notas, perfil aromático, radar sensorial, notas pessoais.",
  "portfolioP.module.training.title": "Formação",
  "portfolioP.module.training.desc": "Formato WSET, fichas pedagógicas, quizzes, conteúdos de formação.",
  "portfolioP.module.pairing.title": "Harmonizações",
  "portfolioP.module.pairing.desc": "Harmonizações com pratos, sugestões de serviço, ocasiões.",
  "portfolioP.module.media.title": "Médias",
  "portfolioP.module.media.desc": "Fotos, vídeos, entrevistas, conteúdos de marketing.",
  "portfolioP.module.trade.title": "Comercial",
  "portfolioP.module.trade.desc": "Preços, embalagens, disponibilidade, mercados de exportação.",

  "portfolioP.position.eyebrow": "Valorização de mercado",
  "portfolioP.position.title": "Compreende onde está cada produto no mercado.",
  "portfolioP.position.subtitle": "Portfolio não se limita a descrever. Com os dados GeoVINUM, os dados de mercado e a IA, emergem:",
  "portfolioP.position.position.title": "Posicionamento de mercado",
  "portfolioP.position.position.desc": "Entrada de gama, premium, super-premium, ícone.",
  "portfolioP.position.competitive.title": "Análise competitiva",
  "portfolioP.position.competitive.desc": "Produtos similares, referências concorrentes, alternativas na mesma categoria.",
  "portfolioP.position.value.title": "Valorização",
  "portfolioP.position.value.desc": "Estimativa de valor, coerência preço / mercado, evolução do posicionamento, perceção dos provadores.",
  "portfolioP.position.differentiation.title": "Diferenciação",
  "portfolioP.position.differentiation.desc": "O que distingue o produto — as suas forças comerciais e argumentos únicos de venda.",
  "portfolioP.position.tier.entry": "Entrada de gama",
  "portfolioP.position.tier.premium": "Premium",
  "portfolioP.position.tier.super_premium": "Super-premium",
  "portfolioP.position.tier.icon": "Ícone",

  "portfolioP.audience.eyebrow": "Adaptado a cada destinatário",
  "portfolioP.audience.title": "Um Portfolio. Cinco rostos.",
  "portfolioP.audience.consumer.title": "Consumidor",
  "portfolioP.audience.consumer.list": "História · Terroir · Prova · Harmonizações · Vídeos",
  "portfolioP.audience.retailer.title": "Garrafeira",
  "portfolioP.audience.retailer.list": "Argumento comercial · Perfil sensorial · Produtos similares · Posicionamento de preço",
  "portfolioP.audience.importer.title": "Importador",
  "portfolioP.audience.importer.list": "Certificações · Volumes disponíveis · Informação de exportação",
  "portfolioP.audience.sommelier.title": "Sommelier",
  "portfolioP.audience.sommelier.list": "Prova detalhada · Harmonizações · Temperatura de serviço",
  "portfolioP.audience.educator.title": "Formador",
  "portfolioP.audience.educator.list": "Conteúdos pedagógicos · Mapas · Quizzes · Recursos educativos",

  "portfolioP.diffusion.eyebrow": "Multicanal",
  "portfolioP.diffusion.title": "Distribuído onde o teu produto viaja.",
  "portfolioP.diffusion.subtitle": "Cada canal pode apresentar módulos diferentes — a informação certa no contexto certo.",
  "portfolioP.diffusion.qr_bottle": "QR code na garrafa",
  "portfolioP.diffusion.qr_fair": "QR code em feira profissional",
  "portfolioP.diffusion.wine_list": "Carta de vinhos digital",
  "portfolioP.diffusion.catalog": "Catálogo comercial",
  "portfolioP.diffusion.export": "Ficha de exportação",
  "portfolioP.diffusion.training": "Suporte de formação",
  "portfolioP.diffusion.private_link": "Link privado ou público",

  "portfolioP.promise.eyebrow": "A promessa",
  "portfolioP.promise.title": "Portfolio transforma uma simples ficha de produto num ativo digital inteligente.",
  "portfolioP.promise.body": "Pré-preenchido com dados GeoVINUM, enriquecido por IA, personalizável até ao infinito e adaptável a cada audiência — Portfolio ajuda-te a compreender, valorizar, apresentar, formar e vender melhor um produto.",
  "portfolioP.promise.tagline": "Uma só ficha. Infinitos usos.",
  "portfolioP.promise.cta": "Tornar-se Beta tester",
  "portfolioP.promise.cta_contact": "Contactar",

  // ----- Portfolio: Como funciona (PT) -----
  "portfolioP.how.eyebrow": "Como funciona",
  "portfolioP.how.title": "Três passos. Depois adapta-se a cada audiência.",
  "portfolioP.how.scan.title": "Identifica o produto",
  "portfolioP.how.scan.desc": "Escolhe uma denominação, um vinho, uma colheita. GeoVINUM recolhe instantaneamente os dados geográficos e históricos.",
  "portfolioP.how.ai.title": "A IA pré-preenche",
  "portfolioP.how.ai.desc": "Storytelling, argumentos, harmonizações, prova, traduções — tudo gerado. Tu manténs o controlo e editas livremente.",
  "portfolioP.how.share.title": "Distribui em todo o lado",
  "portfolioP.how.share.desc": "QR na garrafa, carta de vinhos, ficha de exportação, formação. Cada canal mostra os módulos certos.",

  // ----- Portfolio: Demo (PT) -----
  "portfolioP.demo.eyebrow": "Pré-visualização",
  "portfolioP.demo.title": "Um produto. Quatro leituras. Zero duplicação.",
  "portfolioP.demo.subtitle": "Vê como a mesma ficha se adapta automaticamente ao destinatário.",
  "portfolioP.demo.lens": "Vista audiência",
  "portfolioP.demo.note": "Vista estática. As fichas reais são totalmente interativas — editar, reordenar, mudar idioma, exportar PDF.",
  "portfolioP.demo.consumer.story": "Nascido nos saibros quentes de Margaux, esta colheita conta um longo verão seco. Aveludado no nariz, refinado em boca — um vinho que recompensa a paciência.",
  "portfolioP.demo.retailer.story": "1er Grand Cru icónico com forte procura secundária. PVP > 800€. Excelente relação preço/pontuação vs Latour 2018.",
  "portfolioP.demo.sommelier.story": "Nariz subtil de violeta e cedro. Taninos firmes, acidez elevada. Decantar 2h. Serviço: 17°C. Guarda: 2028–2050.",
  "portfolioP.demo.educator.story": "Lote da Margem Esquerda dominado por Cabernet — caso de estudo ideal para terroir de saibro, estilo Médoc e classificação de 1855.",
  "portfolioP.demo.section.story": "História",
  "portfolioP.demo.section.story.value": "Legado lacrado desde 1855",
  "portfolioP.demo.section.terroir": "Terroir",
  "portfolioP.demo.section.terroir.value": "Saibro · 12m altitude",
  "portfolioP.demo.section.tasting": "Prova",
  "portfolioP.demo.section.tasting.value": "Cassis · Cedro · Grafite",
  "portfolioP.demo.section.pairing": "Harmonização",
  "portfolioP.demo.section.pairing.value": "Borrego · Queijo curado",
  "portfolioP.demo.section.sales": "Ângulo comercial",
  "portfolioP.demo.section.sales.value": "Ícone de investimento",
  "portfolioP.demo.section.sensory": "Perfil sensorial",
  "portfolioP.demo.section.sensory.value": "Corpo cheio · Tanino 7/10",
  "portfolioP.demo.section.similar": "Comparável a",
  "portfolioP.demo.section.similar.value": "Latour · Mouton 2018",
  "portfolioP.demo.section.price": "Posicionamento",
  "portfolioP.demo.section.price.value": "850€ · Super-premium",
  "portfolioP.demo.section.tasting_detail": "Prova detalhada",
  "portfolioP.demo.section.tasting_detail.value": "16,2/20 · Decantar 2h",
  "portfolioP.demo.section.service": "Serviço",
  "portfolioP.demo.section.service.value": "17°C · Copo Bordeaux",
  "portfolioP.demo.section.wset": "Ref WSET",
  "portfolioP.demo.section.wset.value": "Caso L3",
  "portfolioP.demo.section.maps": "Mapas",
  "portfolioP.demo.section.maps.value": "Médoc · Vista parcelar",
  "portfolioP.demo.section.quiz": "Quiz",
  "portfolioP.demo.section.quiz.value": "12 perguntas",
  "portfolioP.demo.section.resources": "Recursos",
  "portfolioP.demo.section.resources.value": "PDF · Slides · Fichas",

  "product.pos.b2c": "Para apaixonados pelo vinho",
  "product.pos.b2b": "Para profissionais do vinho",
  "product.pos.education": "Para educação e formação",
  "product.ideal_for": "Ideal para",
  "product.key_benefits": "Benefícios-chave",

  "audience.beginners": "Iniciantes",
  "audience.wine_lovers": "Apaixonados pelo vinho",
  "audience.travellers": "Viajantes",
  "audience.casual_learners": "Curiosos",
  "audience.wset_students": "Estudantes WSET",
  "audience.sommeliers": "Escanções",
  "audience.wine_pros": "Profissionais do vinho",
  "audience.advanced_enthusiasts": "Entusiastas avançados",
  "audience.importers": "Importadores",
  "audience.distributors": "Distribuidores",
  "audience.wine_merchants": "Garrafeiras",
  "audience.wine_educators": "Formadores",
  "audience.consultants": "Consultores",
  "audience.wine_students": "Estudantes de vinho",
  "audience.hospitality_pros": "Profissionais de hotelaria",
  "audience.wine_schools": "Escolas de vinho",
  "audience.cert_students": "Estudantes em certificação",
  "audience.sales_teams": "Equipas comerciais",
  "audience.educators": "Educadores",
  "audience.tourism": "Projetos de enoturismo",
  "audience.wineries": "Produtores",
  "audience.wine_regions": "Regiões vinícolas",
  "audience.tourism_orgs": "Organizações de turismo",
  "audience.wine_brands": "Marcas de vinho",
  "audience.wine_tourism_ops": "Operadores de enoturismo",
  "audience.wine_bars": "Bares de vinhos",
  "audience.events": "Eventos",
  "audience.hospitality": "Hotelaria",

  "benefit.interactive_maps": "Mapas de vinho interativos",
  "benefit.major_regions": "Principais regiões do mundo",
  "benefit.essential_terroir": "Conhecimento essencial de terroirs",
  "benefit.mobile_friendly": "Acesso otimizado para mobile",
  "benefit.detailed_maps": "Mapas detalhados",
  "benefit.expanded_appellations": "Cobertura ampliada das denominações",
  "benefit.in_depth_terroir": "Informação aprofundada de terroir",
  "benefit.advanced_edu": "Conteúdo educativo avançado",
  "benefit.full_premium_maps": "Acesso completo a mapas premium",
  "benefit.pro_region_db": "Base de dados profissional de regiões",
  "benefit.advanced_insights": "Insights avançados de terroir",
  "benefit.business_ready": "Recursos prontos para o negócio",
  "benefit.structured_paths": "Percursos de aprendizagem estruturados",
  "benefit.regional_courses": "Cursos por região vinícola",
  "benefit.practical_knowledge": "Conhecimentos práticos",
  "benefit.personalized": "Acompanhamento personalizado",
  "benefit.instant_answers": "Respostas instantâneas",
  "benefit.interactive_learning": "Aprendizagem interativa",
  "benefit.expertise_on_demand": "Expertise em vinho à medida",
  "benefit.interactive_exploration": "Exploração interativa",
  "benefit.detailed_regions": "Regiões vinícolas detalhadas",
  "benefit.educational_content": "Conteúdo educativo",
  "benefit.multi_device": "Acesso mobile e desktop",
  "benefit.seamless_integration": "Integração web sem fricção",
  "benefit.engagement_boost": "Maior engagement de visitantes",
  "benefit.terroir_storytelling": "Storytelling de terroir interativo",
  "benefit.no_tech": "Sem necessidade de conhecimentos técnicos",
  "benefit.qr_code": "Experiências por QR code",
  "benefit.interactive_pages": "Páginas de garrafa interativas",
  "benefit.customer_engagement": "Envolvimento do cliente",
  "benefit.guided_tasting": "Provas guiadas",
  "benefit.mobile_first": "Experiência mobile-first",
  "benefit.enhanced_interaction": "Maior interação com visitantes",

  "product.portfolio.tagline": "Ligue cada garrafa à sua história.",
  "product.portfolio.desc": "Crie experiências digitais que permitem aos clientes descobrir a origem, o terroir e a identidade por trás de cada garrafa.",
  "product.tasting.tagline": "Transforme provas de vinho em experiências imersivas.",
  "product.tasting.desc": "Ofereça percursos de prova interativos que ajudam os visitantes a compreender vinhos, regiões e terroirs, aumentando o engagement.",
  "product.coach.tagline": "O seu companheiro pessoal de aprendizagem do vinho.",
  "product.coach.desc": "Uma app de microlearning ao estilo Duolingo que ajuda a compreender mais depressa regiões, castas, denominações e terroirs.",
  "product.maps.tagline": "Mapas de vinho interativos pensados para educação e engagement.",
  "product.maps.desc": "Explore as regiões vinícolas através de mapas interativos com design cuidado que dão vida a terroirs e denominações.",
  "product.iframe.tagline": "Leve mapas de vinho interativos para o seu próprio site.",
  "product.iframe.desc": "Integre facilmente os mapas geoVINUM no seu site, plataforma educativa ou projeto de enoturismo.",
  "product.academy.tagline": "Aprenda vinho com mapas, terroirs e exemplos reais.",
  "product.academy.desc": "Uma plataforma de aprendizagem interativa pensada para tornar a educação do vinho visual, envolvente e fácil de compreender.",
  "coach.plan.weekly": "Plano semanal",
  "coach.plan.yearly": "Plano anual",
  "coach.plan.lifetime": "Plano vitalício",
  "coach.per.week": "/ semana",
  "coach.per.year": "/ ano",
  "coach.per.month": "/ mês",
  "coach.per.one_time": "pagamento único",
  "coach.equivalent": "Equivalente",
  "coach.badge.flexible": "Flexível",
  "coach.badge.best_value": "Melhor valor",
  "coach.badge.lifetime": "Acesso vitalício",
  "coach.feature.learning_content": "Acesso completo ao conteúdo",
  "coach.feature.quizzes": "Quizzes interativos",
  "coach.feature.personalized": "Recomendações personalizadas",
  "coach.feature.ai_coaching": "Coaching IA",
  "coach.feature.all_features": "Acesso completo a todas as funcionalidades",
  "coach.feature.premium_paths": "Percursos premium",
  "coach.feature.exclusive_content": "Conteúdo exclusivo",
  "coach.feature.priority_updates": "Atualizações prioritárias",
  "coach.feature.permanent_access": "Acesso permanente",
  "coach.feature.future_updates": "Futuras atualizações incluídas",
  "coach.feature.premium_content": "Acesso a conteúdo premium",
  "product.type.app": "Aplicação",
  "product.type.physical": "Produto físico",
  "product.type.platform": "Plataforma",
  "product.type.service": "Serviço",
  "home.cta.explore_atlas": "Explorar Atlas",
  "home.cta.browse_courses": "Ver cursos",
  "home.cta.browse_collection": "Ver coleção",
  "home.cta.discover_iframe": "Descobrir Iframe",
  "home.cta.join_waitlist": "Lista de espera",
  "home.s1.eyebrow": "Explore regiões",
  "home.s1.title": "Navegue regiões, denominações e castas com",
  "home.s1.title_em": "mapas interativos.",
  "home.s2.eyebrow": "Aprenda vinho",
  "home.s2.title": "Cursos estruturados, certificações e",
  "home.s2.title_em": "conteúdo educativo.",
  "home.s3.eyebrow": "Colecione regiões",
  "home.s3.title": "Mapas premium e",
  "home.s3.title_em": "posters educativos.",
  "home.s4.eyebrow": "Para profissionais",
  "home.s4.title": "Integre, envolva e",
  "home.s4.title_em": "fidelize os seus clientes.",
  "footer.heading.products": "Produtos",
  "footer.heading.resources": "Recursos",
  "footer.heading.company": "Empresa",
  "footer.heading.social": "Social",
  "footer.link.about": "Sobre",
  "footer.link.contact": "Contacto",
  "footer.link.newsletter": "Newsletter",
  "footer.link.support": "Suporte",

  // ----- Newsletter signup (PT) -----
  "newsletter.placeholder.email": "teu@email.com",
  "newsletter.submit": "Subscrever",
  "newsletter.consent.label": "Aceito receber a newsletter mensal da geoVINUM e consinto o tratamento do meu endereço de email para esse fim. Posso cancelar a subscrição a qualquer momento através do link presente em cada email.",
  "newsletter.success": "Obrigado! Subscrição confirmada. Verifica a tua caixa de entrada.",
  "newsletter.error.consent_required": "Aceita o consentimento para te subscreveres.",
  "newsletter.error.generic": "A subscrição falhou. Tenta novamente.",
  // ----- Newsletter standalone page (PT) -----
  "newsletterPage.meta.title": "Newsletter — geoVINUM",
  "newsletterPage.meta.desc": "Subscreve a newsletter mensal geoVINUM — denominações, terroirs, provas e novidades do vinhedo.",
  "newsletterPage.eyebrow": "Mantém-te atualizado",
  "newsletterPage.title": "A Carta dos Terroirs",
  "newsletterPage.subtitle": "A newsletter geoVINUM — um email por mês. Novos mapas, denominação em destaque, provas e histórias do vinhedo. Sem spam, cancelas com um clique.",
  "newsletterPage.form.eyebrow": "Subscrever",
  "newsletterPage.contactSent.title": "Mensagem recebida — obrigado!",
  "newsletterPage.contactSent.desc": "Recebemos a tua mensagem. A Audrey responde em 48h (dias úteis). Recebeste uma cópia por email. Já que estás por cá, queres ficar em contacto pela newsletter?",

  // ----- Contact form (PT) -----
  "contact.eyebrow": "Contacto",
  "contact.title": "Entra em contacto",
  "contact.subtitle": "Uma pergunta, uma parceria ou um comentário? Escreve-nos — respondemos em 48h em dias úteis.",
  "contact.field.name": "Nome",
  "contact.field.email": "Email",
  "contact.field.subject": "Assunto",
  "contact.field.message": "Mensagem",
  "contact.submit": "Enviar mensagem",
  "contact.consent.label": "Consinto que a geoVINUM trate os meus dados pessoais para responder ao meu pedido. Base legal: o meu consentimento (RGPD art. 6.1.a). Posso retirá-lo a qualquer momento escrevendo para hello@geovinum.io.",
  "contact.success.title": "Mensagem enviada",
  "contact.success.desc": "Recebemos a tua mensagem e responderemos em breve.",
  "contact.error.consent_required": "Aceita o consentimento para enviar a mensagem.",
  "contact.error.generic": "Não foi possível enviar a mensagem. Tenta novamente.",
  "nav.boutique": "Loja",
  "nav.learn": "Aprender",
  "nav.journal": "Diário",
  "nav.pro": "Pro",
  "nav.login": "Entrar",
  "nav.logout": "Sair",
  "nav.account": "Conta",
  "nav.chat": "Conversar",
  "nav.search_placeholder": "Pesquisar regiões, vinhos, lições…",
  "shop.maps.title": "Mapas",
  "shop.books.title": "Livros",
  "shop.ebooks.title": "Ebooks",
  "shop.courses.title": "Cursos",
  "shop.bundles.title": "Coffrets",
  "shop.empty": "Ainda não há produtos nesta categoria.",
  "common.home": "Início",
  "common.search": "Pesquisar",
  "common.loading": "A carregar…",
  "common.no_results": "Sem resultados",
  "common.see_all": "Ver todos os resultados de",
  "common.try_another": "Tente outra palavra-chave.",
  "common.you_might_also": "Também poderá gostar",
  "pillar.explore": "Explorar",
  "pillar.discover": "Descobrir",
  "pillar.experience": "Viver",
  "pillar.learn": "Aprender",
  "pillar.collect": "Colecionar",
  "pillar.integrate": "Integrar",
  "product.status.available": "Disponível",
  "product.status.coming_soon": "Em breve",
  "product.cta.discover": "Descobrir",
  "product.cta.visit": "Visitar",
  "product.cta.contact": "Contactar",
  "product.cta.notify": "Avisar-me",
  "product.cta.become_beta": "Tornar-se Beta tester",
  "product.status.beta_program": "Em breve · Programa Beta",
  "home.pillars.title_a": "Um ecossistema,",
  "home.pillars.title_b": "várias formas de explorar o vinho.",
  "home.pillars.sub": "Dos mapas à aprendizagem e às integrações pro — descubra a família geoVINUM.",
  "home.for.b2c_eyebrow": "Para amantes",
  "home.for.b2c_title": "Descobrir, aprender,",
  "home.for.b2c_title_em": "colecionar.",
  "home.for.b2c_text": "Dos mapas impressos ao coaching IA personalizado — explore o vinho ao seu ritmo.",
  "home.for.b2c_cta": "Descobrir produtos",
  "home.for.b2b_eyebrow": "Para pros",
  "home.for.b2b_title": "Envolva clientes,",
  "home.for.b2b_title_em": "forme equipas, integre.",
  "home.for.b2b_text": "Domínios, escolas e plataformas usam geoVINUM para ensinar mais rápido e envolver por mais tempo.",
  "home.for.b2b_cta": "Soluções Pro",
  "dashboard.welcome_back": "Bem-vinda",
  "dashboard.signed_in_as": "Conectada como",
  "dashboard.your_maps": "Os seus mapas",
  "dashboard.continue_learning": "Continuar a aprender",
  "dashboard.empty_maps": "Ainda não tem compras.",
  "dashboard.empty_learning": "Comece a sua viagem de aprendizagem.",
  "dashboard.enroll": "Inscrever-me",
  "dashboard.enrolling": "A inscrever…",
  "dashboard.continue": "Continuar",
  "dashboard.enrolled_ok": "Inscrição feita! A abrir o curso…",
  "dashboard.already_enrolled": "Já está inscrita neste curso.",
  "dashboard.enroll_failed": "A inscrição falhou. Tente novamente mais tarde.",
  "dashboard.logout": "Sair",

  // --- Iteration 45: added ES/PT translations for critical paths ---
  "auth.welcome_back": "Bem-vindo de volta",
  "auth.welcome_back_sub": "Faça login para continuar.",
  "auth.start_journey": "Comece sua jornada do vinho",
  "auth.create_account": "Crie sua conta",
  "auth.email": "E-mail",
  "auth.password": "Palavra-passe",
  "auth.new_password": "Nova palavra-passe",
  "auth.new_password_label": "Nova palavra-passe",
  "auth.new_password_sub": "Escolha uma nova palavra-passe segura.",
  "auth.confirm_password": "Confirmar palavra-passe",
  "auth.repeat_password": "Repetir palavra-passe",
  "auth.forgot_password": "Esqueceu a palavra-passe?",
  "auth.reset_title": "Redefinir palavra-passe",
  "auth.reset_sub": "Enviaremos um link para redefinir.",
  "auth.check_inbox": "Verifique a sua caixa de entrada",
  "auth.full_name": "Nome completo",
  "auth.full_name_placeholder": "O seu nome",
  "auth.have_account": "Já tem conta ?",
  "auth.no_account": "Não tem conta ?",
  "auth.sign_up": "Criar conta",
  "auth.min6": "Mínimo 6 caracteres",
  "auth.password_updated": "Palavra-passe atualizada",
  "auth.redirecting": "A redirecionar…",
  "auth.err_both": "Preencha os dois campos",
  "auth.err_invalid": "E-mail ou palavra-passe incorretos",
  "auth.err_match": "As palavras-passe não coincidem",
  "auth.err_min": "Demasiado curta (mín. 6 caracteres)",
  "auth.err_generic": "Ocorreu um erro",
  "cookies.save": "Guardar preferências",
  "cookies.accept_all": "Aceitar tudo",
  "cookies.reject_all": "Rejeitar tudo",
  "cookies.customize": "Personalizar",
  "cookies.title": "As suas preferências de cookies",
  "cookies.intro": "Usamos cookies para melhorar a sua experiência.",
  "cookies.necessary": "Necessárias",
  "cookies.analytics": "Analítica",
  "cookies.marketing": "Marketing",
  "cookies.functional": "Funcionais",
  "cookies.manage": "Gerir cookies",
  "cookies.banner_text": "Aceita os cookies para melhorar a sua experiência.",
  "cta.start_free": "Começar grátis",
  "cta.learn_more": "Saber mais",
  "cta.try_now": "Experimentar agora",
  "cta.see_more": "Ver mais",
  "cta.contact_us": "Contacte-nos",
  "cta.download": "Descarregar",
  "cta.book_demo": "Reservar demonstração",
  "cta.subscribe": "Assinar",
  "cta.get_started": "Começar",
  "article.not_found": "Artigo não encontrado",
  "journal.title": "Diário",
  "journal.read_more": "Ler mais",
  "journal.search": "Pesquisar artigos…",
  "journal.no_results": "Sem resultados",
  "cmdk.search": "Pesquisar…",
  "cmdk.no_results": "Sem resultados",
  "cmdk.placeholder": "Procure mapas, regiões, uvas…",
  "lang.fr": "Francês",
  "common.error": "Erro",
  "common.retry": "Tentar novamente",
  "common.close": "Fechar",
  "common.next": "Seguinte",
  "common.previous": "Anterior",
  "common.cancel": "Cancelar",
  "dashboard.welcome": "Olá",
  "dashboard.my_orders": "Os meus pedidos",
  "dashboard.my_subscription": "A minha assinatura",
  "dashboard.my_profile": "O meu perfil",
  "legal.terms": "Termos",
  "legal.privacy": "Privacidade",
  "legal.cookies": "Cookies",
  "thanks.title": "Obrigado pelo seu pedido !",
  "thanks.subtitle": "O seu pedido foi confirmado.",
  "thanks.order_ref": "Referência",
  "thanks.email_sent": "Enviámos um e-mail de confirmação.",
  "thanks.back_home": "Voltar ao início",
  "print.checkout": "Pagar",
  "print.shipping": "Envio",
  "print.subtotal": "Subtotal",
  "print.total": "Total",
  "print.quote": "Calcular envio",
  "print.pay": "Pagar com segurança",
  "trust.secure_payment": "Pagamento seguro",
  "trust.free_shipping": "Envio grátis",
  "trust.satisfaction": "Satisfação garantida",

  // --- Iteration 46: vague 2 ES/PT translations (auto, EN→ES/PT) ---
  "print.paper": "180gsm premium uncoated — museum-grade",
  "pro.shops.cta": "Become a wine-shop partner",
  "journal.ready": "Ready to see it?",
  "journal.pillar.get_started": "Começar",
  "learn.structure": "Structure",
  "thanks.understand_region": "Understand this region",
  "pro.shops.f1_d": "Stock museum-grade maps that sell themselves.",
  "play.h1_a": "Learn by",
  "product.detail1": "High-quality print",
  "pro.schools.f1_t": "Student licenses",
  "library.success": "You're on the list. We'll write when the first editions ship.",
  "print.success_failed": "Pagamento not completed. You can retry from the product page.",
  "journal.pillar.guides": "Guides",
  "play.eyebrow": "Play",
  "print.success_title": "Encomenda received!",
  "print.upsell_title": "Encomenda your map as a poster.",
  "learn.story1": "Wine is often complicated…",
  "product.details": "Details",
  "library.eyebrow": "Coming soon",
  "pro.pros.desc": "Iframe, API and widgets to embed geoVINUM data inside your platform.",
  "cta.log_in": "Entrar",
  "maps.filter": "Filtrar",
  "print.success_back": "Voltar to product",
  "legal.contact_q": "Questions about this document? Contact",
  "journal.filter.all": "Todos",
  "journal.error": "Couldn't reach the journal.",
  "cookies.preferences": "Preferences",
  "pro.shops.headline_b": "understand",
  "trust.premium_print": "Premium print quality",
  "cta.back_to_login": "Voltar to login",
  "play.sub": "Most people forget what they learn. You won't.",
  "pro.pros.f1_t": "Iframe embeds",
  "journal.loading": "A carregar articles…",
  "cta.play": "Play",
  "pro.pros.f2_d": "Query terroir, appellation, and grape data.",
  "learn.sub2": "Understand a wine region in 3 minutes.",
  "journal.pillar.get_started_desc": "Onboarding, how-tos, tutorials.",
  "lang.switch_label": "Language",
  "pro.schools.headline_a": "Faster, clearer,",
  "cookies.essential_desc": "Authentication, security, core functionality.",
  "thanks.receipt_sent": "A receipt has been sent to your email.",
  "play.mini_text": "Zoom, explore, understand.",
  "pro.schools.f1_d": "Bulk access to lessons and quizzes.",
  "cta.contact_sales": "Contact sales",
  "thanks.sub": "Your wine journey starts now.",
  "thanks.test_yourself": "Test yourself",
  "journal.pillar.insights": "Wine insights",
  "journal.empty": "No articles yet.",
  "journal.pillar.for_pros_desc": "Training, selling, scaling with geoVINUM.",
  "dashboard.recommendations": "You might also like",
  "product.rating": "4.8/5 from wine lovers",
  "play.mini_title_c": "of wine.",
  "product.test_knowledge": "Test your knowledge",
  "pro.cta_text": "Tell us about your shop, school or product — we'll tailor a partnership that fits.",
  "print.success_pending": "Verifying your payment…",
  "play.books": "Learn with books",
  "thanks.order_confirmation": "Encomenda confirmation",
  "pro.pros.headline_a": "Integrate",
  "library.sub": "A curated collection of books, tasting notebooks and wine accessories — designed for collectors, sommeliers and lovers of fine geography.",
  "pro.schools.cta": "Discover the academic offer",
  "pro.shops.f2_d": "Maps paired with books and accessories.",
  "play.open_atlas": "Open the atlas",
  "library.notify_me": "Notify me",
  "print.upsell_subtitle": "Premium giclée print on 180gsm uncoated paper, shipped from our local production hub.",
  "library.seo.desc": "A coming-soon shop for wine lovers: handcrafted books, tasting notebooks and curated accessories.",
  "pro.pros.headline_c": "into your product.",
  "cmdk.select": "Open",
  "pro.shops.f2_t": "Gift-ready bundles",
  "cta.go_dashboard": "Go to dashboard",
  "pro.pros.f3_d": "Prebuilt UI blocks you can white-label.",
  "product.learn_this_region": "Learn this region",
  "journal.pillar.guides_desc": "Practical reads to make sense of wine.",
  "cta.view_map": "View map",
  "play.test_knowledge": "Test your wine knowledge",
  "pro.shops.f3_d": "Visual merchandising that invites longer visits.",
  "cta.reset_password": "Reset password",
  "play.h1_b": "playing",
  "pro.hero.eyebrow": "For professionals",
  "library.seo.title": "geoVINUM Library — Books, notebooks & wine accessories",
  "dashboard.your_maps_desc": "Access your purchased maps.",
  "legal.sections": "Sections",
  "journal.eyebrow": "Knowledge hub",
  "library.tiles_eyebrow": "What to expect",
  "pro.pros.title": "Professionals",
  "learn.eyebrow": "Learn",
  "pro.value.sales": "Sales improve",
  "cta.read_lesson": "Read lesson",
  "journal.pillar.for_pros": "For professionals",
  "library.title_a": "The geoVINUM",
  "print.upsell_eyebrow": "Print on demand",
  "pro.schools.f3_t": "Upsell programs",
  "home.final.title": "Iniciar your wine journey",
  "play.books_text": "Hand-picked reads to go deeper — coming soon. Meanwhile, each map doubles as a study tool.",
  "dashboard.explore_more": "Explore more",
  "learn.see_what_you_learn": "See what you learn",
  "pro.pros.f3_t": "Widgets",
  "maps.featured_eyebrow": "Featured",
  "pro.pros.cta": "See the developer docs",
  "pro.schools.title": "Wine schools",
  "thanks.map_ready": "Your map is ready",
  "product.detail3": "Designed for learning",
  "pro.value.teams": "Teams are more confident",
  "product.download_pdf": "Descarregar PDF",
  "maps.no_pieces": "No pieces match those filters yet.",
  "learn.dont_just_learn": "Don't just learn. Remember.",
  "cookies.essential": "Essential",
  "pro.shops.f3_t": "Higher basket size",
  "pro.schools.f2_d": "Large-format maps for on-wall teaching.",
  "common.questions": "questions",
  "pro.pros.f4_d": "Tailored to your domain, brand, or catalog.",
  "product.value_text": "Understand regions, grapes, and structure at a glance.",
  "play.transition_b": "Understand.",
  "cookies.body": "We use cookies to improve your experience. Essential cookies are always on. Analytics and marketing cookies require your consent.",
  "pro.pros.f2_t": "REST API",
  "pro.schools.headline_c": "learning.",
  "pro.schools.headline_b": "more memorable",
  "cookies.analytics_desc": "Traffic measurement and usage analysis.",
  "print.cta": "Encomenda now",
  "footer.rights": "Todos rights reserved.",
  "library.title_b": "library.",
  "learn.sub1": "Short lessons. Clear insights.",
  "product.value_title_a": "See wine",
  "legal.eyebrow": "Legal",
  "play.mini_eyebrow": "Atlas",
  "pro.value.faster": "Visual learning is faster",
  "play.transition_a": "Play. Explore.",
  "cta.take_quiz": "Take quiz",
  "pro.shops.headline_c": "what they taste.",
  "journal.empty_try": "Try another topic or search term.",
  "pro.shops.desc": "Turn every visit into a learning moment. Sell maps, books and gift products that double as teaching tools.",
  "journal.pillar.insights_desc": "Regions, appellations, terroirs explained.",
  "library.tiles_title": "Three product lines",
  "cta.send_link": "Enviar link",
  "cmdk.quick_links": "Quick links",
  "journal.search_placeholder": "Pesquisar articles, regions, guides…",
  "pro.cta": "Work with geoVINUM",
  "pro.pros.f4_t": "Custom maps",
  "pro.hero.sub": "Sell maps, upsell tools, or embed interactive wine visualizations.",
  "cmdk.navigate": "Navigate",
  "pro.schools.f2_t": "Classroom kits",
  "product.value_title_b": "differently",
  "pro.value.understand": "Customers understand better",
  "maps.all_pieces": "Todos pieces",
  "learn.start_here": "Iniciar here",
  "play.learn_basics": "Learn the basics",
  "learn.h1_b": "simply",
  "journal.read_article": "Read article",
  "dashboard.recommendations_desc": "Discover more maps and lessons.",
  "journal.clear_theme": "Clear theme filter",
  "pro.pros.f1_d": "Drop interactive maps into your site in one line.",
  "product.detail2": "Clear visual structure",
  "cta.back_to_journal": "Voltar to the journal",
  "pro.schools.f3_d": "Add visual content to your curriculum.",
  "play.mini_title_b": "Google Maps",
  "learn.story2": "We make it simple.",
  "dashboard.continue_learning_desc": "Pick up where you left off.",
  "pro.pros.headline_b": "geoVINUM",
  "learn.h1_a": "Learn wine",
  "common.from": "From",
  "play.mini_title_a": "The",
  "product.not_found": "Product not found.",
  "pro.shops.headline_a": "Ajuda your customers",
  "pro.schools.desc": "Upsell visual learning tools to students — lessons, maps and quizzes that stick.",
  "pro.shops.f1_t": "Wholesale maps",
  "pro.shops.title": "Wine shops",
  "print.academy_hint": "Subscribers to Academy automatically get –10% on every print.",
  "play.tagline": "Quick, fun, memorable",
  "journal.sub": "Guides, insights and tools to understand wine visually.",
  "cookies.marketing_desc": "Campaign tracking and segmentation.",
  "print.success_subtitle": "Your poster is being prepared and will be shipped to the address you provided.",
  "play.see_wine_world": "See the wine world",

  // === Auto-injected i18n keys for new pages (pt) ===
  "academyM.seo.title": "geoVINUM Academy — Aprende o vinho visualmente",
  "academyM.seo.desc": "Mais de 100 masterclasses em streaming · {days} dias grátis.",
  "academyM.hero.title": "Compreender o vinho, visualmente.",
  "academyM.hero.desc": "Mais de 100 masterclasses em streaming para estudantes WSET, profissionais do vinho e entusiastas. Mapas interativos, terroirs comentados, metodologia de prova — tudo num só lugar.",
  "academyM.hero.binge": "✦ Assista a todas as séries sem limites — acesso total incluído na sua assinatura.",
  "academyM.hero.cta_primary": "Começar {days} dias grátis",
  "academyM.hero.cta_secondary": "Descobrir as séries",
  "academyM.hero.trust.no_commitment": "Sem compromisso",
  "academyM.hero.trust.cancel_anytime": "Cancele a qualquer momento",
  "academyM.hero.trust.hd_streaming": "Streaming HD",
  "academyM.why.eyebrow": "Porquê Academy",
  "academyM.why.title": "O vinho, finalmente claro",
  "academyM.why.desc": "Uma abordagem visual e progressiva do vinho, pensada por profissionais para profissionais e aficionados avançados.",
  "academyM.why.visual.title": "Pedagogia visual",
  "academyM.why.visual.desc": "Mapas interativos, esquemas, visualizações 3D dos terroirs e denominações. Vê o que aprende.",
  "academyM.why.wset.title": "Nível WSET",
  "academyM.why.wset.desc": "Programa alinhado com os padrões WSET 2 e 3. Ideal para rever, aprofundar ou começar uma carreira no vinho.",
  "academyM.why.experts.title": "Especialistas certificados",
  "academyM.why.experts.desc": "Masterclasses conduzidas por sommeliers diplomados, enólogos e jornalistas especializados.",
  "academyM.audience.eyebrow": "Para quem",
  "academyM.audience.title": "Concebido para 3 perfis",
  "academyM.audience.wset.title": "Estudantes WSET",
  "academyM.audience.wset.desc": "Prepare as certificações WSET 2 e 3 com um suporte visual incomparável. Rever uma região leva 15 minutos em vez de uma hora.",
  "academyM.audience.wset.badge": "Mais popular",
  "academyM.audience.pros.title": "Profissionais do vinho",
  "academyM.audience.pros.desc": "Sommeliers, comerciantes, restauradores: aumente o seu nível nas regiões que vende ou apresenta aos seus clientes.",
  "academyM.audience.amateurs.title": "Aficionados avançados",
  "academyM.audience.amateurs.desc": "Adora vinho e quer ir além dos rótulos. Compreender o terroir, o clima, a história — sem jargão.",
  "academyM.how.eyebrow": "Como funciona",
  "academyM.how.title": "3 passos para começar",
  "academyM.how.step1.title": "Ativar o teste",
  "academyM.how.step1.desc": "Inscreva-se em 30 segundos. {days} dias de acesso total, sem cartão para começar.",
  "academyM.how.step2.title": "Escolha uma série",
  "academyM.how.step2.desc": "Navegue mais de 100 masterclasses por categoria: regiões, castas, prova, negócio.",
  "academyM.how.step3.title": "Assista e progrida",
  "academyM.how.step3.desc": "Streaming HD, modo offline na app geoVINUM Atlas. Continue onde parou.",
  "academyM.preview.eyebrow": "Pré-visualização",
  "academyM.preview.title": "Algumas séries da biblioteca",
  "academyM.preview.see_all": "Ver todas as séries",
  "academyM.preview.loading": "A carregar séries…",
  "academyM.slider.featured": "Em destaque",
  "academyM.slider.videos": "vídeos",
  "academyM.slider.goto": "Ir para a série {n}",
  "academyM.final.title": "Comece os seus {days} dias grátis",
  "academyM.final.desc": "Sem cartão. Sem compromisso. Cancele a qualquer momento no seu perfil.",
  "academyM.final.cta": "Começar agora",
  "academyW.seo.title": "Academy — geoVINUM",
  "academyW.seo.desc": "As suas masterclasses em streaming.",
  "academyW.upsell.text": "Explore as séries livremente — a assinatura desbloqueia a visualização ilimitada.",
  "academyW.upsell.cta": "Teste 7 dias grátis",
  "academyW.hero.eyebrow_featured": "Em destaque esta semana",
  "academyW.hero.default_title": "Compreender o vinho, visualmente.",
  "academyW.hero.default_desc": "Mais de 100 masterclasses em streaming, mapas interativos e um atlas pedagógico para estudantes WSET, sommeliers e aficionados.",
  "academyW.hero.watch": "Ver a série",
  "academyW.hero.trial30": "30 dias grátis",
  "academyW.hero.search_cta": "Procurar uma série…",
  "academyW.chip.all": "Tudo",
  "academyW.cat.wine_insights": "Wine insights",
  "academyW.cat.guides": "Guias",
  "academyW.cat.get_started": "Começar",
  "academyW.cat.for_professionals": "Para profissionais",
  "academyW.loading": "A carregar séries…",
  "academyW.empty.title": "Ainda não há séries publicadas",
  "academyW.empty.desc": "As séries Academy estarão disponíveis aqui em breve. Entretanto, descubra os nossos Atlas.",
  "academyW.series_count_one": "série",
  "academyW.series_count_other": "séries",
  "academyW.search.placeholder": "Procurar por série, tema, região…",
  "academyW.search.no_results": "Sem resultados para « {q} »",
  "academyW.videos_label": "vídeos",
  "atlasP.not_found.title": "Produto Atlas não encontrado",
  "atlasP.not_found.cta": "Ver todos os Atlas",
  "atlasP.bc.home": "Início",
  "atlasP.bc.atlas": "Atlas",
  "atlasP.trial_days": "{days} dias grátis",
  "atlasP.who.label": "Para quem?",
  "atlasP.when.label": "Quando usar?",
  "atlasP.when.title": "No momento certo",
  "atlasP.why.label": "Porquê?",
  "atlasP.why.title": "O valor único",
  "atlasP.features.eyebrow": "Funcionalidades",
  "atlasP.features.title": "O que {name} lhe traz",
  "atlasP.upsell.title": "Ir mais longe",
  "atlasP.upsell.discover": "Descobrir",
  "atlasP.final.title": "Pronto para experimentar {name}?",
  "atlasP.final.trial": "{days} dias de teste grátis · sem compromisso · cancele a qualquer momento",
  "atlasP.mini.tagline": "Para começar de forma simples",
  "atlasP.mini.cta_primary": "Abrir a app",
  "atlasP.mini.cta_secondary": "Descobrir geoVINUM Atlas",
  "atlasP.mini.price": "Grátis",
  "atlasP.mini.price_note": "Web — sem instalação",
  "atlasP.mini.audience.label": "Iniciantes e curiosos",
  "atlasP.mini.audience.desc": "Aficionados que descobrem o vinho e querem explorar visualmente as regiões vitivinícolas do mundo a partir de qualquer dispositivo.",
  "atlasP.mini.when": "Quando está num restaurante, numa adega ou em viagem e quer perceber rapidamente de onde vem um vinho.",
  "atlasP.mini.why": "Uma app simples, gratuita e intuitiva — a porta de entrada para o universo geoVINUM.",
  "atlasP.mini.feat.1": "Mais de 30 regiões vitivinícolas mapeadas",
  "atlasP.mini.feat.2": "Pesquisa por denominação",
  "atlasP.mini.feat.3": "Geolocalização dos vinhedos",
  "atlasP.mini.feat.4": "Resumo pedagógico por região",
  "atlasP.mini.feat.5": "100% grátis, sem registo",
  "atlasP.mini.upsell.magnum.title": "geoVINUM Atlas",
  "atlasP.mini.upsell.magnum.desc": "Para ir mais longe na pedagogia",
  "atlasP.mini.upsell.academy.title": "geoVINUM Academy",
  "atlasP.mini.upsell.academy.desc": "Mais de 100 masterclasses em streaming",
  "atlasP.magnum.tagline": "A ferramenta de referência para compreender o vinho",
  "atlasP.magnum.cta_primary": "Começar 30 dias grátis",
  "atlasP.magnum.cta_secondary": "Ver a versão Pro",
  "atlasP.magnum.price": "Assinatura mensal",
  "atlasP.magnum.price_note": "30 dias grátis, sem compromisso",
  "atlasP.magnum.audience.label": "Estudantes WSET, profissionais, aficionados avançados",
  "atlasP.magnum.audience.desc": "Quem quer dominar o vinho com uma ferramenta pedagógica completa: sommeliers em formação, estudantes WSET, profissionais da restauração.",
  "atlasP.magnum.when": "Quando prepara um exame WSET, revê uma região antes de um serviço, ou constrói a sua cultura do vinho a longo prazo.",
  "atlasP.magnum.why": "Mais de 100 masterclasses Academy integradas, mapas interativos em alta definição, fichas detalhadas por denominação, atualização contínua.",
  "atlasP.magnum.feat.1": "Todas as regiões do mundo",
  "atlasP.magnum.feat.2": "Mapas interativos em alta definição",
  "atlasP.magnum.feat.3": "Academy incluída: mais de 100 masterclasses",
  "atlasP.magnum.feat.4": "Fichas detalhadas por denominação",
  "atlasP.magnum.feat.5": "Modo offline para viagens",
  "atlasP.magnum.feat.6": "Atualizações automáticas",
  "atlasP.magnum.upsell.pro.title": "geoVINUM Atlas (B2B)",
  "atlasP.magnum.upsell.pro.desc": "Para importadores e comerciantes",
  "atlasP.magnum.upsell.shop.title": "Mapas impressos",
  "atlasP.magnum.upsell.shop.desc": "Pósteres e enquadrados das suas regiões",
  "atlasP.pro.tagline": "A solução B2B para os profissionais do vinho",
  "atlasP.pro.cta_primary": "Solicitar uma demonstração",
  "atlasP.pro.cta_secondary": "Teste de 30 dias",
  "atlasP.pro.price": "Sob orçamento",
  "atlasP.pro.price_note": "Assinatura B2B anual · 30 dias de teste",
  "atlasP.pro.audience.label": "Importadores, distribuidores, comerciantes, escolas",
  "atlasP.pro.audience.desc": "Estruturas que precisam de formar as suas equipas comerciais, equipar a sua força de vendas, ou integrar a geoVINUM no seu site e catálogo.",
  "atlasP.pro.when": "Quando gere uma equipa comercial, uma sala de prova, uma escola de vinhos ou um site de e-commerce que precisa de vender a história antes do produto.",
  "atlasP.pro.why": "Conta multiutilizador, acesso a toda a biblioteca Academy, API para integrar os nossos mapas no seu site (iframe), formações à medida, suporte prioritário.",
  "atlasP.pro.feat.1": "Contas multiutilizador (equipas)",
  "atlasP.pro.feat.2": "Acesso completo a Academy + Atlas",
  "atlasP.pro.feat.3": "API e iframe para o seu site",
  "atlasP.pro.feat.4": "Mapas white-label personalizáveis",
  "atlasP.pro.feat.5": "Formações à medida incluídas",
  "atlasP.pro.feat.6": "Suporte dedicado e onboarding",
  "atlasP.pro.feat.7": "Relatórios de uso por equipa",
  "atlasP.pro.upsell.iframe.title": "Iframe geoVINUM",
  "atlasP.pro.upsell.iframe.desc": "Integre os nossos mapas no seu site",
  "atlasP.pro.upsell.business.title": "Descobrir Business",
  "atlasP.pro.upsell.business.desc": "Portfolio, Tasting e mais",
  "blogP.seo.title": "Blog — geoVINUM",
  "blogP.seo.desc": "Artigos, guias e conselhos para compreender o vinho visualmente.",
  "blogP.bc.home": "Início",
  "blogP.bc.blog": "Blog",
  "blogP.title": "Blog geoVINUM",
  "blogP.intro": "Wine insights, guias práticos, primeiros passos e conteúdos para profissionais — todo o nosso editorial do vinho num só lugar.",
  "blogP.search.placeholder": "Procurar um artigo…",
  "blogP.chip.all": "Tudo",
  "blogP.cat.wine_insights": "Wine insights",
  "blogP.cat.guides": "Guias",
  "blogP.cat.get_started": "Começar",
  "blogP.cat.for_professionals": "Para profissionais",
  "blogP.cat.ebook": "Ebook",
  "blogP.cat.cepage": "Castas",
  "ebookF.eyebrow": "Ebook gratuito",
  "ebookF.title": "Descarregue o ebook",
  "ebookF.intro": "Deixe o seu email abaixo para receber o PDF de imediato. Também lhe enviamos uma cópia por email para o reler em qualquer dispositivo.",
  "ebookF.email_label": "Email",
  "ebookF.email_placeholder": "voce@dominio.com",
  "ebookF.consent_text": "Aceito que a geoVINUM conserve este email para me enviar o ebook e responder a qualquer pedido relacionado. Direito de acesso, retificação e eliminação a qualquer momento.",
  "ebookF.newsletter_label": "Subscrever também a newsletter geoVINUM (1 a 2 emails/mês — cancelar com um clique).",
  "ebookF.submit": "Receber o ebook",
  "ebookF.submitting": "A enviar…",
  "ebookF.success_title": "Ebook pronto",
  "ebookF.success_email_sent": "Também lhe enviámos o link por email.",
  "ebookF.success_msg": "O download deve abrir num novo separador. Se não, use o botão abaixo.",
  "ebookF.download_btn": "Descarregar agora",
  "ebookF.pages_label": "páginas",
  "ebookF.gdpr_hint": "Os seus dados ficam na geoVINUM. Sem spam.",
  "ebookF.error_email": "Um email válido é obrigatório.",
  "ebookF.error_consent": "Marque a caixa de consentimento para descarregar o ebook.",
  "blogP.loading": "A carregar…",
  "blogP.empty.title": "Ainda sem artigos",
  "blogP.empty.no_results": "Sem resultados para « {q} »",
  "blogP.read_minutes": "min",
  "blogP.article.loading": "A carregar…",
  "blogP.article.not_found": "Artigo não encontrado",
  "blogP.article.back": "Voltar ao blog",
  "blogP.article.by": "Por",
  "blogP.article.read_time": "min de leitura",
  "blogP.article.cross.eyebrow": "Ir mais longe",
  "blogP.article.cross.title": "Explore o universo geoVINUM",
  "blogP.article.cross.shop_label": "Loja",
  "blogP.article.cross.shop_title": "Mapas dos vinhedos",
  "blogP.article.cross.shop_desc": "Pósteres, enquadrados, canvas",
  "blogP.article.cross.academy_label": "Academy",
  "blogP.article.cross.academy_title": "Masterclasses em streaming",
  "blogP.article.cross.academy_desc": "7 dias grátis",
  "blogP.article.cross.terroir_label": "geoVINUM app",
  "blogP.article.cross.terroir_title": "Fale com o terroir",
  "blogP.article.cross.terroir_desc": "Grátis para experimentar · 4 idiomas",
  "bizM.eyebrow": "Comparativo",
  "bizM.title": "Quando · Quem · Porquê",
  "bizM.subtitle": "Encontre num relance a solução geoVINUM que corresponde à sua necessidade.",
  "bizM.col.solution": "Solução",
  "bizM.col.when": "Quando usar",
  "bizM.col.who": "Para quem",
  "bizM.col.why": "Porquê",
  "bizM.col.action": "Ação",
  "bizM.soon": "Em breve",
  "bizM.discover": "Descobrir",
  "bizM.label.when": "Quando",
  "bizM.label.who": "Para quem",
  "bizM.label.why": "Porquê",
  "bizM.magnum.name": "geoVINUM Atlas",
  "bizM.magnum.when": "Preparar um exame WSET, subir de nível, equipar uma equipa de sommelier",
  "bizM.magnum.who": "Sommeliers, estudantes WSET, profissionais de restauração",
  "bizM.magnum.why": "A ferramenta pedagógica mais completa do mercado, mais de 100 masterclasses incluídas",
  "bizM.pro.name": "geoVINUM Atlas",
  "bizM.pro.when": "Formar uma força de vendas, criar um catálogo interativo, equipar uma sala de prova",
  "bizM.pro.who": "Importadores, distribuidores, comerciantes, escolas de vinhos",
  "bizM.pro.why": "Multiutilizador, API white-label, suporte dedicado, ROI mensurável",
  "bizM.iframe.name": "Iframe",
  "bizM.iframe.when": "Tem um site e quer contar a história do terroir antes de vender",
  "bizM.iframe.who": "Vinícolas, sindicatos, postos de turismo, e-commerce do vinho",
  "bizM.iframe.why": "Integração em 1 linha, mapas em alta definição, multi-idioma",
  "bizM.portfolio.name": "Portfolio",
  "bizM.portfolio.when": "Quer digitalizar a experiência do cliente na adega ou loja",
  "bizM.portfolio.who": "Vinícolas, cooperativas, boutiques experienciais",
  "bizM.portfolio.why": "QR imersivos, storytelling visual, transformam visitas em receita",
  "bizM.tasting.name": "Tasting",
  "bizM.tasting.when": "Anima feiras, salões do vinho, eventos enoturísticos",
  "bizM.tasting.who": "Feiras profissionais, organizadores de eventos, hotelaria premium",
  "bizM.tasting.why": "Gamificação lúdica e memorável — aumenta a captação de leads",

  // === searchP keys (pt) ===
  "searchP.seo.title": "Pesquisar — geoVINUM",
  "searchP.seo.desc": "Pesquise no blog, Academy e loja geoVINUM.",
  "searchP.title": "Pesquisar",
  "searchP.intro": "Encontre artigos, séries Academy e mapas impressos — tudo num só lugar.",
  "searchP.placeholder": "Procure uma região, tema, casta, série…",
  "searchP.chip.all": "Tudo",
  "searchP.chip.blog": "Blog",
  "searchP.chip.academy": "Academy",
  "searchP.chip.shop": "Loja",
  "searchP.kind.blog": "Blog",
  "searchP.kind.academy": "Academy",
  "searchP.kind.shop": "Loja",
  "searchP.loading": "A pesquisar…",
  "searchP.from": "a partir de",
  "searchP.empty.prompt": "Escreva algo para começar a pesquisar.",
  "searchP.empty.no_results_title": "Sem resultados",
  "searchP.empty.no_results_desc": "Tente outra região, casta ou tema.",

  // === dashboard keys (pt) ===
  "dashboard.track_shipment": "Acompanhar envio",
  "dashboard.view_all_orders": "Ver todas as encomendas",

  // === hero slides (pt) ===
  "hero.label.for": "Para quem",
  "hero.label.why": "Porquê",
  "hero.see_all": "Todos os produtos",
  "hero.slide.promise.eye": "geoVINUM",
  "hero.slide.promise.title": "Compreender o vinho, visualmente.",
  "hero.slide.promise.for": "Amadores, estudantes e profissionais — de Bordeaux a Mendoza",
  "hero.slide.promise.why": "+3 000 denominações vitivinícolas cartografadas — explore, aprenda e escolha o vinho sem jargão.",
  "hero.slide.promise.cta": "Descobrir geoVINUM",
  "hero.slide.mini.eye": "geoVINUM Atlas",
  "hero.slide.mini.title": "Descubra o vinho no telemóvel, grátis.",
  "hero.slide.mini.for": "Iniciantes curiosos e viajantes",
  "hero.slide.mini.why": "30+ regiões mapeadas, geolocalização, app web 100% grátis.",
  "hero.slide.mini.cta": "Abrir geoVINUM Atlas",
  "hero.slide.terroir.eye": "geoVINUM app",
  "hero.slide.terroir.title": "Fale com o terroir. Em qualquer lugar, em qualquer idioma.",
  "hero.slide.terroir.for": "Amadores, wine bars, comerciantes, escolas e produtores",
  "hero.slide.terroir.why": "Uma só app IA para tudo — perguntas, flashcards, prova às cegas, foto-para-guia, multijogador. PWA, sem instalação.",
  "hero.slide.terroir.cta": "Abrir geoVINUM app",
  "hero.slide.magnum.eye": "geoVINUM Atlas",
  "hero.slide.magnum.title": "Domine o vinho, visualmente.",
  "hero.slide.magnum.for": "Estudantes WSET, sommeliers, profissionais",
  "hero.slide.magnum.why": "100+ masterclasses em vídeo + mapas interativos. 30 dias grátis.",
  "hero.slide.magnum.cta": "Experimentar geoVINUM Atlas grátis",
  "hero.slide.academy.eye": "Academy",
  "hero.slide.academy.title": "Masterclasses do vinho, a pedido.",
  "hero.slide.academy.for": "Qualquer um que queira compreender mesmo o vinho",
  "hero.slide.academy.why": "Assista a 100+ masterclasses HD. 7 dias grátis, depois 9€/mês.",
  "hero.slide.academy.cta": "Começar 7 dias grátis",
  "hero.slide.shop.eye": "Loja",
  "hero.slide.shop.title": "Mapas do vinho para pendurar na parede.",
  "hero.slide.shop.for": "Colecionadores, restaurantes, presentes",
  "hero.slide.shop.why": "Pósteres premium, enquadrados, canvas — impressos na UE, envio mundial.",
  "hero.slide.shop.cta": "Ver os mapas",
  "hero.slide.pro.eye": "geoVINUM Atlas",
  "hero.slide.pro.title": "Equipe a sua equipa de vinho.",
  "hero.slide.pro.for": "Importadores, distribuidores, escolas",
  "hero.slide.pro.why": "Contas multiutilizador, API white-label, formações à medida, suporte prioritário.",
  "hero.slide.pro.cta": "Reservar uma demo",
  "hero.slide.iframe.eye": "Iframe",
  "hero.slide.iframe.title": "Integre os nossos mapas no seu site.",
  "hero.slide.iframe.for": "Vinícolas, postos de turismo, e-commerce",
  "hero.slide.iframe.why": "1 linha de HTML, mapas interativos HD, multi-idioma.",
  "hero.slide.iframe.cta": "Obter o código",
  "hero.slide.blog.eye": "Blog",
  "hero.slide.blog.title": "O vinho explicado, um artigo de cada vez.",
  "hero.slide.blog.for": "Aprendizes para toda a vida",
  "hero.slide.blog.why": "Insights, guias, primeiros passos e dicas pro — todas as semanas.",
  "hero.slide.blog.cta": "Ler o blog",

  // === hero slides extra (pt) ===
  "hero.soon": "Em breve",
  "hero.slide.portfolio.eye": "Portfolio",
  "hero.slide.portfolio.title": "Transforme a sua adega numa história.",
  "hero.slide.portfolio.for": "Vinícolas, cooperativas, boutiques experienciais",
  "hero.slide.portfolio.why": "QR imersivos e storytelling visual — transforme visitas em receita.",
  "hero.slide.portfolio.cta": "Descobrir Portfolio",
  "hero.slide.tasting.eye": "Tasting",
  "hero.slide.tasting.title": "Prova gamificada para os seus eventos.",
  "hero.slide.tasting.for": "Feiras, organizadores de eventos, hotelaria premium",
  "hero.slide.tasting.why": "Gamificação lúdica e memorável — aumenta a captação de leads.",
  "hero.slide.tasting.cta": "Descobrir Tasting",
  "hero.slide.coach.eye": "Coach",
  "hero.slide.coach.title": "O seu coach de vinho IA, 24/7.",
  "hero.slide.coach.for": "Qualquer um que queira um mentor pessoal do vinho",
  "hero.slide.coach.why": "Pergunte tudo: harmonização, regiões, prova — respostas de especialista instantâneas.",
  "hero.slide.coach.cta": "Descobrir Coach",

  // === catalog cta (pt) ===
  "cta.full_catalog": "Descarregar o catálogo (PDF)",
  "cta.product_catalog": "Ficha do produto (PDF)",

  // === catalog capture (pt) ===
  "catalogCapture.eyebrow": "GRÁTIS · PDF · 13 páginas",
  "catalogCapture.title": "Descarregue o catálogo completo",
  "catalogCapture.subtitle": "Apenas o seu email — partilhamos novidades duas vezes por ano, sem spam.",
  "catalogCapture.email": "Email *",
  "catalogCapture.first_name": "Nome próprio (opcional)",
  "catalogCapture.company": "Empresa (opcional)",
  "catalogCapture.category_placeholder": "O seu perfil? (opcional)",
  "catalogCapture.cta": "Descarregar PDF",
  "catalogCapture.loading": "A preparar o PDF…",
  "catalogCapture.skip": "Ignorar — abrir diretamente o PDF",
  "catalogCapture.close": "Fechar",
  "catalogCapture.err.email": "Indique um email válido, por favor.",
  "catalogCapture.privacy": "Ao descarregar, aceita ser contactado(a) sobre a geoVINUM. Cancele em 1 clique.",
  "catalogCapture.cat.domaines": "Vinícola",
  "catalogCapture.cat.cavistes": "Comerciante de vinhos",
  "catalogCapture.cat.negociants": "Casa de negócio",
  "catalogCapture.cat.restaurateurs": "Restaurante",
  "catalogCapture.cat.distributeurs": "Distribuidor",
  "catalogCapture.cat.importateurs": "Importador",
  "catalogCapture.cat.ecoles": "Escola de vinho",
  "catalogCapture.cat.drinks_tech": "Drinks Tech (startup)",
  "catalogCapture.cat.e_commerce": "E-commerce vinho",
  "catalogCapture.cat.autre": "Outro",

  "nav.cartes": "Mapas hub",

  "iframeLive.eyebrow": "Demonstração interativa",
  "iframeLive.title": "Teste o mapa em qualquer denominação.",
  "iframeLive.subtitle": "Escreva o nome de uma denominação, escolha na lista, o mapa recarrega ao vivo — exatamente como aparecerá no seu site.",
  "iframeLive.placeholder": "Sancerre, Chablis, Côte-Rôtie…",
  "iframeLive.cta": "Testar",
  "iframeLive.suggestions_label": "Sugestões:",
  "iframeLive.footer": "A demonstração usa exatamente o mesmo componente que os artigos do blog e o seu futuro embed.",

  "iframeInstall.eyebrow": "Instalação",
  "iframeInstall.title": "Em 4 passos, o seu site exibe o mapa.",
  "iframeInstall.subtitle": "Sem framework imposto. Se conseguir criar uma rota de servidor, consegue integrar geoVINUM.",
  "iframeInstall.step1.title": "1. Subscrever e obter a sua chave API",
  "iframeInstall.step1.body": "Escolha a sua subscrição abaixo. Após o pagamento, recebe imediatamente a sua chave API EtOH por email.",
  "iframeInstall.step2.title": "2. Pedir o token",
  "iframeInstall.step2.body": "A partir do seu servidor, troque a sua chave API por um token JWT curto (1h, formato `{ \"access\": \"<JWT>\" }`) — exatamente como a rota que alimenta este site. Coloque-o em cache 55 min e reencaminhe-o para o front. **Nunca exponha a chave API no navegador.**",
  "iframeInstall.step3.title": "3. Ligar o seu campo de denominação dinâmico",
  "iframeInstall.step3.body": "No seu CMS ou template, leia o nome da denominação a partir da ficha de produto (`product.appellation`, um campo ACF, um metafield Shopify, etc.) e passe-o como `app=` no URL do iframe. Um único campo — cada mapa adapta-se sozinho.",
  "iframeInstall.step4.title": "4. O mapa aparece em cada ficha",
  "iframeInstall.step4.body": "É tudo. Em cada ficha de produto, um iframe `https://iframe.geovinum.app/?token=…&app=<denominação>` é gerado automaticamente — o mapa correto aparece para cada produto, sem qualquer trabalho manual página a página.",
  "iframeInstall.personalize.title": "Personalizar o snippet com a sua chave",
  "iframeInstall.personalize.body": "Cole a sua chave API EtOH abaixo — os snippets vão preencher-se automaticamente. A sua chave **fica no seu navegador**, nunca é enviada para os nossos servidores.",
  "iframeInstall.personalize.placeholder": "ex: 362ec4bf-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
  "iframeInstall.personalize.clear": "Limpar",
  "iframeInstall.personalize.ready": "Snippets personalizados ({0}) — a cópia usa a chave completa.",
  "iframeInstall.show_key": "Mostrar chave",
  "iframeInstall.hide_key": "Ocultar chave",
  "iframeInstall.copy": "Copiar",
  "iframeInstall.copied": "Copiado",
  "iframeInstall.help_prefix": "Precisa de ajuda para integrar?",
  "iframeInstall.help_link": "Contacte-nos",
  "iframeInstall.help_suffix": "— a nossa equipa apoia-o gratuitamente.",

  "cartes.seo_title": "Mapas das denominações vinícolas | geoVINUM",
  "cartes.seo_desc": "Explore cada denominação vinícola num mapa interativo: Sancerre, Chablis, Saint-Émilion, Châteauneuf-du-Pape… geoVINUM cartografa o vinhedo mundial.",
  "cartes.eyebrow": "Catálogo dos terroirs",
  "cartes.h1_line1": "Cada denominação,",
  "cartes.h1_line2": "um mapa interativo.",
  "cartes.subtitle": "Descubra {0} denominações cartografadas em detalhe, com as suas parcelas cadastrais, as suas castas e os seus artigos dedicados.",
  "cartes.subtitle_empty": "Descubra as nossas denominações cartografadas em detalhe, com as suas parcelas cadastrais, as suas castas e os seus artigos dedicados.",
  "cartes.search_placeholder": "Procurar uma denominação… (Sancerre, Châteauneuf, etc.)",
  "cartes.region_all": "Todas",
  "cartes.loading": "A carregar o catálogo…",
  "cartes.empty": "Nenhuma denominação corresponde.",
  "cartes.empty_reset": "Reiniciar",
  "cartes.count_one": "{0} denominação",
  "cartes.count_many": "{0} denominações",
  "cartes.card.eyebrow": "Denominação",
  "cartes.card.preview": "Pré-visualizar mapa",
  "cartes.card.hide_preview": "Ocultar mapa",
  "cartes.card.read": "Ler",
  "cartes.cta.eyebrow": "Para profissionais",
  "cartes.cta.title": "Este mesmo mapa no seu site, em 4 passos.",
  "cartes.cta.subtitle": "Vinotecas, editores, agências de enoturismo: integre o mapa interativo de qualquer denominação no seu site com o nosso iframe.",
  "cartes.cta.button": "Descobrir o iframe geoVINUM",
  "cartes.region.bordeaux":  "Bordéus & Sudoeste",
  "cartes.region.bourgogne": "Borgonha",
  "cartes.region.beaujolais":"Beaujolais",
  "cartes.region.rhone":     "Vale do Ródano",
  "cartes.region.loire":     "Vale do Loire",
  "cartes.region.champagne": "Champanhe & Alsácia",
  "cartes.region.languedoc": "Languedoc-Roussillon",
  "cartes.region.provence":  "Provença & Córsega",
  "cartes.region.jura":      "Jura & Saboia",
  "cartes.region.italy":     "Itália",
  "cartes.region.spain":     "Espanha",
  "cartes.region.portugal":  "Portugal",
  "cartes.region.other":     "Outros terroirs",

  // geoVINUM app landing page
  "terroir.seo.title": "geoVINUM app — A IA do vinho, em qualquer lugar, em qualquer idioma",
  "terroir.seo.desc": "Fale com o terroir. Descubra denominações, castas, produtores e eventos. Crie sua rota dos vinhos, aprenda com flashcards, prove às cegas. Para amadores e profissionais.",
  "terroir.hero.eyebrow": "geoVINUM app · Powered by geoVINUM",
  "terroir.hero.h1_a": "Fale com o terroir.",
  "terroir.hero.h1_b": "Em qualquer lugar, em qualquer idioma.",
  "terroir.hero.sub": "Seu companheiro IA do vinho. Pergunte tudo sobre denominações, castas, produtores, eventos. Construa sua rota enoturística, aprenda com flashcards, prove às cegas, fotografe uma etiqueta e deixe a IA contar. Uma só app — para amadores e profissionais.",
  "terroir.hero.cta_primary": "Abrir a web app",
  "terroir.hero.cta_secondary": "Criar uma conta",
  "terroir.hero.pwa_note": "Sem download · Funciona em qualquer dispositivo · 4 idiomas",
  "terroir.demo.title": "Experimente agora",
  "terroir.demo.sub": "Pré-visualização ao vivo aqui mesmo. Toque numa pergunta para começar.",
  "terroir.demo.open_in_new": "Abrir numa nova aba",
  "terroir.usecases.title": "O que você pode fazer com geoVINUM app",
  "terroir.usecases.sub": "Casos de uso, não uma lista de funcionalidades. Escolha o que combina com seu momento.",
  "terroir.uc.amateur.tag": "Para amadores",
  "terroir.uc.amateur.1.title": "Estou a planear um fim de semana enológico",
  "terroir.uc.amateur.1.body": "Indique datas e região. geoVINUM app monta seu itinerário — vinícolas abertas, provas, restaurantes, as melhores denominações para provar agora.",
  "terroir.uc.amateur.2.title": "Tenho uma garrafa à frente e quero entender",
  "terroir.uc.amateur.2.body": "Fotografe o rótulo. A IA conta a história da denominação, da casta, do produtor, o que esperar no copo e com o que harmonizar.",
  "terroir.uc.amateur.3.title": "Quero aprender um pouco todos os dias",
  "terroir.uc.amateur.3.body": "Micro-cursos diários, flashcards de castas e regiões, prova às cegas. 5 minutos por dia para ser um provador confiante.",
  "terroir.uc.amateur.4.title": "Quero jogar com amigos",
  "terroir.uc.amateur.4.body": "Quiz multijogador sobre denominações, castas, regiões. Perfeito para uma noite, um clube de prova ou um jantar.",
  "terroir.uc.pro.tag": "Para profissionais",
  "terroir.uc.pro.1.title": "Tenho um wine bar",
  "terroir.uc.pro.1.body": "Transforme sua carta de vinhos numa experiência de prova divertida. Os clientes leem um QR code e exploram o terroir de cada garrafa, sozinhos ou em equipa. Mais engajamento, mais garrafas vendidas.",
  "terroir.uc.pro.2.title": "Sou comerciante de vinhos",
  "terroir.uc.pro.2.body": "Adicione um assistente 'geoVINUM app' no seu site ou loja. Seus clientes fazem perguntas, recebem recomendações, montam sua adega — sem precisar de sommelier 24h.",
  "terroir.uc.pro.3.title": "Lidero um clube de enologia",
  "terroir.uc.pro.3.body": "Anime os encontros mensais com jogos multijogador, provas às cegas, flashcards temáticas. Os membros voltam porque aprendem se divertindo.",
  "terroir.uc.pro.4.title": "Ensino vinho",
  "terroir.uc.pro.4.body": "Escolas e academias: módulos, quizzes e sessões de prova às cegas. Acompanhe o progresso, gamifique suas aulas, transforme as salas em experiências.",
  "terroir.uc.pro.5.title": "Sou produtor",
  "terroir.uc.pro.5.body": "Integre geoVINUM app na página da sua quinta. Os visitantes perguntam sobre sua denominação, suas castas, sua história — mesmo quando você não pode atender.",
  "terroir.audiences.title": "Uma só app, dois usos",
  "terroir.audiences.amateur.title": "Amador",
  "terroir.audiences.amateur.body": "Grátis para experimentar · PWA · 4 idiomas.",
  "terroir.audiences.pro.title": "Profissional",
  "terroir.audiences.pro.body": "Wine bars, comerciantes, clubes, escolas, produtores. Experiência personalizada, gamificada, escalável.",
  "terroir.audiences.pro.cta": "Fale com nossa equipa",
  "terroir.how.title": "Como funciona",
  "terroir.how.1.title": "Abra a app",
  "terroir.how.1.body": "Sem instalação. Uma app web — mesma experiência em qualquer dispositivo.",
  "terroir.how.2.title": "Pergunte, escaneie, jogue",
  "terroir.how.2.body": "Escreva uma pergunta, fotografe um rótulo, comece flashcards, desafie um amigo.",
  "terroir.how.3.title": "Melhore todos os dias",
  "terroir.how.3.body": "Micro-cursos, provas às cegas, itinerários. Sua cultura do vinho cresce.",
  "terroir.cta.title": "Pronto para falar com o terroir?",
  "terroir.cta.sub": "Grátis para experimentar. Sem cartão. Em qualquer dispositivo.",
  "terroir.cta.web": "Abrir a web app",
  "terroir.cta.ios": "Abrir a app",

  // geoVINUM app — Formulário Demo Pro
  "pro_demo.eyebrow": "Demo Pro · 30 segundos",
  "pro_demo.title": "Reserve uma demo Pro à medida",
  "pro_demo.subtitle": "Wine bar, comerciante, clube, escola, produtor — integramos geoVINUM app no seu estabelecimento com o seu branding, a sua carta, o seu percurso cliente. Custo por visitante, sem compromisso.",
  "pro_demo.bullet_1": "Experiência personalizada no seu site ou por QR code",
  "pro_demo.bullet_2": "Flashcards gamificadas, provas às cegas, quizzes multijogador",
  "pro_demo.bullet_3": "Captura de leads + dashboard analítico incluídos",
  "pro_demo.form.eyebrow": "Fale-nos sobre si",
  "pro_demo.form.title": "Respondemos em 24h",
  "pro_demo.field.name": "O seu nome",
  "pro_demo.field.email": "Email profissional",
  "pro_demo.field.business": "Estabelecimento",
  "pro_demo.field.visitors": "Visitantes / mês",
  "pro_demo.business.wine_bar": "Wine bar",
  "pro_demo.business.merchant": "Comerciante",
  "pro_demo.business.club": "Clube de enologia",
  "pro_demo.business.school": "Escola / Academia",
  "pro_demo.business.producer": "Produtor",
  "pro_demo.business.other": "Outro",
  "pro_demo.consent_text": "Aceito ser contactado pela equipa geoVINUM sobre o meu pedido de demo. Os meus dados são guardados por 24 meses e posso pedir a sua eliminação a qualquer momento (RGPD).",
  "pro_demo.submit": "Pedir demo",
  "pro_demo.reply_time": "Respondemos em 24h úteis",
  "pro_demo.error.consent": "Aceite o consentimento para continuar.",
  "pro_demo.success.title": "Pedido recebido!",
  "pro_demo.success.body": "Verifique a sua caixa de entrada — acabámos de enviar uma confirmação. Para acelerar, reserve já 20 min:",
  "pro_demo.success.calendar": "Reservar 20 min",

  // Home — secções de produtos simplificadas
  "home.s_terroir.eyebrow": "geoVINUM app · A app IA para todos",
  "home.s_terroir.title": "Fale com o terroir.",
  "home.s_terroir.title_em": "em qualquer idioma.",
  "home.s_terroir.sub": "Amadores e profissionais usam a mesma app IA: perguntas sobre denominações, castas, produtores, rota enoturística, foto de rótulo, quiz multijogador, flashcards diárias.",
  "home.s_academy.eyebrow": "Academy · Netflix do terroir",
  "home.s_academy.title": "O vinho também",
  "home.s_academy.title_em": "se vê.",
  "home.s_academy.sub": "100+ masterclasses HD sobre cada região, casta e tradição. Streaming em qualquer dispositivo. 7 dias grátis, depois 9€/mês.",
  "home.s_shop.eyebrow": "Shop · Print on demand",
  "home.s_shop.title": "Mapas do vinho",
  "home.s_shop.title_em": "para pendurar.",
  "home.s_shop.sub": "Pósteres premium, emoldurados, telas — todas as grandes denominações, impressas e enviadas da UE. O atlas do colecionador.",
  "home.s_iframe.eyebrow": "Iframe · Embed B2B",
  "home.s_iframe.title": "O mapa certo em",
  "home.s_iframe.title_em": "cada ficha.",
  "home.s_iframe.sub": "Sites de vinho, marketplaces, importadores: ligue um campo de denominação e os nossos mapas interativos aparecem automaticamente em cada ficha. De 6 000 a ilimitados, pagamento por pedido a partir de 0,01€.",
  "home.cta.discover_terroir": "Descobrir geoVINUM app",
  "home.cta.open_webapp": "Abrir a web app",
  "home.cta.talk_to_sales": "Falar com vendas",
  "home.closing.title": "Um ecossistema, uma promessa.",
  "home.closing.sub": "Grátis para experimentar, em 4 idiomas, em qualquer dispositivo — sem instalar.",
};

const dict = { en, fr, es, pt };

const I18nContext = createContext(null);

function detectInitialLang() {
  if (typeof window !== "undefined") {
    try {
      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (urlLang && LANG_CODES.includes(urlLang)) return urlLang;
    } catch { /* ignore URL parse errors */ }
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LANG_CODES.includes(stored)) return stored;
  } catch { /* localStorage unavailable */ }
  if (typeof navigator !== "undefined") {
    const nav = (navigator.language || "").slice(0, 2).toLowerCase();
    if (LANG_CODES.includes(nav)) return nav;
  }
  return DEFAULT_LANG;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(DEFAULT_LANG);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line
    setLangState(detectInitialLang());
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    try { document.documentElement.setAttribute("lang", lang); } catch { /* SSR */ }
  }, [lang, ready]);

  const setLang = useCallback((next) => {
    if (!LANG_CODES.includes(next)) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* quota or SSR */ }
    // Sync URL ?lang= so refresh/share preserves the choice and aligns
    // with detectInitialLang() priority order (URL > localStorage).
    if (typeof window !== "undefined") {
      try {
        const url = new URL(window.location.href);
        if (next === DEFAULT_LANG) url.searchParams.delete("lang");
        else url.searchParams.set("lang", next);
        window.history.replaceState({}, "", url.toString());
      } catch { /* URL API or replaceState unavailable */ }
    }
    setLangState(next);
  }, []);

  // Lookup with interpolation: t("foo.bar", { name: "X" })
  const t = useCallback(
    (key, vars) => {
      const table = dict[lang] || {};
      let s = table[key];
      if (s == null) s = dict.en[key];
      if (s == null) s = key;
      if (vars && typeof s === "string") {
        s = s.replace(/\{(\w+)\}/g, (_m, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
      }
      return s;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, ready }), [lang, setLang, t, ready]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within <I18nProvider>");
  return ctx;
}

export function useT() { return useI18n().t; }
export function useLang() { return useI18n().lang; }

export function buildAlternates(path) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const trim = (p) => (p || "/").split("?")[0];
  const base = trim(path);
  const out = LANGS.map((l) => ({
    hreflang: l.code,
    href: `${origin}${base}${l.code === DEFAULT_LANG ? "" : `?lang=${l.code}`}`,
  }));
  out.push({ hreflang: "x-default", href: `${origin}${base}` });
  return out;
}
